import type { BlockCategory } from '@bst/types'
export { getGlassmorphPageHtml, DEFAULT_GLASSMORPH_DATA } from './glassmorphPageHtml'
export type { GlassmorphBrand, GlassmorphPageData } from './glassmorphPageHtml'

export { getCinemaLandingPageHtml, DEFAULT_CINEMA_LANDING_DATA } from './cinemaLandingPageHtml'
export type { CinemaLandingBrand, CinemaLandingPageData } from './cinemaLandingPageHtml'

export { getSwissLandingPageHtml, DEFAULT_SWISS_LANDING_DATA } from './swissLandingPageHtml'
export type { SwissLandingBrand, SwissLandingPageData } from './swissLandingPageHtml'

export { getMaximalLandingPageHtml, DEFAULT_MAXIMAL_LANDING_DATA } from './maximalLandingPageHtml'
export type { MaximalLandingBrand, MaximalLandingPageData } from './maximalLandingPageHtml'

export { getBrutalLandingPageHtml, DEFAULT_BRUTAL_LANDING_DATA } from './brutalLandingPageHtml'
export type { BrutalLandingBrand, BrutalLandingPageData } from './brutalLandingPageHtml'

export { getSerifLandingPageHtml, DEFAULT_SERIF_LANDING_DATA } from './serifLandingPageHtml'
export type { SerifLandingBrand, SerifLandingPageData } from './serifLandingPageHtml'

export { getY2kLandingPageHtml, DEFAULT_Y2K_LANDING_DATA } from './y2kLandingPageHtml'
export type { Y2kLandingBrand, Y2kLandingPageData } from './y2kLandingPageHtml'

export { getOrganicLandingPageHtml, DEFAULT_ORGANIC_LANDING_DATA } from './organicLandingPageHtml'
export type { OrganicLandingBrand, OrganicLandingPageData } from './organicLandingPageHtml'

export { getFilmLandingPageHtml, DEFAULT_FILM_LANDING_DATA } from './filmLandingPageHtml'
export type { FilmLandingBrand, FilmLandingPageData } from './filmLandingPageHtml'

export { getBentoLandingPageHtml, DEFAULT_BENTO_LANDING_DATA } from './bentoLandingPageHtml'
export type { BentoLandingBrand, BentoLandingPageData } from './bentoLandingPageHtml'

export { getBentoPortfolioPageHtml, DEFAULT_BENTO_PORTFOLIO_DATA } from './bentoPortfolioPageHtml'
export type { BentoPortfolioPageData } from './bentoPortfolioPageHtml'

export { getCinemaPortfolioPageHtml, DEFAULT_CINEMA_PORTFOLIO_DATA } from './cinemaPortfolioPageHtml'
export type { CinemaPortfolioPageData, CinemaPortfolioBrand } from './cinemaPortfolioPageHtml'

export { getFilmPortfolioPageHtml, DEFAULT_FILM_PORTFOLIO_DATA } from './filmPortfolioPageHtml'
export type { FilmPortfolioPageData } from './filmPortfolioPageHtml'

export { getMinimalPortfolioPageHtml, DEFAULT_MINIMAL_PORTFOLIO_DATA } from './minimalPortfolioPageHtml'
export type { MinimalPortfolioPageData, MinimalPortfolioBrand } from './minimalPortfolioPageHtml'

export { getOrganicPortfolioPageHtml, DEFAULT_ORGANIC_PORTFOLIO_DATA } from './organicPortfolioPageHtml'
export type { OrganicPortfolioPageData } from './organicPortfolioPageHtml'

export { getSwissPortfolioPageHtml, DEFAULT_SWISS_PORTFOLIO_DATA } from './swissPortfolioPageHtml'
export type { SwissPortfolioPageData, SwissPortfolioBrand } from './swissPortfolioPageHtml'

export { getBrutalPortfolioPageHtml, DEFAULT_BRUTAL_PORTFOLIO_DATA } from './brutalPortfolioPageHtml'
export type { BrutalPortfolioPageData, BrutalPortfolioBrand } from './brutalPortfolioPageHtml'

export { getImmersivePortfolioPageHtml, DEFAULT_IMMERSIVE_PORTFOLIO_DATA } from './immersivePortfolioPageHtml'
export type { ImmersivePortfolioPageData, ImmersivePortfolioBrand } from './immersivePortfolioPageHtml'

export { getNeoPortfolioPageHtml, DEFAULT_NEO_PORTFOLIO_DATA } from './neoPortfolioPageHtml'
export type { NeoPortfolioPageData, NeoPortfolioBrand } from './neoPortfolioPageHtml'

// ── Block Registry ────────────────────────────────────────────────────────────

export type FieldType =
  | 'text'
  | 'richtext'
  | 'image'
  | 'video'
  | 'url'
  | 'color'
  | 'number'
  | 'boolean'
  | 'select'
  | 'array'
  | 'font'
  | 'icon'

export interface FieldSchema {
  type: FieldType
  label: string
  default?: unknown
  options?: Array<{ value: string; label: string }>
  min?: number
  max?: number
  placeholder?: string
  itemSchema?: Record<string, FieldSchema>
  stringArray?: boolean
}

export interface BlockDefinition {
  type: string
  label: string
  category: BlockCategory
  icon: string
  description: string
  defaultData: Record<string, unknown>
  schema: Record<string, FieldSchema>
  tags?: string[]
  /** Not offered in the block library (created by the AI only). */
  hidden?: boolean
}

const registry = new Map<string, BlockDefinition>()

export function registerBlock(def: BlockDefinition): void {
  if (registry.has(def.type)) {
    console.warn(`Block type "${def.type}" already registered — overwriting`)
  }
  registry.set(def.type, def)
}

export function getBlock(type: string): BlockDefinition | undefined {
  return registry.get(type)
}

export function getAllBlocks(): BlockDefinition[] {
  return Array.from(registry.values()).filter((b) => !b.hidden)
}

export function getBlocksByCategory(category: BlockCategory): BlockDefinition[] {
  return getAllBlocks().filter((b) => b.category === category)
}

export function searchBlocks(query: string): BlockDefinition[] {
  const q = query.toLowerCase()
  return getAllBlocks().filter(
    (b) =>
      b.label.toLowerCase().includes(q) ||
      b.description.toLowerCase().includes(q) ||
      b.tags?.some((t) => t.includes(q)),
  )
}

// ── Built-in Block Definitions ────────────────────────────────────────────────

registerBlock({
  type: 'heading',
  label: 'Заголовок',
  category: 'text',
  icon: 'H',
  description: 'Заголовок H1–H6 с полным контролем типографики',
  tags: ['title', 'h1', 'h2', 'text'],
  defaultData: {
    text: 'Ваш заголовок здесь',
    level: 'h1',
    align: 'center',
    fontFamily: '',
    color: '',
  },
  schema: {
    text: { type: 'richtext', label: 'Текст' },
    level: {
      type: 'select',
      label: 'Уровень',
      default: 'h1',
      options: [
        { value: 'h1', label: 'H1 — очень крупный' },
        { value: 'h2', label: 'H2 — крупный' },
        { value: 'h3', label: 'H3 — средний' },
        { value: 'h4', label: 'H4 — небольшой' },
      ],
    },
    align: {
      type: 'select',
      label: 'Выравнивание',
      default: 'center',
      options: [
        { value: 'left', label: 'Слева' },
        { value: 'center', label: 'По центру' },
        { value: 'right', label: 'Справа' },
      ],
    },
    fontSize: { type: 'number', label: 'Размер шрифта (px)', default: 0, min: 0, max: 160 },
    fontWeight: {
      type: 'select', label: 'Жирность', default: '',
      options: [
        { value: '', label: 'По умолчанию' },
        { value: '400', label: '400 — обычный' },
        { value: '600', label: '600 — полужирный' },
        { value: '700', label: '700 — жирный' },
        { value: '800', label: '800 — очень жирный' },
        { value: '900', label: '900 — ультражирный' },
      ],
    },
    fontFamily: { type: 'font', label: 'Шрифт', default: '' },
    color: { type: 'color', label: 'Цвет текста' },
    lineHeight: { type: 'number', label: 'Межстрочный интервал (0 = авто)', default: 0, min: 0, max: 3 },
    letterSpacing: { type: 'number', label: 'Межбуквенный интервал (em)', default: 0, min: -0.1, max: 0.5 },
    textTransform: {
      type: 'select', label: 'Регистр', default: '',
      options: [
        { value: '', label: 'Без изменений' },
        { value: 'uppercase', label: 'ЗАГЛАВНЫЕ' },
        { value: 'lowercase', label: 'строчные' },
        { value: 'capitalize', label: 'Каждое Слово' },
      ],
    },
    gradientEnabled: { type: 'boolean', label: 'Градиентный текст', default: false },
    gradientFrom: { type: 'color', label: 'Цвет градиента (начало)', default: '#5b6af0' },
    gradientTo: { type: 'color', label: 'Цвет градиента (конец)', default: '#ec4899' },
    gradientAngle: { type: 'number', label: 'Угол градиента (°)', default: 135, min: 0, max: 360 },
  },
})

registerBlock({
  type: 'paragraph',
  label: 'Параграф',
  category: 'text',
  icon: '¶',
  description: 'Текстовый блок с Rich Text редактором',
  tags: ['text', 'body', 'content'],
  defaultData: {
    text: 'Введите текст...',
    align: 'left',
    fontFamily: '',
    fontSize: 16,
    lineHeight: 1.7,
    color: '',
  },
  schema: {
    text: { type: 'richtext', label: 'Текст' },
    align: {
      type: 'select',
      label: 'Выравнивание',
      default: 'left',
      options: [
        { value: 'left', label: 'Слева' },
        { value: 'center', label: 'По центру' },
        { value: 'right', label: 'Справа' },
        { value: 'justify', label: 'По ширине' },
      ],
    },
    fontFamily: { type: 'font', label: 'Шрифт', default: '' },
    fontSize: { type: 'number', label: 'Размер шрифта (px)', default: 16, min: 10, max: 72 },
    lineHeight: { type: 'number', label: 'Межстрочный интервал', default: 1.7, min: 1, max: 3 },
    color: { type: 'color', label: 'Цвет текста' },
  },
})

registerBlock({
  type: 'image',
  label: 'Изображение',
  category: 'media',
  icon: '🖼',
  description: 'Одиночное изображение с настройками crop, filters и alt-текста',
  tags: ['photo', 'picture', 'media'],
  defaultData: {
    src: '',
    alt: '',
    caption: '',
    objectFit: 'cover',
    aspectRatio: '16/9',
    linkUrl: '',
  },
  schema: {
    src: { type: 'image', label: 'Изображение' },
    alt: { type: 'text', label: 'Alt текст', placeholder: 'Описание для SEO и accessibility' },
    caption: { type: 'text', label: 'Подпись' },
    objectFit: {
      type: 'select',
      label: 'Заполнение',
      default: 'cover',
      options: [
        { value: 'cover', label: 'Cover' },
        { value: 'contain', label: 'Contain' },
        { value: 'fill', label: 'Fill' },
      ],
    },
  },
})

registerBlock({
  type: 'slider',
  label: 'Слайдер',
  category: 'slider',
  icon: '◀▶',
  description: 'Динамический слайдер на Swiper.js — 10+ режимов отображения',
  tags: ['carousel', 'swiper', 'gallery', 'dynamic'],
  defaultData: {
    slides: [
      { image: '', title: 'Слайд 1', subtitle: '' },
      { image: '', title: 'Слайд 2', subtitle: '' },
    ],
    effect: 'slide',
    autoplay: true,
    autoplayDelay: 4000,
    loop: true,
    showDots: true,
    showArrows: true,
    slidesPerView: 1,
    spaceBetween: 0,
  },
  schema: {
    slides: { type: 'array', label: 'Слайды', itemSchema: {
      title: { type: 'text', label: 'Заголовок' },
      subtitle: { type: 'text', label: 'Подзаголовок' },
    } },
    effect: {
      type: 'select',
      label: 'Эффект',
      default: 'slide',
      options: [
        { value: 'slide', label: 'Слайд' },
        { value: 'fade', label: 'Fade' },
        { value: 'cube', label: 'Cube' },
        { value: 'coverflow', label: 'Coverflow' },
        { value: 'flip', label: 'Flip' },
      ],
    },
    autoplay: { type: 'boolean', label: 'Автопрокрутка', default: true },
    autoplayDelay: { type: 'number', label: 'Задержка (мс)', default: 4000, min: 1000, max: 10000 },
    loop: { type: 'boolean', label: 'Зациклить', default: true },
    showDots: { type: 'boolean', label: 'Точки', default: true },
    showArrows: { type: 'boolean', label: 'Стрелки', default: true },
  },
})

registerBlock({
  type: 'hero-centered',
  label: 'Hero — Центрированный',
  category: 'hero',
  icon: '⬛',
  description: 'Главный экран с центрированным контентом: заголовок, подзаголовок, кнопки',
  tags: ['hero', 'header', 'banner', 'landing'],
  defaultData: {
    title: 'Создайте сайт\nнового поколения',
    subtitle: 'Профессионально, быстро и красиво — без единой строки кода.',
    primaryButton: { text: 'Начать бесплатно', url: '#' },
    secondaryButton: { text: 'Смотреть демо', url: '#' },
    background: { type: 'color', color: '#0d0d0d' },
  },
  schema: {
    title: { type: 'richtext', label: 'Заголовок' },
    subtitle: { type: 'text', label: 'Подзаголовок' },
  },
})

registerBlock({
  type: 'button',
  label: 'Кнопка',
  category: 'text',
  icon: '⬜',
  description: 'Кнопка с настройками стиля, размера и hover-эффектов',
  tags: ['cta', 'link', 'action'],
  defaultData: {
    text: 'Нажмите сюда',
    url: '#',
    variant: 'primary',
    size: 'md',
    align: 'center',
    newTab: false,
    fontFamily: '',
    borderRadius: 8,
    paddingX: 28,
    paddingY: 12,
  },
  schema: {
    text: { type: 'text', label: 'Текст кнопки' },
    url: { type: 'url', label: 'Ссылка' },
    variant: {
      type: 'select',
      label: 'Стиль',
      default: 'primary',
      options: [
        { value: 'primary', label: 'Primary' },
        { value: 'secondary', label: 'Secondary' },
        { value: 'outline', label: 'Outline' },
        { value: 'ghost', label: 'Ghost' },
      ],
    },
    size: {
      type: 'select',
      label: 'Размер',
      default: 'md',
      options: [
        { value: 'sm', label: 'Small' },
        { value: 'md', label: 'Medium' },
        { value: 'lg', label: 'Large' },
        { value: 'xl', label: 'X-Large' },
      ],
    },
    align: {
      type: 'select',
      label: 'Выравнивание',
      default: 'center',
      options: [
        { value: 'left', label: 'Слева' },
        { value: 'center', label: 'По центру' },
        { value: 'right', label: 'Справа' },
      ],
    },
    fontFamily: { type: 'font', label: 'Шрифт', default: '' },
    borderRadius: { type: 'number', label: 'Скругление (px)', default: 8, min: 0, max: 50 },
    paddingX: { type: 'number', label: 'Отступ по горизонтали (px)', default: 28, min: 8, max: 80 },
    paddingY: { type: 'number', label: 'Отступ по вертикали (px)', default: 12, min: 4, max: 40 },
    newTab: { type: 'boolean', label: 'Открыть в новой вкладке', default: false },
  },
})

registerBlock({
  type: 'countdown',
  label: 'Таймер обратного отсчёта',
  category: 'interactive',
  icon: '⏱',
  description: 'Таймер до конкретной даты — создаёт срочность',
  tags: ['timer', 'countdown', 'deadline', 'urgency'],
  defaultData: {
    targetDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    showDays: true,
    showHours: true,
    showMinutes: true,
    showSeconds: true,
    labels: { days: 'дней', hours: 'часов', minutes: 'минут', seconds: 'секунд' },
  },
  schema: {
    targetDate: { type: 'text', label: 'Дата и время окончания (ISO)' },
    showDays: { type: 'boolean', label: 'Показывать дни', default: true },
    showHours: { type: 'boolean', label: 'Показывать часы', default: true },
  },
})

registerBlock({
  type: 'form',
  label: 'Форма',
  category: 'form',
  icon: '📋',
  description: 'Форма обратной связи с настройкой полей, валидацией и интеграциями',
  tags: ['contact', 'lead', 'feedback', 'form'],
  defaultData: {
    fields: [
      { id: '1', type: 'text', label: 'Имя', placeholder: 'Ваше имя', required: true },
      { id: '2', type: 'email', label: 'Email', placeholder: 'your@email.com', required: true },
      { id: '3', type: 'tel', label: 'Телефон', placeholder: '+7 (___) ___-__-__', required: false },
      { id: '4', type: 'text', label: 'Сообщение', placeholder: 'Ваше сообщение', required: false },
    ],
    submitText: 'Отправить',
    successMessage: 'Спасибо! Мы свяжемся с вами в ближайшее время.',
    notifications: {
      email: '',
      telegram: '',
    },
  },
  schema: {
    fields: { type: 'array', label: 'Поля формы', itemSchema: {
      type: { type: 'select', label: 'Тип поля', default: 'text', options: [
        { value: 'text', label: 'Текст' },
        { value: 'email', label: 'Email' },
        { value: 'tel', label: 'Телефон' },
        { value: 'textarea', label: 'Многострочный текст' },
        { value: 'select', label: 'Выпадающий список' },
        { value: 'checkbox', label: 'Чекбокс (согласие)' },
        { value: 'date', label: 'Дата' },
        { value: 'rating', label: 'Рейтинг (звёзды)' },
        { value: 'file', label: 'Загрузка файла' },
        { value: 'step-break', label: '── Разделитель шагов ──' },
      ] },
      label: { type: 'text', label: 'Название поля' },
      placeholder: { type: 'text', label: 'Допустимые типы (для файла: .pdf,.jpg,.png)', placeholder: 'Placeholder / подсказка' },
      options: { type: 'text', label: 'Варианты через запятую (для списка)', placeholder: 'Вариант 1, Вариант 2, Вариант 3' },
      required: { type: 'boolean', label: 'Обязательное', default: false },
      minLength: { type: 'text', label: 'Мин. длина (символов)', placeholder: 'напр. 2' },
      maxLength: { type: 'text', label: 'Макс. длина (символов)', placeholder: 'напр. 200' },
      pattern: { type: 'text', label: 'Регулярное выражение (pattern)', placeholder: 'напр. [0-9]{10}' },
      validationError: { type: 'text', label: 'Сообщение об ошибке', placeholder: 'Введите корректное значение' },
      showIf: { type: 'text', label: 'Показывать если (условие)', placeholder: 'fieldName=value или fieldName!=value' },
    } },
    submitText: { type: 'text', label: 'Текст кнопки', default: 'Отправить' },
    successMessage: { type: 'text', label: 'Сообщение после отправки' },
    redirectUrl: { type: 'text', label: 'Редирект после отправки (URL)', placeholder: '/thank-you' },
  },
})

registerBlock({
  type: 'video',
  label: 'Видео',
  category: 'media',
  icon: '▶',
  description: 'Встроенное видео: YouTube, Vimeo или загруженный файл',
  tags: ['youtube', 'vimeo', 'video', 'media'],
  defaultData: { url: '', title: 'Видео', aspectRatio: '16/9' },
  schema: {
    url: { type: 'url', label: 'Ссылка на видео (YouTube / Vimeo)', placeholder: 'https://youtube.com/watch?v=...' },
    title: { type: 'text', label: 'Название' },
  },
})

registerBlock({
  type: 'features',
  label: 'Преимущества',
  category: 'content',
  icon: '✦',
  description: 'Сетка преимуществ с иконками, заголовками и описаниями',
  tags: ['features', 'benefits', 'icons', 'grid'],
  defaultData: {
    title: 'Почему выбирают нас',
    subtitle: 'Всё что нужно для успешного сайта',
    columns: 3,
    items: [
      { icon: '⚡', title: 'Быстро', description: 'Сайт загружается за менее чем 1 секунду' },
      { icon: '🎨', title: 'Красиво', description: 'Профессиональный дизайн без дизайнера' },
      { icon: '📱', title: 'Адаптивно', description: 'Идеально на любом устройстве' },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок' },
    subtitle: { type: 'text', label: 'Подзаголовок' },
    columns: { type: 'select', label: 'Колонки', default: '3', options: [{ value: '2', label: '2' }, { value: '3', label: '3' }, { value: '4', label: '4' }] },
    items: { type: 'array', label: 'Преимущества', itemSchema: {
      icon: { type: 'text', label: 'Иконка' },
      title: { type: 'text', label: 'Заголовок' },
      description: { type: 'text', label: 'Описание' },
    } },
  },
})

registerBlock({
  type: 'testimonials',
  label: 'Отзывы',
  category: 'content',
  icon: '💬',
  description: 'Карточки с отзывами клиентов',
  tags: ['reviews', 'testimonials', 'social proof'],
  defaultData: {
    title: 'Отзывы клиентов',
    items: [
      { name: 'Анна К.', role: 'Руководитель проекта', text: 'Внедрили за месяц. Производительность команды выросла на 40%.' },
      { name: 'Дмитрий С.', role: 'Предприниматель', text: 'Ровно то, что нужно. Без лишнего — чисто, быстро, эффективно.' },
      { name: 'Мария В.', role: 'Операционный директор', text: 'Автоматизировали рутину и сэкономили 15 часов в неделю.' },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок' },
    items: { type: 'array', label: 'Отзывы', itemSchema: {
      name: { type: 'text', label: 'Имя' },
      role: { type: 'text', label: 'Роль' },
      text: { type: 'richtext', label: 'Отзыв' },
    } },
  },
})

registerBlock({
  type: 'faq',
  label: 'FAQ',
  category: 'interactive',
  icon: '❓',
  description: 'Аккордеон с часто задаваемыми вопросами',
  tags: ['faq', 'questions', 'accordion', 'help'],
  defaultData: {
    title: 'Часто задаваемые вопросы',
    items: [
      { question: 'Как это работает?', answer: 'Выберите блок, заполните текст, опубликуйте. Никаких навыков программирования не нужно.' },
      { question: 'Сколько это стоит?', answer: 'Есть бесплатный план. Платные тарифы открывают расширенные возможности.' },
      { question: 'Можно ли подключить свой домен?', answer: 'Да, на платных тарифах поддерживаются собственные домены.' },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок' },
    items: { type: 'array', label: 'Вопросы и ответы', itemSchema: {
      question: { type: 'text', label: 'Вопрос' },
      answer: { type: 'richtext', label: 'Ответ' },
    } },
  },
})

registerBlock({
  type: 'pricing',
  label: 'Тарифы',
  category: 'commerce',
  icon: '💳',
  description: 'Блок с тарифными планами и сравнением функций',
  tags: ['pricing', 'plans', 'subscription', 'tariff'],
  defaultData: {
    title: 'Тарифные планы',
    plans: [
      { name: 'Free', price: '0', period: 'мес', features: ['1 сайт', 'Поддомен', '500 МБ'], highlighted: false, buttonText: 'Начать бесплатно' },
      { name: 'Personal', price: '790', period: 'мес', features: ['3 сайта', 'Свой домен', '5 ГБ', 'Все блоки'], highlighted: true, buttonText: 'Выбрать' },
      { name: 'Business', price: '1 990', period: 'мес', features: ['10 сайтов', 'CMS', 'E-commerce', 'Аналитика'], highlighted: false, buttonText: 'Выбрать' },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок' },
    plans: { type: 'array', label: 'Тарифы', itemSchema: {
      name: { type: 'text', label: 'Название' },
      price: { type: 'text', label: 'Цена' },
      period: { type: 'text', label: 'Период (мес/год)' },
      buttonText: { type: 'text', label: 'Кнопка' },
      highlighted: { type: 'boolean', label: 'Выделенный' },
    } },
  },
})

registerBlock({
  type: 'cta',
  label: 'CTA — Призыв к действию',
  category: 'content',
  icon: '🎯',
  description: 'Секция с призывом к действию — заголовок и кнопки',
  tags: ['cta', 'call to action', 'conversion', 'banner'],
  defaultData: {
    title: 'Готовы начать?',
    subtitle: 'Создайте первый сайт бесплатно за 5 минут',
    primaryText: 'Начать бесплатно',
    secondaryText: 'Смотреть примеры',
    background: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок' },
    subtitle: { type: 'text', label: 'Подзаголовок' },
    primaryText: { type: 'text', label: 'Кнопка (основная)' },
    secondaryText: { type: 'text', label: 'Кнопка (вторичная)' },
    background: { type: 'color', label: 'Цвет фона' },
  },
})

registerBlock({
  type: 'quote',
  label: 'Цитата',
  category: 'text',
  icon: '❝',
  description: 'Блок-цитата с автором и источником',
  tags: ['quote', 'blockquote', 'cite'],
  defaultData: { text: 'Великие дела начинаются с маленьких шагов.', author: '', source: '' },
  schema: {
    text: { type: 'richtext', label: 'Текст цитаты' },
    author: { type: 'text', label: 'Автор', placeholder: 'Имя автора' },
    source: { type: 'text', label: 'Источник', placeholder: 'Книга, фильм...' },
  },
})

registerBlock({
  type: 'array',
  label: 'Список',
  category: 'text',
  icon: '≡',
  description: 'Маркированный или нумерованный список',
  tags: ['list', 'ul', 'ol', 'bullet'],
  defaultData: { ordered: false, items: ['Первый пункт', 'Второй пункт', 'Третий пункт'] },
  schema: {
    ordered: { type: 'boolean', label: 'Нумерованный', default: false },
    items: { type: 'array', label: 'Пункты', stringArray: true },
  },
})

registerBlock({
  type: 'divider',
  label: 'Разделитель',
  category: 'layout',
  icon: '—',
  description: 'Горизонтальная линия для разделения секций',
  tags: ['divider', 'hr', 'separator', 'line'],
  defaultData: { style: 'solid', color: '#e0e0e0', width: 100, thickness: 1 },
  schema: {
    style: { type: 'select', label: 'Стиль', default: 'solid', options: [{ value: 'solid', label: 'Сплошная' }, { value: 'dashed', label: 'Пунктир' }, { value: 'dotted', label: 'Точки' }] },
    color: { type: 'color', label: 'Цвет' },
    width: { type: 'number', label: 'Ширина (%)', default: 100, min: 10, max: 100 },
    thickness: { type: 'number', label: 'Толщина (px)', default: 1, min: 1, max: 8 },
  },
})

registerBlock({
  type: 'spacer',
  label: 'Отступ',
  category: 'layout',
  icon: '↕',
  description: 'Пустое пространство для управления вертикальными отступами',
  tags: ['spacer', 'space', 'gap', 'padding'],
  defaultData: { height: 60 },
  schema: {
    height: { type: 'number', label: 'Высота (px)', default: 60, min: 8, max: 400 },
  },
})

registerBlock({
  type: 'gallery',
  label: 'Галерея',
  category: 'gallery',
  icon: '⊞',
  description: 'Сетка изображений с настройкой колонок',
  tags: ['gallery', 'grid', 'photos', 'images'],
  defaultData: { columns: 3, gap: 12, images: [] },
  schema: {
    columns: { type: 'select', label: 'Колонки', default: '3', options: [{ value: '2', label: '2' }, { value: '3', label: '3' }, { value: '4', label: '4' }] },
    gap: { type: 'number', label: 'Отступ (px)', default: 12, min: 0, max: 40 },
    images: { type: 'array', label: 'Изображения', itemSchema: {
      src: { type: 'url', label: 'URL', placeholder: 'https://...' },
      alt: { type: 'text', label: 'Alt текст' },
    } },
  },
})

registerBlock({
  type: 'team',
  label: 'Команда',
  category: 'content',
  icon: '👥',
  description: 'Карточки участников команды с фото и ролями',
  tags: ['team', 'people', 'members', 'about'],
  defaultData: {
    title: 'Наша команда',
    members: [
      { name: 'Алексей Иванов', role: 'CEO & Founder', photo: '', bio: 'Эксперт в области продуктового дизайна' },
      { name: 'Мария Смирнова', role: 'Lead Designer', photo: '', bio: 'Создаёт красивые пользовательские интерфейсы' },
      { name: 'Дмитрий Ков', role: 'CTO', photo: '', bio: 'Архитектор технической части платформы' },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок' },
    members: { type: 'array', label: 'Участники', itemSchema: {
      name: { type: 'text', label: 'Имя' },
      role: { type: 'text', label: 'Роль' },
      bio: { type: 'text', label: 'О себе' },
      photo: { type: 'url', label: 'Фото URL' },
    } },
  },
})

registerBlock({
  type: 'navbar',
  label: 'Навигация',
  category: 'navigation',
  icon: '☰',
  description: 'Шапка сайта с логотипом, ссылками и кнопкой',
  tags: ['navbar', 'header', 'navigation', 'menu'],
  defaultData: {
    logo: 'Логотип',
    links: [{ label: 'Возможности', url: '#' }, { label: 'Тарифы', url: '#' }, { label: 'Блог', url: '#' }],
    ctaText: 'Начать',
    sticky: true,
    transparent: false,
  },
  schema: {
    logo: { type: 'text', label: 'Логотип (текст)' },
    ctaText: { type: 'text', label: 'Текст кнопки' },
    sticky: { type: 'boolean', label: 'Прилипающий', default: true },
    transparent: { type: 'boolean', label: 'Прозрачный фон', default: false },
    links: { type: 'array', label: 'Ссылки меню', itemSchema: {
      label: { type: 'text', label: 'Текст' },
      url: { type: 'url', label: 'Ссылка' },
      dropdown: { type: 'text', label: 'Выпадающее меню (label|url через запятую)', placeholder: 'Услуга 1|/services/1, Услуга 2|/services/2' },
    } },
  },
})

registerBlock({
  type: 'footer',
  label: 'Подвал',
  category: 'navigation',
  icon: '▬',
  description: 'Футер сайта с колонками ссылок и копирайтом',
  tags: ['footer', 'bottom', 'links', 'copyright'],
  defaultData: {
    logo: 'Ваш бренд',
    tagline: 'Краткое описание вашей компании',
    copyright: `© ${new Date().getFullYear()} Название компании. Все права защищены.`,
    columns: [
      { title: 'Продукт', links: [{ label: 'Возможности' }, { label: 'Тарифы' }, { label: 'Шаблоны' }] },
      { title: 'Компания', links: [{ label: 'О нас' }, { label: 'Блог' }, { label: 'Карьера' }] },
      { title: 'Поддержка', links: [{ label: 'Документация' }, { label: 'FAQ' }, { label: 'Контакты' }] },
    ],
  },
  schema: {
    logo: { type: 'text', label: 'Логотип' },
    tagline: { type: 'text', label: 'Слоган' },
    copyright: { type: 'text', label: 'Копирайт' },
    columns: { type: 'array', label: 'Колонки', itemSchema: {
      title: { type: 'text', label: 'Заголовок колонки' },
    } },
  },
})

registerBlock({
  type: 'stats',
  label: 'Статистика',
  category: 'content',
  icon: '📊',
  description: 'Большие числа — конверсии, пользователи, достижения',
  tags: ['stats', 'counter', 'numbers', 'metrics'],
  defaultData: {
    title: '',
    items: [
      { value: '10 000', label: 'Пользователей', prefix: '', suffix: '+' },
      { value: '500', label: 'Шаблонов', prefix: '', suffix: '' },
      { value: '99.9', label: 'Uptime', prefix: '', suffix: '%' },
      { value: '24', label: 'Поддержка', prefix: '', suffix: '/7' },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок (необязательно)', placeholder: '' },
    items: { type: 'array', label: 'Показатели', itemSchema: {
      value: { type: 'text', label: 'Значение' },
      label: { type: 'text', label: 'Подпись' },
      prefix: { type: 'text', label: 'Префикс' },
      suffix: { type: 'text', label: 'Суффикс' },
    } },
  },
})

registerBlock({
  type: 'tabs',
  label: 'Вкладки',
  category: 'interactive',
  icon: '🗂',
  description: 'Переключаемые вкладки — разный контент в одном месте',
  tags: ['tabs', 'tabbed', 'navigation', 'content'],
  defaultData: {
    tabs: [
      { label: 'Вкладка 1', content: 'Содержимое первой вкладки.' },
      { label: 'Вкладка 2', content: 'Содержимое второй вкладки.' },
      { label: 'Вкладка 3', content: 'Содержимое третьей вкладки.' },
    ],
  },
  schema: {
    tabs: { type: 'array', label: 'Вкладки', itemSchema: {
      label: { type: 'text', label: 'Название вкладки' },
      content: { type: 'richtext', label: 'Содержимое' },
    } },
  },
})

registerBlock({
  type: 'map',
  label: 'Карта',
  category: 'embed',
  icon: '🗺',
  description: 'Встроенная карта Google Maps или Яндекс.Карты',
  tags: ['map', 'location', 'address', 'google maps', 'yandex'],
  defaultData: {
    address: 'Москва, Россия',
    height: 400,
    zoom: 14,
    provider: 'yandex',
  },
  schema: {
    address: { type: 'text', label: 'Адрес', placeholder: 'Москва, ул. Примерная, 1' },
    height: { type: 'number', label: 'Высота (px)', default: 400, min: 200, max: 800 },
    zoom: { type: 'number', label: 'Масштаб', default: 14, min: 1, max: 20 },
    provider: {
      type: 'select',
      label: 'Провайдер',
      default: 'yandex',
      options: [
        { value: 'yandex', label: 'Яндекс.Карты' },
        { value: 'google', label: 'Google Maps' },
      ],
    },
  },
})

registerBlock({
  type: 'social-links',
  label: 'Соцсети',
  category: 'navigation',
  icon: '🔗',
  description: 'Иконки социальных сетей со ссылками',
  tags: ['social', 'links', 'telegram', 'vk', 'instagram'],
  defaultData: {
    title: '',
    align: 'center',
    size: 40,
    links: [
      { network: 'telegram', url: '#' },
      { network: 'vk', url: '#' },
      { network: 'instagram', url: '#' },
      { network: 'youtube', url: '#' },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок (необязательно)' },
    align: {
      type: 'select',
      label: 'Выравнивание',
      default: 'center',
      options: [
        { value: 'left', label: 'Слева' },
        { value: 'center', label: 'По центру' },
        { value: 'right', label: 'Справа' },
      ],
    },
    size: { type: 'number', label: 'Размер иконок (px)', default: 40, min: 24, max: 80 },
    links: { type: 'array', label: 'Ссылки', itemSchema: {
      network: { type: 'select', label: 'Сеть', options: [
        { value: 'telegram', label: 'Telegram' },
        { value: 'vk', label: 'ВКонтакте' },
        { value: 'instagram', label: 'Instagram' },
        { value: 'youtube', label: 'YouTube' },
        { value: 'twitter', label: 'Twitter/X' },
        { value: 'facebook', label: 'Facebook' },
        { value: 'github', label: 'GitHub' },
        { value: 'linkedin', label: 'LinkedIn' },
        { value: 'whatsapp', label: 'WhatsApp' },
      ] },
      url: { type: 'url', label: 'Ссылка', placeholder: 'https://...' },
    } },
  },
})

registerBlock({
  type: 'accordion',
  label: 'Аккордеон',
  category: 'interactive',
  icon: '☰',
  description: 'Раскрывающиеся секции с произвольным содержимым',
  tags: ['accordion', 'collapse', 'expand', 'sections'],
  defaultData: {
    allowMultiple: false,
    items: [
      { title: 'Раздел 1', content: 'Содержимое первого раздела.' },
      { title: 'Раздел 2', content: 'Содержимое второго раздела.' },
      { title: 'Раздел 3', content: 'Содержимое третьего раздела.' },
    ],
  },
  schema: {
    allowMultiple: { type: 'boolean', label: 'Несколько открытых', default: false },
    items: { type: 'array', label: 'Разделы', itemSchema: {
      title: { type: 'text', label: 'Заголовок' },
      content: { type: 'richtext', label: 'Содержимое' },
    } },
  },
})

registerBlock({
  type: 'progress',
  label: 'Прогресс-бары',
  category: 'interactive',
  icon: '▬',
  description: 'Полосы прогресса — навыки, достижения, загрузка',
  tags: ['progress', 'bar', 'skills', 'percentage'],
  defaultData: {
    title: '',
    items: [
      { label: 'HTML / CSS', value: 95 },
      { label: 'JavaScript', value: 88 },
      { label: 'React', value: 82 },
      { label: 'TypeScript', value: 75 },
    ],
    color: '#5b6af0',
    showPercent: true,
    height: 8,
    animated: true,
  },
  schema: {
    title: { type: 'text', label: 'Заголовок (необязательно)', placeholder: 'Мои навыки' },
    items: { type: 'array', label: 'Строки', itemSchema: {
      label: { type: 'text', label: 'Название' },
      value: { type: 'number', label: 'Значение (0–100)', min: 0, max: 100 },
    } },
    color: { type: 'color', label: 'Цвет заполнения' },
    height: { type: 'number', label: 'Толщина (px)', default: 8, min: 4, max: 24 },
    showPercent: { type: 'boolean', label: 'Показывать процент', default: true },
  },
})

registerBlock({
  type: 'timeline',
  label: 'Таймлайн',
  category: 'content',
  icon: '⏳',
  description: 'Вертикальная лента событий — история компании, шаги процесса',
  tags: ['timeline', 'history', 'steps', 'process', 'events'],
  defaultData: {
    title: 'История',
    items: [
      { date: '2020', title: 'Основание компании', description: 'Начало работы и первые шаги на рынке' },
      { date: '2021', title: 'Первые клиенты', description: 'Запустили продукт и получили первых 100 пользователей' },
      { date: '2022', title: 'Серия A', description: 'Привлекли инвестиции и масштабировали команду' },
      { date: '2023', title: 'Выход на международный рынок', description: 'Открыли офисы в 3 странах' },
    ],
    accent: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок секции' },
    items: { type: 'array', label: 'События', itemSchema: {
      date: { type: 'text', label: 'Дата / Период' },
      title: { type: 'text', label: 'Заголовок' },
      description: { type: 'richtext', label: 'Описание' },
    } },
    accent: { type: 'color', label: 'Цвет акцента' },
  },
})

registerBlock({
  type: 'before-after',
  label: 'До / После',
  category: 'interactive',
  icon: '◧',
  description: 'Сравнение двух изображений с ползунком',
  tags: ['before', 'after', 'comparison', 'slider', 'reveal'],
  defaultData: {
    beforeImage: '',
    afterImage: '',
    beforeLabel: 'До',
    afterLabel: 'После',
    initialPosition: 50,
  },
  schema: {
    beforeImage: { type: 'url', label: 'Изображение «До»', placeholder: 'https://...' },
    afterImage: { type: 'url', label: 'Изображение «После»', placeholder: 'https://...' },
    beforeLabel: { type: 'text', label: 'Подпись «До»', default: 'До' },
    afterLabel: { type: 'text', label: 'Подпись «После»', default: 'После' },
    initialPosition: { type: 'number', label: 'Начальная позиция (%)', default: 50, min: 10, max: 90 },
  },
})

registerBlock({
  type: 'counter-anim',
  label: 'Счётчик (анимация)',
  category: 'interactive',
  icon: '🔢',
  description: 'Числа с анимацией отсчёта — конверсия, пользователи, достижения',
  tags: ['counter', 'number', 'animate', 'stats', 'scroll'],
  defaultData: {
    items: [
      { value: 10000, label: 'Клиентов', prefix: '', suffix: '+' },
      { value: 500, label: 'Проектов', prefix: '', suffix: '' },
      { value: 99, label: 'Uptime', prefix: '', suffix: '%' },
    ],
    duration: 2000,
    color: '#5b6af0',
  },
  schema: {
    items: { type: 'array', label: 'Счётчики', itemSchema: {
      value: { type: 'number', label: 'Конечное значение', min: 0 },
      label: { type: 'text', label: 'Подпись' },
      prefix: { type: 'text', label: 'Префикс (напр. $)' },
      suffix: { type: 'text', label: 'Суффикс (напр. +)' },
    } },
    duration: { type: 'number', label: 'Длительность анимации (мс)', default: 2000, min: 500, max: 5000 },
    color: { type: 'color', label: 'Цвет чисел' },
  },
})

registerBlock({
  type: 'html-embed',
  label: 'HTML / Iframe',
  category: 'embed',
  icon: '</> ',
  description: 'Произвольный HTML-код, iframe или виджет',
  tags: ['embed', 'html', 'iframe', 'code', 'widget', 'custom'],
  defaultData: {
    html: '<!-- Вставьте код виджета или iframe сюда -->',
    caption: '',
    height: 400,
  },
  schema: {
    html: { type: 'richtext', label: 'HTML-код / iframe', placeholder: '<iframe src="..." ...></iframe>' },
    caption: { type: 'text', label: 'Подпись (необязательно)' },
    height: { type: 'number', label: 'Минимальная высота (px)', default: 400, min: 100, max: 1200 },
  },
})

registerBlock({
  type: 'hero-split',
  label: 'Hero — Разделённый',
  category: 'hero',
  icon: '⬒',
  description: 'Главный экран: текст слева, изображение справа (или наоборот)',
  tags: ['hero', 'split', 'landing', 'banner', 'image'],
  defaultData: {
    title: 'Решения, которые\nменяют бизнес',
    subtitle: 'Мы помогаем компаниям расти быстрее с помощью современных инструментов и экспертной поддержки.',
    primaryButton: 'Попробовать бесплатно',
    secondaryButton: 'Узнать больше',
    primaryButtonUrl: '#',
    secondaryButtonUrl: '#',
    image: '',
    imageAlt: 'Иллюстрация к разделу',
    reverse: false,
  },
  schema: {
    title: { type: 'richtext', label: 'Заголовок' },
    subtitle: { type: 'text', label: 'Подзаголовок' },
    primaryButton: { type: 'text', label: 'Основная кнопка — текст', placeholder: 'Попробовать бесплатно' },
    secondaryButton: { type: 'text', label: 'Вторичная кнопка — текст', placeholder: 'Узнать больше' },
    image: { type: 'image', label: 'Изображение' },
    imageAlt: { type: 'text', label: 'Alt-текст изображения', placeholder: 'Описание для SEO' },
    reverse: { type: 'boolean', label: 'Зеркально (изображение слева)', default: false },
  },
})

registerBlock({
  type: 'logo-strip',
  label: 'Логотипы партнёров',
  category: 'content',
  icon: '◈',
  description: 'Горизонтальная строка с логотипами клиентов или партнёров',
  tags: ['logos', 'partners', 'clients', 'brands', 'trust'],
  defaultData: {
    title: 'Нам доверяют',
    items: [
      { name: 'Компания А', logo: '', url: '#' },
      { name: 'Компания Б', logo: '', url: '#' },
      { name: 'Компания В', logo: '', url: '#' },
      { name: 'Компания Г', logo: '', url: '#' },
      { name: 'Компания Д', logo: '', url: '#' },
    ],
    grayscale: true,
    columns: 5,
  },
  schema: {
    title: { type: 'text', label: 'Заголовок (необязательно)', placeholder: 'Нам доверяют' },
    items: { type: 'array', label: 'Логотипы', itemSchema: {
      name: { type: 'text', label: 'Название компании' },
      logo: { type: 'image', label: 'Логотип' },
      url: { type: 'url', label: 'Ссылка', placeholder: 'https://...' },
    } },
    grayscale: { type: 'boolean', label: 'Оттенки серого', default: true },
    columns: { type: 'number', label: 'Колонок в ряду', default: 5, min: 2, max: 8 },
  },
})

registerBlock({
  type: 'process-steps',
  label: 'Этапы процесса',
  category: 'content',
  icon: '⓪',
  description: 'Пронумерованные шаги процесса или инструкция',
  tags: ['steps', 'process', 'how it works', 'numbered', 'guide'],
  defaultData: {
    title: 'Как это работает',
    subtitle: 'Запустить сайт легко — всего три шага',
    items: [
      { title: 'Выберите шаблон', description: 'Подберите готовый дизайн из библиотеки или начните с чистого листа.' },
      { title: 'Настройте блоки', description: 'Добавляйте и редактируйте блоки перетаскиванием — без кода.' },
      { title: 'Опубликуйте', description: 'Нажмите кнопку — и сайт мгновенно появится в интернете.' },
    ],
    accent: '#5b6af0',
    numbered: true,
  },
  schema: {
    title: { type: 'text', label: 'Заголовок' },
    subtitle: { type: 'text', label: 'Подзаголовок' },
    items: { type: 'array', label: 'Шаги', itemSchema: {
      title: { type: 'text', label: 'Заголовок шага' },
      description: { type: 'text', label: 'Описание' },
    } },
    accent: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
    numbered: { type: 'boolean', label: 'Нумерация', default: true },
  },
})

registerBlock({
  type: 'contact-block',
  label: 'Контактная информация',
  category: 'content',
  icon: '✉',
  description: 'Блок с контактными данными: телефон, email, адрес и другие способы связи',
  tags: ['contact', 'phone', 'email', 'address', 'info'],
  defaultData: {
    title: 'Свяжитесь с нами',
    items: [
      { icon: '📞', label: 'Телефон', value: '+7 (800) 555-35-35', link: 'tel:+78005553535' },
      { icon: '✉', label: 'Email', value: 'hello@example.ru', link: 'mailto:hello@example.ru' },
      { icon: '📍', label: 'Адрес', value: 'Москва, ул. Примерная, д. 1', link: '' },
      { icon: '🕐', label: 'Режим работы', value: 'Пн–Пт, 9:00–18:00', link: '' },
    ],
    layout: 'grid',
    background: '#f8f9fa',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок' },
    items: { type: 'array', label: 'Контакты', itemSchema: {
      icon: { type: 'text', label: 'Иконка (эмодзи)' },
      label: { type: 'text', label: 'Подпись' },
      value: { type: 'text', label: 'Значение' },
      link: { type: 'url', label: 'Ссылка (необязательно)', placeholder: 'tel: / mailto: / https://...' },
    } },
    layout: {
      type: 'select',
      label: 'Раскладка',
      default: 'grid',
      options: [
        { value: 'grid', label: 'Сетка' },
        { value: 'list', label: 'Список' },
      ],
    },
    background: { type: 'color', label: 'Цвет фона' },
  },
})

registerBlock({
  type: 'newsletter',
  label: 'Подписка на рассылку',
  category: 'form',
  icon: '📨',
  description: 'Форма подписки на email-рассылку с полем ввода и кнопкой',
  tags: ['newsletter', 'subscribe', 'email', 'form', 'lead'],
  defaultData: {
    title: 'Будьте в курсе новостей',
    subtitle: 'Только полезный контент — никакого спама. Отписаться можно в любой момент.',
    placeholder: 'Ваш email',
    buttonText: 'Подписаться',
    disclaimer: 'Нажимая кнопку, вы соглашаетесь с политикой конфиденциальности.',
    background: '#f0f1fe',
    successMessage: 'Спасибо! Вы успешно подписались на рассылку.',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок' },
    subtitle: { type: 'text', label: 'Подзаголовок' },
    placeholder: { type: 'text', label: 'Placeholder поля', default: 'Ваш email' },
    buttonText: { type: 'text', label: 'Текст кнопки', default: 'Подписаться' },
    disclaimer: { type: 'text', label: 'Дисклеймер под формой', placeholder: 'Без спама...' },
    background: { type: 'color', label: 'Цвет фона' },
    successMessage: { type: 'text', label: 'Сообщение после подписки' },
  },
})

registerBlock({
  type: 'table',
  label: 'Таблица',
  category: 'content',
  icon: '⊞',
  description: 'Таблица данных с заголовками, строками и настройками оформления',
  tags: ['table', 'data', 'grid', 'comparison', 'spreadsheet'],
  defaultData: {
    headers: ['Название', 'Категория', 'Цена', 'Статус'],
    rows: [
      'Базовый план,Подписка,790 ₽/мес,Доступен',
      'Профессиональный,Подписка,1 990 ₽/мес,Доступен',
      'Корпоративный,Подписка,По запросу,Доступен',
    ],
    striped: true,
    bordered: false,
    caption: '',
  },
  schema: {
    headers: { type: 'array', label: 'Заголовки столбцов', stringArray: true },
    rows: { type: 'array', label: 'Строки (значения через запятую)', stringArray: true },
    striped: { type: 'boolean', label: 'Чередующийся фон строк', default: true },
    bordered: { type: 'boolean', label: 'Границы ячеек', default: false },
    caption: { type: 'text', label: 'Подпись таблицы (необязательно)', placeholder: 'Описание таблицы' },
  },
})

registerBlock({
  type: 'icon-features',
  label: 'Иконки с текстом',
  category: 'content',
  icon: '✦',
  description: 'Компактная сетка: иконка, заголовок, короткое описание',
  tags: ['features', 'icons', 'grid', 'benefits', 'highlights'],
  defaultData: {
    title: 'Наши преимущества',
    items: [
      { icon: '⚡', title: 'Молниеносная скорость', text: 'Страницы загружаются менее чем за секунду.' },
      { icon: '🔒', title: 'Надёжная защита', text: 'SSL, резервное копирование и защита от DDoS.' },
      { icon: '📱', title: 'Адаптивный дизайн', text: 'Идеально выглядит на любом устройстве.' },
      { icon: '🎨', title: 'Гибкий дизайн', text: 'Полный контроль над внешним видом сайта.' },
      { icon: '📊', title: 'Встроенная аналитика', text: 'Отслеживайте посетителей и конверсии.' },
      { icon: '🤝', title: 'Поддержка 24/7', text: 'Наши специалисты всегда готовы помочь.' },
    ],
    columns: 3,
  },
  schema: {
    title: { type: 'text', label: 'Заголовок секции' },
    items: { type: 'array', label: 'Элементы', itemSchema: {
      icon: { type: 'text', label: 'Иконка (эмодзи)' },
      title: { type: 'text', label: 'Заголовок' },
      text: { type: 'text', label: 'Описание' },
    } },
    columns: { type: 'number', label: 'Колонок в ряду', default: 3, min: 1, max: 4 },
  },
})

registerBlock({
  type: 'banner',
  label: 'Баннер-уведомление',
  category: 'layout',
  icon: '📢',
  description: 'Узкая полоса-уведомление вверху страницы — акции, новости, важные сообщения',
  tags: ['banner', 'announcement', 'notice', 'strip', 'alert'],
  defaultData: {
    text: '🎉 Специальное предложение — скидка 30% на первый месяц!',
    link: '#',
    linkText: 'Подробнее',
    background: '#5b6af0',
    textColor: '#ffffff',
    closeable: true,
  },
  schema: {
    text: { type: 'text', label: 'Текст объявления' },
    link: { type: 'url', label: 'Ссылка', placeholder: 'https://...' },
    linkText: { type: 'text', label: 'Текст ссылки', default: 'Подробнее' },
    background: { type: 'color', label: 'Цвет фона', default: '#5b6af0' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
    closeable: { type: 'boolean', label: 'Кнопка закрытия', default: true },
  },
})

registerBlock({
  type: 'testimonial-single',
  label: 'Отзыв — крупный',
  category: 'content',
  icon: '❝',
  description: 'Одиночный крупный отзыв клиента с фото, именем и оценкой',
  tags: ['testimonial', 'review', 'quote', 'social proof', 'single'],
  defaultData: {
    quote: '<p>«Полностью изменили наш подход к работе. Время запуска сократилось с недель до нескольких часов. Невероятный результат для любого бизнеса.»</p>',
    name: 'Екатерина Новикова',
    role: 'Директор по маркетингу',
    company: 'TechCorp',
    avatar: '',
    rating: 5,
    background: '#f8f9fa',
  },
  schema: {
    quote: { type: 'richtext', label: 'Текст отзыва' },
    name: { type: 'text', label: 'Имя клиента' },
    role: { type: 'text', label: 'Должность', placeholder: 'CEO / Маркетолог / ...' },
    company: { type: 'text', label: 'Компания' },
    avatar: { type: 'image', label: 'Фото' },
    rating: { type: 'number', label: 'Рейтинг (1–5)', default: 5, min: 1, max: 5 },
    background: { type: 'color', label: 'Цвет фона' },
  },
})

registerBlock({
  type: 'video-hero',
  label: 'Hero — Видеофон',
  category: 'hero',
  icon: '▶',
  description: 'Главный экран с фоновым видео, заголовком и кнопкой призыва к действию',
  tags: ['hero', 'video', 'background', 'fullscreen', 'landing'],
  defaultData: {
    videoUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
    title: 'Будущее начинается здесь',
    subtitle: 'Создавайте сайты нового поколения без ограничений',
    overlay: 'rgba(0,0,0,0.5)',
    primaryButton: { text: 'Начать прямо сейчас', url: '#' },
    height: 600,
  },
  schema: {
    videoUrl: { type: 'url', label: 'Ссылка на видео (YouTube / Vimeo / MP4)', placeholder: 'https://youtube.com/watch?v=...' },
    title: { type: 'richtext', label: 'Заголовок' },
    subtitle: { type: 'text', label: 'Подзаголовок' },
    overlay: { type: 'color', label: 'Затемнение (rgba)', default: 'rgba(0,0,0,0.5)' },
    primaryButton: { type: 'text', label: 'Текст кнопки', placeholder: 'Начать прямо сейчас' },
    height: { type: 'number', label: 'Высота (px)', default: 600, min: 300, max: 1200 },
  },
})

registerBlock({
  type: 'image-text',
  label: 'Изображение + Текст',
  category: 'content',
  icon: '🖼',
  description: 'Изображение слева или справа с заголовком, текстом и кнопкой',
  tags: ['image', 'text', 'split', 'feature', 'about'],
  defaultData: {
    image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&q=80',
    imageAlt: 'Изображение',
    imagePosition: 'left',
    tag: 'О нас',
    title: 'Делаем сложное простым',
    text: 'Наша команда экспертов помогает компаниям трансформироваться и расти. Мы используем современные технологии и проверенные методологии для достижения выдающихся результатов.',
    buttonText: 'Узнать больше',
    buttonUrl: '#',
  },
  schema: {
    image: { type: 'image', label: 'Изображение' },
    imageAlt: { type: 'text', label: 'Alt-текст изображения' },
    imagePosition: { type: 'select', label: 'Положение изображения', options: [{ value: 'left', label: 'Слева' }, { value: 'right', label: 'Справа' }], default: 'left' },
    tag: { type: 'text', label: 'Надпись над заголовком' },
    title: { type: 'text', label: 'Заголовок' },
    text: { type: 'richtext', label: 'Текст' },
    buttonText: { type: 'text', label: 'Текст кнопки' },
    buttonUrl: { type: 'url', label: 'Ссылка кнопки', placeholder: '#' },
  },
})

registerBlock({
  type: 'card-grid',
  label: 'Карточки (сетка)',
  category: 'content',
  icon: '⊞',
  description: 'Сетка карточек с изображением, тегом, заголовком, текстом и ссылкой',
  tags: ['cards', 'grid', 'blog', 'portfolio', 'services'],
  defaultData: {
    title: 'Наши услуги',
    subtitle: 'Полный спектр решений для вашего бизнеса',
    columns: 3,
    items: [
      { image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&q=80', tag: 'Дизайн', title: 'UX/UI Дизайн', text: 'Создаём удобные и красивые интерфейсы', linkText: 'Подробнее', linkUrl: '#' },
      { image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&q=80', tag: 'Разработка', title: 'Веб-разработка', text: 'Разрабатываем быстрые и надёжные сайты', linkText: 'Подробнее', linkUrl: '#' },
      { image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&q=80', tag: 'Аналитика', title: 'Бизнес-аналитика', text: 'Превращаем данные в решения', linkText: 'Подробнее', linkUrl: '#' },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок секции' },
    subtitle: { type: 'text', label: 'Подзаголовок' },
    columns: { type: 'number', label: 'Колонок', default: 3, min: 1, max: 4 },
    items: {
      type: 'array', label: 'Карточки',
      itemSchema: {
        image: { type: 'image', label: 'Изображение' },
        tag: { type: 'text', label: 'Тег' },
        title: { type: 'text', label: 'Заголовок' },
        text: { type: 'text', label: 'Текст' },
        linkText: { type: 'text', label: 'Текст ссылки' },
        linkUrl: { type: 'url', label: 'URL', placeholder: '#' },
      },
    },
  },
})

registerBlock({
  type: 'two-columns',
  label: 'Две колонки',
  category: 'layout',
  icon: '⫿',
  description: 'Макет с двумя колонками с настраиваемым соотношением',
  tags: ['layout', 'columns', 'two-column', 'split'],
  defaultData: {
    ratio: '50/50',
    leftTitle: 'Левая колонка',
    leftText: 'Содержимое левой колонки. Здесь можно разместить любой текст, список преимуществ или другой контент.',
    rightTitle: 'Правая колонка',
    rightText: 'Содержимое правой колонки. Используйте две колонки для сравнения, для пояснений или для параллельного контента.',
    gap: 48,
  },
  schema: {
    ratio: {
      type: 'select', label: 'Соотношение колонок', default: '50/50',
      options: [
        { value: '50/50', label: '50 / 50' }, { value: '40/60', label: '40 / 60' },
        { value: '60/40', label: '60 / 40' }, { value: '33/67', label: '33 / 67' },
        { value: '67/33', label: '67 / 33' },
      ],
    },
    leftTitle: { type: 'text', label: 'Заголовок левой колонки' },
    leftText: { type: 'richtext', label: 'Текст левой колонки' },
    rightTitle: { type: 'text', label: 'Заголовок правой колонки' },
    rightText: { type: 'richtext', label: 'Текст правой колонки' },
    gap: { type: 'number', label: 'Расстояние между колонками (px)', default: 48, min: 8, max: 120 },
  },
})

registerBlock({
  type: 'highlight-box',
  label: 'Выделенный блок',
  category: 'text',
  icon: '💡',
  description: 'Callout-блок для важных сообщений: информация, предупреждение, успех, ошибка',
  tags: ['callout', 'highlight', 'info', 'warning', 'tip', 'alert'],
  defaultData: {
    type: 'info',
    title: 'Важная информация',
    text: 'Обратите внимание на этот важный момент. Здесь можно выделить ключевую информацию, которую пользователь не должен пропустить.',
    icon: 'ℹ️',
  },
  schema: {
    type: {
      type: 'select', label: 'Тип', default: 'info',
      options: [
        { value: 'info', label: 'Информация' }, { value: 'success', label: 'Успех' },
        { value: 'warning', label: 'Предупреждение' }, { value: 'error', label: 'Ошибка' },
        { value: 'tip', label: 'Совет' },
      ],
    },
    title: { type: 'text', label: 'Заголовок' },
    text: { type: 'richtext', label: 'Текст' },
    icon: { type: 'icon', label: 'Иконка' },
  },
})

registerBlock({
  type: 'code-block',
  label: 'Блок кода',
  category: 'text',
  icon: '</>',
  description: 'Блок для отображения кода с подсветкой и кнопкой копирования',
  tags: ['code', 'snippet', 'developer', 'syntax', 'programming'],
  defaultData: {
    code: 'const greeting = (name: string) => {\n  return `Hello, ${name}!`\n}\n\nconsole.log(greeting("World"))',
    language: 'javascript',
    filename: 'example.ts',
    showLineNumbers: true,
    theme: 'dark',
  },
  schema: {
    code: { type: 'richtext', label: 'Код' },
    language: {
      type: 'select', label: 'Язык', default: 'javascript',
      options: [
        { value: 'javascript', label: 'JavaScript' }, { value: 'typescript', label: 'TypeScript' },
        { value: 'python', label: 'Python' }, { value: 'html', label: 'HTML' },
        { value: 'css', label: 'CSS' }, { value: 'json', label: 'JSON' },
        { value: 'bash', label: 'Bash' }, { value: 'sql', label: 'SQL' },
        { value: 'rust', label: 'Rust' }, { value: 'go', label: 'Go' },
      ],
    },
    filename: { type: 'text', label: 'Имя файла (необязательно)' },
    showLineNumbers: { type: 'boolean', label: 'Номера строк', default: true },
    theme: {
      type: 'select', label: 'Тема', default: 'dark',
      options: [{ value: 'dark', label: 'Тёмная' }, { value: 'light', label: 'Светлая' }],
    },
  },
})

registerBlock({
  type: 'masonry-gallery',
  label: 'Галерея Masonry',
  category: 'gallery',
  icon: '⧉',
  description: 'Pinterest-стиль галерея с разными высотами изображений',
  tags: ['gallery', 'masonry', 'pinterest', 'photos', 'portfolio'],
  defaultData: {
    images: [
      { src: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&q=80', alt: 'Горы' },
      { src: 'https://images.unsplash.com/photo-1519681393784-d120267933ba?w=400&q=80', alt: 'Звёзды' },
      { src: 'https://images.unsplash.com/photo-1433086966358-54859d0ed716?w=400&q=80', alt: 'Водопад' },
      { src: 'https://images.unsplash.com/photo-1439853949212-36589f9f4453?w=400&q=80', alt: 'Озеро' },
      { src: 'https://images.unsplash.com/photo-1501854140801-50d01698950b?w=400&q=80', alt: 'Лес' },
      { src: 'https://images.unsplash.com/photo-1518173946687-a4c8892bbd9f?w=400&q=80', alt: 'Поле' },
    ],
    columns: 3,
    gap: 12,
  },
  schema: {
    images: {
      type: 'array', label: 'Изображения',
      itemSchema: {
        src: { type: 'image', label: 'Изображение' },
        alt: { type: 'text', label: 'Alt-текст' },
      },
    },
    columns: { type: 'number', label: 'Колонок', default: 3, min: 2, max: 5 },
    gap: { type: 'number', label: 'Отступ (px)', default: 12, min: 0, max: 48 },
  },
})

registerBlock({
  type: 'comparison',
  label: 'Сравнение',
  category: 'content',
  icon: '⚖',
  description: 'Таблица сравнения: мы против конкурентов',
  tags: ['comparison', 'table', 'features', 'vs', 'competitors'],
  defaultData: {
    title: 'Почему выбирают нас',
    leftTitle: 'Конкуренты',
    rightTitle: 'Наш продукт',
    rightHighlight: true,
    features: [
      { name: 'Простота использования', left: '❌', right: '✅' },
      { name: 'Техподдержка 24/7', left: '❌', right: '✅' },
      { name: 'Без скрытых платежей', left: '❌', right: '✅' },
      { name: 'Бесплатный пробный период', left: '❌', right: '✅' },
      { name: 'Интеграции с CRM', left: '⚠️ Платно', right: '✅ Бесплатно' },
      { name: 'Мобильное приложение', left: '❌', right: '✅' },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок' },
    leftTitle: { type: 'text', label: 'Заголовок левой колонки (конкуренты)' },
    rightTitle: { type: 'text', label: 'Заголовок правой колонки (вы)' },
    rightHighlight: { type: 'boolean', label: 'Выделить правую колонку', default: true },
    features: {
      type: 'array', label: 'Строки сравнения',
      itemSchema: {
        name: { type: 'text', label: 'Название характеристики' },
        left: { type: 'text', label: 'Значение у конкурентов' },
        right: { type: 'text', label: 'Ваше значение' },
      },
    },
  },
})

registerBlock({
  type: 'awards',
  label: 'Награды и признание',
  category: 'content',
  icon: '🏆',
  description: 'Награды, сертификаты, упоминания в прессе',
  tags: ['awards', 'press', 'certifications', 'trust', 'recognition'],
  defaultData: {
    title: 'Нас признают лучшими',
    items: [
      { image: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2f/Forbes_logo.svg/320px-Forbes_logo.svg.png', name: 'Forbes', description: 'Лучший стартап года', link: '#' },
      { image: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/TechCrunch_logo.svg/320px-TechCrunch_logo.svg.png', name: 'TechCrunch', description: 'Выбор редакции', link: '#' },
      { image: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/e9/Wired_magazine_logo.svg/320px-Wired_magazine_logo.svg.png', name: 'Wired', description: 'Инновация 2026', link: '#' },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок' },
    items: {
      type: 'array', label: 'Награды',
      itemSchema: {
        image: { type: 'image', label: 'Логотип / изображение' },
        name: { type: 'text', label: 'Название' },
        description: { type: 'text', label: 'Описание' },
        link: { type: 'url', label: 'Ссылка', placeholder: '#' },
      },
    },
  },
})

registerBlock({
  type: 'guarantee-bar',
  label: 'Гарантии (полоса)',
  category: 'content',
  icon: '🔒',
  description: 'Горизонтальная полоса доверия: SSL, возврат, рейтинг',
  tags: ['trust', 'badges', 'guarantee', 'security', 'ssl', 'returns'],
  defaultData: {
    background: '#f8f9fa',
    items: [
      { icon: '🔒', text: 'SSL-защита данных' },
      { icon: '↩️', text: 'Возврат в течение 30 дней' },
      { icon: '⭐', text: 'Рейтинг 4.9 из 5' },
      { icon: '📦', text: 'Быстрая доставка' },
      { icon: '💬', text: 'Поддержка 24/7' },
    ],
  },
  schema: {
    background: { type: 'color', label: 'Цвет фона', default: '#f8f9fa' },
    items: {
      type: 'array', label: 'Элементы',
      itemSchema: {
        icon: { type: 'icon', label: 'Иконка' },
        text: { type: 'text', label: 'Текст' },
      },
    },
  },
})

registerBlock({
  type: 'hero-minimal',
  label: 'Hero — Минимализм',
  category: 'hero',
  icon: '✦',
  description: 'Лаконичный hero с крупной типографикой без лишних элементов',
  tags: ['hero', 'minimal', 'landing', 'typography', 'clean'],
  defaultData: {
    label: 'Новинка 2026',
    title: 'Создавайте\nбез границ',
    subtitle: 'Инструмент нового поколения для тех, кто ценит скорость и качество',
    primaryButton: 'Попробовать бесплатно',
    secondaryButton: 'Смотреть демо',
    align: 'center',
    background: '#ffffff',
    titleColor: '#0f0f0f',
  },
  schema: {
    label: { type: 'text', label: 'Надпись-метка (над заголовком)' },
    title: { type: 'text', label: 'Заголовок (используйте \\n для переноса)' },
    subtitle: { type: 'text', label: 'Подзаголовок' },
    primaryButton: { type: 'text', label: 'Основная кнопка' },
    secondaryButton: { type: 'text', label: 'Вторичная кнопка (пусто = скрыть)' },
    align: {
      type: 'select', label: 'Выравнивание', default: 'center',
      options: [{ value: 'center', label: 'По центру' }, { value: 'left', label: 'Слева' }],
    },
    background: { type: 'color', label: 'Цвет фона', default: '#ffffff' },
    titleColor: { type: 'color', label: 'Цвет заголовка', default: '#0f0f0f' },
  },
})

registerBlock({
  type: 'testimonials-carousel',
  label: 'Отзывы (карусель)',
  category: 'slider',
  icon: '💬',
  description: 'Карусель отзывов с навигацией prev/next и автопроигрыванием',
  tags: ['testimonials', 'carousel', 'reviews', 'slider', 'social proof'],
  defaultData: {
    title: 'Что говорят клиенты',
    autoplay: true,
    autoplayDelay: 5000,
    items: [
      { name: 'Алексей Смирнов', role: 'CEO', company: 'TechStart', text: 'Невероятный продукт! Сократил время разработки сайтов в 5 раз. Теперь наша команда успевает делать втрое больше проектов за тот же срок.', avatar: 'https://randomuser.me/api/portraits/men/32.jpg', rating: 5 },
      { name: 'Марина Ковалёва', role: 'Marketing Director', company: 'GrowthLab', text: 'Наконец-то инструмент, который не требует программиста. Я сама запустила 3 лендинга за одну неделю, и все они конвертируют отлично.', avatar: 'https://randomuser.me/api/portraits/women/44.jpg', rating: 5 },
      { name: 'Дмитрий Петров', role: 'Founder', company: 'DigitalAgency', text: 'Белый лейбл — это то, что нам было нужно. Теперь продаём собственный конструктор клиентам. Дополнительный источник дохода!', avatar: 'https://randomuser.me/api/portraits/men/67.jpg', rating: 5 },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок секции' },
    autoplay: { type: 'boolean', label: 'Автопроигрывание', default: true },
    autoplayDelay: { type: 'number', label: 'Задержка автопроигрывания (мс)', default: 5000, min: 1000, max: 15000 },
    items: {
      type: 'array', label: 'Отзывы',
      itemSchema: {
        name: { type: 'text', label: 'Имя' },
        role: { type: 'text', label: 'Должность' },
        company: { type: 'text', label: 'Компания' },
        text: { type: 'richtext', label: 'Текст отзыва' },
        avatar: { type: 'image', label: 'Аватар' },
        rating: { type: 'number', label: 'Рейтинг (1-5)', default: 5, min: 1, max: 5 },
      },
    },
  },
})

registerBlock({
  type: 'image-carousel',
  label: 'Карусель изображений',
  category: 'slider',
  icon: '▷',
  description: 'Слайдшоу изображений со стрелками, точками и подписями',
  tags: ['carousel', 'images', 'slideshow', 'gallery', 'photos'],
  defaultData: {
    showCaptions: true,
    showDots: true,
    showArrows: true,
    autoplay: false,
    aspectRatio: '16/9',
    images: [
      { src: 'https://images.unsplash.com/photo-1531297484001-80022131f5a1?w=1200&q=80', alt: 'Технологии', caption: 'Современные технологии' },
      { src: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=1200&q=80', alt: 'Рабочее место', caption: 'Эффективная работа' },
      { src: 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=1200&q=80', alt: 'Команда', caption: 'Сильная команда' },
    ],
  },
  schema: {
    showCaptions: { type: 'boolean', label: 'Показывать подписи', default: true },
    showDots: { type: 'boolean', label: 'Точки навигации', default: true },
    showArrows: { type: 'boolean', label: 'Стрелки навигации', default: true },
    autoplay: { type: 'boolean', label: 'Автопроигрывание', default: false },
    aspectRatio: {
      type: 'select', label: 'Соотношение сторон', default: '16/9',
      options: [
        { value: '16/9', label: '16:9' }, { value: '4/3', label: '4:3' },
        { value: '1/1', label: '1:1' }, { value: '21/9', label: '21:9' },
        { value: '3/2', label: '3:2' },
      ],
    },
    images: {
      type: 'array', label: 'Изображения',
      itemSchema: {
        src: { type: 'image', label: 'Изображение' },
        alt: { type: 'text', label: 'Alt-текст' },
        caption: { type: 'text', label: 'Подпись' },
      },
    },
  },
})

registerBlock({
  type: 'product-showcase',
  label: 'Витрина товара',
  category: 'commerce',
  icon: '🛍',
  description: 'Карточка одного товара с изображением, ценой, характеристиками и CTA',
  tags: ['product', 'showcase', 'ecommerce', 'price', 'buy'],
  defaultData: {
    image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=600&q=80',
    imageAlt: 'Продукт',
    badge: 'Хит продаж',
    name: 'Premium Watch Pro',
    description: 'Умные часы нового поколения с AMOLED-дисплеем, GPS-трекером и 7-дневным временем работы от батареи.',
    price: '24 990 ₽',
    oldPrice: '34 990 ₽',
    buttonText: 'Добавить в корзину',
    imagePosition: 'left',
    features: [{ value: 'AMOLED дисплей 1.4"' }, { value: 'GPS + ГЛОНАСС' }, { value: 'Пульсоксиметр' }, { value: '7 дней без зарядки' }, { value: 'Водозащита IP68' }],
  },
  schema: {
    image: { type: 'image', label: 'Изображение товара' },
    imageAlt: { type: 'text', label: 'Alt-текст' },
    badge: { type: 'text', label: 'Бейдж (пусто = скрыть)' },
    name: { type: 'text', label: 'Название товара' },
    description: { type: 'richtext', label: 'Описание' },
    price: { type: 'text', label: 'Цена' },
    oldPrice: { type: 'text', label: 'Старая цена (пусто = скрыть)' },
    buttonText: { type: 'text', label: 'Текст кнопки' },
    imagePosition: {
      type: 'select', label: 'Положение изображения', default: 'left',
      options: [{ value: 'left', label: 'Слева' }, { value: 'right', label: 'Справа' }],
    },
    features: {
      type: 'array', label: 'Характеристики',
      itemSchema: { value: { type: 'text', label: 'Характеристика' } },
    },
  },
})

registerBlock({
  type: 'product-grid',
  label: 'Каталог товаров',
  category: 'commerce',
  icon: '🛒',
  description: 'Сетка карточек товаров с изображением, ценой и кнопкой',
  tags: ['products', 'catalog', 'ecommerce', 'shop', 'grid'],
  defaultData: {
    title: 'Популярные товары',
    columns: 3,
    products: [
      { image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400&q=80', badge: 'Хит', name: 'Smart Watch', price: '24 990 ₽', oldPrice: '34 990 ₽', buttonText: 'В корзину' },
      { image: 'https://images.unsplash.com/photo-1585386959984-a4155224a1ad?w=400&q=80', badge: 'Новинка', name: 'Аромадиффузор', price: '4 490 ₽', oldPrice: '', buttonText: 'В корзину' },
      { image: 'https://images.unsplash.com/photo-1491553895911-0055eca6402d?w=400&q=80', badge: '-20%', name: 'Кроссовки Pro', price: '8 990 ₽', oldPrice: '11 990 ₽', buttonText: 'В корзину' },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок секции' },
    columns: { type: 'number', label: 'Колонок', default: 3, min: 2, max: 4 },
    products: {
      type: 'array', label: 'Товары',
      itemSchema: {
        image: { type: 'image', label: 'Изображение' },
        badge: { type: 'text', label: 'Бейдж' },
        name: { type: 'text', label: 'Название' },
        price: { type: 'text', label: 'Цена' },
        oldPrice: { type: 'text', label: 'Старая цена' },
        buttonText: { type: 'text', label: 'Текст кнопки' },
      },
    },
  },
})

registerBlock({
  type: 'payment-logos',
  label: 'Способы оплаты',
  category: 'commerce',
  icon: '💳',
  description: 'Полоса с логотипами платёжных систем',
  tags: ['payment', 'logos', 'visa', 'mastercard', 'mir', 'ecommerce', 'checkout'],
  defaultData: {
    title: 'Принимаем к оплате',
    background: '#f8f9fa',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок' },
    background: { type: 'color', label: 'Цвет фона', default: '#f8f9fa' },
  },
})

registerBlock({
  type: 'quiz',
  label: 'Квиз',
  category: 'interactive',
  icon: '🎯',
  description: 'Многошаговый опрос с результатом и лид-захватом',
  tags: ['quiz', 'survey', 'steps', 'lead', 'interactive', 'opros'],
  defaultData: {
    title: 'Подберём решение за 1 минуту',
    subtitle: 'Ответьте на 3 вопроса',
    background: '#f0f4ff',
    accentColor: '#5b6af0',
    steps: [
      {
        id: 'q1',
        question: 'Какова ваша цель?',
        description: '',
        image: '',
        type: 'single',
        required: true,
        options: [
          { id: 'q1a1', label: 'Увеличить продажи', image: '', nextStepId: null },
          { id: 'q1a2', label: 'Привлечь клиентов', image: '', nextStepId: null },
          { id: 'q1a3', label: 'Улучшить сервис', image: '', nextStepId: null },
          { id: 'q1a4', label: 'Другое', image: '', nextStepId: null },
        ],
      },
      {
        id: 'q2',
        question: 'Какой у вас бюджет?',
        description: '',
        image: '',
        type: 'single',
        required: true,
        options: [
          { id: 'q2a1', label: 'До 30 000 ₽', image: '', nextStepId: null },
          { id: 'q2a2', label: '30–100 тыс ₽', image: '', nextStepId: null },
          { id: 'q2a3', label: '100–300 тыс ₽', image: '', nextStepId: null },
          { id: 'q2a4', label: 'Более 300 тыс ₽', image: '', nextStepId: null },
        ],
      },
      {
        id: 'q3',
        question: 'Когда хотите начать?',
        description: '',
        image: '',
        type: 'single',
        required: true,
        options: [
          { id: 'q3a1', label: 'Немедленно', image: '', nextStepId: null },
          { id: 'q3a2', label: 'В течение месяца', image: '', nextStepId: null },
          { id: 'q3a3', label: 'Через 3 месяца', image: '', nextStepId: null },
          { id: 'q3a4', label: 'Планирую', image: '', nextStepId: null },
        ],
      },
    ],
    outcomes: [
      {
        id: 'o_budget',
        title: 'Стартовый пакет — для вас!',
        description: 'Оптимальное решение для бизнеса с ограниченным бюджетом.',
        image: '',
        ctaText: 'Узнать подробнее',
        ctaUrl: '#',
        conditions: [{ stepId: 'q2', optionId: 'q2a1' }],
      },
      {
        id: 'o_default',
        title: 'Подберём лучшее решение!',
        description: 'Наш специалист свяжется с вами и подберёт оптимальный план.',
        image: '',
        ctaText: 'Получить предложение',
        ctaUrl: '#contact',
        conditions: [],
      },
    ],
    allowRetake: true,
    shareEnabled: true,
    resultTitle: 'Отлично! Оставьте контакты',
    resultSubtitle: 'Мы подберём для вас лучшее решение',
    submitText: 'Получить результат',
    successText: 'Спасибо! Мы свяжемся с вами в течение 24 часов.',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок квиза' },
    subtitle: { type: 'text', label: 'Подзаголовок' },
    background: { type: 'color', label: 'Цвет фона', default: '#f0f4ff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
    resultTitle: { type: 'text', label: 'Заголовок последнего шага' },
    resultSubtitle: { type: 'text', label: 'Текст последнего шага' },
    submitText: { type: 'text', label: 'Текст кнопки отправки', default: 'Получить результат' },
    successText: { type: 'text', label: 'Сообщение после отправки' },
    allowRetake: { type: 'boolean', label: 'Разрешить повтор', default: true },
    shareEnabled: { type: 'boolean', label: 'Кнопка «Поделиться»', default: true },
    optionsText: { type: 'text', label: 'Варианты шага 1 (по одному на строку)', placeholder: 'Вариант 1\nВариант 2' },
  },
})

registerBlock({
  type: 'calculator',
  label: 'Калькулятор',
  category: 'interactive',
  icon: '🧮',
  description: 'Интерактивный калькулятор стоимости услуг',
  tags: ['calculator', 'price', 'cost', 'interactive', 'kalkulator', 'stoimost'],
  defaultData: {
    title: 'Рассчитайте стоимость',
    subtitle: 'Получите точную цену за несколько секунд',
    background: '#fff',
    accentColor: '#5b6af0',
    currency: '₽',
    basePrice: 5000,
    params: [
      { id: 'area', label: 'Площадь (м²)', type: 'range', min: 10, max: 500, step: 10, default: 50, multiplier: 800 },
      { id: 'floors', label: 'Этажность', type: 'select', options: ['1 этаж', '2 этажа', '3+ этажа'], multipliers: [1, 1.4, 1.8], default: 0 },
      { id: 'urgency', label: 'Срочность', type: 'radio', options: ['Стандарт', 'Ускоренно', 'Срочно'], multipliers: [1, 1.25, 1.6], default: 0 },
    ],
    resultLabel: 'Ориентировочная стоимость',
    ctaText: 'Получить точный расчёт',
    ctaUrl: '#',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок' },
    subtitle: { type: 'text', label: 'Подзаголовок' },
    background: { type: 'color', label: 'Цвет фона', default: '#fff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
    currency: { type: 'text', label: 'Символ валюты', default: '₽' },
    basePrice: { type: 'number', label: 'Базовая цена', default: 5000, min: 0, max: 10000000 },
    resultLabel: { type: 'text', label: 'Подпись к сумме', default: 'Ориентировочная стоимость' },
    ctaText: { type: 'text', label: 'Текст кнопки', default: 'Получить точный расчёт' },
    ctaUrl: { type: 'url', label: 'Ссылка кнопки', default: '#' },
  },
})

registerBlock({
  type: 'chat-widget',
  label: 'Виджет чата',
  category: 'interactive',
  icon: '💬',
  description: 'Плавающая кнопка связи: WhatsApp, Viber, Telegram, ВКонтакте',
  tags: ['chat', 'whatsapp', 'viber', 'telegram', 'vk', 'widget', 'float', 'contact'],
  defaultData: {
    label: 'Написать нам',
    whatsapp: '',
    viber: '',
    telegram: '',
    vk: '',
    phone: '',
    position: 'bottom-right',
    primaryColor: '#25D366',
    showLabel: true,
  },
  schema: {
    label: { type: 'text', label: 'Текст подсказки', default: 'Написать нам' },
    whatsapp: { type: 'text', label: 'WhatsApp (номер: +79001234567)', default: '' },
    viber: { type: 'text', label: 'Viber (номер: +79001234567)', default: '' },
    telegram: { type: 'text', label: 'Telegram (ник без @)', default: '' },
    vk: { type: 'text', label: 'ВКонтакте (ссылка или id)', default: '' },
    phone: { type: 'text', label: 'Телефон (tel:+7...)', default: '' },
    primaryColor: { type: 'color', label: 'Цвет кнопки', default: '#25D366' },
    position: { type: 'select', label: 'Позиция', options: [{ value: 'bottom-right', label: 'Справа' }, { value: 'bottom-left', label: 'Слева' }], default: 'bottom-right' },
    showLabel: { type: 'boolean', label: 'Показывать подсказку', default: true },
  },
})

registerBlock({
  type: 'live-chat',
  label: 'Онлайн-чат',
  category: 'interactive',
  icon: '🗨',
  description: 'Встраивает виджет онлайн-консультанта: JivoChat, Crisp, Tawk.to или свой скрипт',
  tags: ['chat', 'jivosite', 'jivochat', 'crisp', 'tawk', 'consultant', 'support', 'live', 'online'],
  defaultData: {
    platform: 'jivosite',
    widgetId: '',
    customScript: '',
  },
  schema: {
    platform: {
      type: 'select', label: 'Платформа',
      options: [
        { value: 'jivosite', label: 'JivoChat (JivoSite)' },
        { value: 'crisp', label: 'Crisp' },
        { value: 'tawk', label: 'Tawk.to' },
        { value: 'custom', label: 'Свой скрипт' },
      ],
      default: 'jivosite',
    },
    widgetId: { type: 'text', label: 'ID виджета (JivoChat: 8-символьный код, Crisp: website ID, Tawk: property/widget ID)', default: '' },
    customScript: { type: 'richtext', label: 'Свой HTML/JS скрипт (для платформы "Свой скрипт")', default: '' },
  },
})

registerBlock({
  type: 'announcement-bar',
  label: 'Строка объявления',
  category: 'content',
  icon: '📢',
  description: 'Тонкая информационная полоса сверху страницы — акции, новости, важные сообщения',
  tags: ['announcement', 'bar', 'promo', 'sale', 'top', 'notice', 'banner'],
  defaultData: {
    text: '🎉 Скидка 20% на все услуги только до 31 декабря! Успейте воспользоваться.',
    link: '',
    linkText: 'Подробнее →',
    background: '#5b6af0',
    textColor: '#ffffff',
    dismissible: true,
    sticky: true,
  },
  schema: {
    text: { type: 'text', label: 'Текст объявления' },
    link: { type: 'url', label: 'Ссылка (необязательно)', default: '' },
    linkText: { type: 'text', label: 'Текст ссылки', default: 'Подробнее →' },
    background: { type: 'color', label: 'Цвет фона', default: '#5b6af0' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
    dismissible: { type: 'boolean', label: 'Кнопка закрыть', default: true },
    sticky: { type: 'boolean', label: 'Зафиксировать сверху', default: true },
  },
})

registerBlock({
  type: 'popup',
  label: 'Всплывающее окно',
  category: 'interactive',
  icon: '🪟',
  description: 'Модальный попап с задержкой или при попытке уйти со страницы',
  tags: ['popup', 'modal', 'exit-intent', 'lead', 'capture', 'email', 'subscribe'],
  defaultData: {
    title: 'Получите скидку 10%',
    text: 'Оставьте email и мы отправим вам промокод прямо сейчас.',
    image: '',
    ctaText: 'Получить скидку',
    ctaUrl: '#',
    trigger: 'delay',
    delaySeconds: 5,
    background: '#ffffff',
    accentColor: '#5b6af0',
    showOnce: true,
  },
  schema: {
    title: { type: 'text', label: 'Заголовок' },
    text: { type: 'text', label: 'Описание' },
    image: { type: 'image', label: 'Изображение (необязательно)' },
    ctaText: { type: 'text', label: 'Текст кнопки', default: 'Получить' },
    ctaUrl: { type: 'url', label: 'Ссылка кнопки', default: '#' },
    trigger: { type: 'select', label: 'Триггер показа', options: [{ value: 'delay', label: 'По таймеру' }, { value: 'exit', label: 'При попытке уйти' }], default: 'delay' },
    delaySeconds: { type: 'number', label: 'Задержка (сек)', default: 5, min: 1, max: 60 },
    background: { type: 'color', label: 'Цвет фона', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
    showOnce: { type: 'boolean', label: 'Показывать только раз (за сессию)', default: true },
  },
})

registerBlock({
  type: 'marquee',
  label: 'Бегущая строка',
  category: 'content',
  icon: '🎞',
  description: 'Плавно прокручивающийся ряд текста, логотипов или иконок',
  tags: ['ticker', 'scroll', 'marquee', 'logo', 'infinite'],
  defaultData: {
    items: [
      { text: 'Быстро', icon: '⚡' },
      { text: 'Надёжно', icon: '🔒' },
      { text: 'Красиво', icon: '✨' },
      { text: 'Удобно', icon: '🎯' },
      { text: 'Современно', icon: '🚀' },
    ],
    speed: 30,
    direction: 'left',
    background: '#f8f9fa',
    textColor: '#1f2937',
    separator: '•',
    fontSize: 18,
    pauseOnHover: true,
  },
  schema: {
    items: { type: 'array', label: 'Элементы', itemSchema: {
      text: { type: 'text', label: 'Текст' },
      icon: { type: 'text', label: 'Иконка (emoji или URL)' },
    } },
    speed: { type: 'number', label: 'Скорость (сек на цикл)', default: 30, min: 5, max: 120 },
    direction: { type: 'select', label: 'Направление', options: [{ value: 'left', label: 'Влево' }, { value: 'right', label: 'Вправо' }], default: 'left' },
    background: { type: 'color', label: 'Фон', default: '#f8f9fa' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#1f2937' },
    fontSize: { type: 'number', label: 'Размер шрифта (px)', default: 18, min: 12, max: 48 },
    pauseOnHover: { type: 'boolean', label: 'Пауза при наведении', default: true },
  },
})

registerBlock({
  type: 'sticky-cta',
  label: 'Прилипающий призыв к действию',
  category: 'interactive',
  icon: '📢',
  description: 'Фиксированная полоска внизу страницы с кнопкой — не даёт посетителю уйти без конверсии',
  tags: ['cta', 'sticky', 'button', 'fixed', 'banner'],
  defaultData: {
    text: 'Получите бесплатную консультацию прямо сейчас!',
    buttonText: 'Записаться',
    buttonUrl: '#form',
    background: '#1f2937',
    textColor: '#ffffff',
    accentColor: '#5b6af0',
    showDelay: 3,
    dismissible: true,
  },
  schema: {
    text: { type: 'text', label: 'Текст полоски' },
    buttonText: { type: 'text', label: 'Текст кнопки' },
    buttonUrl: { type: 'url', label: 'Ссылка кнопки' },
    background: { type: 'color', label: 'Цвет фона', default: '#1f2937' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Цвет кнопки', default: '#5b6af0' },
    showDelay: { type: 'number', label: 'Задержка появления (сек)', default: 3, min: 0, max: 30 },
    dismissible: { type: 'boolean', label: 'Можно закрыть', default: true },
  },
})

registerBlock({
  type: 'split-hero',
  label: 'Hero — Раздвоенный',
  category: 'hero',
  icon: '⬛⬛',
  description: 'Главный экран, разделённый на два равных блока с разными фонами',
  tags: ['hero', 'split', 'two', 'half', 'landing', 'choice'],
  defaultData: {
    height: 560,
    leftBackground: '#0f0f0f',
    leftTextColor: '#ffffff',
    leftTitle: 'Для бизнеса',
    leftText: 'Профессиональные инструменты для создания корпоративных сайтов и лендингов',
    leftButtonText: 'Узнать больше',
    leftButtonUrl: '#',
    rightBackground: '#5b6af0',
    rightTextColor: '#ffffff',
    rightTitle: 'Для фрилансеров',
    rightText: 'Быстрое создание портфолио и персональных сайтов без знания кода',
    rightButtonText: 'Начать сейчас',
    rightButtonUrl: '#',
  },
  schema: {
    height: { type: 'number', label: 'Высота (px)', default: 560, min: 300, max: 900 },
    leftBackground: { type: 'color', label: 'Фон левой половины', default: '#0f0f0f' },
    leftImage: { type: 'image', label: 'Фото левой половины (необязательно)' },
    leftTextColor: { type: 'color', label: 'Цвет текста слева', default: '#ffffff' },
    leftTitle: { type: 'text', label: 'Заголовок слева' },
    leftText: { type: 'text', label: 'Текст слева' },
    leftButtonText: { type: 'text', label: 'Кнопка слева' },
    leftButtonUrl: { type: 'url', label: 'Ссылка слева', default: '#' },
    rightBackground: { type: 'color', label: 'Фон правой половины', default: '#5b6af0' },
    rightImage: { type: 'image', label: 'Фото правой половины (необязательно)' },
    rightTextColor: { type: 'color', label: 'Цвет текста справа', default: '#ffffff' },
    rightTitle: { type: 'text', label: 'Заголовок справа' },
    rightText: { type: 'text', label: 'Текст справа' },
    rightButtonText: { type: 'text', label: 'Кнопка справа' },
    rightButtonUrl: { type: 'url', label: 'Ссылка справа', default: '#' },
  },
})

registerBlock({
  type: 'alternating-features',
  label: 'Чередующиеся секции',
  category: 'content',
  icon: '↕',
  description: 'Apple-стиль: чередование картинки и текста — чётные строки зеркалятся',
  tags: ['features', 'alternating', 'apple', 'showcase', 'product', 'bento'],
  defaultData: {
    title: 'Почему выбирают нас',
    accentColor: '#5b6af0',
    background: '#ffffff',
    gap: 80,
    items: [
      {
        tag: 'Скорость',
        title: 'Результат за 30 минут — не за 30 дней',
        text: 'Выберите шаблон, добавьте свой контент и опубликуйте. Нет нужды ждать разработчика или осваивать сложные инструменты.',
        image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&q=80',
        imageAlt: 'Команда работает быстро',
        buttonText: 'Попробовать бесплатно',
        buttonUrl: '#',
      },
      {
        tag: 'Гибкость',
        title: 'Полный контроль над дизайном',
        text: 'Настройте каждый пиксель под свой бренд. Собственные цвета, шрифты, CSS — никаких ограничений для вашей творческой свободы.',
        image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80',
        imageAlt: 'Дизайн без ограничений',
        buttonText: 'Изучить возможности',
        buttonUrl: '#',
      },
      {
        tag: 'Результат',
        title: 'Lighthouse 90+ гарантировано',
        text: 'Все опубликованные сайты автоматически оптимизируются: lazy loading, WebP, critical CSS, CDN. Ваши посетители получат быстрый сайт без дополнительных настроек.',
        image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80',
        imageAlt: 'Высокая производительность',
        buttonText: 'Узнать о скорости',
        buttonUrl: '#',
      },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок секции (необязательно)' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
    background: { type: 'color', label: 'Цвет фона', default: '#ffffff' },
    gap: { type: 'number', label: 'Расстояние между строками (px)', default: 80, min: 24, max: 160 },
    items: {
      type: 'array', label: 'Секции',
      itemSchema: {
        tag: { type: 'text', label: 'Тег над заголовком' },
        title: { type: 'text', label: 'Заголовок' },
        text: { type: 'richtext', label: 'Текст' },
        image: { type: 'image', label: 'Изображение' },
        imageAlt: { type: 'text', label: 'Alt-текст изображения' },
        buttonText: { type: 'text', label: 'Текст ссылки (необязательно)' },
        buttonUrl: { type: 'url', label: 'Ссылка', default: '#' },
      },
    },
  },
})

registerBlock({
  type: 'trust-bar',
  label: 'Полоса доверия',
  category: 'content',
  icon: '🔒',
  description: 'Горизонтальный ряд иконок с заголовками — гарантии, преимущества, факты',
  tags: ['trust', 'icons', 'benefits', 'bar', 'badges', 'horizontal'],
  defaultData: {
    background: '#ffffff',
    border: true,
    items: [
      { icon: '🔒', title: 'SSL-защита', text: 'Все данные передаются по HTTPS' },
      { icon: '⚡', title: 'Быстрая загрузка', text: 'Lighthouse 90+ из коробки' },
      { icon: '↩️', title: '30 дней возврат', text: 'Вернём деньги без вопросов' },
      { icon: '🌍', title: 'CDN по всему миру', text: 'Cloudflare в 200+ городах' },
      { icon: '💬', title: 'Поддержка 24/7', text: 'Отвечаем в течение 2 минут' },
    ],
  },
  schema: {
    background: { type: 'color', label: 'Цвет фона', default: '#ffffff' },
    border: { type: 'boolean', label: 'Границы сверху и снизу', default: true },
    items: {
      type: 'array', label: 'Элементы',
      itemSchema: {
        icon: { type: 'icon', label: 'Иконка' },
        title: { type: 'text', label: 'Заголовок' },
        text: { type: 'text', label: 'Описание' },
      },
    },
  },
})

registerBlock({
  type: 'lottie',
  label: 'Lottie анимация',
  category: 'media',
  icon: '🎬',
  description: 'JSON-анимация Lottie — векторные анимации из After Effects / LottieFiles',
  tags: ['lottie', 'animation', 'json', 'vector', 'motion', 'interactive'],
  defaultData: {
    src: 'https://assets10.lottiefiles.com/packages/lf20_tno6cg2w.json',
    title: '',
    caption: '',
    width: '400px',
    height: 400,
    loop: true,
    autoplay: true,
    speed: 1,
    align: 'center',
    padding: 40,
    background: 'transparent',
  },
  schema: {
    src: { type: 'url', label: 'URL .json файла анимации', placeholder: 'https://assets.lottiefiles.com/...' },
    title: { type: 'text', label: 'Заголовок (необязательно)' },
    caption: { type: 'text', label: 'Подпись под анимацией' },
    width: { type: 'text', label: 'Ширина (px или %)', default: '400px' },
    height: { type: 'number', label: 'Высота (px)', default: 400, min: 100, max: 800 },
    loop: { type: 'boolean', label: 'Повторять анимацию', default: true },
    autoplay: { type: 'boolean', label: 'Автовоспроизведение', default: true },
    speed: { type: 'number', label: 'Скорость (1 = обычная)', default: 1, min: 0.1, max: 3 },
    align: { type: 'select', label: 'Выравнивание', options: [{ value: 'center', label: 'По центру' }, { value: 'left', label: 'Слева' }, { value: 'right', label: 'Справа' }], default: 'center' },
    padding: { type: 'number', label: 'Отступ (px)', default: 40, min: 0, max: 120 },
    background: { type: 'color', label: 'Цвет фона', default: 'transparent' },
  },
})

registerBlock({
  type: 'scroll-snap',
  label: 'Полноэкранный скролл',
  category: 'hero',
  icon: '📜',
  description: 'Fullpage-скролл: каждый слайд занимает весь экран, прокрутка по одному',
  tags: ['fullpage', 'scroll-snap', 'fullscreen', 'slides', 'presentation', 'cinematic'],
  defaultData: {
    height: 100,
    heightUnit: 'vh',
    textColor: '#ffffff',
    slides: [
      { background: '#0f0f0f', title: 'Первый экран', subtitle: 'Прокрутите вниз, чтобы увидеть следующий', buttonText: 'Начать', buttonUrl: '#' },
      { background: '#5b6af0', title: 'Второй экран', subtitle: 'Каждый слайд — полноэкранный блок контента', buttonText: 'Далее', buttonUrl: '#' },
      { background: '#ec4899', title: 'Третий экран', subtitle: 'Навигационные точки справа помогают ориентироваться', buttonText: 'Попробовать', buttonUrl: '#' },
    ],
  },
  schema: {
    height: { type: 'number', label: 'Высота (в единицах ниже)', default: 100, min: 50, max: 200 },
    heightUnit: { type: 'select', label: 'Единица высоты', options: [{ value: 'vh', label: 'vh (процент экрана)' }, { value: 'px', label: 'px (пиксели)' }], default: 'vh' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
    slides: {
      type: 'array', label: 'Слайды',
      itemSchema: {
        background: { type: 'color', label: 'Цвет фона' },
        image: { type: 'image', label: 'Фоновое фото (необязательно)' },
        title: { type: 'text', label: 'Заголовок' },
        subtitle: { type: 'text', label: 'Подзаголовок' },
        buttonText: { type: 'text', label: 'Текст кнопки' },
        buttonUrl: { type: 'url', label: 'Ссылка кнопки', default: '#' },
      },
    },
  },
})

registerBlock({
  type: 'horizontal-scroll',
  label: 'Горизонтальный скролл',
  category: 'content',
  icon: '↔',
  description: 'Карточки с горизонтальной прокруткой — перетаскивание мышью и тач',
  tags: ['horizontal', 'scroll', 'cards', 'drag', 'netflix', 'swipe'],
  defaultData: {
    title: 'Наши проекты',
    subtitle: 'Прокрутите вправо, чтобы увидеть все',
    cardWidth: 300,
    cardHeight: 360,
    gap: 20,
    borderRadius: 12,
    background: '#f8f9fa',
    accentColor: '#5b6af0',
    cards: [
      { image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&q=80', tag: 'Дизайн', title: 'Редизайн сайта IT-компании', text: 'Увеличили конверсию на 47% благодаря современному дизайну', link: '#' },
      { image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&q=80', tag: 'Разработка', title: 'Корпоративный портал', text: 'Внутренний инструмент для 500+ сотрудников', link: '#' },
      { image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80', tag: 'Аналитика', title: 'Dashboard аналитики', text: 'Визуализация данных в реальном времени', link: '#' },
      { image: 'https://images.unsplash.com/photo-1542744094-3a31f272c490?w=600&q=80', tag: 'E-commerce', title: 'Интернет-магазин', text: 'Продажи выросли на 200% за 3 месяца', link: '#' },
      { image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&q=80', tag: 'Маркетинг', title: 'Landing page кампания', text: 'CTR 8.4% при среднем по рынку 2%', link: '#' },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок' },
    subtitle: { type: 'text', label: 'Подзаголовок' },
    cardWidth: { type: 'number', label: 'Ширина карточки (px)', default: 300, min: 180, max: 500 },
    gap: { type: 'number', label: 'Отступ между карточками (px)', default: 20, min: 4, max: 60 },
    borderRadius: { type: 'number', label: 'Скругление (px)', default: 12, min: 0, max: 24 },
    background: { type: 'color', label: 'Фон секции', default: '#f8f9fa' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
    cards: {
      type: 'array', label: 'Карточки',
      itemSchema: {
        image: { type: 'image', label: 'Изображение' },
        tag: { type: 'text', label: 'Тег' },
        title: { type: 'text', label: 'Заголовок' },
        text: { type: 'text', label: 'Описание' },
        link: { type: 'url', label: 'Ссылка', placeholder: '#' },
      },
    },
  },
})

registerBlock({
  type: 'metric-cards',
  label: 'Метрики бизнеса',
  category: 'content',
  icon: '📈',
  description: 'Крупные числовые показатели с иконками, описанием и трендом роста',
  tags: ['metrics', 'kpi', 'numbers', 'dashboard', 'stats', 'business'],
  defaultData: {
    title: 'Результаты в цифрах',
    background: '#ffffff',
    columns: 4,
    metrics: [
      { icon: '👥', value: '50 000+', label: 'Активных пользователей', change: '+12% за месяц', up: true },
      { icon: '🌐', value: '180 000+', label: 'Опубликованных сайтов', change: '+8% за месяц', up: true },
      { icon: '⭐', value: '4.9', label: 'Средний рейтинг', change: '↑ 0.1 за квартал', up: true },
      { icon: '⚡', value: '97 мс', label: 'Среднее TTFB', change: '−5 мс за квартал', up: false },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок (необязательно)' },
    background: { type: 'color', label: 'Цвет фона', default: '#ffffff' },
    columns: { type: 'number', label: 'Колонок', default: 4, min: 1, max: 6 },
    metrics: {
      type: 'array', label: 'Показатели',
      itemSchema: {
        icon: { type: 'icon', label: 'Иконка' },
        value: { type: 'text', label: 'Значение (напр. 50 000+)' },
        label: { type: 'text', label: 'Подпись' },
        change: { type: 'text', label: 'Изменение (необязательно)' },
        up: { type: 'boolean', label: 'Рост (зелёный)', default: true },
      },
    },
  },
})

registerBlock({
  type: 'parallax',
  label: 'Параллакс-секция',
  category: 'hero',
  icon: '🏔',
  description: 'Фоновое изображение с эффектом параллакс-скролла и текстом поверх',
  tags: ['parallax', 'scroll', 'background', 'image', 'effect', 'visual'],
  defaultData: {
    image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1600&q=80',
    height: 500,
    overlay: 'rgba(0,0,0,0.45)',
    title: 'Живописные горные пейзажи',
    subtitle: 'Природа во всей своей красоте — прокрутите страницу и почувствуйте глубину',
    textColor: '#ffffff',
    align: 'center',
    speed: 0.4,
  },
  schema: {
    image: { type: 'image', label: 'Фоновое изображение' },
    height: { type: 'number', label: 'Высота секции (px)', default: 500, min: 200, max: 900 },
    overlay: { type: 'color', label: 'Затемнение (rgba)', default: 'rgba(0,0,0,0.45)' },
    title: { type: 'text', label: 'Заголовок' },
    subtitle: { type: 'text', label: 'Подзаголовок' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
    align: { type: 'select', label: 'Выравнивание', options: [{ value: 'center', label: 'По центру' }, { value: 'left', label: 'Слева' }], default: 'center' },
    speed: { type: 'number', label: 'Интенсивность параллакса (0–1)', default: 0.4, min: 0, max: 1 },
  },
})

registerBlock({
  type: 'feature-comparison',
  label: 'Сравнительная таблица',
  category: 'content',
  icon: '⊞',
  description: 'Таблица сравнения планов или продуктов с галочками и значениями',
  tags: ['comparison', 'table', 'features', 'pricing', 'plans', 'vs'],
  defaultData: {
    title: 'Сравните возможности',
    subtitle: 'Выберите план, который подходит именно вам',
    accentColor: '#5b6af0',
    background: '#ffffff',
    highlightCol: 1,
    columns: ['Free', 'Personal', 'Business'],
    rows: [
      { feature: 'Количество сайтов', values: ['1', '3', '10'] },
      { feature: 'Свой домен', values: ['✗', '✓', '✓'] },
      { feature: 'Хранилище', values: ['500 МБ', '5 ГБ', '50 ГБ'] },
      { feature: 'Все блоки', values: ['✗', '✓', '✓'] },
      { feature: 'CMS', values: ['✗', '✗', '✓'] },
      { feature: 'E-commerce', values: ['✗', '✗', '✓'] },
      { feature: 'Аналитика', values: ['✗', '✗', '✓'] },
      { feature: 'Без брендинга BST', values: ['✗', '✓', '✓'] },
      { feature: 'Приоритетная поддержка', values: ['✗', '✗', '✓'] },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок' },
    subtitle: { type: 'text', label: 'Подзаголовок' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
    background: { type: 'color', label: 'Цвет фона', default: '#ffffff' },
    highlightCol: { type: 'number', label: 'Выделить колонку (0-based)', default: 1, min: 0, max: 5 },
    columns: { type: 'array', label: 'Заголовки колонок', stringArray: true },
    rows: {
      type: 'array', label: 'Строки',
      itemSchema: {
        feature: { type: 'text', label: 'Функция' },
      },
    },
  },
})

registerBlock({
  type: 'reviews-grid',
  label: 'Отзывы (сетка)',
  category: 'content',
  icon: '⭐',
  description: 'Сетка карточек отзывов с рейтингом, аватаром и источником',
  tags: ['reviews', 'ratings', 'stars', 'testimonials', 'grid', 'social proof'],
  defaultData: {
    title: 'Что говорят наши клиенты',
    subtitle: 'Более 2 000 компаний уже используют BST для своих сайтов',
    columns: 3,
    accentColor: '#f59e0b',
    background: '#f8f9fa',
    reviews: [
      { name: 'Алексей Соколов', role: 'CEO, StartupX', rating: 5, text: 'Запустили лендинг за 2 часа. Конверсия выросла на 40% по сравнению с предыдущим сайтом. Впечатляет!', source: 'Google Reviews' },
      { name: 'Мария Козлова', role: 'Маркетолог', rating: 5, text: 'Наконец-то инструмент, который не требует разработчика. Делаю сайты сама — быстро и красиво.', source: 'Яндекс Карты' },
      { name: 'Дмитрий Жуков', role: 'Founder, Agency Pro', rating: 5, text: 'White label режим — это просто мечта. Продаём собственный конструктор клиентам, дополнительный доход!', source: 'VC.ru' },
      { name: 'Елена Морозова', role: 'SMM специалист', rating: 4, text: 'Блоки анимации и параллакс-эффекты — вещь! Клиенты в восторге от визуала.', source: 'Google Reviews' },
      { name: 'Павел Орлов', role: 'Интернет-магазин', rating: 5, text: 'E-commerce работает как часы. Платёжная система настроена за 10 минут, первый заказ пришёл в тот же день.', source: 'Trustpilot' },
      { name: 'Анна Волкова', role: 'Контент-менеджер', rating: 5, text: 'Лучший конструктор, с которым я работала. Особенно нравится квиз-блок — лиды идут потоком.', source: 'AppSumo' },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок секции' },
    subtitle: { type: 'text', label: 'Подзаголовок' },
    columns: { type: 'number', label: 'Колонок', default: 3, min: 1, max: 4 },
    accentColor: { type: 'color', label: 'Цвет звёзд', default: '#f59e0b' },
    background: { type: 'color', label: 'Цвет фона', default: '#f8f9fa' },
    reviews: {
      type: 'array', label: 'Отзывы',
      itemSchema: {
        name: { type: 'text', label: 'Имя' },
        role: { type: 'text', label: 'Должность/компания' },
        avatar: { type: 'image', label: 'Аватар (необязательно)' },
        rating: { type: 'number', label: 'Рейтинг (1-5)', default: 5, min: 1, max: 5 },
        text: { type: 'text', label: 'Текст отзыва' },
        source: { type: 'text', label: 'Источник (напр. Google)' },
      },
    },
  },
})

registerBlock({
  type: 'faq-search',
  label: 'FAQ с поиском',
  category: 'interactive',
  icon: '🔍',
  description: 'Блок вопросов-ответов с полем поиска в реальном времени',
  tags: ['faq', 'search', 'filter', 'questions', 'answers', 'accordion'],
  defaultData: {
    title: 'Часто задаваемые вопросы',
    placeholder: 'Поиск по вопросам...',
    accentColor: '#5b6af0',
    background: '#ffffff',
    items: [
      { question: 'Как начать работу с BST?', answer: 'Зарегистрируйтесь бесплатно, создайте сайт из готовых блоков и опубликуйте его в один клик. Никаких технических навыков не требуется.' },
      { question: 'Можно ли подключить свой домен?', answer: 'Да, начиная с тарифа Personal вы можете подключить любой домен. Мы автоматически настроим SSL-сертификат.' },
      { question: 'Есть ли бесплатный тариф?', answer: 'Да, тариф Free позволяет создать 1 сайт на поддомене .mybst.ru с базовыми блоками — без ограничений по времени.' },
      { question: 'Как работает электронная коммерция?', answer: 'На тарифе Business вы можете добавлять товары, принимать оплату через Stripe и ЮKassa, управлять заказами из панели BST.' },
      { question: 'Поддерживается ли многоязычность?', answer: 'Функция мультиязычного сайта доступна на тарифе Business и выше. Вы можете создать отдельные версии страниц для каждого языка.' },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок секции' },
    placeholder: { type: 'text', label: 'Placeholder поиска', default: 'Поиск по вопросам...' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
    background: { type: 'color', label: 'Цвет фона', default: '#ffffff' },
    items: {
      type: 'array', label: 'Вопросы и ответы',
      itemSchema: {
        question: { type: 'text', label: 'Вопрос' },
        answer: { type: 'text', label: 'Ответ' },
      },
    },
  },
})

registerBlock({
  type: 'lightbox-gallery',
  label: 'Галерея с лайтбоксом',
  category: 'gallery',
  icon: '🖼',
  description: 'Сетка фотографий — клик открывает полноэкранный просмотр с навигацией',
  tags: ['gallery', 'lightbox', 'photos', 'zoom', 'fullscreen', 'portfolio'],
  defaultData: {
    title: 'Галерея',
    columns: 3,
    gap: 12,
    borderRadius: 8,
    aspectRatio: '4/3',
    images: [
      { src: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80', alt: 'Горы', caption: 'Горный пейзаж' },
      { src: 'https://images.unsplash.com/photo-1511884642898-4c92249e20b6?w=800&q=80', alt: 'Лес', caption: 'Лесной массив' },
      { src: 'https://images.unsplash.com/photo-1501854140801-50d01698950b?w=800&q=80', alt: 'Озеро', caption: 'Горное озеро' },
      { src: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=800&q=80', alt: 'Скалы', caption: 'Горные скалы' },
      { src: 'https://images.unsplash.com/photo-1519681393784-d120267933ba?w=800&q=80', alt: 'Ночь', caption: 'Горная ночь' },
      { src: 'https://images.unsplash.com/photo-1434394354979-a235cd36269d?w=800&q=80', alt: 'Закат', caption: 'Горный закат' },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок (необязательно)' },
    columns: { type: 'number', label: 'Количество колонок', default: 3, min: 2, max: 5 },
    gap: { type: 'number', label: 'Отступ (px)', default: 12, min: 0, max: 40 },
    borderRadius: { type: 'number', label: 'Скругление углов (px)', default: 8, min: 0, max: 24 },
    aspectRatio: {
      type: 'select', label: 'Соотношение сторон', default: '4/3',
      options: [{ value: '1/1', label: '1:1 (квадрат)' }, { value: '4/3', label: '4:3' }, { value: '16/9', label: '16:9' }, { value: '3/2', label: '3:2' }],
    },
    images: {
      type: 'array', label: 'Изображения',
      itemSchema: {
        src: { type: 'image', label: 'Изображение' },
        alt: { type: 'text', label: 'Alt-текст' },
        caption: { type: 'text', label: 'Подпись в лайтбоксе' },
      },
    },
  },
})

registerBlock({
  type: 'video-section',
  label: 'Видео с описанием',
  category: 'media',
  icon: '▶',
  description: 'Красиво оформленный блок с видео, заголовком и описанием',
  tags: ['video', 'youtube', 'vimeo', 'embed', 'media'],
  defaultData: {
    title: 'Посмотрите, как это работает',
    subtitle: 'Всего 3 минуты — и вы увидите всё, что умеет BST',
    url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
    description: '',
    aspectRatio: '16/9',
    maxWidth: 900,
    background: '#ffffff',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок над видео' },
    subtitle: { type: 'text', label: 'Подзаголовок' },
    url: { type: 'url', label: 'Ссылка YouTube или Vimeo', placeholder: 'https://youtube.com/watch?v=...' },
    description: { type: 'text', label: 'Описание под видео (необязательно)' },
    aspectRatio: {
      type: 'select', label: 'Соотношение сторон', default: '16/9',
      options: [{ value: '16/9', label: '16:9' }, { value: '4/3', label: '4:3' }, { value: '1/1', label: '1:1' }],
    },
    maxWidth: { type: 'number', label: 'Максимальная ширина (px)', default: 900, min: 400, max: 1400 },
    background: { type: 'color', label: 'Цвет фона', default: '#ffffff' },
  },
})

registerBlock({
  type: 'typewriter',
  label: 'Печатная машинка',
  category: 'hero',
  icon: '⌨',
  description: 'Hero с анимацией печатания: меняющиеся слова набираются и стираются',
  tags: ['typewriter', 'typing', 'animation', 'hero', 'text'],
  defaultData: {
    prefix: 'Делаем всё',
    words: ['Профессионально', 'Быстро', 'Красиво', 'Надёжно'],
    subtitle: 'Профессиональный результат — с первого раза, без кода и лишних усилий',
    buttonText: 'Начать бесплатно',
    buttonUrl: '#',
    fontSize: 64,
    typingSpeed: 80,
    pauseMs: 1800,
    background: '#0f0f0f',
    textColor: '#ffffff',
    accentColor: '#5b6af0',
    align: 'center',
  },
  schema: {
    prefix: { type: 'text', label: 'Статичный текст перед анимацией', placeholder: 'Мы делаем всё' },
    words: { type: 'array', label: 'Слова для анимации', stringArray: true },
    subtitle: { type: 'text', label: 'Подзаголовок' },
    buttonText: { type: 'text', label: 'Текст кнопки (пусто = скрыть)' },
    buttonUrl: { type: 'url', label: 'Ссылка кнопки', default: '#' },
    fontSize: { type: 'number', label: 'Размер заголовка (px)', default: 64, min: 28, max: 120 },
    typingSpeed: { type: 'number', label: 'Скорость печатания (мс/символ)', default: 80, min: 20, max: 300 },
    pauseMs: { type: 'number', label: 'Пауза после слова (мс)', default: 1800, min: 500, max: 5000 },
    background: { type: 'color', label: 'Цвет фона', default: '#0f0f0f' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Цвет анимированного слова', default: '#5b6af0' },
    align: { type: 'select', label: 'Выравнивание', options: [{ value: 'center', label: 'По центру' }, { value: 'left', label: 'Слева' }], default: 'center' },
  },
})

registerBlock({
  type: 'pricing-toggle',
  label: 'Тарифы с переключателем',
  category: 'content',
  icon: '💰',
  description: 'Ценовые карточки с переключением ежемесячной/ежегодной оплаты и скидкой',
  tags: ['pricing', 'plans', 'toggle', 'annual', 'monthly', 'saas'],
  defaultData: {
    title: 'Выберите тарифный план',
    subtitle: 'Начните бесплатно, масштабируйтесь по мере роста',
    savingsBadge: 'Экономия 20%',
    accentColor: '#5b6af0',
    background: '#f8f9fa',
    plans: [
      {
        name: 'Free',
        monthlyPrice: '0 ₽',
        yearlyPrice: '0 ₽',
        popular: false,
        features: ['1 сайт', 'Поддомен .mybst.ru', '500 МБ хранилища', 'Базовые блоки'],
        buttonText: 'Начать бесплатно',
        buttonUrl: '#',
      },
      {
        name: 'Personal',
        monthlyPrice: '790 ₽',
        yearlyPrice: '590 ₽',
        popular: false,
        features: ['3 сайта', 'Свой домен', '5 ГБ хранилища', 'Все блоки', 'Без брендинга BST'],
        buttonText: 'Выбрать Personal',
        buttonUrl: '#',
      },
      {
        name: 'Business',
        monthlyPrice: '1 990 ₽',
        yearlyPrice: '1 490 ₽',
        popular: true,
        features: ['10 сайтов', 'CMS', 'E-commerce', 'Аналитика', 'Коллаборация', 'Приоритетная поддержка'],
        buttonText: 'Выбрать Business',
        buttonUrl: '#',
      },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок' },
    subtitle: { type: 'text', label: 'Подзаголовок' },
    savingsBadge: { type: 'text', label: 'Текст бейджа скидки', default: 'Экономия 20%' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
    background: { type: 'color', label: 'Цвет фона', default: '#f8f9fa' },
    plans: {
      type: 'array', label: 'Тарифы',
      itemSchema: {
        name: { type: 'text', label: 'Название тарифа' },
        monthlyPrice: { type: 'text', label: 'Ежемесячная цена' },
        yearlyPrice: { type: 'text', label: 'Ежегодная цена (в месяц)' },
        popular: { type: 'boolean', label: 'Популярный', default: false },
        buttonText: { type: 'text', label: 'Текст кнопки' },
        buttonUrl: { type: 'url', label: 'Ссылка кнопки', default: '#' },
      },
    },
  },
})

registerBlock({
  type: 'gradient-hero',
  label: 'Hero — Градиент',
  category: 'hero',
  icon: '🌈',
  description: 'Герой-секция с плавно анимированным градиентным фоном',
  tags: ['hero', 'gradient', 'animated', 'landing', 'colorful'],
  defaultData: {
    title: 'Будущее начинается здесь',
    subtitle: 'Создавайте сайты нового поколения без ограничений — быстро, красиво, профессионально',
    buttonText: 'Начать прямо сейчас',
    buttonUrl: '#',
    button2Text: 'Смотреть демо',
    button2Url: '#',
    color1: '#5b6af0',
    color2: '#ec4899',
    color3: '#f59e0b',
    textColor: '#ffffff',
    height: 540,
    animated: true,
  },
  schema: {
    title: { type: 'text', label: 'Заголовок' },
    subtitle: { type: 'text', label: 'Подзаголовок' },
    buttonText: { type: 'text', label: 'Кнопка 1 (светлая)', placeholder: 'Начать' },
    buttonUrl: { type: 'url', label: 'Ссылка кнопки 1', default: '#' },
    button2Text: { type: 'text', label: 'Кнопка 2 (прозрачная)', placeholder: 'Узнать больше' },
    button2Url: { type: 'url', label: 'Ссылка кнопки 2', default: '#' },
    color1: { type: 'color', label: 'Цвет 1', default: '#5b6af0' },
    color2: { type: 'color', label: 'Цвет 2', default: '#ec4899' },
    color3: { type: 'color', label: 'Цвет 3', default: '#f59e0b' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
    height: { type: 'number', label: 'Минимальная высота (px)', default: 540, min: 300, max: 1000 },
    animated: { type: 'boolean', label: 'Анимация градиента', default: true },
  },
})

registerBlock({
  type: 'image-hotspots',
  label: 'Изображение с точками',
  category: 'media',
  icon: '📍',
  description: 'Изображение с интерактивными метками — идеально для инфографики и схем',
  defaultData: {
    image: '',
    hotspots: [
      { x: 30, y: 40, label: 'Точка 1', description: 'Описание первой точки' },
      { x: 65, y: 55, label: 'Точка 2', description: 'Описание второй точки' },
    ],
  },
  schema: {
    image: { type: 'image', label: 'Изображение' },
    hotspots: {
      type: 'array',
      label: 'Точки',
      itemSchema: {
        x: { type: 'number', label: 'X (%)', default: 50, min: 0, max: 100 },
        y: { type: 'number', label: 'Y (%)', default: 50, min: 0, max: 100 },
        label: { type: 'text', label: 'Заголовок точки' },
        description: { type: 'text', label: 'Описание точки' },
      },
    },
  },
  tags: ['hotspot', 'image', 'interactive', 'infographic'],
})

registerBlock({
  type: 'step-form',
  label: 'Многошаговая форма',
  category: 'form',
  icon: '🪜',
  description: 'Форма с несколькими шагами — повышает конверсию за счёт разбивки на этапы',
  defaultData: {
    steps: [
      {
        title: 'Шаг 1 — Контактные данные',
        fields: [
          { label: 'Имя', type: 'text', required: true },
          { label: 'Email', type: 'email', required: true },
        ],
      },
      {
        title: 'Шаг 2 — О задаче',
        fields: [
          { label: 'Тема', type: 'select', required: true, options: 'Консультация\nПроект\nПартнёрство' },
          { label: 'Сообщение', type: 'text', required: false },
        ],
      },
    ],
    submitText: 'Отправить заявку',
    buttonColor: '#5b6af0',
    successTitle: 'Спасибо!',
    successText: 'Мы свяжемся с вами в ближайшее время.',
    action: '',
  },
  schema: {
    steps: {
      type: 'array',
      label: 'Шаги',
      itemSchema: {
        title: { type: 'text', label: 'Заголовок шага' },
        fields: {
          type: 'array',
          label: 'Поля',
          itemSchema: {
            label: { type: 'text', label: 'Подпись поля' },
            type: { type: 'select', label: 'Тип', default: 'text', options: [
              { value: 'text', label: 'Текст' },
              { value: 'email', label: 'Email' },
              { value: 'tel', label: 'Телефон' },
              { value: 'select', label: 'Выбор' },
              { value: 'textarea', label: 'Многострочный текст' },
            ]},
            required: { type: 'boolean', label: 'Обязательное', default: false },
            options: { type: 'text', label: 'Варианты (по одному на строку, для select)' },
          },
        },
      },
    },
    submitText: { type: 'text', label: 'Текст кнопки отправки', default: 'Отправить' },
    buttonColor: { type: 'color', label: 'Цвет кнопки', default: '#5b6af0' },
    successTitle: { type: 'text', label: 'Заголовок успеха', default: 'Спасибо!' },
    successText: { type: 'text', label: 'Текст успеха', default: 'Заявка принята.' },
    action: { type: 'url', label: 'action URL (необязательно)', default: '' },
  },
  tags: ['form', 'multi-step', 'wizard', 'lead', 'conversion'],
})

registerBlock({
  type: 'faq-categories',
  label: 'FAQ с категориями',
  category: 'content',
  icon: '🗂️',
  description: 'Раздел вопросов и ответов с фильтрацией по категориям',
  defaultData: {
    title: 'Часто задаваемые вопросы',
    categories: [
      {
        name: 'Общие вопросы',
        items: [
          { q: 'Как начать работу?', a: 'Зарегистрируйтесь на сайте и следуйте инструкциям в личном кабинете.' },
          { q: 'Есть ли пробный период?', a: 'Да, мы предлагаем 14 дней бесплатного доступа к полной версии.' },
        ],
      },
      {
        name: 'Оплата',
        items: [
          { q: 'Какие способы оплаты доступны?', a: 'Банковские карты, СБП, ЮMoney и другие.' },
          { q: 'Можно ли получить возврат?', a: 'Да, в течение 30 дней с момента оплаты.' },
        ],
      },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок раздела' },
    categories: {
      type: 'array',
      label: 'Категории',
      itemSchema: {
        name: { type: 'text', label: 'Название категории' },
        items: {
          type: 'array',
          label: 'Вопросы',
          itemSchema: {
            q: { type: 'text', label: 'Вопрос' },
            a: { type: 'text', label: 'Ответ' },
          },
        },
      },
    },
  },
  tags: ['faq', 'accordion', 'categories', 'tabs', 'questions'],
})

registerBlock({
  type: 'spin-wheel',
  label: 'Колесо удачи',
  category: 'interactive',
  icon: '🎡',
  description: 'Интерактивное колесо фортуны — соберите email и выдайте случайный приз. Повышает вовлечённость.',
  defaultData: {
    title: 'Испытайте удачу!',
    subtitle: 'Введите email и крутите колесо фортуны',
    buttonText: 'Крутить колесо!',
    requireEmail: true,
    background: '#f8f9fa',
    segments: [
      { label: 'Скидка 10%', color: '#5b6af0', prize: 'Купон -10%: WHEEL10' },
      { label: 'Скидка 20%', color: '#ec4899', prize: 'Купон -20%: WHEEL20' },
      { label: 'Подарок', color: '#f59e0b', prize: 'Бесплатная доставка' },
      { label: 'Повторите', color: '#22c55e', prize: 'Попробуйте снова!' },
      { label: 'Скидка 5%', color: '#8b5cf6', prize: 'Купон -5%: WHEEL5' },
      { label: 'Бонус', color: '#ef4444', prize: 'Бонусные баллы +100' },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок' },
    subtitle: { type: 'text', label: 'Подзаголовок' },
    buttonText: { type: 'text', label: 'Текст кнопки', default: 'Крутить!' },
    requireEmail: { type: 'boolean', label: 'Требовать email', default: true },
    background: { type: 'color', label: 'Фон секции', default: '#f8f9fa' },
    segments: {
      type: 'array',
      label: 'Сегменты колеса',
      itemSchema: {
        label: { type: 'text', label: 'Подпись на колесе' },
        color: { type: 'color', label: 'Цвет сегмента', default: '#5b6af0' },
        prize: { type: 'text', label: 'Текст приза при выигрыше' },
      },
    },
  },
  tags: ['wheel', 'fortune', 'gamification', 'lead', 'email', 'promo', 'interactive'],
})

registerBlock({
  type: 'before-after-v2',
  label: 'До/После (сетка)',
  category: 'media',
  icon: '🔄',
  description: 'Сетка интерактивных сравнений До/После — для портфолио, ремонта, услуг',
  defaultData: {
    title: 'Наши работы',
    columns: 2,
    items: [
      { before: '', after: '', label: 'Проект 1' },
      { before: '', after: '', label: 'Проект 2' },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок' },
    columns: { type: 'number', label: 'Колонок', default: 2, min: 1, max: 3 },
    items: {
      type: 'array',
      label: 'Пары изображений',
      itemSchema: {
        label: { type: 'text', label: 'Подпись' },
        before: { type: 'image', label: 'До' },
        after: { type: 'image', label: 'После' },
      },
    },
  },
  tags: ['before-after', 'comparison', 'portfolio', 'gallery', 'slider'],
})

registerBlock({
  type: 'tabs-v2',
  label: 'Вкладки улучшенные',
  category: 'content',
  icon: '📑',
  description: 'Вкладки с иконками, изображениями и HTML-контентом. Стили: underline или pills.',
  defaultData: {
    title: '',
    variant: 'underline',
    accentColor: '#5b6af0',
    tabs: [
      { label: 'Вкладка 1', icon: '⚡', content: 'Содержимое первой вкладки.', image: '' },
      { label: 'Вкладка 2', icon: '🚀', content: 'Содержимое второй вкладки.', image: '' },
      { label: 'Вкладка 3', icon: '✨', content: 'Содержимое третьей вкладки.', image: '' },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок (необязательно)' },
    variant: { type: 'select', label: 'Стиль вкладок', default: 'underline', options: [
      { value: 'underline', label: 'Подчёркивание' },
      { value: 'pills', label: 'Таблетки (pills)' },
    ]},
    accentColor: { type: 'color', label: 'Цвет активной вкладки', default: '#5b6af0' },
    tabs: {
      type: 'array',
      label: 'Вкладки',
      itemSchema: {
        label: { type: 'text', label: 'Заголовок вкладки' },
        icon: { type: 'text', label: 'Иконка (эмодзи)' },
        content: { type: 'richtext', label: 'Текст содержимого' },
        image: { type: 'image', label: 'Изображение (необязательно)' },
      },
    },
  },
  tags: ['tabs', 'content', 'accordion', 'features', 'interactive'],
})

registerBlock({
  type: 'cms-collection',
  label: 'CMS Коллекция',
  category: 'content',
  icon: '🗃',
  description: 'Динамический список элементов из CMS коллекции (блог, портфолио, товары)',
  defaultData: {
    title: 'Последние записи',
    collectionSlug: '',
    displayType: 'grid',
    columns: 3,
    limit: 6,
    titleField: 'title',
    bodyField: 'body',
    imageField: 'image',
    dateField: '',
    linkField: '',
    buttonText: 'Читать далее',
    cardBg: '#ffffff',
    accentColor: '#5b6af0',
    showDate: true,
    showExcerpt: true,
    excerptLength: 120,
  },
  schema: {
    title: { type: 'text', label: 'Заголовок секции', default: '' },
    collectionSlug: { type: 'text', label: 'Slug коллекции (из CMS)', placeholder: 'blog, portfolio, news...' },
    displayType: {
      type: 'select',
      label: 'Тип отображения',
      default: 'grid',
      options: [
        { value: 'grid', label: 'Сетка карточек' },
        { value: 'list', label: 'Список' },
        { value: 'masonry', label: 'Masonry' },
        { value: 'featured', label: 'Первый крупный + остальные' },
      ],
    },
    columns: { type: 'number', label: 'Колонок (для сетки)', default: 3, min: 1, max: 4 },
    limit: { type: 'number', label: 'Макс. количество элементов', default: 6, min: 1, max: 24 },
    titleField: { type: 'text', label: 'Поле заголовка', default: 'title', placeholder: 'title' },
    bodyField: { type: 'text', label: 'Поле текста/описания', default: 'body', placeholder: 'body' },
    imageField: { type: 'text', label: 'Поле изображения', default: 'image', placeholder: 'image' },
    dateField: { type: 'text', label: 'Поле даты (необязательно)', default: '', placeholder: 'date' },
    linkField: { type: 'text', label: 'Поле ссылки (необязательно)', default: '', placeholder: 'url' },
    buttonText: { type: 'text', label: 'Текст кнопки "Читать"', default: 'Читать далее' },
    cardBg: { type: 'color', label: 'Фон карточки', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
    showDate: { type: 'boolean', label: 'Показывать дату', default: true },
    showExcerpt: { type: 'boolean', label: 'Показывать отрывок текста', default: true },
    excerptLength: { type: 'number', label: 'Длина отрывка (символов)', default: 120, min: 40, max: 400 },
    filterField: { type: 'text', label: 'Поле для фильтрации (например: category, tag)', placeholder: 'category' },
    showFilter: { type: 'boolean', label: 'Показывать фильтры', default: false },
    searchable: { type: 'boolean', label: 'Поиск по заголовку', default: false },
    sortField: { type: 'text', label: 'Поле для сортировки (дата, название)', placeholder: 'date' },
    sortOrder: { type: 'select', label: 'Порядок сортировки', options: [{ value: 'asc', label: 'По возрастанию' }, { value: 'desc', label: 'По убыванию' }], default: 'desc' },
  },
  tags: ['cms', 'blog', 'dynamic', 'collection', 'list', 'grid'],
})

registerBlock({
  type: 'cms-list',
  label: 'CMS Список (компактный)',
  category: 'content',
  icon: '📋',
  description: 'Компактный список из CMS коллекции — cards, list или table layout',
  defaultData: {
    sectionTitle: '',
    collectionSlug: '',
    template: 'cards',
    columns: 3,
    limit: 12,
    titleField: 'title',
    descriptionField: 'description',
    imageField: 'image',
    linkField: 'url',
    tagField: 'tag',
    accentColor: '#5b6af0',
  },
  schema: {
    sectionTitle: { type: 'text', label: 'Заголовок секции (необязательно)' },
    collectionSlug: { type: 'text', label: 'Slug коллекции', placeholder: 'blog, portfolio, news...' },
    template: { type: 'select', label: 'Шаблон', default: 'cards', options: [
      { value: 'cards', label: 'Карточки' },
      { value: 'list', label: 'Список' },
      { value: 'table', label: 'Таблица' },
    ]},
    columns: { type: 'number', label: 'Колонок (для cards)', default: 3, min: 1, max: 4 },
    limit: { type: 'number', label: 'Максимум записей', default: 12, min: 1, max: 50 },
    titleField: { type: 'text', label: 'Поле заголовка', default: 'title' },
    descriptionField: { type: 'text', label: 'Поле описания', default: 'description' },
    imageField: { type: 'text', label: 'Поле изображения', default: 'image' },
    linkField: { type: 'text', label: 'Поле ссылки', default: 'url' },
    tagField: { type: 'text', label: 'Поле тега/категории', default: 'tag' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['cms', 'list', 'collection', 'dynamic', 'blog', 'cards'],
})

registerBlock({
  type: 'qr-code',
  label: 'QR-код',
  category: 'media',
  icon: '▦',
  description: 'Автоматически генерирует QR-код для любого URL или текста',
  defaultData: {
    url: 'https://example.com',
    size: 200,
    title: 'Отсканируйте QR-код',
    subtitle: '',
    align: 'center',
  },
  schema: {
    url: { type: 'url', label: 'URL или текст', default: 'https://example.com' },
    size: { type: 'number', label: 'Размер (px)', default: 200, min: 100, max: 500 },
    title: { type: 'text', label: 'Заголовок' },
    subtitle: { type: 'text', label: 'Подзаголовок' },
    align: { type: 'select', label: 'Выравнивание', default: 'center', options: [
      { value: 'left', label: 'По левому краю' },
      { value: 'center', label: 'По центру' },
      { value: 'right', label: 'По правому краю' },
    ]},
  },
  tags: ['qr', 'code', 'scan', 'link', 'mobile'],
})

registerBlock({
  type: 'iframe-embed',
  label: 'Embed (iframe)',
  category: 'media',
  icon: '🪟',
  description: 'Встроить любой внешний сайт, карту, виджет или сервис через iframe',
  defaultData: {
    src: '',
    height: 500,
    title: '',
    border: true,
    borderRadius: 12,
  },
  schema: {
    src: { type: 'url', label: 'URL для встраивания' },
    height: { type: 'number', label: 'Высота (px)', default: 500, min: 100, max: 2000 },
    title: { type: 'text', label: 'Заголовок (доступность)' },
    border: { type: 'boolean', label: 'Показывать рамку', default: true },
    borderRadius: { type: 'number', label: 'Скругление углов (px)', default: 12, min: 0, max: 32 },
  },
  tags: ['iframe', 'embed', 'map', 'widget', 'external'],
})

registerBlock({
  type: 'rating-summary',
  label: 'Рейтинг и отзывы',
  category: 'content',
  icon: '⭐',
  description: 'Сводный рейтинг с распределением оценок — идеально для landing pages с отзывами',
  defaultData: {
    overall: 4.8,
    reviewCount: 147,
    accentColor: '#f59e0b',
    distribution: [
      { stars: 5, count: 112 },
      { stars: 4, count: 24 },
      { stars: 3, count: 8 },
      { stars: 2, count: 2 },
      { stars: 1, count: 1 },
    ],
  },
  schema: {
    overall: { type: 'number', label: 'Общий рейтинг', default: 4.8, min: 0, max: 5 },
    reviewCount: { type: 'number', label: 'Количество отзывов', default: 0, min: 0, max: 999999 },
    accentColor: { type: 'color', label: 'Цвет звёзд', default: '#f59e0b' },
    distribution: {
      type: 'array',
      label: 'Распределение оценок',
      itemSchema: {
        stars: { type: 'number', label: 'Звёзд', default: 5, min: 1, max: 5 },
        count: { type: 'number', label: 'Количество', default: 0, min: 0, max: 99999 },
      },
    },
  },
  tags: ['rating', 'reviews', 'stars', 'trust', 'social-proof'],
})

registerBlock({
  type: 'coupon-reveal',
  label: 'Промокод (раскрыть)',
  category: 'interactive',
  icon: '🎟️',
  description: 'Скрытый промокод с кнопкой «Получить» — повышает вовлечённость, кнопка копирования',
  defaultData: {
    code: 'PROMO2024',
    discount: 'Скидка 20%',
    description: 'Используйте этот промокод при оформлении заказа',
    buttonText: 'Получить промокод',
    accentColor: '#5b6af0',
    expiry: '',
  },
  schema: {
    code: { type: 'text', label: 'Промокод', default: 'PROMO2024' },
    discount: { type: 'text', label: 'Заголовок скидки', default: 'Скидка 20%' },
    description: { type: 'text', label: 'Описание', default: 'Используйте этот промокод при оформлении заказа' },
    buttonText: { type: 'text', label: 'Текст кнопки', default: 'Получить промокод' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
    expiry: { type: 'text', label: 'Срок действия (дата, необязательно)', placeholder: '31 декабря 2024' },
  },
  tags: ['coupon', 'promo', 'discount', 'code', 'reveal', 'gamification'],
})

registerBlock({
  type: 'social-share',
  label: 'Кнопки «Поделиться»',
  category: 'interactive',
  icon: '📤',
  description: 'Кнопки шаринга: ВКонтакте, Telegram, WhatsApp, Twitter, Facebook, копировать ссылку',
  defaultData: {
    title: 'Поделиться',
    accentColor: '#5b6af0',
    networks: ['vk', 'telegram', 'whatsapp', 'twitter', 'copy'],
  },
  schema: {
    title: { type: 'text', label: 'Подпись (необязательно)', default: 'Поделиться' },
    accentColor: { type: 'color', label: 'Акцент (не используется)', default: '#5b6af0' },
    networks: {
      type: 'array',
      label: 'Сети (vk, telegram, whatsapp, twitter, facebook, copy)',
      stringArray: true,
    },
  },
  tags: ['share', 'social', 'viral', 'vk', 'telegram', 'whatsapp'],
})

registerBlock({
  type: 'countdown-redirect',
  label: 'Таймер с редиректом',
  category: 'interactive',
  icon: '⏱️',
  description: 'Обратный отсчёт до конца акции. При истечении показывает сообщение и/или редиректит',
  defaultData: {
    title: 'Акция заканчивается через:',
    subtitle: 'Успейте воспользоваться предложением!',
    deadline: '',
    redirectUrl: '',
    endText: 'Предложение закончилось',
    accentColor: '#ef4444',
    background: '#fff5f5',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок' },
    subtitle: { type: 'text', label: 'Подзаголовок' },
    deadline: { type: 'text', label: 'Дата окончания (ISO: 2024-12-31T23:59:59)', placeholder: '2024-12-31T23:59:59' },
    redirectUrl: { type: 'url', label: 'URL для редиректа после окончания (необязательно)' },
    endText: { type: 'text', label: 'Текст после окончания', default: 'Предложение закончилось' },
    accentColor: { type: 'color', label: 'Цвет цифр', default: '#ef4444' },
    background: { type: 'color', label: 'Фон секции', default: '#fff5f5' },
  },
  tags: ['countdown', 'timer', 'redirect', 'deadline', 'urgency', 'sale'],
})

registerBlock({
  type: 'number-counter',
  label: 'Счётчик цифр',
  category: 'interactive',
  icon: '🔢',
  description: 'Анимированные числа, которые считаются при появлении в зоне видимости — статистика, достижения',
  defaultData: {
    items: [
      { value: 500, suffix: '+', label: 'Довольных клиентов' },
      { value: 12, suffix: ' лет', label: 'На рынке' },
      { value: 98, suffix: '%', label: 'Положительных отзывов' },
      { value: 50, suffix: 'K', label: 'Выполненных проектов' },
    ],
    accentColor: '#5b6af0',
    duration: 2000,
    columns: 4,
  },
  schema: {
    items: {
      type: 'array',
      label: 'Счётчики',
      itemSchema: {
        value: { type: 'number', label: 'Число', default: 100, min: 0, max: 999999 },
        suffix: { type: 'text', label: 'Суффикс (+, %, K и т.д.)', default: '+' },
        label: { type: 'text', label: 'Подпись', default: 'Клиентов' },
      },
    },
    accentColor: { type: 'color', label: 'Цвет числа', default: '#5b6af0' },
    duration: { type: 'number', label: 'Длительность анимации (мс)', default: 2000, min: 500, max: 5000 },
    columns: { type: 'number', label: 'Колонок', default: 4, min: 1, max: 6 },
  },
  tags: ['counter', 'number', 'stats', 'achievements', 'animate', 'scroll'],
})

registerBlock({
  type: 'progress-bars',
  label: 'Прогресс-бары / Навыки',
  category: 'content',
  icon: '📊',
  description: 'Горизонтальные прогресс-бары с анимацией — навыки, характеристики, достижения',
  defaultData: {
    title: 'Наши навыки',
    items: [
      { label: 'Веб-разработка', value: 92 },
      { label: 'Дизайн', value: 85 },
      { label: 'SEO-продвижение', value: 78 },
      { label: 'Маркетинг', value: 70 },
    ],
    accentColor: '#5b6af0',
    trackColor: '#e5e7eb',
    showPercent: true,
    animated: true,
  },
  schema: {
    title: { type: 'text', label: 'Заголовок блока', default: 'Наши навыки' },
    items: {
      type: 'array',
      label: 'Навыки',
      itemSchema: {
        label: { type: 'text', label: 'Название', default: 'Навык' },
        value: { type: 'number', label: 'Процент (0–100)', default: 80, min: 0, max: 100 },
      },
    },
    accentColor: { type: 'color', label: 'Цвет заливки', default: '#5b6af0' },
    trackColor: { type: 'color', label: 'Цвет фона', default: '#e5e7eb' },
    showPercent: { type: 'boolean', label: 'Показывать процент', default: true },
    animated: { type: 'boolean', label: 'Анимация при появлении', default: true },
  },
  tags: ['progress', 'skills', 'bars', 'percent', 'animated', 'achievements'],
})

registerBlock({
  type: 'map-embed',
  label: 'Карта',
  category: 'interactive',
  icon: '🗺️',
  description: 'Встроенная карта Google Maps или Яндекс.Карты с кастомными параметрами',
  defaultData: {
    provider: 'google',
    googleQuery: 'Москва, Красная площадь',
    yandexUrl: '',
    height: 400,
    zoom: 14,
    showAddress: true,
    address: 'г. Москва, Красная площадь',
  },
  schema: {
    provider: { type: 'select', label: 'Провайдер', default: 'google', options: [{ value: 'google', label: 'Google Maps' }, { value: 'yandex', label: 'Яндекс.Карты' }] },
    googleQuery: { type: 'text', label: 'Адрес (Google)', placeholder: 'Москва, ул. Арбат, 1' },
    yandexUrl: { type: 'text', label: 'URL Яндекс.Карт (embed)', placeholder: 'https://yandex.ru/maps/-/XXXXX' },
    height: { type: 'number', label: 'Высота карты (px)', default: 400, min: 200, max: 800 },
    zoom: { type: 'number', label: 'Масштаб', default: 14, min: 8, max: 19 },
    showAddress: { type: 'boolean', label: 'Показать адрес под картой', default: true },
    address: { type: 'text', label: 'Текст адреса', default: '' },
  },
  tags: ['map', 'location', 'google', 'yandex', 'address', 'navigation'],
})

registerBlock({
  type: 'timeline-vertical',
  label: 'Вертикальная временная шкала',
  category: 'content',
  icon: '📅',
  description: 'Вертикальный таймлайн с датами, заголовками и описаниями — история компании, этапы проекта',
  defaultData: {
    title: 'История компании',
    accentColor: '#5b6af0',
    items: [
      { year: '2020', title: 'Основание компании', text: 'Начало пути — первый офис и команда из 5 человек' },
      { year: '2021', title: 'Первые 100 клиентов', text: 'Достигли отметки в 100 довольных клиентов' },
      { year: '2022', title: 'Расширение', text: 'Открыли второй офис и выросли до 30 сотрудников' },
      { year: '2024', title: 'Лидер рынка', text: 'Заняли топ-3 позицию в своей нише' },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'История компании' },
    accentColor: { type: 'color', label: 'Цвет линии и точек', default: '#5b6af0' },
    items: {
      type: 'array',
      label: 'События',
      itemSchema: {
        year: { type: 'text', label: 'Год / Дата', default: '2024' },
        title: { type: 'text', label: 'Заголовок', default: 'Событие' },
        text: { type: 'text', label: 'Описание', default: '' },
      },
    },
  },
  tags: ['timeline', 'history', 'events', 'dates', 'story', 'milestones'],
})

registerBlock({
  type: 'lottie-animation',
  label: 'Lottie-анимация',
  category: 'media',
  icon: '🎬',
  description: 'Векторная JSON-анимация Lottie — логотипы, иконки, иллюстрации без потери качества',
  defaultData: {
    src: 'https://assets10.lottiefiles.com/packages/lf20_jcikwtux.json',
    width: '300px',
    height: '300px',
    loop: true,
    autoplay: true,
    trigger: 'auto',
    caption: '',
  },
  schema: {
    src: { type: 'url', label: 'URL JSON-анимации (LottieFiles)', placeholder: 'https://assets10.lottiefiles.com/...' },
    width: { type: 'text', label: 'Ширина (px / %)', default: '300px' },
    height: { type: 'text', label: 'Высота (px / %)', default: '300px' },
    loop: { type: 'boolean', label: 'Зациклить', default: true },
    autoplay: { type: 'boolean', label: 'Воспроизводить сразу', default: true },
    trigger: { type: 'select', label: 'Запуск', default: 'auto', options: [{ value: 'auto', label: 'Автоматически' }, { value: 'scroll', label: 'При появлении' }, { value: 'hover', label: 'При наведении' }] },
    caption: { type: 'text', label: 'Подпись (необязательно)', default: '' },
  },
  tags: ['lottie', 'animation', 'json', 'vector', 'illustration', 'motion'],
})

registerBlock({
  type: 'sticky-bar',
  label: 'Акционная полоса (Sticky Bar)',
  category: 'interactive',
  icon: '📢',
  description: 'Закреплённая полоска-объявление вверху страницы — скидки, акции, важные сообщения',
  defaultData: {
    text: '🔥 Скидка 20% до конца месяца!',
    buttonText: 'Получить скидку',
    buttonUrl: '#',
    background: '#5b6af0',
    textColor: '#ffffff',
    dismissible: true,
    position: 'top',
  },
  schema: {
    text: { type: 'text', label: 'Текст объявления', default: '🔥 Скидка 20% до конца месяца!' },
    buttonText: { type: 'text', label: 'Текст кнопки (пусто = нет кнопки)', default: 'Подробнее' },
    buttonUrl: { type: 'url', label: 'URL кнопки', default: '#' },
    background: { type: 'color', label: 'Цвет фона', default: '#5b6af0' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
    dismissible: { type: 'boolean', label: 'Кнопка закрыть', default: true },
    position: { type: 'select', label: 'Позиция', default: 'top', options: [{ value: 'top', label: 'Вверху' }, { value: 'bottom', label: 'Внизу' }] },
  },
  tags: ['sticky', 'bar', 'announcement', 'promo', 'banner', 'top', 'notification'],
})

registerBlock({
  type: 'cms-detail-hero',
  label: 'CMS-запись: Герой',
  category: 'content',
  icon: '📄',
  description: 'Герой-блок для страниц CMS-шаблонов. Используйте {{title}}, {{image}}, {{text}}, {{date}} — они заменятся данными записи при публикации',
  defaultData: {
    title: '{{title}}',
    subtitle: '{{subtitle}}',
    image: '{{image}}',
    date: '{{date}}',
    author: '{{author}}',
    category: '{{category}}',
    showDate: true,
    showAuthor: true,
    showCategory: true,
    accentColor: '#5b6af0',
    layout: 'centered',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок (шаблон)', default: '{{title}}' },
    subtitle: { type: 'text', label: 'Подзаголовок / lead', default: '{{subtitle}}' },
    image: { type: 'image', label: 'Изображение (шаблон)', default: '{{image}}' },
    date: { type: 'text', label: 'Дата (шаблон)', default: '{{date}}' },
    author: { type: 'text', label: 'Автор (шаблон)', default: '{{author}}' },
    category: { type: 'text', label: 'Категория (шаблон)', default: '{{category}}' },
    showDate: { type: 'boolean', label: 'Показывать дату', default: true },
    showAuthor: { type: 'boolean', label: 'Показывать автора', default: true },
    showCategory: { type: 'boolean', label: 'Показывать категорию', default: true },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
    layout: { type: 'select', label: 'Макет', default: 'centered', options: [{ value: 'centered', label: 'По центру' }, { value: 'left', label: 'Слева' }] },
  },
  tags: ['cms', 'template', 'detail', 'post', 'article', 'hero', 'dynamic'],
})

registerBlock({
  type: 'cms-detail-body',
  label: 'CMS-запись: Контент',
  category: 'content',
  icon: '📝',
  description: 'Блок для вывода основного текста CMS-записи. Используйте {{content}}, {{body}}, {{description}}',
  defaultData: {
    content: '{{content}}',
    maxWidth: 760,
  },
  schema: {
    content: { type: 'richtext', label: 'Контент (шаблон)', default: '{{content}}' },
    maxWidth: { type: 'number', label: 'Максимальная ширина (px)', default: 760, min: 400, max: 1400 },
  },
  tags: ['cms', 'template', 'body', 'content', 'text', 'article', 'dynamic'],
})

registerBlock({
  type: 'portfolio-filter',
  label: 'Портфолио с фильтром',
  category: 'gallery',
  icon: '🎨',
  description: 'Сетка проектов/работ с кнопками фильтрации по категориям — плавная анимация переключения',
  defaultData: {
    title: 'Наши работы',
    subtitle: '',
    columns: 3,
    gap: 16,
    borderRadius: 12,
    accentColor: '#5b6af0',
    allLabel: 'Все',
    items: [
      { image: 'https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?w=600&q=80', title: 'Сайт для кафе', category: 'Веб-дизайн', link: '#' },
      { image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80', title: 'Мобильное приложение', category: 'Приложения', link: '#' },
      { image: 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=600&q=80', title: 'Корпоративный сайт', category: 'Веб-дизайн', link: '#' },
      { image: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=600&q=80', title: 'Брендинг компании', category: 'Брендинг', link: '#' },
      { image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=600&q=80', title: 'Дизайн упаковки', category: 'Брендинг', link: '#' },
      { image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600&q=80', title: 'E-commerce платформа', category: 'Приложения', link: '#' },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наши работы' },
    subtitle: { type: 'text', label: 'Подзаголовок' },
    allLabel: { type: 'text', label: 'Надпись кнопки «Все»', default: 'Все' },
    columns: { type: 'number', label: 'Колонок (десктоп)', default: 3, min: 2, max: 5 },
    gap: { type: 'number', label: 'Отступ между карточками (px)', default: 16, min: 0, max: 40 },
    borderRadius: { type: 'number', label: 'Скругление углов (px)', default: 12, min: 0, max: 30 },
    accentColor: { type: 'color', label: 'Цвет активного фильтра', default: '#5b6af0' },
    items: {
      type: 'array',
      label: 'Работы',
      itemSchema: {
        image: { type: 'image', label: 'Изображение' },
        title: { type: 'text', label: 'Название' },
        category: { type: 'text', label: 'Категория', placeholder: 'Веб-дизайн' },
        link: { type: 'url', label: 'Ссылка', default: '#' },
        description: { type: 'text', label: 'Описание (при наведении)', default: '' },
      },
    },
  },
  tags: ['portfolio', 'gallery', 'filter', 'works', 'projects', 'isotope', 'grid', 'категории'],
})

registerBlock({
  type: 'tiktok-embed',
  label: 'TikTok / Reels',
  category: 'media',
  icon: '🎵',
  description: 'Встраивает видео из TikTok или Instagram Reels. Вставьте ссылку на видео.',
  defaultData: { url: '', platform: 'tiktok' },
  schema: {
    platform: { type: 'select', label: 'Платформа', default: 'tiktok', options: [
      { value: 'tiktok', label: 'TikTok' },
      { value: 'reels', label: 'Instagram Reels' },
    ]},
    url: { type: 'url', label: 'Ссылка на видео', placeholder: 'https://www.tiktok.com/@user/video/123...' },
  },
  tags: ['tiktok', 'reels', 'instagram', 'video', 'social', 'short', 'viral'],
})

registerBlock({
  type: 'pdf-viewer',
  label: 'PDF-просмотрщик',
  category: 'embed',
  icon: '📄',
  description: 'Встраивает PDF-документ прямо на страницу — через браузер или Google Docs Viewer',
  defaultData: { url: '', height: 600, viewer: 'native' },
  schema: {
    url: { type: 'url', label: 'Ссылка на PDF-файл', placeholder: 'https://example.com/document.pdf' },
    height: { type: 'number', label: 'Высота (px)', default: 600, min: 300, max: 1200 },
    viewer: { type: 'select', label: 'Просмотрщик', default: 'native', options: [
      { value: 'native', label: 'Встроенный (браузер)' },
      { value: 'google', label: 'Google Docs Viewer' },
    ]},
  },
  tags: ['pdf', 'document', 'viewer', 'file', 'embed', 'download'],
})

registerBlock({
  type: 'translate-widget',
  label: 'Перевод страницы',
  category: 'interactive',
  icon: '🌐',
  description: 'Добавляет виджет Google Translate — посетители могут перевести страницу одним кликом',
  defaultData: { languages: 'en,de,fr,es,zh-CN,ar,tr', style: 'inline' },
  schema: {
    languages: { type: 'text', label: 'Языки (коды через запятую)', default: 'en,de,fr,es,zh-CN,ar,tr', placeholder: 'en,de,fr,es' },
    style: { type: 'select', label: 'Расположение', default: 'inline', options: [
      { value: 'inline', label: 'Встроенный список' },
      { value: 'float-left', label: 'Плавающий слева' },
      { value: 'float-right', label: 'Плавающий справа' },
    ]},
  },
  tags: ['translate', 'language', 'google', 'localization', 'multilingual', 'перевод'],
})

registerBlock({
  type: 'flip-cards',
  label: 'Флип-карточки',
  category: 'content',
  icon: '🔄',
  description: 'Карточки, переворачивающиеся при наведении — лицевая и обратная стороны с разным контентом',
  defaultData: {
    title: 'Наши преимущества',
    columns: 4,
    height: 220,
    frontBg: '#5b6af0',
    backBg: '#ffffff',
    frontColor: '#ffffff',
    backColor: '#1a1a1a',
    borderRadius: 16,
    cards: [
      { frontIcon: '🚀', frontTitle: 'Быстро', backText: 'Запускаем проект за 3 дня с момента согласования' },
      { frontIcon: '💎', frontTitle: 'Качество', backText: 'Гарантируем результат или возвращаем деньги' },
      { frontIcon: '🔒', frontTitle: 'Надёжно', backText: '24/7 поддержка и мониторинг вашего проекта' },
      { frontIcon: '💰', frontTitle: 'Выгодно', backText: 'Цены на 30% ниже рыночных при высоком качестве' },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок секции (пусто = без заголовка)', default: '' },
    columns: { type: 'number', label: 'Колонок', default: 4, min: 2, max: 6 },
    height: { type: 'number', label: 'Высота карточки (px)', default: 220, min: 120, max: 500 },
    frontBg: { type: 'color', label: 'Цвет лицевой стороны', default: '#5b6af0' },
    backBg: { type: 'color', label: 'Цвет обратной стороны', default: '#ffffff' },
    frontColor: { type: 'color', label: 'Цвет текста лицевой', default: '#ffffff' },
    backColor: { type: 'color', label: 'Цвет текста обратной', default: '#1a1a1a' },
    borderRadius: { type: 'number', label: 'Скругление (px)', default: 16, min: 0, max: 40 },
    cards: {
      type: 'array',
      label: 'Карточки',
      itemSchema: {
        frontIcon: { type: 'text', label: 'Иконка/emoji', placeholder: '🚀' },
        frontTitle: { type: 'text', label: 'Заголовок лицевой стороны' },
        backText: { type: 'richtext', label: 'Текст обратной стороны' },
        backImage: { type: 'image', label: 'Фон обратной стороны (изображение)', default: '' },
      },
    },
  },
  tags: ['flip', 'card', 'hover', 'features', '3d', 'animation', 'interactive'],
})

registerBlock({
  type: 'ai-image',
  label: 'AI-изображение',
  category: 'media',
  icon: '🤖',
  description: 'Генерирует изображение по текстовому описанию (prompt) через Pollinations AI — бесплатно, без API-ключа',
  defaultData: {
    prompt: '',
    imageUrl: '',
    alt: '',
    genWidth: 1200,
    genHeight: 800,
    displayMaxWidth: 1200,
    borderRadius: 12,
    seed: '',
    fit: 'cover',
  },
  schema: {
    prompt: { type: 'richtext', label: 'Описание изображения (prompt)', placeholder: 'красивый горный пейзаж на закате, фотореалистичный' },
    imageUrl: { type: 'image', label: 'URL изображения (заполнится при генерации)', default: '' },
    alt: { type: 'text', label: 'Alt-текст для SEO и доступности' },
    genWidth: { type: 'number', label: 'Ширина генерации (px)', default: 1200, min: 256, max: 2048 },
    genHeight: { type: 'number', label: 'Высота генерации (px)', default: 800, min: 256, max: 2048 },
    displayMaxWidth: { type: 'number', label: 'Макс. ширина на странице (px)', default: 1200, min: 200, max: 2000 },
    borderRadius: { type: 'number', label: 'Скругление углов (px)', default: 12, min: 0, max: 50 },
    fit: { type: 'select', label: 'Масштабирование', default: 'cover', options: [
      { value: 'cover', label: 'Заполнить (cover)' },
      { value: 'contain', label: 'Вписать (contain)' },
      { value: 'fill', label: 'Растянуть (fill)' },
    ]},
  },
  tags: ['ai', 'image', 'generate', 'prompt', 'pollinations', 'нейросеть', 'генерация'],
})

registerBlock({
  type: 'telegram-post',
  label: 'Telegram: Канал / Пост',
  category: 'interactive',
  icon: '✈️',
  description: 'Встраивает Telegram-канал (превью с кнопкой подписки) или конкретный пост из канала',
  defaultData: {
    channel: '',
    postId: '',
    mode: 'channel',
    color: '#5b6af0',
    height: 500,
    dark: false,
  },
  schema: {
    mode: { type: 'select', label: 'Режим', default: 'channel', options: [
      { value: 'channel', label: 'Превью канала (iframe)' },
      { value: 'post', label: 'Конкретный пост (виджет)' },
    ]},
    channel: { type: 'text', label: 'Имя канала (без @)', placeholder: 'myawesomechannel' },
    postId: { type: 'text', label: 'ID поста (для режима «Пост»)', placeholder: '42' },
    color: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
    height: { type: 'number', label: 'Высота превью канала (px)', default: 500, min: 300, max: 1000 },
    dark: { type: 'boolean', label: 'Тёмная тема виджета', default: false },
  },
  tags: ['telegram', 'tg', 'channel', 'post', 'social', 'messenger', 'россия'],
})

registerBlock({
  type: 'audio-embed',
  label: 'Аудио / Подкаст',
  category: 'media',
  icon: '🎧',
  description: 'Встраивает аудио-плеер: Spotify, SoundCloud, или файл MP3/OGG',
  defaultData: {
    platform: 'spotify',
    url: '',
    height: 152,
    theme: 'light',
    customCode: '',
  },
  schema: {
    platform: { type: 'select', label: 'Платформа', default: 'spotify', options: [
      { value: 'spotify', label: 'Spotify (трек / плейлист / подкаст)' },
      { value: 'soundcloud', label: 'SoundCloud' },
      { value: 'audio', label: 'Аудиофайл (MP3/OGG/WAV)' },
      { value: 'custom', label: 'Свой код' },
    ]},
    url: { type: 'url', label: 'URL трека / файла', placeholder: 'https://open.spotify.com/track/...' },
    height: { type: 'number', label: 'Высота плеера (px)', default: 152, min: 80, max: 500 },
    theme: { type: 'select', label: 'Тема', default: 'light', options: [
      { value: 'light', label: 'Светлая' },
      { value: 'dark', label: 'Тёмная (Spotify)' },
    ]},
    customCode: { type: 'richtext', label: 'Свой HTML-код (для «Свой код»)', default: '' },
  },
  tags: ['audio', 'spotify', 'soundcloud', 'podcast', 'music', 'player', 'mp3'],
})

registerBlock({
  type: 'booking-widget',
  label: 'Онлайн-запись / Бронирование',
  category: 'interactive',
  icon: '📅',
  description: 'Встраивает форму онлайн-записи: Calendly, YClients, Dikidi или свой код',
  defaultData: {
    platform: 'calendly',
    url: '',
    height: 630,
    customCode: '',
  },
  schema: {
    platform: { type: 'select', label: 'Сервис', default: 'calendly', options: [
      { value: 'calendly', label: 'Calendly' },
      { value: 'yclients', label: 'YClients (Россия)' },
      { value: 'dikidi', label: 'Dikidi (Россия)' },
      { value: 'iframe', label: 'Свой iframe-URL' },
      { value: 'custom', label: 'Свой код' },
    ]},
    url: { type: 'url', label: 'URL сервиса', placeholder: 'https://calendly.com/your-username/30min' },
    height: { type: 'number', label: 'Высота виджета (px)', default: 630, min: 300, max: 1200 },
    customCode: { type: 'richtext', label: 'Свой HTML-код (для «Свой код»)', default: '' },
  },
  tags: ['booking', 'calendar', 'calendly', 'yclients', 'dikidi', 'schedule', 'appointment', 'запись'],
})

registerBlock({
  type: 'instagram-feed',
  label: 'Instagram: Лента',
  category: 'interactive',
  icon: '📷',
  description: 'Встраивает ленту Instagram через Behold.so, Elfsight или Snapwidget — вставьте ID вашего виджета',
  defaultData: {
    platform: 'behold',
    widgetId: '',
    customScript: '',
    height: 480,
  },
  schema: {
    platform: { type: 'select', label: 'Сервис', default: 'behold', options: [
      { value: 'behold', label: 'Behold.so (бесплатно)' },
      { value: 'elfsight', label: 'Elfsight' },
      { value: 'snapwidget', label: 'Snapwidget' },
      { value: 'custom', label: 'Свой код' },
    ]},
    widgetId: { type: 'text', label: 'ID виджета', placeholder: 'получите на сайте выбранного сервиса' },
    height: { type: 'number', label: 'Высота (px)', default: 480, min: 200, max: 1200 },
    customScript: { type: 'richtext', label: 'Свой HTML-код (для «Свой код»)', default: '' },
  },
  tags: ['instagram', 'social', 'feed', 'photo', 'gallery', 'behold', 'elfsight'],
})

registerBlock({
  type: 'vk-community',
  label: 'ВКонтакте: Сообщество',
  category: 'interactive',
  icon: '🔷',
  description: 'Виджет сообщества ВКонтакте: кнопка подписки, участники, новости',
  defaultData: {
    groupId: '',
    mode: '1',
    wide: false,
    noCover: false,
    color1: 'FFFFFF',
    color2: '000000',
    color3: '5B6AF0',
  },
  schema: {
    groupId: { type: 'text', label: 'ID сообщества ВКонтакте (числовой)', placeholder: '123456789' },
    mode: { type: 'select', label: 'Режим виджета', default: '1', options: [
      { value: '1', label: 'Сообщество (участники + подписка)' },
      { value: '3', label: 'Новости сообщества' },
      { value: '0', label: 'Стандартный' },
    ]},
    wide: { type: 'boolean', label: 'Широкий виджет', default: false },
    noCover: { type: 'boolean', label: 'Скрыть обложку', default: false },
    color1: { type: 'text', label: 'Цвет фона (HEX без #)', default: 'FFFFFF', placeholder: 'FFFFFF' },
    color2: { type: 'text', label: 'Цвет текста (HEX без #)', default: '000000', placeholder: '000000' },
    color3: { type: 'text', label: 'Цвет кнопки (HEX без #)', default: '5B6AF0', placeholder: '5B6AF0' },
  },
  tags: ['vk', 'вконтакте', 'social', 'widget', 'community', 'россия', 'russia'],
})

registerBlock({
  type: 'language-switcher',
  label: 'Переключатель языков',
  category: 'interactive',
  icon: '🌐',
  description: 'Виджет выбора языка для мультиязычных сайтов',
  defaultData: {
    style: 'buttons',
    position: 'center',
    languages: [
      { code: 'ru', label: 'RU', flag: '🇷🇺' },
      { code: 'en', label: 'EN', flag: '🇺🇸' },
    ],
    showFlags: true,
    background: 'transparent',
    activeColor: '#5b6af0',
    borderRadius: 6,
  },
  schema: {
    style: { type: 'select', label: 'Стиль', default: 'buttons', options: [{ value: 'buttons', label: 'Кнопки' }, { value: 'dropdown', label: 'Выпадающий список' }, { value: 'flags', label: 'Только флаги' }] },
    position: { type: 'select', label: 'Выравнивание', default: 'center', options: [{ value: 'left', label: 'Слева' }, { value: 'center', label: 'По центру' }, { value: 'right', label: 'Справа' }] },
    showFlags: { type: 'boolean', label: 'Показывать флаги', default: true },
    activeColor: { type: 'color', label: 'Цвет активного языка', default: '#5b6af0' },
    borderRadius: { type: 'number', label: 'Скругление (px)', default: 6, min: 0, max: 20 },
  },
  tags: ['language', 'switcher', 'multilingual', 'i18n', 'перевод', 'язык'],
})

registerBlock({
  type: 'rich-text',
  label: 'Форматированный текст',
  category: 'text',
  icon: '📄',
  description: 'Блок с полным HTML-содержимым — поддерживает заголовки, списки, ссылки, жирный и курсивный текст',
  defaultData: {
    content: '<h2>Заголовок раздела</h2><p>Здесь начинается ваш текст. Вы можете форматировать его — <strong>жирный</strong>, <em>курсив</em>, <a href="#">ссылки</a> и многое другое.</p><ul><li>Пункт 1</li><li>Пункт 2</li><li>Пункт 3</li></ul>',
    maxWidth: 800,
    padding: 24,
    fontSize: 16,
    lineHeight: 1.8,
    color: '#374151',
    linkColor: '#5b6af0',
  },
  schema: {
    content: { type: 'richtext', label: 'HTML содержимое' },
    maxWidth: { type: 'number', label: 'Максимальная ширина (px)', default: 800, min: 400, max: 1400 },
    padding: { type: 'number', label: 'Отступы (px)', default: 24, min: 0, max: 80 },
    fontSize: { type: 'number', label: 'Размер шрифта (px)', default: 16, min: 12, max: 24 },
    lineHeight: { type: 'number', label: 'Межстрочный интервал', default: 1.8, min: 1.2, max: 3 },
    color: { type: 'color', label: 'Цвет текста', default: '#374151' },
    linkColor: { type: 'color', label: 'Цвет ссылок', default: '#5b6af0' },
  },
  tags: ['text', 'html', 'rich', 'content', 'article', 'blog', 'текст', 'статья'],
})

registerBlock({
  type: 'table-of-contents',
  label: 'Содержание',
  category: 'content',
  icon: '📑',
  description: 'Автоматически генерирует оглавление из заголовков на странице',
  defaultData: {
    title: 'Содержание',
    levels: ['h2', 'h3'],
    style: 'bullets',
    background: '#f9fafb',
    borderRadius: 10,
    padding: 20,
    titleColor: '#111',
    linkColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок блока', default: 'Содержание' },
    style: { type: 'select', label: 'Стиль', default: 'bullets', options: [{ value: 'bullets', label: 'Маркированный' }, { value: 'numbered', label: 'Нумерованный' }, { value: 'minimal', label: 'Минимальный' }] },
    background: { type: 'color', label: 'Фон', default: '#f9fafb' },
    borderRadius: { type: 'number', label: 'Скругление', default: 10, min: 0, max: 24 },
    titleColor: { type: 'color', label: 'Цвет заголовка', default: '#111' },
    linkColor: { type: 'color', label: 'Цвет ссылок', default: '#5b6af0' },
  },
  tags: ['toc', 'contents', 'navigation', 'outline', 'содержание', 'навигация'],
})

registerBlock({
  type: 'author-bio',
  label: 'Биография автора',
  category: 'content',
  icon: '👤',
  description: 'Карточка автора для статей и блогов — фото, имя, должность, соцсети',
  defaultData: {
    name: 'Имя Автора',
    title: 'Главный редактор',
    bio: 'Эксперт с 10-летним опытом в области цифрового маркетинга. Пишет о технологиях, бизнесе и веб-разработке.',
    avatar: '',
    twitter: '',
    linkedin: '',
    website: '',
    layout: 'horizontal',
    background: '#f9fafb',
    borderRadius: 12,
  },
  schema: {
    name: { type: 'text', label: 'Имя', default: 'Имя Автора' },
    title: { type: 'text', label: 'Должность / специализация', default: 'Главный редактор' },
    bio: { type: 'richtext', label: 'Описание' },
    avatar: { type: 'image', label: 'Фото' },
    layout: { type: 'select', label: 'Расположение', default: 'horizontal', options: [{ value: 'horizontal', label: 'Горизонтальное' }, { value: 'vertical', label: 'Вертикальное' }] },
    twitter: { type: 'url', label: 'Twitter/X профиль', placeholder: 'https://x.com/username' },
    linkedin: { type: 'url', label: 'LinkedIn профиль', placeholder: 'https://linkedin.com/in/username' },
    website: { type: 'url', label: 'Сайт', placeholder: 'https://example.com' },
    background: { type: 'color', label: 'Фон', default: '#f9fafb' },
    borderRadius: { type: 'number', label: 'Скругление (px)', default: 12, min: 0, max: 32 },
  },
  tags: ['author', 'bio', 'profile', 'blog', 'card', 'автор', 'профиль', 'биография'],
})

registerBlock({
  type: 'progress-bars',
  label: 'Полосы прогресса',
  category: 'content',
  icon: '📊',
  description: 'Визуализация навыков, прогресса, результатов — анимированные полосы с процентами',
  defaultData: {
    title: 'Наши навыки',
    items: [
      { label: 'Дизайн', percent: 95, color: '#5b6af0' },
      { label: 'Разработка', percent: 88, color: '#8b5cf6' },
      { label: 'SEO', percent: 75, color: '#10b981' },
      { label: 'Маркетинг', percent: 82, color: '#f59e0b' },
    ],
    showPercent: true,
    barHeight: 10,
    borderRadius: 5,
    background: '#e5e7eb',
    animate: true,
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наши навыки' },
    items: { type: 'array', label: 'Навыки', itemSchema: {
      label: { type: 'text', label: 'Название', default: 'Навык' },
      percent: { type: 'number', label: 'Процент', default: 80, min: 0, max: 100 },
      color: { type: 'color', label: 'Цвет', default: '#5b6af0' },
    }},
    showPercent: { type: 'boolean', label: 'Показывать %', default: true },
    barHeight: { type: 'number', label: 'Высота полосы (px)', default: 10, min: 4, max: 24 },
    borderRadius: { type: 'number', label: 'Скругление (px)', default: 5, min: 0, max: 12 },
    animate: { type: 'boolean', label: 'Анимация при появлении', default: true },
  },
  tags: ['progress', 'skills', 'bars', 'chart', 'навыки', 'прогресс', 'диаграмма'],
})

registerBlock({
  type: 'step-form',
  label: 'Форма-воронка (шаги)',
  category: 'interactive',
  icon: '🧩',
  description: 'Многошаговая форма с вопросами — квиз для генерации лидов',
  defaultData: {
    title: 'Получите персональное предложение',
    submitText: 'Получить результат',
    successTitle: 'Спасибо!',
    successMessage: 'Мы свяжемся с вами в ближайшее время.',
    steps: [
      { question: 'Что вас интересует?', type: 'choice', options: ['Услуга A', 'Услуга B', 'Услуга C'] },
      { question: 'Укажите ваш бюджет', type: 'choice', options: ['До 50 000 ₽', '50–150 000 ₽', 'Более 150 000 ₽'] },
      { question: 'Ваше имя', type: 'text', placeholder: 'Иван Иванов' },
      { question: 'Телефон или e-mail', type: 'text', placeholder: '+7 (___) ___-__-__' },
    ],
    buttonColor: '#5b6af0',
    background: 'linear-gradient(135deg,#f0f1ff,#e0e7ff)',
    borderRadius: 16,
  },
  schema: {
    title: { type: 'text', label: 'Заголовок формы', default: 'Получите персональное предложение' },
    submitText: { type: 'text', label: 'Текст финальной кнопки', default: 'Получить результат' },
    successTitle: { type: 'text', label: 'Заголовок успеха', default: 'Спасибо!' },
    successMessage: { type: 'text', label: 'Сообщение успеха', default: 'Мы свяжемся с вами в ближайшее время.' },
    buttonColor: { type: 'color', label: 'Цвет кнопок', default: '#5b6af0' },
    borderRadius: { type: 'number', label: 'Скругление (px)', default: 16, min: 0, max: 32 },
  },
  tags: ['form', 'quiz', 'funnel', 'steps', 'lead', 'форма', 'квиз', 'воронка'],
})

registerBlock({
  type: 'social-share-bar',
  label: 'Кнопки "Поделиться"',
  category: 'interactive',
  icon: '📤',
  description: 'Кнопки для шеринга страницы в социальных сетях',
  defaultData: {
    title: 'Поделиться',
    networks: ['vk', 'telegram', 'whatsapp', 'twitter', 'facebook'],
    style: 'buttons',
    align: 'center',
    size: 'medium',
  },
  schema: {
    title: { type: 'text', label: 'Подпись', default: 'Поделиться' },
    style: { type: 'select', label: 'Стиль', default: 'buttons', options: [{ value: 'buttons', label: 'Кнопки' }, { value: 'icons', label: 'Иконки' }, { value: 'minimal', label: 'Минималистичный' }] },
    align: { type: 'select', label: 'Выравнивание', default: 'center', options: [{ value: 'left', label: 'Слева' }, { value: 'center', label: 'По центру' }, { value: 'right', label: 'Справа' }] },
  },
  tags: ['share', 'social', 'vk', 'telegram', 'whatsapp', 'шеринг', 'поделиться'],
})

registerBlock({
  type: 'floating-cta',
  label: 'Плавающая CTA-кнопка',
  category: 'interactive',
  icon: '📌',
  description: 'Фиксированная кнопка призыва к действию в углу экрана',
  defaultData: {
    text: 'Заказать звонок',
    url: '#',
    icon: '📞',
    position: 'bottom-right',
    background: '#5b6af0',
    textColor: '#ffffff',
    borderRadius: 50,
    showAfterScroll: 300,
  },
  schema: {
    text: { type: 'text', label: 'Текст кнопки', default: 'Заказать звонок' },
    url: { type: 'url', label: 'Ссылка', default: '#' },
    icon: { type: 'text', label: 'Иконка (эмодзи)', default: '📞' },
    position: { type: 'select', label: 'Позиция', default: 'bottom-right', options: [{ value: 'bottom-right', label: 'Снизу справа' }, { value: 'bottom-left', label: 'Снизу слева' }, { value: 'bottom-center', label: 'Снизу по центру' }] },
    background: { type: 'color', label: 'Цвет кнопки', default: '#5b6af0' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
  },
  tags: ['cta', 'floating', 'sticky', 'button', 'fixed', 'кнопка', 'плавающая'],
})

registerBlock({
  type: 'reviews-aggregate',
  label: 'Сводка отзывов',
  category: 'content',
  icon: '⭐',
  description: 'Общий рейтинг с распределением оценок и количеством отзывов',
  defaultData: {
    title: 'Отзывы клиентов',
    overall: 4.8,
    reviewCount: 248,
    distribution: [
      { stars: 5, count: 180 },
      { stars: 4, count: 45 },
      { stars: 3, count: 15 },
      { stars: 2, count: 5 },
      { stars: 1, count: 3 },
    ],
    accentColor: '#f59e0b',
    showCta: true,
    ctaText: 'Читать все отзывы',
    ctaUrl: '#',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Отзывы клиентов' },
    overall: { type: 'number', label: 'Общий рейтинг', default: 4.8, min: 1, max: 5 },
    reviewCount: { type: 'number', label: 'Количество отзывов', default: 248, min: 0 },
    accentColor: { type: 'color', label: 'Цвет звёзд', default: '#f59e0b' },
    showCta: { type: 'boolean', label: 'Показать кнопку', default: true },
    ctaText: { type: 'text', label: 'Текст кнопки', default: 'Читать все отзывы' },
    ctaUrl: { type: 'url', label: 'Ссылка кнопки', default: '#' },
  },
  tags: ['rating', 'reviews', 'stars', 'aggregate', 'рейтинг', 'отзывы', 'звёзды'],
})

registerBlock({
  type: 'notification-bar',
  label: 'Уведомительная строка',
  category: 'layout',
  icon: '📢',
  description: 'Цветная строка с важным сообщением и опциональной кнопкой',
  defaultData: {
    text: 'Специальное предложение действует только до 31 июля! Успейте оформить заказ.',
    type: 'info',
    dismissible: true,
    link: '',
    linkText: 'Подробнее →',
    showIcon: true,
  },
  schema: {
    text: { type: 'text', label: 'Текст сообщения', default: 'Специальное предложение...' },
    type: { type: 'select', label: 'Тип', default: 'info', options: [{ value: 'info', label: 'Инфо' }, { value: 'success', label: 'Успех' }, { value: 'warning', label: 'Предупреждение' }, { value: 'error', label: 'Ошибка' }] },
    dismissible: { type: 'boolean', label: 'Можно закрыть', default: true },
    link: { type: 'url', label: 'Ссылка', default: '' },
    linkText: { type: 'text', label: 'Текст ссылки', default: 'Подробнее →' },
    showIcon: { type: 'boolean', label: 'Показать иконку', default: true },
  },
  tags: ['notification', 'bar', 'alert', 'info', 'warning', 'уведомление', 'строка'],
})

registerBlock({
  type: 'split-cta',
  label: 'Разделённый CTA',
  category: 'interactive',
  icon: '⚡',
  description: 'Двухколоночный блок с текстом слева и кнопками справа',
  defaultData: {
    title: 'Готовы начать?',
    subtitle: 'Создайте свой первый сайт за 5 минут — бесплатно.',
    primaryText: 'Начать бесплатно',
    primaryUrl: '#',
    secondaryText: 'Посмотреть демо',
    secondaryUrl: '#',
    background: '#0f0f23',
    textColor: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Готовы начать?' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    primaryText: { type: 'text', label: 'Основная кнопка', default: 'Начать бесплатно' },
    primaryUrl: { type: 'url', label: 'Ссылка основной кнопки', default: '#' },
    secondaryText: { type: 'text', label: 'Дополнительная кнопка', default: 'Посмотреть демо' },
    secondaryUrl: { type: 'url', label: 'Ссылка доп. кнопки', default: '#' },
    background: { type: 'color', label: 'Фон', default: '#0f0f23' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
  },
  tags: ['cta', 'split', 'buttons', 'dual', 'призыв', 'двойной'],
})

registerBlock({
  type: 'cookie-consent-bar',
  label: 'Cookie-уведомление',
  category: 'interactive',
  icon: '🍪',
  description: 'Уведомление о cookie с кнопками принятия и отклонения (GDPR/ФЗ-152)',
  defaultData: {
    text: 'Мы используем файлы cookie для улучшения вашего опыта. Продолжая использовать сайт, вы соглашаетесь с нашей политикой конфиденциальности.',
    acceptText: 'Принять',
    declineText: 'Отказаться',
    position: 'bottom',
    background: '#0f0f1a',
    textColor: '#ffffff',
    accentColor: '#5b6af0',
    policyUrl: '/privacy',
    policyText: 'Подробнее',
  },
  schema: {
    text: { type: 'text', label: 'Текст уведомления', default: 'Мы используем файлы cookie...' },
    acceptText: { type: 'text', label: 'Кнопка принятия', default: 'Принять' },
    declineText: { type: 'text', label: 'Кнопка отказа', default: 'Отказаться' },
    position: { type: 'select', label: 'Позиция', default: 'bottom', options: [{ value: 'bottom', label: 'Внизу' }, { value: 'top', label: 'Вверху' }] },
    background: { type: 'color', label: 'Фон', default: '#0f0f1a' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
    policyUrl: { type: 'url', label: 'URL политики', default: '/privacy' },
    policyText: { type: 'text', label: 'Текст ссылки на политику', default: 'Подробнее' },
  },
  tags: ['cookie', 'gdpr', 'privacy', 'consent', 'куки', 'согласие', 'конфиденциальность'],
})

registerBlock({
  type: 'subscription-form',
  label: 'Форма подписки на рассылку',
  category: 'form',
  icon: '📧',
  description: 'Форма для сбора email-адресов с интеграцией в почтовые сервисы',
  defaultData: {
    title: 'Подпишитесь на новости',
    subtitle: 'Получайте полезные материалы каждую неделю',
    placeholder: 'Введите ваш email',
    buttonText: 'Подписаться',
    successText: 'Вы подписались! Спасибо.',
    background: '#f8f9fa',
    accentColor: '#5b6af0',
    layout: 'inline',
    showIcon: true,
    gdprText: 'Нажимая кнопку, вы соглашаетесь с обработкой персональных данных',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Подпишитесь на новости' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    placeholder: { type: 'text', label: 'Placeholder email', default: 'Введите ваш email' },
    buttonText: { type: 'text', label: 'Текст кнопки', default: 'Подписаться' },
    successText: { type: 'text', label: 'Сообщение об успехе', default: 'Вы подписались! Спасибо.' },
    background: { type: 'color', label: 'Фон', default: '#f8f9fa' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
    layout: { type: 'select', label: 'Раскладка', default: 'inline', options: [{ value: 'inline', label: 'В строку' }, { value: 'stacked', label: 'Вертикально' }] },
    gdprText: { type: 'text', label: 'GDPR текст', default: 'Нажимая кнопку, вы соглашаетесь...' },
  },
  tags: ['email', 'subscription', 'newsletter', 'mailchimp', 'подписка', 'рассылка'],
})

registerBlock({
  type: 'image-grid',
  label: 'Фотосетка',
  category: 'gallery',
  icon: '🖼',
  description: 'Равномерная сетка изображений с подписями и лайтбоксом',
  defaultData: {
    images: [
      { url: '', alt: 'Фото 1', caption: '' },
      { url: '', alt: 'Фото 2', caption: '' },
      { url: '', alt: 'Фото 3', caption: '' },
      { url: '', alt: 'Фото 4', caption: '' },
      { url: '', alt: 'Фото 5', caption: '' },
      { url: '', alt: 'Фото 6', caption: '' },
    ],
    columns: 3,
    gap: 12,
    borderRadius: 8,
    aspectRatio: '4/3',
    lightbox: true,
    title: '',
  },
  schema: {
    columns: { type: 'number', label: 'Колонки', default: 3, min: 1, max: 6 },
    gap: { type: 'number', label: 'Отступы (px)', default: 12, min: 0, max: 40 },
    borderRadius: { type: 'number', label: 'Скругление (px)', default: 8, min: 0, max: 24 },
    aspectRatio: { type: 'select', label: 'Соотношение сторон', default: '4/3', options: [{ value: '1/1', label: 'Квадрат' }, { value: '4/3', label: '4:3' }, { value: '16/9', label: '16:9' }, { value: '3/4', label: '3:4 (портрет)' }] },
    lightbox: { type: 'boolean', label: 'Открывать в лайтбоксе', default: true },
    title: { type: 'text', label: 'Заголовок', default: '' },
  },
  tags: ['grid', 'photos', 'gallery', 'images', 'portfolio', 'сетка', 'фото', 'галерея'],
})

registerBlock({
  type: 'contact-cards',
  label: 'Контактные карточки',
  category: 'content',
  icon: '📇',
  description: 'Карточки с контактной информацией: телефон, email, адрес, соцсети',
  defaultData: {
    title: 'Контакты',
    subtitle: 'Свяжитесь с нами удобным способом',
    cards: [
      { icon: '📞', title: 'Телефон', value: '+7 (495) 000-00-00', link: 'tel:+74950000000' },
      { icon: '📧', title: 'Email', value: 'info@example.com', link: 'mailto:info@example.com' },
      { icon: '📍', title: 'Адрес', value: 'Москва, ул. Примерная, 1', link: '' },
    ],
    columns: 3,
    background: '#f8f9fa',
    accentColor: '#5b6af0',
    cardBg: '#ffffff',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Контакты' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    columns: { type: 'number', label: 'Колонки', default: 3, min: 1, max: 4 },
    background: { type: 'color', label: 'Фон секции', default: '#f8f9fa' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
    cardBg: { type: 'color', label: 'Фон карточки', default: '#ffffff' },
  },
  tags: ['contact', 'cards', 'phone', 'email', 'address', 'контакты', 'карточки'],
})

registerBlock({
  type: 'expandable-gallery',
  label: 'Раскрывающаяся галерея',
  category: 'gallery',
  icon: '🔎',
  description: 'Галерея с кнопкой "Показать ещё" — сначала показывает N изображений',
  defaultData: {
    images: Array.from({ length: 9 }, (_, i) => ({ url: '', alt: `Фото ${i + 1}` })),
    initialCount: 6,
    columns: 3,
    gap: 12,
    borderRadius: 8,
    showMoreText: 'Показать ещё',
    showLessText: 'Свернуть',
    accentColor: '#5b6af0',
  },
  schema: {
    initialCount: { type: 'number', label: 'Показывать изначально', default: 6, min: 1, max: 20 },
    columns: { type: 'number', label: 'Колонки', default: 3, min: 1, max: 6 },
    gap: { type: 'number', label: 'Отступы (px)', default: 12, min: 0, max: 40 },
    borderRadius: { type: 'number', label: 'Скругление (px)', default: 8, min: 0, max: 24 },
    showMoreText: { type: 'text', label: 'Текст кнопки "Ещё"', default: 'Показать ещё' },
    showLessText: { type: 'text', label: 'Текст кнопки "Свернуть"', default: 'Свернуть' },
    accentColor: { type: 'color', label: 'Цвет кнопки', default: '#5b6af0' },
  },
  tags: ['gallery', 'expandable', 'load-more', 'portfolio', 'галерея', 'развернуть'],
})

registerBlock({
  type: 'video-popup',
  label: 'Видео в лайтбоксе',
  category: 'media',
  icon: '▶',
  description: 'Обложка с кнопкой Play — видео открывается в всплывающем окне',
  defaultData: {
    videoUrl: '',
    thumbnail: '',
    title: 'Смотрите наше видео',
    subtitle: '',
    buttonText: 'Смотреть видео',
    accentColor: '#5b6af0',
    overlayColor: 'rgba(0,0,0,0.6)',
    borderRadius: 12,
  },
  schema: {
    videoUrl: { type: 'video', label: 'URL видео (YouTube/Vimeo/MP4)', default: '' },
    thumbnail: { type: 'image', label: 'Обложка (thumbnail)', default: '' },
    title: { type: 'text', label: 'Заголовок', default: 'Смотрите наше видео' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    buttonText: { type: 'text', label: 'Текст кнопки', default: 'Смотреть видео' },
    accentColor: { type: 'color', label: 'Цвет кнопки', default: '#5b6af0' },
  },
  tags: ['video', 'popup', 'lightbox', 'play', 'youtube', 'видео', 'лайтбокс'],
})

registerBlock({
  type: 'qr-code',
  label: 'QR-код',
  category: 'interactive',
  icon: '⬛',
  description: 'QR-код для ссылки, телефона, текста или email',
  defaultData: {
    value: 'https://mybst.ru',
    size: 200,
    title: 'Сканируйте QR-код',
    subtitle: 'Для быстрого перехода на сайт',
    align: 'center',
    fgColor: '#000000',
    bgColor: '#ffffff',
    borderRadius: 8,
    padding: 16,
  },
  schema: {
    value: { type: 'url', label: 'Содержимое QR-кода', default: 'https://mybst.ru' },
    size: { type: 'number', label: 'Размер (px)', default: 200, min: 80, max: 400 },
    title: { type: 'text', label: 'Заголовок', default: 'Сканируйте QR-код' },
    subtitle: { type: 'text', label: 'Подпись', default: '' },
    align: { type: 'select', label: 'Выравнивание', default: 'center', options: [{ value: 'left', label: 'Слева' }, { value: 'center', label: 'По центру' }, { value: 'right', label: 'Справа' }] },
    fgColor: { type: 'color', label: 'Цвет модулей', default: '#000000' },
    bgColor: { type: 'color', label: 'Фон QR', default: '#ffffff' },
    borderRadius: { type: 'number', label: 'Скругление (px)', default: 8, min: 0, max: 24 },
  },
  tags: ['qr', 'code', 'qrcode', 'scan', 'link', 'КР-код', 'сканирование'],
})

registerBlock({
  type: 'icon-list',
  label: 'Список с иконками',
  category: 'content',
  icon: '✓',
  description: 'Вертикальный список пунктов с кастомными эмодзи-иконками',
  defaultData: {
    title: '',
    items: [
      { icon: '✅', text: 'Быстрая настройка — сайт за 5 минут' },
      { icon: '✅', text: 'Без технических знаний' },
      { icon: '✅', text: 'Поддержка 24/7' },
    ],
    columns: 1,
    gap: 12,
    iconSize: 20,
    fontSize: 15,
    iconColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: '' },
    columns: { type: 'select', label: 'Колонки', default: '1', options: [{ value: '1', label: '1 колонка' }, { value: '2', label: '2 колонки' }] },
    iconSize: { type: 'number', label: 'Размер иконки (px)', default: 20, min: 12, max: 40 },
    fontSize: { type: 'number', label: 'Размер текста (px)', default: 15, min: 10, max: 24 },
    gap: { type: 'number', label: 'Отступы между пунктами (px)', default: 12, min: 4, max: 32 },
    iconColor: { type: 'color', label: 'Цвет иконки', default: '#5b6af0' },
  },
  tags: ['list', 'icons', 'checklist', 'features', 'benefits', 'список', 'иконки', 'чеклист'],
})

registerBlock({
  type: 'side-by-side-cta',
  label: 'Двойной лендинг',
  category: 'hero',
  icon: '⬛⬜',
  description: 'Две половины экрана — левая и правая с разным контентом и кнопками',
  defaultData: {
    left: {
      background: '#0f0f23',
      textColor: '#ffffff',
      eyebrow: 'Для бизнеса',
      title: 'Профессиональные сайты',
      text: 'Создавайте сайты с конверсией до 30% выше конкурентов.',
      buttonText: 'Начать сейчас',
      buttonUrl: '#',
      buttonBg: '#5b6af0',
    },
    right: {
      background: '#f8f9fa',
      textColor: '#111111',
      eyebrow: 'Для фрилансеров',
      title: 'Лендинги за час',
      text: 'Готовые шаблоны и блоки. Никаких знаний кода.',
      buttonText: 'Попробовать бесплатно',
      buttonUrl: '#',
      buttonBg: '#111111',
    },
    minHeight: 500,
  },
  schema: {
    minHeight: { type: 'number', label: 'Минимальная высота (px)', default: 500, min: 300, max: 900 },
  },
  tags: ['split', 'dual', 'hero', 'landing', 'two-column', 'раздельный', 'двойной', 'лендинг'],
})

registerBlock({
  type: 'testimonial-video',
  label: 'Видео-отзыв',
  category: 'content',
  icon: '🎤',
  description: 'Отзыв клиента с видео-превью, фото, именем и должностью',
  defaultData: {
    videoUrl: '',
    thumbnail: '',
    name: 'Иван Петров',
    role: 'Генеральный директор',
    company: 'АО «Ромашка»',
    quote: 'Благодаря BST мы запустили наш сайт за один день. Конверсия выросла на 40%!',
    rating: 5,
    accentColor: '#5b6af0',
    background: '#f8f9ff',
  },
  schema: {
    videoUrl: { type: 'video', label: 'URL видео (YouTube/Vimeo/MP4)', default: '' },
    thumbnail: { type: 'image', label: 'Превью видео', default: '' },
    name: { type: 'text', label: 'Имя', default: 'Иван Петров' },
    role: { type: 'text', label: 'Должность', default: 'Генеральный директор' },
    company: { type: 'text', label: 'Компания', default: '' },
    quote: { type: 'richtext', label: 'Цитата', default: '' },
    rating: { type: 'number', label: 'Рейтинг (1-5)', default: 5, min: 0, max: 5 },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
    background: { type: 'color', label: 'Фон', default: '#f8f9ff' },
  },
  tags: ['testimonial', 'video', 'review', 'quote', 'отзыв', 'видео', 'клиент'],
})

registerBlock({
  type: 'feature-showcase',
  label: 'Витрина функции',
  category: 'content',
  icon: '✨',
  description: 'Большой блок с изображением, заголовком, списком преимуществ и кнопкой CTA',
  defaultData: {
    eyebrow: 'Ключевая возможность',
    title: 'Всё что нужно в одном месте',
    text: 'Наш продукт объединяет все инструменты, которые вам нужны для успеха.',
    benefits: ['Экономит до 5 часов в неделю', 'Интеграция с 50+ сервисами', 'Поддержка 24/7', 'Без лишних настроек'],
    image: '',
    imagePosition: 'right',
    buttonText: 'Узнать больше',
    buttonUrl: '#',
    accentColor: '#5b6af0',
    background: '#ffffff',
  },
  schema: {
    eyebrow: { type: 'text', label: 'Надпись над заголовком', default: '' },
    title: { type: 'text', label: 'Заголовок', default: 'Всё в одном' },
    text: { type: 'richtext', label: 'Описание', default: '' },
    benefits: { type: 'array', label: 'Преимущества', default: [] },
    image: { type: 'image', label: 'Изображение', default: '' },
    imagePosition: { type: 'select', label: 'Позиция изображения', default: 'right', options: [{ value: 'left', label: 'Слева' }, { value: 'right', label: 'Справа' }] },
    buttonText: { type: 'text', label: 'Текст кнопки', default: 'Узнать больше' },
    buttonUrl: { type: 'text', label: 'URL кнопки', default: '#' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
  },
  tags: ['feature', 'showcase', 'benefits', 'image', 'highlight', 'функция', 'преимущества'],
})

registerBlock({
  type: 'numbered-steps',
  label: 'Нумерованные шаги',
  category: 'content',
  icon: '📋',
  description: 'Шаги с большими декоративными цифрами и описаниями',
  defaultData: {
    title: 'Как это работает',
    subtitle: 'Три простых шага до результата',
    steps: [
      { number: '01', title: 'Регистрация', text: 'Создайте аккаунт за 30 секунд — никаких карт не нужно.' },
      { number: '02', title: 'Настройка', text: 'Выберите шаблон и настройте под себя за несколько минут.' },
      { number: '03', title: 'Запуск', text: 'Опубликуйте сайт одним кликом и получайте клиентов.' },
    ],
    accentColor: '#5b6af0',
    background: '#ffffff',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Как это работает' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    steps: { type: 'array', label: 'Шаги', default: [] },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
  },
  tags: ['steps', 'numbered', 'how-it-works', 'process', 'шаги', 'процесс', 'как это работает'],
})

registerBlock({
  type: 'hero-gradient',
  label: 'Герой с градиентом',
  category: 'hero',
  icon: '🌈',
  description: 'Полноэкранный герой с градиентным фоном, заголовком и подзаголовком',
  defaultData: {
    title: 'Создавайте. Вдохновляйте. Побеждайте.',
    subtitle: 'Лучший инструмент для создания сайтов без кода',
    buttonText: 'Начать бесплатно',
    buttonUrl: '#',
    buttonText2: 'Смотреть демо',
    buttonUrl2: '#',
    gradientFrom: '#667eea',
    gradientTo: '#764ba2',
    textColor: '#ffffff',
    minHeight: 600,
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Создавайте.' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    buttonText: { type: 'text', label: 'Текст кнопки 1', default: 'Начать' },
    buttonUrl: { type: 'text', label: 'URL кнопки 1', default: '#' },
    buttonText2: { type: 'text', label: 'Текст кнопки 2', default: '' },
    buttonUrl2: { type: 'text', label: 'URL кнопки 2', default: '#' },
    gradientFrom: { type: 'color', label: 'Цвет градиента (начало)', default: '#667eea' },
    gradientTo: { type: 'color', label: 'Цвет градиента (конец)', default: '#764ba2' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
    minHeight: { type: 'number', label: 'Минимальная высота (px)', default: 600, min: 300, max: 1000 },
  },
  tags: ['hero', 'gradient', 'header', 'landing', 'герой', 'градиент', 'лендинг'],
})

registerBlock({
  type: 'dark-section',
  label: 'Тёмная секция',
  category: 'layout',
  icon: '🌙',
  description: 'Контентная секция на тёмном фоне — выделяется на светлых страницах',
  defaultData: {
    title: 'Готовы к следующему уровню?',
    text: 'Присоединяйтесь к тысячам компаний, которые уже используют наш продукт для роста.',
    buttonText: 'Попробовать бесплатно',
    buttonUrl: '#',
    background: '#0f172a',
    accentColor: '#5b6af0',
    items: [
      { icon: '⚡', title: 'Быстро', text: 'Готово за минуты' },
      { icon: '🔒', title: 'Надёжно', text: 'SSL и защита данных' },
      { icon: '📈', title: 'Эффективно', text: 'Рост конверсий' },
    ],
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Готовы к следующему уровню?' },
    text: { type: 'richtext', label: 'Текст', default: '' },
    buttonText: { type: 'text', label: 'Текст кнопки', default: 'Начать' },
    buttonUrl: { type: 'text', label: 'URL кнопки', default: '#' },
    background: { type: 'color', label: 'Фон', default: '#0f172a' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
    items: { type: 'array', label: 'Тезисы (иконка + заголовок + текст)', default: [] },
  },
  tags: ['dark', 'cta', 'section', 'тёмный', 'секция'],
})

registerBlock({
  type: 'comparison-grid',
  label: 'Сравнение',
  category: 'content',
  icon: '⚖️',
  description: 'Сравнение двух вариантов — «без нас» и «с нами»',
  defaultData: {
    title: 'Почему именно мы',
    labelA: 'Без нас',
    labelB: 'С нами',
    colorA: '#ef4444',
    colorB: '#10b981',
    rows: [
      { label: 'Скорость запуска', a: 'Недели разработки', b: 'Часы — без кода' },
      { label: 'Стоимость', a: 'От 100 000 ₽', b: 'От 790 ₽/мес' },
      { label: 'Обновления', a: 'Всё через разработчика', b: 'Сами в любое время' },
      { label: 'Поддержка', a: 'Только в рабочее время', b: 'Круглосуточно' },
    ],
    background: '#ffffff',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Почему именно мы' },
    labelA: { type: 'text', label: 'Заголовок колонки «Без нас»', default: 'Без нас' },
    labelB: { type: 'text', label: 'Заголовок колонки «С нами»', default: 'С нами' },
    colorA: { type: 'color', label: 'Цвет колонки A', default: '#ef4444' },
    colorB: { type: 'color', label: 'Цвет колонки B', default: '#10b981' },
    rows: { type: 'array', label: 'Строки сравнения', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
  },
  tags: ['comparison', 'table', 'vs', 'versus', 'сравнение', 'таблица'],
})

registerBlock({
  type: 'quote-large',
  label: 'Большая цитата',
  category: 'content',
  icon: '💬',
  description: 'Крупная цитата с декоративными кавычками, автором и фото',
  defaultData: {
    quote: 'Это лучший инструмент для создания сайтов, который я когда-либо использовал. Рекомендую всем!',
    author: 'Алексей Смирнов',
    role: 'CEO компании «Иновейт»',
    avatar: '',
    accentColor: '#5b6af0',
    background: '#f8f9ff',
    textColor: '#111111',
  },
  schema: {
    quote: { type: 'richtext', label: 'Цитата', default: '' },
    author: { type: 'text', label: 'Автор', default: '' },
    role: { type: 'text', label: 'Должность/компания', default: '' },
    avatar: { type: 'image', label: 'Фото автора', default: '' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
    background: { type: 'color', label: 'Фон', default: '#f8f9ff' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#111111' },
  },
  tags: ['quote', 'testimonial', 'цитата', 'отзыв', 'автор'],
})

registerBlock({
  type: 'download-card',
  label: 'Карточка загрузки',
  category: 'interactive',
  icon: '⬇️',
  description: 'Карточка с файлом или приложением для скачивания — иконка, название, описание, кнопка',
  defaultData: {
    title: 'Скачать приложение',
    subtitle: 'Доступно для iOS и Android',
    description: 'Управляйте своим сайтом прямо с телефона',
    items: [
      { icon: '🍎', label: 'App Store', url: '#', badge: 'https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg' },
      { icon: '🤖', label: 'Google Play', url: '#', badge: '' },
    ],
    image: '',
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Скачать приложение' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    description: { type: 'richtext', label: 'Описание', default: '' },
    items: { type: 'array', label: 'Кнопки (иконка, текст, URL)', default: [] },
    image: { type: 'image', label: 'Превью (скриншот)', default: '' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Цвет кнопок', default: '#5b6af0' },
  },
  tags: ['download', 'app', 'mobile', 'store', 'скачать', 'приложение', 'магазин'],
})

registerBlock({
  type: 'badge-wall',
  label: 'Награды и сертификаты',
  category: 'content',
  icon: '🏆',
  description: 'Сетка наград, сертификатов и достижений с иконками',
  defaultData: {
    title: 'Наши достижения',
    subtitle: 'Признанное качество',
    badges: [
      { icon: '🏆', title: 'Лучший стартап 2024', org: 'Techcrunch' },
      { icon: '⭐', title: 'Рейтинг 5/5', org: 'G2 Crowd' },
      { icon: '🥇', title: 'Выбор редактора', org: 'Product Hunt' },
      { icon: '🛡️', title: 'ISO 27001', org: 'Сертификат безопасности' },
    ],
    background: '#ffffff',
    accentColor: '#f59e0b',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наши достижения' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    badges: { type: 'array', label: 'Награды (иконка, заголовок, организация)', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#f59e0b' },
  },
  tags: ['badges', 'awards', 'certificates', 'achievements', 'награды', 'сертификаты', 'достижения'],
})

registerBlock({
  type: 'faq-modern',
  label: 'FAQ с поиском',
  category: 'content',
  icon: '🔍',
  description: 'Часто задаваемые вопросы с поиском по вопросам и аккордеон-раскрытием',
  defaultData: {
    title: 'Часто задаваемые вопросы',
    subtitle: 'Не нашли ответа? Напишите нам!',
    showSearch: true,
    items: [
      { question: 'Сколько стоит сервис?', answer: 'Мы предлагаем несколько тарифных планов. Начните с бесплатного — без карты.' },
      { question: 'Нужны ли знания кода?', answer: 'Нет! Всё управляется через визуальный редактор. Перетаскивайте блоки, настраивайте цвета.' },
      { question: 'Можно ли подключить свой домен?', answer: 'Да, вы можете подключить любой домен. Инструкция в настройках сайта.' },
      { question: 'Есть ли поддержка?', answer: 'Да — чат поддержки, база знаний и видеоуроки доступны всем пользователям.' },
    ],
    accentColor: '#5b6af0',
    background: '#ffffff',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'FAQ' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    showSearch: { type: 'boolean', label: 'Показать поиск', default: true },
    items: { type: 'array', label: 'Вопросы и ответы', default: [] },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
  },
  tags: ['faq', 'questions', 'accordion', 'search', 'вопросы', 'ответы', 'аккордеон', 'поиск'],
})

registerBlock({
  type: 'product-spotlight',
  label: 'Прожектор продукта',
  category: 'commerce',
  icon: '🎯',
  description: 'Крупная карточка одного продукта с изображением, характеристиками и кнопками',
  defaultData: {
    name: 'Premium Bundle',
    tagline: 'Всё включено',
    description: 'Идеальное решение для быстрого старта. Включает всё необходимое для создания профессионального сайта.',
    price: '2 990 ₽',
    pricePeriod: '/мес',
    oldPrice: '5 990 ₽',
    features: ['Неограниченные страницы', 'Кастомный домен', 'Приоритетная поддержка', 'Все шаблоны'],
    image: '',
    buttonText: 'Купить сейчас',
    buttonUrl: '#',
    accentColor: '#5b6af0',
    background: '#f8f9ff',
    badge: 'Хит продаж',
  },
  schema: {
    name: { type: 'text', label: 'Название продукта', default: 'Premium Bundle' },
    tagline: { type: 'text', label: 'Слоган', default: '' },
    description: { type: 'richtext', label: 'Описание', default: '' },
    price: { type: 'text', label: 'Цена', default: '2 990 ₽' },
    pricePeriod: { type: 'text', label: 'Период', default: '/мес' },
    oldPrice: { type: 'text', label: 'Старая цена', default: '' },
    features: { type: 'array', label: 'Список включённого', default: [] },
    image: { type: 'image', label: 'Изображение продукта', default: '' },
    buttonText: { type: 'text', label: 'Текст кнопки', default: 'Купить' },
    buttonUrl: { type: 'text', label: 'URL кнопки', default: '#' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
    background: { type: 'color', label: 'Фон', default: '#f8f9ff' },
    badge: { type: 'text', label: 'Бейдж (например, «Хит»)', default: '' },
  },
  tags: ['product', 'spotlight', 'featured', 'pricing', 'продукт', 'товар', 'цена'],
})

registerBlock({
  type: 'about-section',
  label: 'О компании',
  category: 'content',
  icon: '🏢',
  description: 'Секция «О нас» — история, миссия, фото команды/офиса',
  defaultData: {
    eyebrow: 'О нас',
    title: 'Мы создаём цифровое будущее',
    text: 'Основанная в 2020 году, наша компания помогает тысячам предпринимателей создавать сайты без знаний кода. Мы верим, что у каждого должна быть возможность присутствовать онлайн.',
    image: '',
    stats: [
      { number: '10 000+', label: 'Клиентов' },
      { number: '99.9%', label: 'Uptime' },
      { number: '3 года', label: 'На рынке' },
    ],
    buttonText: 'Наша команда',
    buttonUrl: '#',
    background: '#ffffff',
    imagePosition: 'right',
  },
  schema: {
    eyebrow: { type: 'text', label: 'Надпись над заголовком', default: 'О нас' },
    title: { type: 'text', label: 'Заголовок', default: 'Мы создаём цифровое будущее' },
    text: { type: 'richtext', label: 'Текст', default: '' },
    image: { type: 'image', label: 'Изображение', default: '' },
    stats: { type: 'array', label: 'Статистика (число + подпись)', default: [] },
    buttonText: { type: 'text', label: 'Текст кнопки', default: '' },
    buttonUrl: { type: 'text', label: 'URL кнопки', default: '#' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    imagePosition: { type: 'select', label: 'Позиция изображения', default: 'right', options: [{ value: 'left', label: 'Слева' }, { value: 'right', label: 'Справа' }] },
  },
  tags: ['about', 'company', 'history', 'mission', 'о нас', 'компания', 'история'],
})

registerBlock({
  type: 'skills-grid',
  label: 'Навыки и технологии',
  category: 'content',
  icon: '💡',
  description: 'Сетка навыков, технологий или инструментов с иконками и прогресс-барами',
  defaultData: {
    title: 'Наши технологии',
    subtitle: 'Используем лучшее из мира разработки',
    items: [
      { icon: '⚛️', name: 'React', level: 95 },
      { icon: '🐍', name: 'Python', level: 88 },
      { icon: '🐳', name: 'Docker', level: 82 },
      { icon: '☁️', name: 'AWS', level: 79 },
      { icon: '🔷', name: 'TypeScript', level: 92 },
      { icon: '🗄️', name: 'PostgreSQL', level: 85 },
    ],
    showProgress: true,
    accentColor: '#5b6af0',
    background: '#ffffff',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наши технологии' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    items: { type: 'array', label: 'Технологии (иконка, название, уровень 0-100)', default: [] },
    showProgress: { type: 'boolean', label: 'Показать прогресс-бар', default: true },
    accentColor: { type: 'color', label: 'Цвет прогресса', default: '#5b6af0' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
  },
  tags: ['skills', 'technologies', 'stack', 'progress', 'навыки', 'технологии', 'стек'],
})

registerBlock({
  type: 'milestone-list',
  label: 'Вехи и достижения',
  category: 'content',
  icon: '🚩',
  description: 'Вертикальный список вех компании/проекта с датами и описаниями',
  defaultData: {
    title: 'Наш путь',
    items: [
      { year: '2020', title: 'Основание компании', text: 'Запустили первую версию продукта с тремя сотрудниками.' },
      { year: '2021', title: 'Первые 1000 клиентов', text: 'За год привлекли тысячу активных пользователей.' },
      { year: '2022', title: 'Раунд А', text: 'Привлекли $2M инвестиций для масштабирования.' },
      { year: '2024', title: 'Международный запуск', text: 'Вышли на рынки Европы и Азии.' },
    ],
    accentColor: '#5b6af0',
    background: '#ffffff',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наш путь' },
    items: { type: 'array', label: 'Вехи (год, заголовок, текст)', default: [] },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
  },
  tags: ['milestones', 'achievements', 'history', 'timeline', 'вехи', 'история', 'достижения'],
})

registerBlock({
  type: 'contact-minimal',
  label: 'Контакты (минимал)',
  category: 'content',
  icon: '📞',
  description: 'Минималистичный блок с контактными данными: телефон, email, адрес',
  defaultData: {
    title: 'Свяжитесь с нами',
    subtitle: 'Ответим в течение часа',
    items: [
      { icon: '📞', label: 'Телефон', value: '+7 (800) 123-45-67', url: 'tel:+78001234567' },
      { icon: '✉️', label: 'Email', value: 'hello@example.com', url: 'mailto:hello@example.com' },
      { icon: '📍', label: 'Адрес', value: 'Москва, ул. Арбат, д. 1', url: '' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
    layout: 'row',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Свяжитесь с нами' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    items: { type: 'array', label: 'Контакты (иконка, подпись, значение, URL)', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
    layout: { type: 'select', label: 'Макет', default: 'row', options: [{ value: 'row', label: 'В ряд' }, { value: 'column', label: 'Столбцом' }] },
  },
  tags: ['contact', 'contacts', 'phone', 'email', 'address', 'контакты', 'телефон', 'адрес'],
})

registerBlock({
  type: 'image-fullwidth',
  label: 'Полноширинное изображение',
  category: 'media',
  icon: '🖼️',
  description: 'Декоративное изображение во всю ширину с опциональной подписью',
  defaultData: {
    src: '',
    alt: '',
    caption: '',
    maxHeight: 500,
    objectFit: 'cover',
    rounded: false,
  },
  schema: {
    src: { type: 'image', label: 'Изображение', default: '' },
    alt: { type: 'text', label: 'Alt текст', default: '' },
    caption: { type: 'text', label: 'Подпись', default: '' },
    maxHeight: { type: 'number', label: 'Максимальная высота (px)', default: 500, min: 100, max: 1000 },
    objectFit: { type: 'select', label: 'Обрезка', default: 'cover', options: [{ value: 'cover', label: 'Обрезать (cover)' }, { value: 'contain', label: 'Вместить (contain)' }, { value: 'fill', label: 'Растянуть' }] },
    rounded: { type: 'boolean', label: 'Скруглённые углы', default: false },
  },
  tags: ['image', 'photo', 'full-width', 'fullwidth', 'изображение', 'фото', 'полноширинное'],
})

registerBlock({
  type: 'service-row',
  label: 'Услуги в ряд',
  category: 'content',
  icon: '🛠️',
  description: 'Горизонтальный список услуг с иконками и кратким описанием',
  defaultData: {
    title: 'Наши услуги',
    subtitle: 'Полный цикл разработки и поддержки',
    services: [
      { icon: '💻', title: 'Разработка', text: 'Создаём сайты и приложения любой сложности' },
      { icon: '🎨', title: 'Дизайн', text: 'Разрабатываем UI/UX для любых платформ' },
      { icon: '📈', title: 'SEO', text: 'Продвигаем сайты в топ поисковых систем' },
      { icon: '🚀', title: 'Маркетинг', text: 'Помогаем привлечь новых клиентов' },
    ],
    columns: 4,
    background: '#ffffff',
    accentColor: '#5b6af0',
    iconBg: '#f0f0ff',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наши услуги' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    services: { type: 'array', label: 'Услуги (иконка, заголовок, текст)', default: [] },
    columns: { type: 'number', label: 'Колонок', default: 4, min: 2, max: 6 },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
    iconBg: { type: 'color', label: 'Фон иконки', default: '#f0f0ff' },
  },
  tags: ['services', 'features', 'icons', 'grid', 'услуги', 'функции', 'сетка'],
})

registerBlock({
  type: 'roadmap',
  label: 'Роадмап',
  category: 'content',
  icon: '🗺️',
  description: 'Дорожная карта продукта или проекта с фазами и статусами',
  defaultData: {
    title: 'Планы развития',
    subtitle: 'Что мы разрабатываем прямо сейчас',
    phases: [
      { quarter: 'Q1 2024', title: 'Запуск MVP', status: 'done', items: ['Базовый редактор', 'Публикация сайтов', 'Регистрация'] },
      { quarter: 'Q2 2024', title: 'Расширение', status: 'done', items: ['CMS', 'E-commerce', 'AI блоки'] },
      { quarter: 'Q3 2024', title: 'Монетизация', status: 'active', items: ['Тарифные планы', 'Платежи', 'API'] },
      { quarter: 'Q4 2024', title: 'Масштаб', status: 'planned', items: ['Мобильное приложение', 'Маркетплейс', 'White Label'] },
    ],
    accentColor: '#5b6af0',
    background: '#ffffff',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Роадмап' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    phases: { type: 'array', label: 'Фазы (квартал, заголовок, статус, список)', default: [] },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
  },
  tags: ['roadmap', 'plan', 'phases', 'timeline', 'роадмап', 'план', 'дорожная карта'],
})

registerBlock({
  type: 'cta-banner-dark',
  label: 'Тёмный баннер CTA',
  category: 'interactive',
  icon: '🎬',
  description: 'Привлекающий внимание тёмный баннер с призывом к действию',
  defaultData: {
    title: 'Начните прямо сейчас',
    subtitle: '14 дней бесплатно. Карта не нужна.',
    buttonText: 'Создать сайт',
    buttonUrl: '#',
    buttonText2: 'Смотреть демо',
    buttonUrl2: '#',
    background: '#1e293b',
    accentColor: '#5b6af0',
    textColor: '#ffffff',
    pattern: 'dots',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Начните прямо сейчас' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    buttonText: { type: 'text', label: 'Текст кнопки 1', default: 'Начать' },
    buttonUrl: { type: 'text', label: 'URL кнопки 1', default: '#' },
    buttonText2: { type: 'text', label: 'Текст кнопки 2', default: '' },
    buttonUrl2: { type: 'text', label: 'URL кнопки 2', default: '#' },
    background: { type: 'color', label: 'Фон', default: '#1e293b' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
    pattern: { type: 'select', label: 'Паттерн', default: 'dots', options: [{ value: 'none', label: 'Нет' }, { value: 'dots', label: 'Точки' }, { value: 'grid', label: 'Сетка' }] },
  },
  tags: ['cta', 'banner', 'dark', 'call-to-action', 'призыв', 'баннер', 'тёмный'],
})

registerBlock({
  type: 'team-compact',
  label: 'Компактная команда',
  category: 'content',
  icon: '👥',
  description: 'Горизонтальный ряд членов команды с аватарами, именами и должностями',
  defaultData: {
    title: 'Наша команда',
    members: [
      { name: 'Анна Иванова', role: 'CEO & Founder', avatar: '', linkedin: '' },
      { name: 'Пётр Смирнов', role: 'CTO', avatar: '', linkedin: '' },
      { name: 'Мария Козлова', role: 'Head of Design', avatar: '', linkedin: '' },
      { name: 'Иван Петров', role: 'Lead Developer', avatar: '', linkedin: '' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
    avatarSize: 80,
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Команда' },
    members: { type: 'array', label: 'Члены команды (имя, должность, аватар, LinkedIn)', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
    avatarSize: { type: 'number', label: 'Размер аватара (px)', default: 80, min: 48, max: 160 },
  },
  tags: ['team', 'people', 'members', 'команда', 'люди', 'участники'],
})

registerBlock({
  type: 'logo-cloud',
  label: 'Облако логотипов',
  category: 'content',
  icon: '🏷️',
  description: 'Сетка логотипов клиентов или партнёров',
  defaultData: {
    title: 'Нам доверяют',
    logos: [
      { src: '', alt: 'Client 1', url: '' },
      { src: '', alt: 'Client 2', url: '' },
      { src: '', alt: 'Client 3', url: '' },
      { src: '', alt: 'Client 4', url: '' },
      { src: '', alt: 'Client 5', url: '' },
      { src: '', alt: 'Client 6', url: '' },
    ],
    columns: 6,
    grayscale: true,
    background: '#ffffff',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок (можно оставить пустым)', default: 'Нам доверяют' },
    logos: { type: 'array', label: 'Логотипы (src, alt, url)', default: [] },
    columns: { type: 'number', label: 'Колонок', default: 6, min: 2, max: 8 },
    grayscale: { type: 'boolean', label: 'Чёрно-белые (hover = цвет)', default: true },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
  },
  tags: ['logos', 'clients', 'partners', 'brands', 'логотипы', 'клиенты', 'партнёры', 'бренды'],
})

registerBlock({
  type: 'dual-cta',
  label: 'Двойной CTA',
  category: 'content',
  icon: '↔️',
  description: 'Два призыва к действию рядом: основной и вторичный',
  defaultData: {
    title: 'Готовы начать?',
    subtitle: 'Попробуйте бесплатно или свяжитесь с нами',
    primaryLabel: 'Начать бесплатно',
    primaryUrl: '#',
    secondaryLabel: 'Связаться с нами',
    secondaryUrl: '#',
    background: '#5b6af0',
    textColor: '#ffffff',
    note: '14 дней бесплатно · Без кредитной карты',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Готовы начать?' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    primaryLabel: { type: 'text', label: 'Кнопка 1 текст', default: 'Начать бесплатно' },
    primaryUrl: { type: 'url', label: 'Кнопка 1 ссылка', default: '#' },
    secondaryLabel: { type: 'text', label: 'Кнопка 2 текст', default: 'Связаться' },
    secondaryUrl: { type: 'url', label: 'Кнопка 2 ссылка', default: '#' },
    note: { type: 'text', label: 'Примечание под кнопками', default: '' },
    background: { type: 'color', label: 'Фон', default: '#5b6af0' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
  },
  tags: ['cta', 'buttons', 'action', 'призыв', 'кнопки'],
})

registerBlock({
  type: 'resource-links',
  label: 'Список ресурсов',
  category: 'content',
  icon: '📎',
  description: 'Список ссылок для скачивания: PDF, гайды, шаблоны',
  defaultData: {
    title: 'Полезные материалы',
    subtitle: 'Скачайте бесплатно',
    resources: [
      { icon: '📄', label: 'Руководство пользователя', size: '2.4 MB', url: '#' },
      { icon: '📊', label: 'Шаблон коммерческого предложения', size: '1.1 MB', url: '#' },
      { icon: '🎓', label: 'Обучающий курс', size: '8.7 MB', url: '#' },
    ],
    background: '#f8fafc',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Полезные материалы' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    resources: { type: 'array', label: 'Ресурсы (icon, label, size, url)', default: [] },
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
  },
  tags: ['download', 'pdf', 'files', 'resources', 'скачать', 'файлы', 'ресурсы'],
})

registerBlock({
  type: 'trust-badges',
  label: 'Знаки доверия',
  category: 'content',
  icon: '🛡️',
  description: 'SSL, гарантии, награды — элементы, повышающие доверие',
  defaultData: {
    title: '',
    badges: [
      { icon: '🔒', label: 'SSL защита', text: '256-bit шифрование' },
      { icon: '↩️', label: 'Возврат средств', text: '30 дней гарантия' },
      { icon: '⭐', label: 'Рейтинг 4.9', text: '2,400+ отзывов' },
      { icon: '🏆', label: 'Лучший продукт', text: 'ProductHunt 2025' },
    ],
    background: '#ffffff',
    borderColor: '#e5e7eb',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок (необязательно)', default: '' },
    badges: { type: 'array', label: 'Значки (icon, label, text)', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    borderColor: { type: 'color', label: 'Цвет рамки', default: '#e5e7eb' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
  },
  tags: ['trust', 'badges', 'ssl', 'guarantee', 'доверие', 'гарантия', 'безопасность'],
})

registerBlock({
  type: 'app-store-buttons',
  label: 'Кнопки приложений',
  category: 'content',
  icon: '📱',
  description: 'App Store + Google Play кнопки для мобильного приложения',
  defaultData: {
    title: 'Доступно на всех платформах',
    subtitle: 'Скачайте наше приложение',
    mockupImage: '',
    appStoreUrl: '#',
    googlePlayUrl: '#',
    appGalleryUrl: '',
    rating: '4.8',
    reviews: '12,000+',
    background: '#0f172a',
    textColor: '#f1f5f9',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Доступно на всех платформах' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    mockupImage: { type: 'image', label: 'Изображение телефона', default: '' },
    appStoreUrl: { type: 'url', label: 'App Store ссылка', default: '#' },
    googlePlayUrl: { type: 'url', label: 'Google Play ссылка', default: '#' },
    appGalleryUrl: { type: 'url', label: 'Huawei AppGallery (необязательно)', default: '' },
    rating: { type: 'text', label: 'Рейтинг', default: '4.8' },
    reviews: { type: 'text', label: 'Кол-во отзывов', default: '12,000+' },
    background: { type: 'color', label: 'Фон', default: '#0f172a' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#f1f5f9' },
  },
  tags: ['app', 'mobile', 'appstore', 'playmarket', 'приложение', 'мобильное'],
})

registerBlock({
  type: 'case-study',
  label: 'Кейс',
  category: 'content',
  icon: '📈',
  description: 'Карточка кейса с результатами: до/после, метрики',
  defaultData: {
    company: 'ООО «Пример»',
    logo: '',
    industry: 'E-commerce',
    challenge: 'Низкая конверсия на сайте — 0.8%',
    solution: 'Полное переосмысление UX и воронки продаж',
    metrics: [
      { label: 'Рост конверсии', value: '+340%' },
      { label: 'Выручка', value: '₽18M' },
      { label: 'Срок', value: '3 мес.' },
    ],
    quote: 'За 3 месяца работы конверсия выросла в 3.4 раза — результат превзошёл все ожидания.',
    author: 'Иван Петров, CEO',
    image: '',
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    company: { type: 'text', label: 'Компания', default: 'Пример' },
    logo: { type: 'image', label: 'Логотип компании', default: '' },
    industry: { type: 'text', label: 'Отрасль', default: '' },
    challenge: { type: 'text', label: 'Задача', default: '' },
    solution: { type: 'text', label: 'Решение', default: '' },
    metrics: { type: 'array', label: 'Метрики (label, value)', default: [] },
    quote: { type: 'text', label: 'Цитата клиента', default: '' },
    author: { type: 'text', label: 'Автор цитаты', default: '' },
    image: { type: 'image', label: 'Фото результата', default: '' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
  },
  tags: ['case', 'study', 'portfolio', 'results', 'кейс', 'результаты', 'портфолио'],
})

registerBlock({
  type: 'changelog',
  label: 'Changelog / Обновления',
  category: 'content',
  icon: '📋',
  description: 'Список обновлений продукта с датами и тегами',
  defaultData: {
    title: 'Что нового',
    entries: [
      { version: 'v2.1.0', date: '15 июля 2026', tag: 'new', items: ['Тёмная тема', 'AI-генерация контента', 'Улучшен поиск'] },
      { version: 'v2.0.0', date: '1 июля 2026', tag: 'major', items: ['Полный редизайн интерфейса', 'Новый редактор блоков'] },
      { version: 'v1.9.5', date: '20 июня 2026', tag: 'fix', items: ['Исправлены ошибки авторизации', 'Ускорена загрузка'] },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Что нового' },
    entries: { type: 'array', label: 'Записи (version, date, tag, items[])', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
  },
  tags: ['changelog', 'updates', 'release', 'notes', 'changelog', 'обновления', 'релизы'],
})

registerBlock({
  type: 'company-values',
  label: 'Ценности компании',
  category: 'content',
  icon: '💎',
  description: 'Корпоративные ценности и принципы с иконками',
  defaultData: {
    title: 'Наши ценности',
    subtitle: 'Принципы, которыми мы руководствуемся',
    values: [
      { icon: '🎯', title: 'Результат', text: 'Мы фокусируемся на реальных результатах для клиентов' },
      { icon: '🤝', title: 'Доверие', text: 'Честность и прозрачность в каждом взаимодействии' },
      { icon: '🚀', title: 'Инновации', text: 'Постоянное совершенствование и поиск новых решений' },
      { icon: '💡', title: 'Экспертиза', text: 'Глубокие знания и профессиональный подход' },
    ],
    columns: 2,
    background: '#f8fafc',
    accentColor: '#5b6af0',
    iconBg: '#ede9fe',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наши ценности' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    values: { type: 'array', label: 'Ценности (icon, title, text)', default: [] },
    columns: { type: 'number', label: 'Колонок', default: 2, min: 1, max: 4 },
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
    iconBg: { type: 'color', label: 'Фон иконки', default: '#ede9fe' },
  },
  tags: ['values', 'principles', 'culture', 'mission', 'ценности', 'принципы', 'культура'],
})

registerBlock({
  type: 'waitlist',
  label: 'Список ожидания',
  category: 'interactive',
  icon: '⏳',
  description: 'Email-форма для сбора waitlist перед запуском',
  defaultData: {
    title: 'Скоро открываем',
    subtitle: 'Оставьте email, чтобы получить ранний доступ',
    placeholder: 'your@email.com',
    buttonText: 'Записаться в список',
    note: 'Никакого спама. Только одно письмо при запуске.',
    countdown: '',
    spotsLeft: 47,
    showSpots: true,
    background: '#0f172a',
    accentColor: '#818cf8',
    textColor: '#f1f5f9',
    successText: 'Вы в списке! Мы напишем при запуске.',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Скоро открываем' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    placeholder: { type: 'text', label: 'Placeholder email', default: 'your@email.com' },
    buttonText: { type: 'text', label: 'Текст кнопки', default: 'Записаться' },
    note: { type: 'text', label: 'Примечание', default: '' },
    showSpots: { type: 'boolean', label: 'Показывать оставшиеся места', default: true },
    spotsLeft: { type: 'number', label: 'Осталось мест', default: 47 },
    successText: { type: 'text', label: 'Текст успеха', default: 'Вы в списке!' },
    background: { type: 'color', label: 'Фон', default: '#0f172a' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#818cf8' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#f1f5f9' },
  },
  tags: ['waitlist', 'coming-soon', 'launch', 'email', 'waitlist', 'список ожидания', 'скоро'],
})

registerBlock({
  type: 'job-card',
  label: 'Вакансия',
  category: 'content',
  icon: '💼',
  description: 'Карточка открытой вакансии',
  defaultData: {
    title: 'Открытые вакансии',
    jobs: [
      { title: 'Frontend-разработчик', department: 'Разработка', location: 'Москва / Удалённо', type: 'Полная занятость', salary: 'от 200 000 ₽', url: '#' },
      { title: 'Product Designer', department: 'Дизайн', location: 'Удалённо', type: 'Полная занятость', salary: 'от 180 000 ₽', url: '#' },
      { title: 'Head of Marketing', department: 'Маркетинг', location: 'Москва', type: 'Полная занятость', salary: 'от 250 000 ₽', url: '#' },
    ],
    buttonText: 'Откликнуться',
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Открытые вакансии' },
    jobs: { type: 'array', label: 'Вакансии (title, department, location, type, salary, url)', default: [] },
    buttonText: { type: 'text', label: 'Текст кнопки', default: 'Откликнуться' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
  },
  tags: ['jobs', 'careers', 'vacancy', 'hiring', 'вакансии', 'карьера', 'работа'],
})

registerBlock({
  type: 'event-schedule',
  label: 'Расписание мероприятия',
  category: 'content',
  icon: '📅',
  description: 'Программа конференции, вебинара или другого события',
  defaultData: {
    title: 'Программа конференции',
    date: '25 июля 2026',
    sessions: [
      { time: '09:00', title: 'Регистрация участников', speaker: '', type: 'break' },
      { time: '10:00', title: 'Открытие и приветственное слово', speaker: 'Иван Иванов, CEO', type: 'keynote' },
      { time: '11:00', title: 'AI в разработке продукта', speaker: 'Мария Петрова', type: 'talk' },
      { time: '12:30', title: 'Обед', speaker: '', type: 'break' },
      { time: '14:00', title: 'Практический воркшоп: No-code инструменты', speaker: 'Алексей Сидоров', type: 'workshop' },
      { time: '16:00', title: 'Нетворкинг и закрытие', speaker: '', type: 'break' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Название мероприятия', default: 'Программа' },
    date: { type: 'text', label: 'Дата', default: '' },
    sessions: { type: 'array', label: 'Сессии (time, title, speaker, type)', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
  },
  tags: ['event', 'conference', 'schedule', 'agenda', 'мероприятие', 'конференция', 'расписание'],
})

registerBlock({
  type: 'feature-matrix',
  label: 'Матрица функций',
  category: 'content',
  icon: '📊',
  description: 'Таблица сравнения функций по тарифам или продуктам',
  defaultData: {
    title: 'Полное сравнение функций',
    columns: ['Бесплатный', 'Про', 'Бизнес'],
    rows: [
      { feature: 'Количество сайтов', values: ['1', '10', 'Без лимита'] },
      { feature: 'Кастомный домен', values: ['—', '✓', '✓'] },
      { feature: 'Аналитика', values: ['Базовая', 'Расширенная', 'Полная'] },
      { feature: 'Поддержка', values: ['Email', 'Приоритет', '24/7 телефон'] },
      { feature: 'AI-функции', values: ['—', '10/мес', 'Без лимита'] },
    ],
    background: '#ffffff',
    headerBg: '#5b6af0',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Сравнение функций' },
    columns: { type: 'array', label: 'Заголовки колонок', default: [] },
    rows: { type: 'array', label: 'Строки (feature, values[])', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    headerBg: { type: 'color', label: 'Фон заголовка', default: '#5b6af0' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
  },
  tags: ['comparison', 'features', 'table', 'matrix', 'сравнение', 'таблица', 'функции'],
})

registerBlock({
  type: 'video-section',
  label: 'Видео-секция',
  category: 'media',
  icon: '🎬',
  description: 'Полноширинный фон-видео с текстом поверх',
  defaultData: {
    videoUrl: '',
    poster: '',
    title: 'Мы создаём будущее',
    subtitle: 'Посмотрите как мы это делаем',
    overlay: 0.5,
    textColor: '#ffffff',
    buttonText: 'Узнать подробнее',
    buttonUrl: '#',
    minHeight: 500,
  },
  schema: {
    videoUrl: { type: 'video', label: 'Ссылка на видео (MP4)', default: '' },
    poster: { type: 'image', label: 'Постер (пока видео не загружено)', default: '' },
    title: { type: 'text', label: 'Заголовок', default: '' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    overlay: { type: 'number', label: 'Затемнение (0–1)', default: 0.5, min: 0, max: 1 },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
    buttonText: { type: 'text', label: 'Текст кнопки', default: '' },
    buttonUrl: { type: 'url', label: 'Ссылка кнопки', default: '#' },
    minHeight: { type: 'number', label: 'Мин. высота (px)', default: 500, min: 200, max: 900 },
  },
  tags: ['video', 'background', 'hero', 'media', 'видео', 'фон', 'медиа'],
})

registerBlock({
  type: 'stats-dark',
  label: 'Метрики (тёмный фон)',
  category: 'content',
  icon: '📉',
  description: 'Ключевые метрики на тёмном фоне',
  defaultData: {
    title: 'Наши результаты в цифрах',
    subtitle: '',
    stats: [
      { value: '10,000+', label: 'Активных клиентов', icon: '👥' },
      { value: '99.9%', label: 'Uptime сервиса', icon: '⚡' },
      { value: '$12M', label: 'Выручки клиентов', icon: '💰' },
      { value: '4.9 ⭐', label: 'Средний рейтинг', icon: '🏆' },
    ],
    background: '#0f172a',
    accentColor: '#818cf8',
    textColor: '#f1f5f9',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Результаты в цифрах' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    stats: { type: 'array', label: 'Метрики (value, label, icon)', default: [] },
    background: { type: 'color', label: 'Фон', default: '#0f172a' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#818cf8' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#f1f5f9' },
  },
  tags: ['stats', 'metrics', 'dark', 'numbers', 'метрики', 'цифры', 'тёмный'],
})

registerBlock({
  type: 'testimonials-masonry',
  label: 'Отзывы — плитка',
  category: 'content',
  icon: '💬',
  description: 'Отзывы в виде плиточной сетки (masonry) с фото',
  defaultData: {
    title: 'Что говорят наши клиенты',
    testimonials: [
      { name: 'Анна К.', role: 'Маркетолог', avatar: '', text: 'Потрясающий продукт! Создала сайт за 2 часа без навыков программирования.', rating: 5 },
      { name: 'Дмитрий С.', role: 'CEO стартапа', avatar: '', text: 'Лучший инструмент для быстрого запуска. Перешли с Webflow и ни разу не пожалели. Функций намного больше.', rating: 5 },
      { name: 'Мария Л.', role: 'Дизайнер', avatar: '', text: 'Дизайн-возможности на уровне Figma, только в браузере.', rating: 5 },
      { name: 'Павел В.', role: 'Фрилансер', avatar: '', text: 'Зарабатываю на 40% больше, делая сайты для клиентов через BST. Рекомендую всем фрилансерам.', rating: 5 },
    ],
    background: '#f8fafc',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Что говорят клиенты' },
    testimonials: { type: 'array', label: 'Отзывы (name, role, avatar, text, rating)', default: [] },
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
  },
  tags: ['testimonials', 'reviews', 'masonry', 'grid', 'отзывы', 'мнения', 'клиенты'],
})

registerBlock({
  type: 'blog-featured',
  label: 'Избранная статья',
  category: 'content',
  icon: '📰',
  description: 'Выделенная карточка блог-статьи или новости',
  defaultData: {
    label: 'Статья месяца',
    title: '10 ошибок при создании лендинга, которые убивают конверсию',
    excerpt: 'Разбираем реальные кейсы: почему красивый дизайн не продаёт и как это исправить за один вечер.',
    image: '',
    author: 'Иван Иванов',
    authorAvatar: '',
    date: '15 июля 2026',
    readTime: '8 мин',
    tags: ['Конверсия', 'UX', 'Лендинг'],
    url: '#',
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    label: { type: 'text', label: 'Ярлык над заголовком', default: 'Рекомендуем' },
    title: { type: 'text', label: 'Заголовок статьи', default: '' },
    excerpt: { type: 'text', label: 'Аннотация', default: '' },
    image: { type: 'image', label: 'Обложка', default: '' },
    author: { type: 'text', label: 'Автор', default: '' },
    authorAvatar: { type: 'image', label: 'Фото автора', default: '' },
    date: { type: 'text', label: 'Дата', default: '' },
    readTime: { type: 'text', label: 'Время чтения', default: '' },
    tags: { type: 'array', label: 'Теги', default: [] },
    url: { type: 'url', label: 'Ссылка на статью', default: '#' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
  },
  tags: ['blog', 'article', 'news', 'featured', 'блог', 'статья', 'новость'],
})

registerBlock({
  type: 'press-quotes',
  label: 'Цитаты прессы',
  category: 'content',
  icon: '📰',
  description: 'Упоминания в СМИ с логотипами изданий',
  defaultData: {
    title: 'О нас пишут',
    quotes: [
      { logo: '', publication: 'TechCrunch', quote: 'Революционный конструктор сайтов, который переосмысляет понятие no-code', url: '#' },
      { logo: '', publication: 'Forbes', quote: 'Стартап привлёк $2M за первый квартал', url: '#' },
      { logo: '', publication: 'VC.ru', quote: 'Лучший российский конкурент Tilda с AI-функциями', url: '#' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'О нас пишут' },
    quotes: { type: 'array', label: 'Упоминания (logo, publication, quote, url)', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
  },
  tags: ['press', 'media', 'quotes', 'publications', 'пресса', 'СМИ', 'упоминания'],
})

registerBlock({
  type: 'member-cards',
  label: 'Карточки подписки',
  category: 'commerce',
  icon: '💳',
  description: 'Карточки членства / подписки с преимуществами',
  defaultData: {
    title: 'Выберите ваш уровень',
    subtitle: 'Присоединитесь к нашему сообществу',
    cards: [
      { name: 'Стартер', price: '990 ₽/мес', icon: '🌱', color: '#22c55e', perks: ['Доступ к базовому контенту', 'Email-рассылка', 'Закрытый чат'] },
      { name: 'Профи', price: '2 490 ₽/мес', icon: '🚀', color: '#5b6af0', perks: ['Все преимущества Стартера', 'Вебинары и мастер-классы', 'Шаблоны и материалы', 'Менторская поддержка'] },
      { name: 'VIP', price: '5 990 ₽/мес', icon: '👑', color: '#f59e0b', perks: ['Всё из Профи', 'Личные консультации 2×/мес', 'Ранний доступ к новинкам', 'Приоритетная поддержка'] },
    ],
    background: '#f8fafc',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Выберите уровень' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    cards: { type: 'array', label: 'Карточки (name, price, icon, color, perks[])', default: [] },
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
  },
  tags: ['membership', 'subscription', 'pricing', 'cards', 'членство', 'подписка', 'тарифы'],
})

registerBlock({
  type: 'portfolio-carousel',
  label: 'Портфолио — карусель',
  category: 'gallery',
  icon: '🖼️',
  description: 'Горизонтально прокручиваемый портфель работ',
  defaultData: {
    title: 'Наши работы',
    subtitle: '',
    items: [
      { image: '', title: 'Проект 1', tags: ['Дизайн', 'Web'] },
      { image: '', title: 'Проект 2', tags: ['Mobile', 'UX'] },
      { image: '', title: 'Проект 3', tags: ['Branding'] },
      { image: '', title: 'Проект 4', tags: ['E-commerce'] },
    ],
    cardWidth: 320,
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наши работы' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    items: { type: 'array', label: 'Работы (image, title, tags[])', default: [] },
    cardWidth: { type: 'number', label: 'Ширина карточки (px)', default: 320, min: 200, max: 600 },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
  },
  tags: ['portfolio', 'carousel', 'gallery', 'works', 'портфолио', 'карусель', 'работы'],
})

registerBlock({
  type: 'map-contacts',
  label: 'Карта + контакты',
  category: 'interactive',
  icon: '🗺️',
  description: 'Карта рядом с контактной информацией',
  defaultData: {
    title: 'Как нас найти',
    address: 'Москва, Пресненская наб., 12',
    phone: '+7 (495) 000-00-00',
    email: 'hello@example.com',
    hours: 'Пн–Пт: 9:00–18:00',
    mapQuery: 'Москва, Пресненская набережная 12',
    mapProvider: 'yandex',
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Как нас найти' },
    address: { type: 'text', label: 'Адрес', default: '' },
    phone: { type: 'text', label: 'Телефон', default: '' },
    email: { type: 'text', label: 'Email', default: '' },
    hours: { type: 'text', label: 'Часы работы', default: '' },
    mapQuery: { type: 'text', label: 'Адрес для карты', default: 'Москва' },
    mapProvider: { type: 'select', label: 'Провайдер карты', options: [{ value: 'yandex', label: 'Яндекс Карты' }, { value: 'google', label: 'Google Maps' }, { value: 'osm', label: 'OpenStreetMap' }], default: 'yandex' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
  },
  tags: ['map', 'contacts', 'location', 'address', 'карта', 'контакты', 'адрес'],
})

registerBlock({
  type: 'image-grid',
  label: 'Фотосетка',
  category: 'gallery',
  icon: '🖼',
  description: 'Сетка фотографий с лайтбоксом при клике',
  defaultData: {
    title: '',
    images: [
      { src: '', alt: '' },
      { src: '', alt: '' },
      { src: '', alt: '' },
      { src: '', alt: '' },
      { src: '', alt: '' },
      { src: '', alt: '' },
    ],
    columns: 3,
    gap: 8,
    rounded: true,
    background: '#ffffff',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: '' },
    images: { type: 'array', label: 'Изображения (src, alt)', default: [] },
    columns: { type: 'number', label: 'Колонок', default: 3, min: 2, max: 6 },
    gap: { type: 'number', label: 'Отступ (px)', default: 8, min: 0, max: 32 },
    rounded: { type: 'boolean', label: 'Скруглённые углы', default: true },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
  },
  tags: ['gallery', 'photos', 'grid', 'images', 'lightbox', 'галерея', 'фото', 'сетка'],
})

registerBlock({
  type: 'social-wall',
  label: 'Социальная стена',
  category: 'interactive',
  icon: '📲',
  description: 'Посты из соцсетей в виде карточек (UGC-стена)',
  defaultData: {
    title: '#нашклиент в соцсетях',
    posts: [
      { network: 'instagram', avatar: '', name: '@user1', text: 'Невероятный продукт! Советую всем 🔥', likes: 234, image: '' },
      { network: 'twitter', avatar: '', name: '@user2', text: 'Только что создал свой сайт за 20 минут. Это магия какая-то!', likes: 89, image: '' },
      { network: 'vk', avatar: '', name: 'Анна К.', text: 'Перешла с Tilda — небо и земля. Больше функций, дешевле и быстрее.', likes: 156, image: '' },
    ],
    columns: 3,
    background: '#f8fafc',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: '#нашклиент' },
    posts: { type: 'array', label: 'Посты (network, avatar, name, text, likes, image)', default: [] },
    columns: { type: 'number', label: 'Колонок', default: 3, min: 1, max: 4 },
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
    accentColor: { type: 'color', label: 'Цвет акцента', default: '#5b6af0' },
  },
  tags: ['social', 'wall', 'ugc', 'instagram', 'twitter', 'vk', 'соцсети', 'отзывы', 'публикации'],
})

// ── NEW BLOCKS (20) ───────────────────────────────────────────────────────────

registerBlock({
  type: 'service-pricing',
  label: 'Прайс-лист услуг',
  category: 'content',
  icon: '💰',
  description: 'Список услуг с ценами, описаниями и кнопкой — идеально для агентств, фрилансеров и салонов',
  defaultData: {
    title: 'Наши услуги',
    subtitle: 'Прозрачное ценообразование',
    currency: '₽',
    services: [
      { name: 'Базовый сайт', price: '15 000', duration: 'от 3 дней', description: 'Лендинг до 5 страниц, адаптивный дизайн, базовое SEO', popular: false },
      { name: 'Корпоративный сайт', price: '45 000', duration: 'от 2 недель', description: 'Многостраничный сайт, интеграции, CMS, аналитика', popular: true },
      { name: 'Интернет-магазин', price: '90 000', duration: 'от 4 недель', description: 'Полноценный e-commerce, оплата онлайн, управление заказами', popular: false },
    ],
    buttonText: 'Заказать',
    buttonUrl: '#contact',
    background: '#ffffff',
    accentColor: '#5b6af0',
    popularLabel: 'Популярно',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наши услуги' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    currency: { type: 'text', label: 'Символ валюты', default: '₽' },
    services: { type: 'array', label: 'Услуги (name, price, duration, description, popular)', default: [] },
    buttonText: { type: 'text', label: 'Текст кнопки', default: 'Заказать' },
    buttonUrl: { type: 'url', label: 'Ссылка кнопки', default: '#contact' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцент', default: '#5b6af0' },
    popularLabel: { type: 'text', label: 'Метка популярного', default: 'Популярно' },
  },
  tags: ['pricing', 'services', 'rates', 'price', 'menu', 'прайс', 'услуги', 'цены'],
})

registerBlock({
  type: 'feature-checklist',
  label: 'Сравнение функций',
  category: 'content',
  icon: '✅',
  description: 'Таблица функций с галочками и крестиками — для сравнения тарифов или продуктов',
  defaultData: {
    title: 'Что включено',
    plans: ['Бесплатно', 'Pro', 'Enterprise'],
    planColors: ['#9ca3af', '#5b6af0', '#7c3aed'],
    features: [
      { name: 'Количество сайтов', values: ['1', '10', 'Без лимита'] },
      { name: 'Кастомный домен', values: [false, true, true] },
      { name: 'E-commerce', values: [false, true, true] },
      { name: 'Аналитика', values: [false, true, true] },
      { name: 'API доступ', values: [false, false, true] },
      { name: 'Приоритетная поддержка', values: [false, false, true] },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Что включено' },
    plans: { type: 'array', label: 'Колонки (названия планов)', default: [], stringArray: true },
    features: { type: 'array', label: 'Строки (name, values[])', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцент', default: '#5b6af0' },
  },
  tags: ['features', 'comparison', 'checklist', 'table', 'plans', 'сравнение', 'функции', 'тарифы'],
})

registerBlock({
  type: 'recipe-card',
  label: 'Карточка рецепта',
  category: 'content',
  icon: '🍽',
  description: 'Красивая карточка рецепта с ингредиентами, шагами, временем и нутриентами',
  defaultData: {
    title: 'Паста карбонара',
    image: '',
    prepTime: '15 мин',
    cookTime: '20 мин',
    servings: '4 порции',
    difficulty: 'Средне',
    description: 'Классическая итальянская паста с беконом, яйцами и пармезаном',
    ingredients: ['400г спагетти', '200г бекон', '4 яйца', '100г пармезан', 'Соль, чёрный перец'],
    steps: ['Отварить пасту до al dente.', 'Обжарить бекон до хрустящей корочки.', 'Взбить яйца с пармезаном.', 'Соединить пасту с беконом вне огня, добавить яичную смесь.'],
    calories: '650',
    protein: '28г',
    carbs: '72г',
    fat: '24г',
    background: '#ffffff',
    accentColor: '#f59e0b',
  },
  schema: {
    title: { type: 'text', label: 'Название рецепта', default: '' },
    image: { type: 'image', label: 'Фото блюда', default: '' },
    prepTime: { type: 'text', label: 'Время подготовки', default: '' },
    cookTime: { type: 'text', label: 'Время приготовления', default: '' },
    servings: { type: 'text', label: 'Порций', default: '' },
    difficulty: { type: 'select', label: 'Сложность', default: 'Средне', options: [{value:'Лёгко',label:'Лёгко'},{value:'Средне',label:'Средне'},{value:'Сложно',label:'Сложно'}] },
    description: { type: 'text', label: 'Описание', default: '' },
    ingredients: { type: 'array', label: 'Ингредиенты', default: [], stringArray: true },
    steps: { type: 'array', label: 'Шаги', default: [], stringArray: true },
    calories: { type: 'text', label: 'Калорий', default: '' },
    protein: { type: 'text', label: 'Белки', default: '' },
    carbs: { type: 'text', label: 'Углеводы', default: '' },
    fat: { type: 'text', label: 'Жиры', default: '' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцент', default: '#f59e0b' },
  },
  tags: ['recipe', 'food', 'cooking', 'ingredients', 'steps', 'рецепт', 'еда', 'кулинария'],
})

registerBlock({
  type: 'how-it-works',
  label: 'Как это работает',
  category: 'content',
  icon: '⚙️',
  description: 'Пошаговый процесс с номерами, иконками — «Как это работает», «3 шага» и т.д.',
  defaultData: {
    title: 'Как это работает',
    subtitle: 'Три простых шага до результата',
    steps: [
      { number: '01', icon: '📝', title: 'Зарегистрируйтесь', description: 'Создайте аккаунт за 30 секунд — без карты' },
      { number: '02', icon: '🎨', title: 'Создайте сайт', description: 'Выберите шаблон и настройте с drag-and-drop' },
      { number: '03', icon: '🚀', title: 'Опубликуйте', description: 'Нажмите «Опубликовать» и сайт уже в сети' },
    ],
    layout: 'horizontal',
    connectorLine: true,
    background: '#f8fafc',
    accentColor: '#5b6af0',
    numberColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Как это работает' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    steps: { type: 'array', label: 'Шаги (number, icon, title, description)', default: [] },
    layout: { type: 'select', label: 'Раскладка', default: 'horizontal', options: [{value:'horizontal',label:'Горизонтально'},{value:'vertical',label:'Вертикально'}] },
    connectorLine: { type: 'boolean', label: 'Линия-коннектор', default: true },
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
    accentColor: { type: 'color', label: 'Акцент', default: '#5b6af0' },
    numberColor: { type: 'color', label: 'Цвет номеров', default: '#5b6af0' },
  },
  tags: ['steps', 'process', 'how-it-works', 'numbered', 'шаги', 'процесс', 'как'],
})

registerBlock({
  type: 'price-slider',
  label: 'Калькулятор цены',
  category: 'interactive',
  icon: '🎚',
  description: 'Интерактивный калькулятор с ползунком — цена меняется при перетаскивании',
  defaultData: {
    title: 'Рассчитайте стоимость',
    subtitle: 'Перетащите ползунок для расчёта',
    sliderLabel: 'Количество пользователей',
    sliderMin: 1,
    sliderMax: 100,
    sliderDefault: 10,
    sliderStep: 1,
    pricePerUnit: 990,
    currency: '₽',
    period: '/мес',
    freeUpTo: 5,
    freeLabel: 'Бесплатно',
    features: ['Неограниченные сайты', 'Поддержка 24/7', 'SSL-сертификат'],
    buttonText: 'Начать',
    buttonUrl: '#signup',
    background: '#f8fafc',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Рассчитайте стоимость' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    sliderLabel: { type: 'text', label: 'Подпись ползунка', default: 'Пользователей' },
    sliderMin: { type: 'number', label: 'Мин. значение', default: 1 },
    sliderMax: { type: 'number', label: 'Макс. значение', default: 100 },
    sliderDefault: { type: 'number', label: 'Нач. значение', default: 10 },
    sliderStep: { type: 'number', label: 'Шаг', default: 1 },
    pricePerUnit: { type: 'number', label: 'Цена за единицу', default: 990 },
    currency: { type: 'text', label: 'Валюта', default: '₽' },
    period: { type: 'text', label: 'Период', default: '/мес' },
    freeUpTo: { type: 'number', label: 'Бесплатно до', default: 0 },
    freeLabel: { type: 'text', label: 'Ярлык бесплатно', default: 'Бесплатно' },
    features: { type: 'array', label: 'Включённые функции', default: [], stringArray: true },
    buttonText: { type: 'text', label: 'Текст кнопки', default: 'Начать' },
    buttonUrl: { type: 'url', label: 'Ссылка', default: '#signup' },
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
    accentColor: { type: 'color', label: 'Акцент', default: '#5b6af0' },
  },
  tags: ['calculator', 'pricing', 'slider', 'interactive', 'калькулятор', 'ценообразование', 'слайдер'],
})

registerBlock({
  type: 'job-listings',
  label: 'Вакансии',
  category: 'interactive',
  icon: '💼',
  description: 'Список вакансий с фильтрами по отделу — для HR-страниц и карьерных разделов',
  defaultData: {
    title: 'Открытые вакансии',
    subtitle: 'Присоединяйтесь к нашей команде',
    showFilters: true,
    jobs: [
      { title: 'Frontend-разработчик', department: 'Разработка', type: 'Полная занятость', location: 'Москва / Remote', salary: '180 000 – 250 000 ₽', url: '#' },
      { title: 'UI/UX Дизайнер', department: 'Дизайн', type: 'Полная занятость', location: 'Удалённо', salary: '120 000 – 180 000 ₽', url: '#' },
      { title: 'Маркетолог', department: 'Маркетинг', type: 'Полная занятость', location: 'Москва', salary: '100 000 – 150 000 ₽', url: '#' },
      { title: 'Контент-менеджер', department: 'Маркетинг', type: 'Частичная занятость', location: 'Удалённо', salary: 'по договорённости', url: '#' },
    ],
    applyText: 'Откликнуться',
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Открытые вакансии' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    showFilters: { type: 'boolean', label: 'Показывать фильтры', default: true },
    jobs: { type: 'array', label: 'Вакансии (title, department, type, location, salary, url)', default: [] },
    applyText: { type: 'text', label: 'Текст кнопки', default: 'Откликнуться' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцент', default: '#5b6af0' },
  },
  tags: ['jobs', 'careers', 'hiring', 'vacancies', 'hr', 'вакансии', 'карьера', 'работа'],
})

registerBlock({
  type: 'donation-block',
  label: 'Пожертвование',
  category: 'interactive',
  icon: '❤️',
  description: 'Блок для сбора пожертвований с предустановленными суммами — для НКО и фондов',
  defaultData: {
    title: 'Поддержите нас',
    subtitle: 'Ваш вклад помогает нам делать мир лучше',
    presets: ['100', '500', '1000', '5000'],
    currency: '₽',
    customLabel: 'Другая сумма',
    buttonText: 'Пожертвовать',
    buttonUrl: '#donate',
    showRecurring: true,
    impact: 'На 500₽ можно купить учебники для 2 детей',
    background: '#fff5f5',
    accentColor: '#ef4444',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Поддержите нас' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    presets: { type: 'array', label: 'Суммы', default: [], stringArray: true },
    currency: { type: 'text', label: 'Валюта', default: '₽' },
    customLabel: { type: 'text', label: 'Своя сумма', default: 'Другая сумма' },
    buttonText: { type: 'text', label: 'Текст кнопки', default: 'Пожертвовать' },
    buttonUrl: { type: 'url', label: 'URL оплаты', default: '#' },
    showRecurring: { type: 'boolean', label: 'Опция ежемесячно', default: true },
    impact: { type: 'text', label: 'Описание импакта', default: '' },
    background: { type: 'color', label: 'Фон', default: '#fff5f5' },
    accentColor: { type: 'color', label: 'Акцент', default: '#ef4444' },
  },
  tags: ['donation', 'charity', 'ngo', 'nonprofit', 'fundraising', 'пожертвование', 'благотворительность'],
})

registerBlock({
  type: 'podcast-player',
  label: 'Подкаст-плеер',
  category: 'media',
  icon: '🎙',
  description: 'Список эпизодов подкаста с HTML5 аудио-плеером, датами и описаниями',
  defaultData: {
    title: 'Наш подкаст',
    subtitle: 'Выпуски',
    episodes: [
      { title: 'Как запустить стартап за 30 дней', description: 'Разговариваем о быстром запуске MVP', duration: '42:15', date: '18 июля 2026', audioUrl: '', coverImage: '' },
      { title: 'SEO в 2026 году — что изменилось', description: 'Новые алгоритмы Google и ИИ в поиске', duration: '38:40', date: '11 июля 2026', audioUrl: '', coverImage: '' },
      { title: 'Монетизация контента: 10 стратегий', description: 'Способы заработать на аудитории', duration: '55:20', date: '4 июля 2026', audioUrl: '', coverImage: '' },
    ],
    subscribeSpotify: '',
    subscribeApple: '',
    subscribeYoutube: '',
    background: '#0f0f0f',
    accentColor: '#5b6af0',
    textColor: '#ffffff',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наш подкаст' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: 'Выпуски' },
    episodes: { type: 'array', label: 'Эпизоды (title, description, duration, date, audioUrl, coverImage)', default: [] },
    subscribeSpotify: { type: 'url', label: 'Spotify', default: '' },
    subscribeApple: { type: 'url', label: 'Apple Podcasts', default: '' },
    subscribeYoutube: { type: 'url', label: 'YouTube', default: '' },
    background: { type: 'color', label: 'Фон', default: '#0f0f0f' },
    accentColor: { type: 'color', label: 'Акцент', default: '#5b6af0' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
  },
  tags: ['podcast', 'audio', 'player', 'episodes', 'подкаст', 'аудио', 'плеер'],
})

registerBlock({
  type: 'product-bundle',
  label: 'Набор товаров',
  category: 'commerce',
  icon: '📦',
  description: 'Блок «Купить набором» — несколько товаров со скидкой, показывает экономию',
  defaultData: {
    title: 'Выгодный набор',
    badge: '🔥 Экономия 30%',
    items: [
      { name: 'Базовый план', image: '', regularPrice: 790 },
      { name: 'AI-модуль', image: '', regularPrice: 490 },
      { name: 'SEO Pro', image: '', regularPrice: 390 },
    ],
    bundlePrice: 1190,
    currency: '₽',
    period: '/мес',
    buttonText: 'Купить набор',
    buttonUrl: '#',
    guarantee: '14-дневная гарантия возврата',
    background: '#f0fdf4',
    accentColor: '#22c55e',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Выгодный набор' },
    badge: { type: 'text', label: 'Значок', default: '🔥 Экономия 30%' },
    items: { type: 'array', label: 'Товары (name, image, regularPrice)', default: [] },
    bundlePrice: { type: 'number', label: 'Цена набора', default: 0 },
    currency: { type: 'text', label: 'Валюта', default: '₽' },
    period: { type: 'text', label: 'Период', default: '' },
    buttonText: { type: 'text', label: 'Текст кнопки', default: 'Купить набор' },
    buttonUrl: { type: 'url', label: 'Ссылка', default: '#' },
    guarantee: { type: 'text', label: 'Гарантия', default: '' },
    background: { type: 'color', label: 'Фон', default: '#f0fdf4' },
    accentColor: { type: 'color', label: 'Акцент', default: '#22c55e' },
  },
  tags: ['bundle', 'product', 'deal', 'savings', 'набор', 'скидка'],
})

registerBlock({
  type: 'size-guide',
  label: 'Таблица размеров',
  category: 'commerce',
  icon: '📏',
  description: 'Таблица размеров одежды или обуви с переключением единиц измерения',
  defaultData: {
    title: 'Таблица размеров',
    headers: ['Размер', 'Грудь (см)', 'Талия (см)', 'Бёдра (см)'],
    rows: [
      ['XS', '80–84', '60–64', '86–90'],
      ['S', '84–88', '64–68', '90–94'],
      ['M', '88–92', '68–72', '94–98'],
      ['L', '92–96', '72–76', '98–102'],
      ['XL', '96–100', '76–80', '102–106'],
    ],
    note: 'При сомнении выбирайте больший размер.',
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Таблица размеров' },
    headers: { type: 'array', label: 'Заголовки колонок', default: [], stringArray: true },
    rows: { type: 'array', label: 'Строки таблицы', default: [] },
    note: { type: 'text', label: 'Примечание', default: '' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцент', default: '#5b6af0' },
  },
  tags: ['size', 'guide', 'chart', 'fashion', 'размеры', 'одежда', 'таблица'],
})

registerBlock({
  type: 'gift-card',
  label: 'Подарочная карта',
  category: 'commerce',
  icon: '🎁',
  description: 'Блок продажи подарочных карт с выбором номинала и кнопкой оплаты',
  defaultData: {
    title: 'Подарочные карты',
    subtitle: 'Подарите возможность выбора',
    amounts: ['500', '1000', '2000', '5000'],
    currency: '₽',
    features: ['Действует 12 месяцев', 'Отправим на email сразу', 'Работает на все товары'],
    buttonText: 'Купить карту',
    buttonUrl: '#',
    background: '#ffffff',
    accentColor: '#ec4899',
    cardColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Подарочные карты' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    amounts: { type: 'array', label: 'Номиналы', default: [], stringArray: true },
    currency: { type: 'text', label: 'Валюта', default: '₽' },
    features: { type: 'array', label: 'Преимущества', default: [], stringArray: true },
    buttonText: { type: 'text', label: 'Текст кнопки', default: 'Купить карту' },
    buttonUrl: { type: 'url', label: 'Ссылка', default: '#' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцент', default: '#ec4899' },
    cardColor: { type: 'color', label: 'Цвет карты', default: '#5b6af0' },
  },
  tags: ['gift', 'card', 'voucher', 'present', 'подарок', 'карта', 'сертификат'],
})

registerBlock({
  type: 'upsell-block',
  label: 'Рекомендуемые товары',
  category: 'commerce',
  icon: '🛍',
  description: 'Блок up-sell и cross-sell — «С этим товаром покупают» или «Вам может понравиться»',
  defaultData: {
    title: 'С этим товаром покупают',
    products: [
      { name: 'Аксессуар Pro', image: '', price: '1 290 ₽', oldPrice: '1 990 ₽', badge: '-35%', url: '#' },
      { name: 'Расширение Plus', image: '', price: '690 ₽', oldPrice: '', badge: 'Хит', url: '#' },
      { name: 'Подписка Annual', image: '', price: '5 990 ₽', oldPrice: '11 988 ₽', badge: '-50%', url: '#' },
    ],
    buttonText: 'В корзину',
    background: '#f8fafc',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Вам может понравиться' },
    products: { type: 'array', label: 'Товары (name, image, price, oldPrice, badge, url)', default: [] },
    buttonText: { type: 'text', label: 'Кнопка', default: 'В корзину' },
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
    accentColor: { type: 'color', label: 'Акцент', default: '#5b6af0' },
  },
  tags: ['upsell', 'cross-sell', 'recommendations', 'products', 'рекомендации', 'похожие'],
})

registerBlock({
  type: 'split-scroll-feature',
  label: 'Прилипающий лейаут',
  category: 'layout',
  icon: '📌',
  description: 'Левая колонка фиксируется при скролле, правая прокручивается — для детального показа функций',
  defaultData: {
    title: 'Всё что нужно',
    subtitle: 'для вашего сайта',
    stickyImage: '',
    panels: [
      { title: 'Drag-and-drop редактор', description: 'Перетаскивайте блоки, редактируйте текст прямо на странице. Никакого кода.', icon: '🖱' },
      { title: '176+ готовых блоков', description: 'Выбирайте из библиотеки профессиональных блоков для любого типа сайта.', icon: '🧩' },
      { title: 'AI-помощник', description: 'Генерируйте тексты, изображения и секции с помощью искусственного интеллекта.', icon: '🤖' },
      { title: 'Мгновенная публикация', description: 'Нажмите «Опубликовать» — сайт доступен в сети в эту же секунду.', icon: '🚀' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок (лев. колонка)', default: 'Всё что нужно' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    stickyImage: { type: 'image', label: 'Изображение (лев. колонка)', default: '' },
    panels: { type: 'array', label: 'Панели (title, description, icon)', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцент', default: '#5b6af0' },
  },
  tags: ['sticky', 'scroll', 'layout', 'features', 'split', 'прилипающий', 'прокрутка'],
})

registerBlock({
  type: 'gradient-blob-hero',
  label: 'Герой с анимированными блобами',
  category: 'hero',
  icon: '🫧',
  description: 'Современный hero с анимированными градиентными пятнами — как у Stripe, Linear, Vercel',
  defaultData: {
    title: 'Создавайте сайты нового поколения',
    subtitle: 'Мощный конструктор с AI, аналитикой и e-commerce — всё в одном месте',
    primaryButtonText: 'Начать бесплатно',
    primaryButtonUrl: '#signup',
    secondaryButtonText: 'Смотреть демо',
    secondaryButtonUrl: '#demo',
    badge: '✨ Новинка 2026',
    color1: '#818cf8',
    color2: '#34d399',
    color3: '#fb7185',
    background: '#030712',
    textColor: '#ffffff',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: '' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    primaryButtonText: { type: 'text', label: 'Кнопка 1', default: 'Начать бесплатно' },
    primaryButtonUrl: { type: 'url', label: 'Ссылка 1', default: '#' },
    secondaryButtonText: { type: 'text', label: 'Кнопка 2', default: 'Демо' },
    secondaryButtonUrl: { type: 'url', label: 'Ссылка 2', default: '#' },
    badge: { type: 'text', label: 'Значок', default: '' },
    color1: { type: 'color', label: 'Блоб 1', default: '#818cf8' },
    color2: { type: 'color', label: 'Блоб 2', default: '#34d399' },
    color3: { type: 'color', label: 'Блоб 3', default: '#fb7185' },
    background: { type: 'color', label: 'Фон', default: '#030712' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
  },
  tags: ['hero', 'blob', 'gradient', 'animated', 'modern', 'героj', 'анимация'],
})

registerBlock({
  type: 'clients-wall',
  label: 'Стена клиентов',
  category: 'gallery',
  icon: '🏆',
  description: 'Стена с логотипами клиентов разных размеров — «Нам доверяют»',
  defaultData: {
    title: 'Нам доверяют лидеры рынка',
    subtitle: 'Более 5 000 компаний выбрали BST',
    clients: [
      { name: 'Яндекс', logo: '', size: 'large' },
      { name: 'Сбер', logo: '', size: 'medium' },
      { name: 'ВКонтакте', logo: '', size: 'medium' },
      { name: 'Ozon', logo: '', size: 'large' },
      { name: 'Wildberries', logo: '', size: 'small' },
      { name: 'Mail.ru', logo: '', size: 'small' },
    ],
    grayscale: true,
    showNames: true,
    background: '#f8fafc',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Нам доверяют' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    clients: { type: 'array', label: 'Клиенты (name, logo, size: large/medium/small)', default: [] },
    grayscale: { type: 'boolean', label: 'Ч/б логотипы', default: true },
    showNames: { type: 'boolean', label: 'Показывать названия', default: false },
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
    accentColor: { type: 'color', label: 'Акцент', default: '#5b6af0' },
  },
  tags: ['clients', 'logos', 'wall', 'trust', 'brands', 'клиенты', 'логотипы', 'стена'],
})

registerBlock({
  type: 'org-chart',
  label: 'Оргсхема',
  category: 'content',
  icon: '🏛',
  description: 'Визуальная схема организационной структуры компании с аватарами и должностями',
  defaultData: {
    title: 'Наша команда',
    rootName: 'Иван Иванов',
    rootRole: 'CEO & Основатель',
    rootAvatar: '',
    children: [
      { name: 'Мария Петрова', role: 'CTO', avatar: '', children: [
        { name: 'Алексей Сидоров', role: 'Lead Dev', avatar: '', children: [] },
        { name: 'Юлия Кузнецова', role: 'Designer', avatar: '', children: [] },
      ]},
      { name: 'Дмитрий Волков', role: 'CMO', avatar: '', children: [
        { name: 'Анна Смирнова', role: 'SEO Manager', avatar: '', children: [] },
      ]},
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
    lineColor: '#e5e7eb',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наша команда' },
    rootName: { type: 'text', label: 'Имя главы', default: '' },
    rootRole: { type: 'text', label: 'Должность главы', default: '' },
    rootAvatar: { type: 'image', label: 'Фото главы', default: '' },
    children: { type: 'array', label: 'Подчинённые (name, role, avatar, children[])', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцент', default: '#5b6af0' },
    lineColor: { type: 'color', label: 'Цвет линий', default: '#e5e7eb' },
  },
  tags: ['org', 'chart', 'organization', 'hierarchy', 'оргсхема', 'структура', 'иерархия'],
})

registerBlock({
  type: 'link-in-bio',
  label: 'Ссылки (Link in Bio)',
  category: 'content',
  icon: '🔗',
  description: 'Страница ссылок в стиле Linktree — для Instagram, TikTok и других соцсетей',
  defaultData: {
    avatar: '',
    name: '@username',
    bio: 'Блогер · Дизайнер · Автор курсов 🚀',
    links: [
      { title: '🎓 Мой курс по дизайну', url: '#', style: 'primary' },
      { title: '📺 YouTube-канал', url: '#', style: 'outline' },
      { title: '💬 Telegram чат', url: '#', style: 'outline' },
      { title: '🛍 Магазин', url: '#', style: 'outline' },
    ],
    socialLinks: [
      { platform: 'instagram', url: '#' },
      { platform: 'youtube', url: '#' },
      { platform: 'telegram', url: '#' },
    ],
    background: '#f0f9ff',
    accentColor: '#5b6af0',
    textColor: '#111827',
  },
  schema: {
    avatar: { type: 'image', label: 'Аватар', default: '' },
    name: { type: 'text', label: 'Имя / @username', default: '' },
    bio: { type: 'text', label: 'Описание', default: '' },
    links: { type: 'array', label: 'Ссылки (title, url, style: primary/outline)', default: [] },
    socialLinks: { type: 'array', label: 'Соцсети (platform, url)', default: [] },
    background: { type: 'color', label: 'Фон', default: '#f0f9ff' },
    accentColor: { type: 'color', label: 'Акцент', default: '#5b6af0' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#111827' },
  },
  tags: ['linktree', 'link-in-bio', 'instagram', 'creator', 'social', 'ссылки', 'блогер'],
})

registerBlock({
  type: 'video-bg-section',
  label: 'Секция с видеофоном',
  category: 'content',
  icon: '🎬',
  description: 'Контентный блок с автовоспроизводящимся видео на фоне — текст и кнопка поверх видео',
  defaultData: {
    videoUrl: '',
    title: 'Ваш заголовок здесь',
    subtitle: 'Короткое описание вашего предложения',
    buttonText: 'Узнать больше',
    buttonUrl: '#',
    overlayOpacity: 50,
    overlayColor: '#000000',
    textColor: '#ffffff',
    minHeight: 500,
    alignment: 'center',
  },
  schema: {
    videoUrl: { type: 'url', label: 'URL видео (mp4)', default: '' },
    title: { type: 'text', label: 'Заголовок', default: '' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    buttonText: { type: 'text', label: 'Кнопка', default: '' },
    buttonUrl: { type: 'url', label: 'Ссылка', default: '#' },
    overlayOpacity: { type: 'number', label: 'Затемнение (%)', default: 50, min: 0, max: 100 },
    overlayColor: { type: 'color', label: 'Цвет оверлея', default: '#000000' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
    minHeight: { type: 'number', label: 'Мин. высота (px)', default: 500, min: 200 },
    alignment: { type: 'select', label: 'Выравнивание', default: 'center', options: [{value:'left',label:'Лево'},{value:'center',label:'Центр'},{value:'right',label:'Право'}] },
  },
  tags: ['video', 'background', 'section', 'видео', 'фон', 'секция'],
})

registerBlock({
  type: 'sticky-announcement',
  label: 'Прилипающее объявление',
  category: 'navigation',
  icon: '📢',
  description: 'Фиксированная полоска сверху или снизу с объявлением, акцией или таймером',
  defaultData: {
    text: '🔥 Скидка 30% на все тарифы до конца месяца',
    linkText: 'Активировать',
    linkUrl: '#',
    position: 'top',
    dismissible: true,
    showCountdown: false,
    countdownDate: '',
    background: '#5b6af0',
    textColor: '#ffffff',
    linkColor: '#fbbf24',
  },
  schema: {
    text: { type: 'text', label: 'Текст объявления', default: '' },
    linkText: { type: 'text', label: 'Текст ссылки', default: '' },
    linkUrl: { type: 'url', label: 'URL ссылки', default: '#' },
    position: { type: 'select', label: 'Позиция', default: 'top', options: [{value:'top',label:'Сверху'},{value:'bottom',label:'Снизу'}] },
    dismissible: { type: 'boolean', label: 'Можно закрыть', default: true },
    showCountdown: { type: 'boolean', label: 'Показывать таймер', default: false },
    countdownDate: { type: 'text', label: 'Дата окончания (ISO)', default: '' },
    background: { type: 'color', label: 'Фон', default: '#5b6af0' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
    linkColor: { type: 'color', label: 'Цвет ссылки', default: '#fbbf24' },
  },
  tags: ['sticky', 'announcement', 'bar', 'promotion', 'top-bar', 'прилипающий', 'объявление'],
})

registerBlock({
  type: 'image-hotspots-v2',
  label: 'Интерактивное изображение v2',
  category: 'interactive',
  icon: '🗺',
  description: 'Изображение с кликабельными точками-пинами и всплывающими подсказками',
  defaultData: {
    image: '',
    alt: 'Интерактивная схема',
    hotspots: [
      { x: 30, y: 40, label: 'Точка 1', description: 'Описание первой точки', icon: '📍' },
      { x: 60, y: 25, label: 'Точка 2', description: 'Описание второй точки', icon: '📍' },
      { x: 70, y: 65, label: 'Точка 3', description: 'Описание третьей точки', icon: '📍' },
    ],
    pinColor: '#5b6af0',
    tooltipBg: '#1f2937',
    tooltipText: '#ffffff',
    background: '#f8fafc',
  },
  schema: {
    image: { type: 'image', label: 'Изображение', default: '' },
    alt: { type: 'text', label: 'Alt текст', default: '' },
    hotspots: { type: 'array', label: 'Точки (x%, y%, label, description, icon)', default: [] },
    pinColor: { type: 'color', label: 'Цвет пина', default: '#5b6af0' },
    tooltipBg: { type: 'color', label: 'Фон подсказки', default: '#1f2937' },
    tooltipText: { type: 'color', label: 'Текст подсказки', default: '#ffffff' },
    background: { type: 'color', label: 'Фон секции', default: '#f8fafc' },
  },
  tags: ['hotspots', 'interactive', 'image', 'map', 'pins', 'хотспоты', 'карта'],
})

registerBlock({
  type: 'flash-sale',
  label: 'Флэш-распродажа',
  category: 'commerce',
  icon: '⚡',
  description: 'Блок распродажи с таймером, товарами и скидкой — для акций и распродаж',
  defaultData: {
    title: '⚡ Флэш-распродажа',
    subtitle: 'Предложение ограничено!',
    endDate: '',
    products: [
      { name: 'Продукт 1', image: '', oldPrice: 2990, newPrice: 1490, badge: '-50%' },
      { name: 'Продукт 2', image: '', oldPrice: 1990, newPrice: 990, badge: '-50%' },
    ],
    currency: '₽',
    buttonText: 'Купить со скидкой',
    buttonUrl: '#',
    background: '#0f0f0f',
    accentColor: '#ef4444',
    textColor: '#ffffff',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: '⚡ Флэш-распродажа' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: 'Предложение ограничено!' },
    endDate: { type: 'text', label: 'Дата окончания (ISO)', default: '' },
    products: { type: 'array', label: 'Товары (name, image, oldPrice, newPrice, badge)', default: [] },
    currency: { type: 'text', label: 'Валюта', default: '₽' },
    buttonText: { type: 'text', label: 'Текст кнопки', default: 'Купить со скидкой' },
    buttonUrl: { type: 'url', label: 'Ссылка кнопки', default: '#' },
    background: { type: 'color', label: 'Фон', default: '#0f0f0f' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#ef4444' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
  },
  tags: ['sale', 'flash', 'discount', 'countdown', 'ecommerce', 'флэш', 'распродажа', 'скидка'],
})

registerBlock({
  type: 'webinar-registration',
  label: 'Регистрация на вебинар',
  category: 'content',
  icon: '🎥',
  description: 'Лендинг-блок для вебинара — дата, спикеры, форма регистрации',
  defaultData: {
    title: 'Бесплатный вебинар',
    date: '25 июля 2026',
    time: '18:00 МСК',
    duration: '90 минут',
    speakers: [
      { name: 'Иван Иванов', role: 'CEO & Co-founder', avatar: '', bio: 'Эксперт с 10-летним опытом' },
    ],
    topics: ['Как запустить стартап за 30 дней', 'Топ-5 ошибок основателей', 'Q&A сессия'],
    formTitle: 'Зарегистрируйтесь бесплатно',
    buttonText: 'Получить доступ',
    background: '#0a0a0a',
    accentColor: '#5b6af0',
    textColor: '#ffffff',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Бесплатный вебинар' },
    date: { type: 'text', label: 'Дата', default: '' },
    time: { type: 'text', label: 'Время', default: '' },
    duration: { type: 'text', label: 'Продолжительность', default: '' },
    speakers: { type: 'array', label: 'Спикеры (name, role, avatar, bio)', default: [] },
    topics: { type: 'array', label: 'Темы', default: [], stringArray: true },
    formTitle: { type: 'text', label: 'Заголовок формы', default: 'Зарегистрируйтесь бесплатно' },
    buttonText: { type: 'text', label: 'Текст кнопки', default: 'Получить доступ' },
    background: { type: 'color', label: 'Фон', default: '#0a0a0a' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
  },
  tags: ['webinar', 'registration', 'event', 'online', 'вебинар', 'регистрация', 'мероприятие'],
})

registerBlock({
  type: 'certificate-block',
  label: 'Сертификат / Диплом',
  category: 'content',
  icon: '🏅',
  description: 'Красивый блок с сертификатом или дипломом — для курсов и достижений',
  defaultData: {
    title: 'Сертификат об окончании',
    subtitle: 'Настоящий документ подтверждает, что',
    recipientName: 'Иван Иванов',
    courseName: 'Курс по веб-разработке',
    completionDate: 'Июль 2026',
    description: 'успешно завершил курс и продемонстрировал отличные знания',
    badgeText: 'Удостоверение № BST-2026-001',
    signatureName: 'Директор курса',
    logo: '',
    background: '#fffbeb',
    accentColor: '#d97706',
    borderColor: '#f59e0b',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок сертификата', default: 'Сертификат об окончании' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: 'Настоящий документ подтверждает, что' },
    recipientName: { type: 'text', label: 'Имя получателя', default: 'Иван Иванов' },
    courseName: { type: 'text', label: 'Название курса', default: '' },
    completionDate: { type: 'text', label: 'Дата завершения', default: '' },
    description: { type: 'text', label: 'Описание достижения', default: '' },
    badgeText: { type: 'text', label: 'Текст номера/значка', default: '' },
    signatureName: { type: 'text', label: 'Имя для подписи', default: '' },
    logo: { type: 'image', label: 'Логотип', default: '' },
    background: { type: 'color', label: 'Фон', default: '#fffbeb' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#d97706' },
    borderColor: { type: 'color', label: 'Цвет рамки', default: '#f59e0b' },
  },
  tags: ['certificate', 'diploma', 'achievement', 'course', 'award', 'сертификат', 'диплом', 'курс'],
})

registerBlock({
  type: 'course-outline',
  label: 'Программа курса',
  category: 'content',
  icon: '📚',
  description: 'Блок с программой онлайн-курса — модули, уроки, продолжительность',
  defaultData: {
    title: 'Программа курса',
    subtitle: '8 модулей · 36 уроков · 12 часов',
    modules: [
      { title: 'Введение', duration: '45 мин', lessons: ['Знакомство с курсом', 'Установка инструментов', 'Первый проект'], locked: false },
      { title: 'Основы HTML', duration: '2 часа', lessons: ['Структура HTML', 'Теги и атрибуты', 'Формы'], locked: false },
      { title: 'Продвинутый CSS', duration: '3 часа', lessons: ['Flexbox', 'Grid', 'Анимации', 'Переменные'], locked: true },
    ],
    buttonText: 'Записаться на курс',
    buttonUrl: '#',
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Программа курса' },
    subtitle: { type: 'text', label: 'Подзаголовок (модули/уроки/часы)', default: '' },
    modules: { type: 'array', label: 'Модули (title, duration, lessons[], locked)', default: [] },
    buttonText: { type: 'text', label: 'Текст кнопки', default: 'Записаться на курс' },
    buttonUrl: { type: 'url', label: 'Ссылка кнопки', default: '#' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['course', 'curriculum', 'lessons', 'education', 'program', 'курс', 'программа', 'уроки'],
})

registerBlock({
  type: 'mega-footer',
  label: 'Мега-футер',
  category: 'navigation',
  icon: '🦶',
  description: 'Многоколоночный подвал сайта с навигацией, контактами и соцсетями',
  defaultData: {
    logo: '',
    siteName: 'Название сайта',
    tagline: 'Краткое описание вашей компании',
    columns: [
      { title: 'Продукт', links: [{ text: 'Возможности', url: '#' }, { text: 'Цены', url: '#' }, { text: 'Шаблоны', url: '#' }] },
      { title: 'Компания', links: [{ text: 'О нас', url: '#' }, { text: 'Блог', url: '#' }, { text: 'Карьера', url: '#' }] },
      { title: 'Поддержка', links: [{ text: 'Документация', url: '#' }, { text: 'Связаться', url: '#' }] },
    ],
    socialLinks: [{ platform: 'telegram', url: '#' }, { platform: 'vk', url: '#' }],
    copyrightText: `© ${new Date().getFullYear()} Название компании. Все права защищены.`,
    showNewsletter: true,
    newsletterPlaceholder: 'Ваш email',
    newsletterButtonText: 'Подписаться',
    background: '#0f172a',
    textColor: '#94a3b8',
    accentColor: '#5b6af0',
  },
  schema: {
    logo: { type: 'image', label: 'Логотип', default: '' },
    siteName: { type: 'text', label: 'Название сайта', default: '' },
    tagline: { type: 'text', label: 'Слоган', default: '' },
    columns: { type: 'array', label: 'Колонки (title, links[])', default: [] },
    socialLinks: { type: 'array', label: 'Соцсети (platform, url)', default: [] },
    copyrightText: { type: 'text', label: 'Копирайт', default: '' },
    showNewsletter: { type: 'boolean', label: 'Показывать подписку', default: true },
    newsletterPlaceholder: { type: 'text', label: 'Плейсхолдер email', default: 'Ваш email' },
    newsletterButtonText: { type: 'text', label: 'Текст кнопки подписки', default: 'Подписаться' },
    background: { type: 'color', label: 'Фон', default: '#0f172a' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#94a3b8' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['footer', 'mega', 'navigation', 'links', 'copyright', 'футер', 'подвал', 'навигация'],
})

registerBlock({
  type: 'floating-contact-bar',
  label: 'Плавающая панель контактов',
  category: 'navigation',
  icon: '📞',
  description: 'Фиксированная боковая панель с контактами — WhatsApp, Telegram, телефон',
  defaultData: {
    position: 'right',
    contacts: [
      { type: 'whatsapp', number: '79001234567', tooltip: 'WhatsApp' },
      { type: 'telegram', username: 'username', tooltip: 'Telegram' },
      { type: 'phone', number: '+7 900 123-45-67', tooltip: 'Позвонить' },
      { type: 'email', address: 'info@example.com', tooltip: 'Написать' },
    ],
    background: '#25D366',
    accentColor: '#ffffff',
  },
  schema: {
    position: { type: 'select', label: 'Позиция', default: 'right', options: [{ value: 'left', label: 'Слева' }, { value: 'right', label: 'Справа' }] },
    contacts: { type: 'array', label: 'Контакты (type, number/username/address, tooltip)', default: [] },
    background: { type: 'color', label: 'Цвет кнопок', default: '#25D366' },
    accentColor: { type: 'color', label: 'Цвет иконок', default: '#ffffff' },
  },
  tags: ['floating', 'contact', 'whatsapp', 'telegram', 'phone', 'fixed', 'плавающий', 'контакты', 'мессенджер'],
})

registerBlock({
  type: 'review-summary',
  label: 'Сводка отзывов',
  category: 'content',
  icon: '⭐',
  description: 'Сводная таблица рейтингов в стиле Google/Amazon — средний балл + распределение',
  defaultData: {
    title: 'Отзывы покупателей',
    totalReviews: 1284,
    averageRating: 4.7,
    distribution: [
      { stars: 5, count: 980 },
      { stars: 4, count: 200 },
      { stars: 3, count: 60 },
      { stars: 2, count: 24 },
      { stars: 1, count: 20 },
    ],
    platforms: [
      { name: 'Google', logo: '', rating: 4.8, url: '#' },
      { name: 'Яндекс', logo: '', rating: 4.6, url: '#' },
    ],
    background: '#ffffff',
    accentColor: '#f59e0b',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Отзывы покупателей' },
    totalReviews: { type: 'number', label: 'Всего отзывов', default: 0, min: 0 },
    averageRating: { type: 'number', label: 'Средний рейтинг', default: 5, min: 0, max: 5 },
    distribution: { type: 'array', label: 'Распределение (stars, count)', default: [] },
    platforms: { type: 'array', label: 'Платформы (name, logo, rating, url)', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#f59e0b' },
  },
  tags: ['reviews', 'ratings', 'stars', 'testimonials', 'google', 'отзывы', 'рейтинг', 'звёзды'],
})

registerBlock({
  type: 'content-upgrade',
  label: 'Контент-апгрейд',
  category: 'interactive',
  icon: '🔓',
  description: 'Размытый контент с формой разблокировки — email-gate для лид-генерации',
  defaultData: {
    title: 'Получите полный гайд бесплатно',
    description: 'Введите email и получите PDF с полным руководством',
    preview: 'В этом гайде вы узнаете всё о построении успешного онлайн-бизнеса с нуля. Первые три главы...\n\nГлава 1: Выбор ниши...\n\nГлава 2: Создание MVP...',
    blurLines: 5,
    buttonText: '🔓 Разблокировать контент',
    placeholder: 'Ваш email',
    successTitle: 'Гайд отправлен!',
    successText: 'Проверьте почту — письмо придёт в течение 1 минуты.',
    background: '#f8fafc',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Получите полный гайд бесплатно' },
    description: { type: 'text', label: 'Описание', default: '' },
    preview: { type: 'text', label: 'Превью контента (будет размыт)', default: '' },
    blurLines: { type: 'number', label: 'Строк в размытой области', default: 5, min: 1, max: 20 },
    buttonText: { type: 'text', label: 'Текст кнопки', default: '🔓 Разблокировать контент' },
    placeholder: { type: 'text', label: 'Плейсхолдер email', default: 'Ваш email' },
    successTitle: { type: 'text', label: 'Заголовок после разблокировки', default: '' },
    successText: { type: 'text', label: 'Текст после разблокировки', default: '' },
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['lead', 'gate', 'email', 'upgrade', 'unlock', 'лид', 'разблокировать', 'email-gate'],
})

registerBlock({
  type: 'tabs-product',
  label: 'Вкладки товара',
  category: 'commerce',
  icon: '📋',
  description: 'Вкладки для страницы товара — Описание, Характеристики, Отзывы, Доставка',
  defaultData: {
    tabs: [
      { title: 'Описание', content: 'Подробное описание вашего товара. Расскажите о преимуществах, материалах и особенностях.' },
      { title: 'Характеристики', content: 'Вес: 1.2 кг\nРазмеры: 30×20×10 см\nМатериал: Нержавеющая сталь\nЦвет: Серебристый' },
      { title: 'Отзывы', content: 'Здесь будут отзывы покупателей.' },
      { title: 'Доставка', content: 'Доставка по России — 2-5 рабочих дней. Бесплатно при заказе от 2000₽.' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    tabs: { type: 'array', label: 'Вкладки (title, content)', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['tabs', 'product', 'description', 'specs', 'reviews', 'вкладки', 'товар', 'характеристики'],
})

registerBlock({
  type: 'social-proof-live',
  label: 'Живое социальное доказательство',
  category: 'interactive',
  icon: '👁',
  description: 'Динамическая строка с живыми данными — «847 человек смотрят прямо сейчас»',
  defaultData: {
    messages: [
      '🔥 {n} человек сейчас просматривают эту страницу',
      '✅ {total} покупок за сегодня',
      '⏰ Осталось всего {stock} штук',
      '👁 {n} человек добавили в корзину за час',
    ],
    minVisitors: 15,
    maxVisitors: 847,
    totalSales: 1284,
    stockLeft: 7,
    refreshInterval: 5,
    position: 'bottom-right',
    background: '#1f2937',
    textColor: '#ffffff',
    accentColor: '#22c55e',
  },
  schema: {
    messages: { type: 'array', label: 'Шаблоны сообщений ({n}, {total}, {stock})', default: [], stringArray: true },
    minVisitors: { type: 'number', label: 'Мин. посетителей', default: 15, min: 1 },
    maxVisitors: { type: 'number', label: 'Макс. посетителей', default: 847, min: 1 },
    totalSales: { type: 'number', label: 'Всего продаж', default: 1284, min: 0 },
    stockLeft: { type: 'number', label: 'Остаток на складе', default: 7, min: 0 },
    refreshInterval: { type: 'number', label: 'Интервал обновления (сек)', default: 5, min: 1 },
    position: { type: 'select', label: 'Позиция', default: 'bottom-right', options: [{ value: 'bottom-right', label: 'Снизу справа' }, { value: 'bottom-left', label: 'Снизу слева' }, { value: 'top-right', label: 'Сверху справа' }, { value: 'top-left', label: 'Сверху слева' }] },
    background: { type: 'color', label: 'Фон', default: '#1f2937' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#22c55e' },
  },
  tags: ['social-proof', 'live', 'visitors', 'urgency', 'scarcity', 'живой', 'трафик', 'срочность'],
})

registerBlock({
  type: 'affiliate-cta',
  label: 'Партнёрский блок',
  category: 'content',
  icon: '🤝',
  description: 'Блок с раскрытием рекламного характера и партнёрской кнопкой',
  defaultData: {
    disclosureText: '⚠️ Этот пост содержит партнёрские ссылки. Мы получаем комиссию при покупке без доп. стоимости для вас.',
    productName: 'Название продукта',
    productDescription: 'Краткое описание почему вы рекомендуете этот продукт',
    productRating: 4.8,
    totalReviews: 2847,
    price: '2 990 ₽',
    oldPrice: '4 990 ₽',
    buttonText: '🛒 Купить со скидкой',
    buttonUrl: '#',
    note: 'Акция заканчивается скоро',
    background: '#fffbeb',
    accentColor: '#d97706',
    buttonColor: '#ef4444',
  },
  schema: {
    disclosureText: { type: 'text', label: 'Текст раскрытия', default: '' },
    productName: { type: 'text', label: 'Название продукта', default: '' },
    productDescription: { type: 'text', label: 'Описание', default: '' },
    productRating: { type: 'number', label: 'Рейтинг', default: 5, min: 0, max: 5 },
    totalReviews: { type: 'number', label: 'Число отзывов', default: 0, min: 0 },
    price: { type: 'text', label: 'Цена', default: '' },
    oldPrice: { type: 'text', label: 'Старая цена', default: '' },
    buttonText: { type: 'text', label: 'Текст кнопки', default: '🛒 Купить со скидкой' },
    buttonUrl: { type: 'url', label: 'Ссылка кнопки', default: '#' },
    note: { type: 'text', label: 'Примечание', default: '' },
    background: { type: 'color', label: 'Фон', default: '#fffbeb' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#d97706' },
    buttonColor: { type: 'color', label: 'Цвет кнопки', default: '#ef4444' },
  },
  tags: ['affiliate', 'partner', 'cta', 'disclosure', 'recommendation', 'партнёр', 'рекомендация', 'реклама'],
})

registerBlock({
  type: 'terms-block',
  label: 'Условия и согласие',
  category: 'content',
  icon: '📜',
  description: 'Блок с условиями обслуживания или политикой — с чекбоксом принятия',
  defaultData: {
    title: 'Условия использования',
    content: '1. Использование сервиса\nНастоящие Условия регулируют использование сервиса BST...\n\n2. Регистрация аккаунта\nДля использования сервиса необходима регистрация...',
    checkboxLabel: 'Я прочитал и соглашаюсь с условиями использования',
    buttonText: 'Принять и продолжить',
    buttonUrl: '#',
    showAccept: true,
    background: '#f9fafb',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Условия использования' },
    content: { type: 'text', label: 'Текст условий', default: '' },
    checkboxLabel: { type: 'text', label: 'Текст чекбокса', default: '' },
    buttonText: { type: 'text', label: 'Текст кнопки', default: 'Принять и продолжить' },
    buttonUrl: { type: 'url', label: 'Ссылка кнопки', default: '#' },
    showAccept: { type: 'boolean', label: 'Показывать кнопку принятия', default: true },
    background: { type: 'color', label: 'Фон', default: '#f9fafb' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['terms', 'policy', 'legal', 'accept', 'conditions', 'условия', 'политика', 'согласие'],
})

registerBlock({
  type: 'roadmap-v2',
  label: 'Роадмап продукта v2',
  category: 'content',
  icon: '🗺',
  description: 'Интерактивный роадмап с голосованием — «Планируется», «В разработке», «Готово»',
  defaultData: {
    title: 'Что будем строить',
    subtitle: 'Помогите нам расставить приоритеты',
    items: [
      { status: 'done', title: 'Drag & Drop редактор', description: 'Визуальный редактор с перетаскиванием блоков', votes: 284 },
      { status: 'in-progress', title: 'Мобильное приложение', description: 'Редактирование сайтов с iPhone и Android', votes: 192 },
      { status: 'planned', title: 'Marketplace блоков', description: 'Покупайте и продавайте кастомные блоки', votes: 156 },
      { status: 'planned', title: 'AI-генерация изображений', description: 'Создание изображений для блоков с помощью AI', votes: 143 },
    ],
    background: '#f8fafc',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Что будем строить' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    items: { type: 'array', label: 'Пункты роадмапа (status, title, description, votes)', default: [] },
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['roadmap', 'product', 'voting', 'planned', 'features', 'роадмап', 'план', 'голосование'],
})

registerBlock({
  type: 'booking-widget-v2',
  label: 'Форма записи v2',
  category: 'interactive',
  icon: '📅',
  description: 'Улучшенная форма записи с выбором услуги, даты и времени',
  defaultData: {
    title: 'Запишитесь онлайн',
    services: [
      { name: 'Консультация', duration: '30 мин', price: 'Бесплатно' },
      { name: 'Полный аудит', duration: '2 часа', price: '5 000 ₽' },
    ],
    timeSlots: ['09:00', '10:00', '11:00', '12:00', '14:00', '15:00', '16:00', '17:00'],
    note: 'Мы свяжемся для подтверждения',
    buttonText: 'Подтвердить запись',
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Запишитесь онлайн' },
    services: { type: 'array', label: 'Услуги (name, duration, price)', default: [] },
    timeSlots: { type: 'array', label: 'Временные слоты', default: [], stringArray: true },
    note: { type: 'text', label: 'Примечание', default: '' },
    buttonText: { type: 'text', label: 'Текст кнопки', default: 'Подтвердить запись' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['booking', 'appointment', 'calendar', 'schedule', 'запись', 'бронирование', 'расписание'],
})

registerBlock({
  type: 'loyalty-program',
  label: 'Программа лояльности',
  category: 'commerce',
  icon: '💎',
  description: 'Блок программы лояльности — уровни, баллы, преимущества',
  defaultData: {
    title: 'Программа лояльности',
    subtitle: 'Зарабатывайте баллы с каждой покупкой',
    levels: [
      { name: 'Стартер', icon: '🥉', pointsRequired: 0, color: '#cd7f32', perks: ['1% кэшбэк', 'Доступ к распродажам'] },
      { name: 'Серебро', icon: '🥈', pointsRequired: 1000, color: '#9ca3af', perks: ['3% кэшбэк', 'Бесплатная доставка', 'Ранний доступ'] },
      { name: 'Золото', icon: '🥇', pointsRequired: 5000, color: '#f59e0b', perks: ['5% кэшбэк', 'Приоритетная поддержка', 'Эксклюзивные предложения', 'Персональный менеджер'] },
    ],
    buttonText: 'Присоединиться',
    buttonUrl: '#',
    background: '#0f0f1a',
    accentColor: '#f59e0b',
    textColor: '#ffffff',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Программа лояльности' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    levels: { type: 'array', label: 'Уровни (name, icon, pointsRequired, color, perks[])', default: [] },
    buttonText: { type: 'text', label: 'Текст кнопки', default: 'Присоединиться' },
    buttonUrl: { type: 'url', label: 'Ссылка кнопки', default: '#' },
    background: { type: 'color', label: 'Фон', default: '#0f0f1a' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#f59e0b' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
  },
  tags: ['loyalty', 'rewards', 'points', 'membership', 'лояльность', 'баллы', 'уровни'],
})

registerBlock({
  type: 'exit-popup',
  label: 'Попап при уходе',
  category: 'interactive',
  icon: '🚪',
  description: 'Всплывающий оффер при попытке уйти со страницы (exit intent)',
  defaultData: {
    title: 'Подождите! Не уходите 👋',
    subtitle: 'Мы приготовили специальное предложение',
    offerText: 'Скидка 20% на первый заказ',
    couponCode: 'WELCOME20',
    inputPlaceholder: 'Введите ваш email',
    buttonText: 'Получить скидку',
    declineText: 'Нет, я хочу переплатить',
    triggerDelay: 3000,
    triggerScroll: 50,
    background: '#fff',
    accentColor: '#5b6af0',
    overlayColor: 'rgba(0,0,0,0.6)',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Подождите! Не уходите 👋' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    offerText: { type: 'text', label: 'Текст оффера', default: '' },
    couponCode: { type: 'text', label: 'Промокод', default: '' },
    inputPlaceholder: { type: 'text', label: 'Плейсхолдер email', default: 'Введите ваш email' },
    buttonText: { type: 'text', label: 'Текст кнопки', default: 'Получить скидку' },
    declineText: { type: 'text', label: 'Текст отказа', default: '' },
    triggerDelay: { type: 'number', label: 'Задержка показа (мс)', default: 3000, min: 0 },
    triggerScroll: { type: 'number', label: 'Триггер прокрутки (%)', default: 50, min: 0, max: 100 },
    background: { type: 'color', label: 'Фон попапа', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
    overlayColor: { type: 'text', label: 'Цвет оверлея (rgba)', default: 'rgba(0,0,0,0.6)' },
  },
  tags: ['exit', 'popup', 'intent', 'offer', 'discount', 'попап', 'уход', 'оффер'],
})

registerBlock({
  type: 'referral-widget',
  label: 'Реферальная программа',
  category: 'interactive',
  icon: '🔗',
  description: 'Виджет для реферальной программы — уникальная ссылка и статистика',
  defaultData: {
    title: 'Приглашайте друзей — получайте награды',
    description: 'Поделитесь своей ссылкой и получайте 30% с каждого платежа приглашённого',
    referralUrl: 'https://mybst.ru/r/YOUR_CODE',
    reward: '30% комиссии навсегда',
    steps: ['Скопируйте вашу ссылку', 'Поделитесь с друзьями', 'Получайте 30% с каждого платежа'],
    stats: [
      { label: 'Приглашено', value: '12' },
      { label: 'Активных', value: '7' },
      { label: 'Заработано', value: '4 200 ₽' },
    ],
    buttonText: 'Скопировать ссылку',
    background: '#f0fdf4',
    accentColor: '#22c55e',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Приглашайте друзей — получайте награды' },
    description: { type: 'text', label: 'Описание', default: '' },
    referralUrl: { type: 'url', label: 'Реферальная ссылка', default: '' },
    reward: { type: 'text', label: 'Описание награды', default: '' },
    steps: { type: 'array', label: 'Шаги', default: [], stringArray: true },
    stats: { type: 'array', label: 'Статистика (label, value)', default: [] },
    buttonText: { type: 'text', label: 'Текст кнопки', default: 'Скопировать ссылку' },
    background: { type: 'color', label: 'Фон', default: '#f0fdf4' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#22c55e' },
  },
  tags: ['referral', 'affiliate', 'invite', 'rewards', 'реферал', 'пригласить', 'комиссия'],
})

registerBlock({
  type: 'age-gate',
  label: 'Возрастное ограничение',
  category: 'interactive',
  icon: '🔞',
  description: 'Оверлей с проверкой возраста — для алкоголя, табака и взрослого контента',
  defaultData: {
    logo: '',
    title: 'Вам есть 18 лет?',
    subtitle: 'Этот сайт содержит информацию об алкогольных напитках',
    confirmText: 'Мне есть 18 лет',
    declineText: 'Мне нет 18 лет',
    declineRedirectUrl: 'https://youtube.com',
    legalText: 'Чрезмерное употребление алкоголя вредит вашему здоровью',
    background: '#1a1a2e',
    accentColor: '#5b6af0',
    textColor: '#ffffff',
  },
  schema: {
    logo: { type: 'image', label: 'Логотип', default: '' },
    title: { type: 'text', label: 'Заголовок', default: 'Вам есть 18 лет?' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    confirmText: { type: 'text', label: 'Текст кнопки «Да»', default: 'Мне есть 18 лет' },
    declineText: { type: 'text', label: 'Текст кнопки «Нет»', default: 'Мне нет 18 лет' },
    declineRedirectUrl: { type: 'url', label: 'URL при отказе', default: 'https://youtube.com' },
    legalText: { type: 'text', label: 'Юридический текст', default: '' },
    background: { type: 'color', label: 'Фон', default: '#1a1a2e' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
  },
  tags: ['age', 'gate', 'verification', '18+', 'adult', 'возраст', 'проверка', 'ограничение'],
})

registerBlock({
  type: 'cookie-preferences',
  label: 'Настройки cookie',
  category: 'navigation',
  icon: '🍪',
  description: 'GDPR-совместимая панель управления cookie с категориями',
  defaultData: {
    title: 'Настройки конфиденциальности',
    description: 'Мы используем файлы cookie для улучшения работы сайта',
    categories: [
      { name: 'Необходимые', description: 'Обязательные для работы сайта. Нельзя отключить.', enabled: true, required: true },
      { name: 'Аналитика', description: 'Помогают понять, как вы используете сайт (Google Analytics и т.д.)', enabled: true, required: false },
      { name: 'Маркетинг', description: 'Используются для показа персонализированной рекламы', enabled: false, required: false },
    ],
    saveText: 'Сохранить настройки',
    acceptAllText: 'Принять все',
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Настройки конфиденциальности' },
    description: { type: 'text', label: 'Описание', default: '' },
    categories: { type: 'array', label: 'Категории (name, description, enabled, required)', default: [] },
    saveText: { type: 'text', label: 'Текст кнопки сохранения', default: 'Сохранить настройки' },
    acceptAllText: { type: 'text', label: 'Текст кнопки «Принять все»', default: 'Принять все' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['cookie', 'gdpr', 'privacy', 'preferences', 'конфиденциальность', 'GDPR'],
})

registerBlock({
  type: 'data-table',
  label: 'Таблица данных',
  category: 'content',
  icon: '📊',
  description: 'Адаптивная таблица с данными для сравнения тарифов, характеристик или показателей',
  defaultData: {
    title: 'Сравнение тарифов',
    headers: ['Параметр', 'Бесплатно', 'Pro', 'Enterprise'],
    rows: [
      ['Сайтов', '1', '10', 'Без лимита'],
      ['Хранилище', '500 МБ', '10 ГБ', '100 ГБ'],
      ['Посетителей/мес', '10 000', '100 000', 'Без лимита'],
      ['Поддержка', 'Email', 'Приоритетная', '24/7 Telegram'],
      ['Цена', 'Бесплатно', '790 ₽/мес', '2490 ₽/мес'],
    ],
    highlightColumn: 1,
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Сравнение тарифов' },
    headers: { type: 'array', label: 'Заголовки столбцов', default: [], stringArray: true },
    rows: { type: 'array', label: 'Строки таблицы (массив массивов строк)', default: [] },
    highlightColumn: { type: 'number', label: 'Выделенный столбец (0-based)', default: 1, min: 0 },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['table', 'data', 'comparison', 'specs', 'таблица', 'данные', 'сравнение'],
})

registerBlock({
  type: 'split-hero-v2',
  label: 'Сплит-герой v2',
  category: 'hero',
  icon: '🖼️',
  description: 'Двухколоночный герой: изображение слева, текст и CTA справа с анимацией появления',
  defaultData: {
    eyebrow: 'Новинка 2025',
    title: 'Создавайте сайты быстрее, чем когда-либо',
    subtitle: 'Профессиональный результат без кода — для бизнеса любого масштаба',
    primaryCta: 'Начать бесплатно',
    primaryCtaUrl: '#',
    secondaryCta: 'Смотреть демо',
    secondaryCtaUrl: '#',
    image: 'https://images.unsplash.com/photo-1551650975-87deedd944c3?w=800&q=80',
    imageAlt: 'Платформа BST',
    badge: '⭐ 4.9 / 5',
    features: ['Без кода', 'Хостинг включён', 'Поддержка 24/7'],
    background: '#ffffff',
    accentColor: '#5b6af0',
    imagePosition: 'left',
  },
  schema: {
    eyebrow: { type: 'text', label: 'Надбровный текст', default: 'Новинка 2025' },
    title: { type: 'text', label: 'Заголовок', default: 'Создавайте сайты быстрее' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    primaryCta: { type: 'text', label: 'Текст кнопки', default: 'Начать' },
    primaryCtaUrl: { type: 'text', label: 'URL кнопки', default: '#' },
    secondaryCta: { type: 'text', label: 'Текст вторичной кнопки', default: '' },
    secondaryCtaUrl: { type: 'text', label: 'URL вторичной кнопки', default: '#' },
    image: { type: 'image', label: 'Изображение', default: '' },
    imageAlt: { type: 'text', label: 'Alt изображения', default: '' },
    badge: { type: 'text', label: 'Бейдж (оценка/статус)', default: '' },
    features: { type: 'array', label: 'Список преимуществ', default: [], stringArray: true },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
    imagePosition: { type: 'select', label: 'Позиция изображения', default: 'left', options: [{ value: 'left', label: 'Слева' }, { value: 'right', label: 'Справа' }] },
  },
  tags: ['hero', 'split', 'landing', 'герой', 'двухколоночный'],
})

registerBlock({
  type: 'typewriter-hero',
  label: 'Герой с эффектом печатной машинки',
  category: 'hero',
  icon: '⌨️',
  description: 'Динамический герой с анимированным текстом, меняющимся по эффекту печатной машинки',
  defaultData: {
    prefix: 'Мы делаем',
    words: ['сайты быстрее', 'дизайн проще', 'бизнес успешнее', 'клиентов счастливее'],
    subtitle: 'Профессиональный конструктор сайтов без программирования',
    ctaText: 'Попробовать бесплатно',
    ctaUrl: '#',
    background: '#0f172a',
    textColor: '#ffffff',
    accentColor: '#5b6af0',
    cursorColor: '#5b6af0',
  },
  schema: {
    prefix: { type: 'text', label: 'Постоянный текст перед словами', default: 'Мы делаем' },
    words: { type: 'array', label: 'Сменяющиеся слова', default: [], stringArray: true },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    ctaText: { type: 'text', label: 'Текст CTA', default: 'Начать' },
    ctaUrl: { type: 'text', label: 'URL CTA', default: '#' },
    background: { type: 'color', label: 'Фон', default: '#0f172a' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Цвет анимированных слов', default: '#5b6af0' },
    cursorColor: { type: 'color', label: 'Цвет курсора', default: '#5b6af0' },
  },
  tags: ['hero', 'typewriter', 'animation', 'dynamic', 'печатная машинка', 'анимация'],
})

registerBlock({
  type: 'magazine-grid',
  label: 'Редакционная сетка',
  category: 'content',
  icon: '📰',
  description: 'Редакционная / журнальная сетка публикаций с акцентной первой статьёй',
  defaultData: {
    title: 'Последние статьи',
    articles: [
      { title: 'Как создать лендинг за 1 день', category: 'Гайд', date: '15 янв 2025', image: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800&q=80', excerpt: 'Пошаговая инструкция по созданию продающего лендинга с нуля без программирования.', url: '#', featured: true },
      { title: '10 секретов высокой конверсии', category: 'Маркетинг', date: '10 янв 2025', image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&q=80', excerpt: 'Разбираем лучшие практики оформления CTA и структуры страниц.', url: '#', featured: false },
      { title: 'SEO для малого бизнеса', category: 'SEO', date: '5 янв 2025', image: 'https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?w=400&q=80', excerpt: 'Базовые шаги для выхода сайта в ТОП поисковой выдачи.', url: '#', featured: false },
      { title: 'Дизайн без дизайнера', category: 'Дизайн', date: '1 янв 2025', image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=400&q=80', excerpt: 'Инструменты и приёмы, которые заменят дизайнера для стартапа.', url: '#', featured: false },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок раздела', default: 'Последние статьи' },
    articles: { type: 'array', label: 'Статьи', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['blog', 'magazine', 'editorial', 'grid', 'articles', 'журнал', 'статьи'],
})

registerBlock({
  type: 'feature-tabs',
  label: 'Табы с фичами',
  category: 'content',
  icon: '🗂️',
  description: 'Интерактивные вкладки с описанием возможностей продукта и изображениями',
  defaultData: {
    title: 'Всё что нужно для роста',
    tabs: [
      { label: 'Редактор', icon: '✏️', title: 'Визуальный редактор без кода', description: 'Перетаскивайте блоки, меняйте цвета и шрифты. Ваш сайт готов за минуты.', image: 'https://images.unsplash.com/photo-1551650975-87deedd944c3?w=600&q=80', bullets: ['Drag & Drop интерфейс', '200+ готовых блоков', 'Мобильный предпросмотр'] },
      { label: 'Аналитика', icon: '📊', title: 'Аналитика в реальном времени', description: 'Следите за трафиком, конверсиями и поведением пользователей прямо в редакторе.', image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80', bullets: ['Тепловые карты', 'Воронки конверсий', 'UTM-метки'] },
      { label: 'SEO', icon: '🔍', title: 'Встроенный SEO-аудит', description: 'Оптимизируйте страницы прямо в редакторе — система подскажет что улучшить.', image: 'https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?w=600&q=80', bullets: ['Анализ мета-тегов', 'Советы по контенту', 'Скорость загрузки'] },
    ],
    background: '#f8fafc',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Возможности продукта' },
    tabs: { type: 'array', label: 'Вкладки', default: [] },
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['tabs', 'features', 'product', 'showcase', 'вкладки', 'функции'],
})

registerBlock({
  type: 'pricing-annual-toggle',
  label: 'Тарифы с переключателем периода',
  category: 'commerce',
  icon: '🔄',
  description: 'Тарифные карточки с переключением между месячной и годовой оплатой (скидка до 30%)',
  defaultData: {
    title: 'Простые и прозрачные цены',
    subtitle: 'Выберите план и начните бесплатно. Смените тариф в любое время.',
    discountBadge: 'Сэкономьте 30%',
    plans: [
      { name: 'Старт', priceMonthly: 0, priceAnnual: 0, currency: '₽', period: 'мес', description: 'Для личных проектов', features: ['1 сайт', '500 МБ', '10 000 посетителей', 'Поддержка email'], cta: 'Начать бесплатно', ctaUrl: '#', highlighted: false },
      { name: 'Pro', priceMonthly: 990, priceAnnual: 693, currency: '₽', period: 'мес', description: 'Для малого бизнеса', features: ['10 сайтов', '10 ГБ', '100 000 посетителей', 'Приоритетная поддержка', 'Свой домен', 'Аналитика'], cta: 'Попробовать 14 дней', ctaUrl: '#', highlighted: true },
      { name: 'Бизнес', priceMonthly: 2490, priceAnnual: 1743, currency: '₽', period: 'мес', description: 'Для агентств', features: ['Без лимита сайтов', '100 ГБ', 'Без лимита посетителей', 'Поддержка 24/7', 'API доступ', 'White-label'], cta: 'Связаться с нами', ctaUrl: '#', highlighted: false },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Тарифы' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    discountBadge: { type: 'text', label: 'Текст бейджа скидки', default: 'Сэкономьте 30%' },
    plans: { type: 'array', label: 'Тарифы', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['pricing', 'toggle', 'annual', 'monthly', 'plans', 'тарифы', 'цены', 'переключатель'],
})

registerBlock({
  type: 'roi-calculator',
  label: 'Калькулятор ROI',
  category: 'interactive',
  icon: '💹',
  description: 'Интерактивный калькулятор возврата инвестиций с ползунками и мгновенным результатом',
  defaultData: {
    title: 'Рассчитайте свою выгоду',
    subtitle: 'Узнайте, сколько вы сэкономите с BST по сравнению с агентством',
    inputs: [
      { id: 'sites', label: 'Количество сайтов в год', min: 1, max: 50, default: 5, step: 1 },
      { id: 'cost', label: 'Средняя стоимость сайта у агентства (тыс. ₽)', min: 30, max: 500, default: 100, step: 10 },
    ],
    formula: 'sites * cost * 1000 * 0.85',
    resultPrefix: 'Экономия:',
    resultSuffix: '₽/год',
    ctaText: 'Начать экономить',
    ctaUrl: '#',
    background: '#f0f4ff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Калькулятор выгоды' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    inputs: { type: 'array', label: 'Входные параметры', default: [] },
    formula: { type: 'text', label: 'Формула расчёта (JS-выражение, переменные = id)', default: '' },
    resultPrefix: { type: 'text', label: 'Префикс результата', default: 'Экономия:' },
    resultSuffix: { type: 'text', label: 'Суффикс результата', default: '₽/год' },
    ctaText: { type: 'text', label: 'Текст CTA', default: 'Начать' },
    ctaUrl: { type: 'text', label: 'URL CTA', default: '#' },
    background: { type: 'color', label: 'Фон', default: '#f0f4ff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['calculator', 'roi', 'savings', 'interactive', 'калькулятор', 'ROI', 'выгода'],
})

registerBlock({
  type: 'changelog-v2',
  label: 'Журнал изменений v2',
  category: 'content',
  icon: '📋',
  description: 'Красивый журнал обновлений продукта с версиями, датами и цветными метками типов изменений',
  defaultData: {
    title: 'Что нового',
    subtitle: 'Следите за обновлениями продукта',
    entries: [
      { version: '2.5.0', date: '15 января 2025', type: 'major', title: 'Новый редактор блоков', description: 'Полностью переработан визуальный редактор: drag-and-drop, мультиселект, история изменений.', tags: ['Новое', 'Улучшение'] },
      { version: '2.4.2', date: '3 января 2025', type: 'fix', title: 'Исправления производительности', description: 'Ускорена загрузка редактора на 40%, исправлены ошибки сохранения на мобильных устройствах.', tags: ['Исправление'] },
      { version: '2.4.0', date: '20 декабря 2024', type: 'feature', title: 'Интеграция с CRM', description: 'Добавлена нативная интеграция с amoCRM и Bitrix24. Заявки из форм автоматически попадают в сделки.', tags: ['Новое', 'Интеграция'] },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Что нового' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    entries: { type: 'array', label: 'Записи', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['changelog', 'updates', 'versions', 'history', 'журнал', 'обновления', 'версии'],
})

registerBlock({
  type: 'delivery-tracker',
  label: 'Трекер доставки',
  category: 'commerce',
  icon: '🚚',
  description: 'Визуальный трекер статуса заказа / доставки с прогресс-шкалой и шагами',
  defaultData: {
    title: 'Статус вашего заказа',
    orderNumber: '#BST-2025-4872',
    estimatedDate: '20 января 2025',
    steps: [
      { label: 'Заказ принят', description: '15 янв, 10:32', done: true, active: false },
      { label: 'Обработка', description: '15 янв, 14:00', done: true, active: false },
      { label: 'Передан в доставку', description: '16 янв, 09:15', done: true, active: true },
      { label: 'В пути', description: 'Ожидается 19-20 янв', done: false, active: false },
      { label: 'Доставлен', description: 'Ожидается 20 янв', done: false, active: false },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Статус заказа' },
    orderNumber: { type: 'text', label: 'Номер заказа', default: '' },
    estimatedDate: { type: 'text', label: 'Ожидаемая дата доставки', default: '' },
    steps: { type: 'array', label: 'Шаги трекера', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['delivery', 'tracking', 'order', 'status', 'shipping', 'трекер', 'доставка', 'заказ'],
})

registerBlock({
  type: 'restaurant-menu-v2',
  label: 'Меню ресторана v2',
  category: 'content',
  icon: '🍽️',
  description: 'Красивое меню ресторана / кафе с категориями, описанием блюд и фото',
  defaultData: {
    title: 'Наше меню',
    subtitle: 'Свежие ингредиенты, авторские рецепты',
    categories: [
      { name: 'Завтраки', icon: '🍳', items: [{ name: 'Авокадо тост', description: 'Цельнозерновой хлеб, авокадо, яйцо пашот, чили', price: '490 ₽', image: 'https://images.unsplash.com/photo-1588137378633-dea1336ce1e2?w=200&q=80', badge: 'Хит' }, { name: 'Омлет с трюфелем', description: 'Три яйца, трюфельное масло, пармезан, прованские травы', price: '690 ₽', image: '', badge: '' }] },
      { name: 'Основные блюда', icon: '🥩', items: [{ name: 'Рибай стейк', description: 'Мраморная говядина, розмарин, чеснок, картофель', price: '1890 ₽', image: 'https://images.unsplash.com/photo-1546833999-b9f581a1996d?w=200&q=80', badge: 'Шеф рекомендует' }, { name: 'Паста карбонара', description: 'Спагетти, гуанчале, пармезан, желток', price: '790 ₽', image: '', badge: '' }] },
    ],
    background: '#fffbf5',
    accentColor: '#c2410c',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наше меню' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    categories: { type: 'array', label: 'Категории блюд', default: [] },
    background: { type: 'color', label: 'Фон', default: '#fffbf5' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#c2410c' },
  },
  tags: ['restaurant', 'menu', 'food', 'cafe', 'dining', 'меню', 'ресторан', 'блюда'],
})

registerBlock({
  type: 'event-countdown-v2',
  label: 'Обратный отсчёт события v2',
  category: 'interactive',
  icon: '⏱️',
  description: 'Мощный блок с обратным отсчётом до события: дата, место, спикеры и CTA',
  defaultData: {
    title: 'Конференция BST Summit 2025',
    subtitle: 'Главное событие года для создателей сайтов и digital-предпринимателей',
    targetDate: '2025-09-15T10:00:00',
    location: 'Москва, Центр Digital',
    badge: '500+ участников',
    speakers: [
      { name: 'Иван Петров', role: 'CEO, BST', avatar: 'https://i.pravatar.cc/60?img=11' },
      { name: 'Мария Сидорова', role: 'Head of Design', avatar: 'https://i.pravatar.cc/60?img=22' },
      { name: 'Алексей Козлов', role: 'Growth Hacker', avatar: 'https://i.pravatar.cc/60?img=33' },
    ],
    ctaText: 'Зарегистрироваться',
    ctaUrl: '#',
    background: '#0f172a',
    accentColor: '#5b6af0',
    textColor: '#ffffff',
  },
  schema: {
    title: { type: 'text', label: 'Название события', default: '' },
    subtitle: { type: 'text', label: 'Описание', default: '' },
    targetDate: { type: 'text', label: 'Дата события (ISO)', default: '2025-12-31T00:00:00' },
    location: { type: 'text', label: 'Место проведения', default: '' },
    badge: { type: 'text', label: 'Бейдж', default: '' },
    speakers: { type: 'array', label: 'Спикеры', default: [] },
    ctaText: { type: 'text', label: 'Текст CTA', default: 'Зарегистрироваться' },
    ctaUrl: { type: 'text', label: 'URL CTA', default: '#' },
    background: { type: 'color', label: 'Фон', default: '#0f172a' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
  },
  tags: ['countdown', 'event', 'conference', 'timer', 'таймер', 'событие', 'конференция'],
})

registerBlock({
  type: 'case-study-v2',
  label: 'Кейс v2',
  category: 'content',
  icon: '📈',
  description: 'Развёрнутый кейс с задачей, решением, результатами и отзывом клиента',
  defaultData: {
    eyebrow: 'Кейс',
    title: 'Как мы увеличили продажи интернет-магазина на 240%',
    clientName: 'ООО «Стильный дом»',
    clientIndustry: 'E-commerce / Мебель',
    problem: 'Старый сайт терял 70% посетителей уже на главной странице. Конверсия составляла 0.3%.',
    solution: 'Разработали новый лендинг на BST за 3 дня. A/B тест подтвердил рост конверсии в 5 раз.',
    results: [
      { metric: '240%', label: 'Рост продаж за 3 месяца' },
      { metric: '5x', label: 'Конверсия лендинга' },
      { metric: '3 дня', label: 'Срок запуска' },
      { metric: '−80%', label: 'Бюджет на разработку' },
    ],
    quote: 'BST полностью изменил наш подход к продажам онлайн. Теперь мы сами обновляем сайт каждую неделю.',
    quoteName: 'Дмитрий Волков, директор',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&q=80',
    ctaText: 'Читать полный кейс',
    ctaUrl: '#',
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    eyebrow: { type: 'text', label: 'Надбровный текст', default: 'Кейс' },
    title: { type: 'text', label: 'Заголовок кейса', default: '' },
    clientName: { type: 'text', label: 'Название клиента', default: '' },
    clientIndustry: { type: 'text', label: 'Отрасль клиента', default: '' },
    problem: { type: 'text', label: 'Проблема', default: '' },
    solution: { type: 'text', label: 'Решение', default: '' },
    results: { type: 'array', label: 'Результаты (metric + label)', default: [] },
    quote: { type: 'text', label: 'Цитата клиента', default: '' },
    quoteName: { type: 'text', label: 'Имя автора цитаты', default: '' },
    image: { type: 'image', label: 'Изображение кейса', default: '' },
    ctaText: { type: 'text', label: 'Текст CTA', default: 'Читать кейс' },
    ctaUrl: { type: 'text', label: 'URL CTA', default: '#' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['case-study', 'results', 'proof', 'client', 'success', 'кейс', 'результаты', 'клиент'],
})

registerBlock({
  type: 'sponsor-levels',
  label: 'Уровни спонсорства',
  category: 'content',
  icon: '🏅',
  description: 'Отображение уровней спонсоров/партнёров с логотипами, сгруппированных по статусу',
  defaultData: {
    title: 'Наши партнёры и спонсоры',
    subtitle: 'Доверяют лучшие компании России',
    levels: [
      { name: 'Платиновый', color: '#94a3b8', background: '#f8fafc', sponsors: [{ name: 'ПАО Сбербанк', logo: '', url: '#' }, { name: 'Яндекс', logo: '', url: '#' }] },
      { name: 'Золотой', color: '#d97706', background: '#fffbeb', sponsors: [{ name: 'Mail.ru Group', logo: '', url: '#' }, { name: 'Тинькофф', logo: '', url: '#' }, { name: 'МТС', logo: '', url: '#' }] },
      { name: 'Серебряный', color: '#64748b', background: '#f8fafc', sponsors: [{ name: 'Avito', logo: '', url: '#' }, { name: '2ГИС', logo: '', url: '#' }, { name: 'HeadHunter', logo: '', url: '#' }, { name: 'OZON', logo: '', url: '#' }] },
    ],
    ctaText: 'Стать партнёром',
    ctaUrl: '#',
    background: '#ffffff',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наши партнёры' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    levels: { type: 'array', label: 'Уровни спонсорства', default: [] },
    ctaText: { type: 'text', label: 'Текст CTA', default: 'Стать партнёром' },
    ctaUrl: { type: 'text', label: 'URL CTA', default: '#' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
  },
  tags: ['sponsors', 'partners', 'levels', 'logos', 'спонсоры', 'партнёры', 'уровни'],
})

registerBlock({
  type: 'savings-calculator',
  label: 'Калькулятор экономии',
  category: 'interactive',
  icon: '💰',
  description: 'Наглядный калькулятор: показывает разницу между текущими затратами и экономией с продуктом',
  defaultData: {
    title: 'Посчитайте экономию',
    subtitle: 'Сравните стоимость агентства и BST для вашего бизнеса',
    leftLabel: 'Агентская разработка',
    rightLabel: 'BST за год',
    items: [
      { label: 'Разработка сайта', leftCost: 150000, rightCost: 0 },
      { label: 'Хостинг', leftCost: 12000, rightCost: 0 },
      { label: 'Поддержка и обновления', leftCost: 60000, rightCost: 0 },
      { label: 'Подписка BST Pro', leftCost: 0, rightCost: 8388 },
    ],
    currency: '₽',
    ctaText: 'Сэкономить сейчас',
    ctaUrl: '#',
    background: '#f0fdf4',
    accentColor: '#16a34a',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Посчитайте экономию' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    leftLabel: { type: 'text', label: 'Заголовок левой колонки', default: 'Было' },
    rightLabel: { type: 'text', label: 'Заголовок правой колонки', default: 'Стало' },
    items: { type: 'array', label: 'Статьи расходов', default: [] },
    currency: { type: 'text', label: 'Валюта', default: '₽' },
    ctaText: { type: 'text', label: 'Текст CTA', default: 'Сэкономить' },
    ctaUrl: { type: 'text', label: 'URL CTA', default: '#' },
    background: { type: 'color', label: 'Фон', default: '#f0fdf4' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#16a34a' },
  },
  tags: ['calculator', 'savings', 'cost', 'comparison', 'калькулятор', 'экономия', 'затраты'],
})

registerBlock({
  type: 'gradient-cards',
  label: 'Карточки с градиентом',
  category: 'content',
  icon: '🎨',
  description: 'Сетка карточек с яркими градиентными фонами, иконками и описанием',
  defaultData: {
    title: 'Почему выбирают нас',
    cards: [
      { icon: '🚀', title: 'Быстрый запуск', description: 'Сайт готов за 30 минут. Выберите шаблон, добавьте контент и публикуйте.', gradient: 'linear-gradient(135deg,#667eea,#764ba2)' },
      { icon: '🛡️', title: 'Надёжность 99.9%', description: 'Инфраструктура на CDN обеспечивает мгновенную загрузку по всему миру.', gradient: 'linear-gradient(135deg,#f093fb,#f5576c)' },
      { icon: '📱', title: 'Мобильная адаптация', description: 'Все блоки автоматически адаптируются к любому размеру экрана.', gradient: 'linear-gradient(135deg,#4facfe,#00f2fe)' },
      { icon: '💡', title: 'AI-ассистент', description: 'Встроенный ИИ помогает создавать тексты, подбирать цвета и структуру.', gradient: 'linear-gradient(135deg,#43e97b,#38f9d7)' },
      { icon: '🔗', title: 'Интеграции', description: 'Подключите CRM, аналитику, платёжные системы и мессенджеры в один клик.', gradient: 'linear-gradient(135deg,#fa709a,#fee140)' },
      { icon: '📊', title: 'Аналитика', description: 'Встроенные дашборды, тепловые карты и воронки без сторонних инструментов.', gradient: 'linear-gradient(135deg,#a18cd1,#fbc2eb)' },
    ],
    background: '#ffffff',
    columns: 3,
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Почему выбирают нас' },
    cards: { type: 'array', label: 'Карточки', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    columns: { type: 'number', label: 'Колонок (2-4)', default: 3, min: 2, max: 4 },
  },
  tags: ['cards', 'gradient', 'features', 'colorful', 'карточки', 'градиент', 'функции'],
})

registerBlock({
  type: 'property-card',
  label: 'Карточка недвижимости',
  category: 'content',
  icon: '🏠',
  description: 'Карточки объектов недвижимости с фото, характеристиками и ценой',
  defaultData: {
    title: 'Наши объекты',
    subtitle: 'Актуальные предложения',
    properties: [
      { title: '3-комнатная квартира', address: 'Москва, Арбат, 15', price: '18 500 000 ₽', pricePerSqm: '185 000 ₽/м²', area: '100 м²', rooms: 3, floor: '7/12', image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=400&q=80', badge: 'Новостройка', features: ['Паркинг', 'Консьерж', 'Лифт'] },
      { title: 'Студия в центре', address: 'Москва, Тверская, 8', price: '7 200 000 ₽', pricePerSqm: '200 000 ₽/м²', area: '36 м²', rooms: 1, floor: '3/9', image: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=400&q=80', badge: 'Вторичка', features: ['Мебель', 'Техника'] },
      { title: 'Пентхаус с видом', address: 'Москва, Пресня, 22', price: '45 000 000 ₽', pricePerSqm: '225 000 ₽/м²', area: '200 м²', rooms: 5, floor: '20/20', image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400&q=80', badge: 'Эксклюзив', features: ['Терраса', 'Вид на Москву', 'Паркинг'] },
    ],
    ctaText: 'Узнать подробнее',
    background: '#f8fafc',
    accentColor: '#0ea5e9',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наши объекты' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    properties: { type: 'array', label: 'Объекты недвижимости', default: [] },
    ctaText: { type: 'text', label: 'Текст кнопки карточки', default: 'Подробнее' },
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#0ea5e9' },
  },
  tags: ['property', 'real-estate', 'apartment', 'listing', 'недвижимость', 'квартира', 'объекты'],
})

registerBlock({
  type: 'doctor-profile',
  label: 'Профиль врача / специалиста',
  category: 'content',
  icon: '👨‍⚕️',
  description: 'Профессиональный профиль врача или специалиста с опытом, специализацией и записью',
  defaultData: {
    doctors: [
      { name: 'Иванова Мария Александровна', specialty: 'Кардиолог, высшая категория', photo: 'https://i.pravatar.cc/200?img=47', experience: '18 лет', degree: 'Доктор медицинских наук', price: 'от 3 500 ₽', rating: 4.9, reviewCount: 248, description: 'Специализируется на диагностике и лечении сердечно-сосудистых заболеваний, профилактике инфаркта и реабилитации.', education: ['МГМУ им. Сеченова, 2006', 'Ординатура, НМИЦК, 2008'], certifications: ['Европейский диплом по кардиологии', 'Сертификат ЭхоКГ'], ctaText: 'Записаться на приём', ctaUrl: '#' },
      { name: 'Петров Алексей Дмитриевич', specialty: 'Невролог, первая категория', photo: 'https://i.pravatar.cc/200?img=69', experience: '12 лет', degree: 'Кандидат медицинских наук', price: 'от 2 800 ₽', rating: 4.8, reviewCount: 183, description: 'Специализируется на лечении мигрени, болей в спине, головокружений и нарушений сна.', education: ['РНИМУ им. Пирогова, 2012', 'Ординатура, Институт неврологии, 2014'], certifications: ['Мастер-класс по ботулинотерапии', 'Нейросонография'], ctaText: 'Записаться на приём', ctaUrl: '#' },
    ],
    background: '#ffffff',
    accentColor: '#0ea5e9',
  },
  schema: {
    doctors: { type: 'array', label: 'Врачи / специалисты', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#0ea5e9' },
  },
  tags: ['doctor', 'medical', 'specialist', 'profile', 'clinic', 'врач', 'специалист', 'клиника', 'профиль'],
})

registerBlock({
  type: 'knowledge-base',
  label: 'База знаний',
  category: 'content',
  icon: '📚',
  description: 'Структурированная база знаний / FAQ с категориями и превью статей',
  defaultData: {
    title: 'База знаний',
    subtitle: 'Найдите ответы на частые вопросы',
    searchPlaceholder: 'Поиск по статьям...',
    categories: [
      { name: 'Начало работы', icon: '🚀', articles: [{ title: 'Как создать первый сайт', views: 2840, url: '#' }, { title: 'Подключение домена', views: 1920, url: '#' }, { title: 'Публикация сайта', views: 1540, url: '#' }] },
      { name: 'Редактор', icon: '✏️', articles: [{ title: 'Добавление блоков', views: 3100, url: '#' }, { title: 'Настройка шрифтов и цветов', views: 2300, url: '#' }, { title: 'Мобильная версия', views: 1800, url: '#' }] },
      { name: 'Домены и хостинг', icon: '🌐', articles: [{ title: 'Как подключить свой домен', views: 4200, url: '#' }, { title: 'SSL-сертификат', views: 1600, url: '#' }, { title: 'DNS настройки', views: 1200, url: '#' }] },
      { name: 'Оплата', icon: '💳', articles: [{ title: 'Тарифы и оплата', views: 2900, url: '#' }, { title: 'Возврат средств', views: 850, url: '#' }, { title: 'Счёт для организаций', views: 720, url: '#' }] },
    ],
    background: '#f8fafc',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'База знаний' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    searchPlaceholder: { type: 'text', label: 'Placeholder поиска', default: 'Поиск...' },
    categories: { type: 'array', label: 'Категории', default: [] },
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['knowledge-base', 'faq', 'help', 'articles', 'search', 'база знаний', 'справка', 'статьи'],
})

registerBlock({
  type: 'floating-badge-hero',
  label: 'Герой с плавающими бейджами',
  category: 'hero',
  icon: '🏆',
  description: 'Привлекательный герой с центральным изображением и плавающими бейджами с достижениями',
  defaultData: {
    eyebrow: 'Лучший конструктор сайтов 2025',
    title: 'Создавайте сайты, которые продают',
    subtitle: 'Профессиональный дизайн без программирования. Запустите первый сайт за 30 минут.',
    ctaText: 'Попробовать бесплатно',
    ctaUrl: '#',
    secondaryCta: 'Смотреть примеры',
    secondaryCtaUrl: '#',
    image: 'https://images.unsplash.com/photo-1551650975-87deedd944c3?w=600&q=80',
    badges: [
      { icon: '⭐', text: '4.9 / 5 рейтинг', color: '#fbbf24', bg: '#fffbeb', position: 'top-left' },
      { icon: '🚀', text: '10 000+ сайтов', color: '#5b6af0', bg: '#eef2ff', position: 'top-right' },
      { icon: '🔒', text: 'SSL бесплатно', color: '#16a34a', bg: '#f0fdf4', position: 'bottom-left' },
      { icon: '⚡', text: 'Загрузка < 1 сек', color: '#ea580c', bg: '#fff7ed', position: 'bottom-right' },
    ],
    background: '#f0f4ff',
    accentColor: '#5b6af0',
  },
  schema: {
    eyebrow: { type: 'text', label: 'Надбровный текст', default: '' },
    title: { type: 'text', label: 'Заголовок', default: '' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    ctaText: { type: 'text', label: 'Текст CTA', default: 'Начать' },
    ctaUrl: { type: 'text', label: 'URL CTA', default: '#' },
    secondaryCta: { type: 'text', label: 'Вторичный CTA', default: '' },
    secondaryCtaUrl: { type: 'text', label: 'URL вторичного CTA', default: '#' },
    image: { type: 'image', label: 'Центральное изображение', default: '' },
    badges: { type: 'array', label: 'Плавающие бейджи', default: [] },
    background: { type: 'color', label: 'Фон', default: '#f0f4ff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['hero', 'badges', 'floating', 'awards', 'achievements', 'герой', 'бейджи', 'достижения'],
})

registerBlock({
  type: 'icon-features-v2',
  label: 'Функции с иконками v2',
  category: 'content',
  icon: '✨',
  description: 'Расширенная сетка функций с иконками, номером, заголовком и hover-эффектом подсветки',
  defaultData: {
    title: 'Возможности платформы',
    subtitle: 'Всё необходимое для создания и роста вашего онлайн-присутствия',
    features: [
      { icon: '🎨', number: '01', title: 'Дизайн без кода', description: 'Интуитивный редактор с 200+ готовых блоков для любого типа страницы.' },
      { icon: '⚡', number: '02', title: 'Молниеносная скорость', description: 'Страницы генерируются на CDN и загружаются менее чем за секунду.' },
      { icon: '📱', number: '03', title: 'Mobile-first', description: 'Каждый блок автоматически адаптируется под смартфоны и планшеты.' },
      { icon: '🔍', number: '04', title: 'SEO из коробки', description: 'Мета-теги, структурированные данные, sitemap — всё настраивается в редакторе.' },
      { icon: '🔗', number: '05', title: '50+ интеграций', description: 'CRM, мессенджеры, платёжные системы, email-маркетинг — одним кликом.' },
      { icon: '📊', number: '06', title: 'Встроенная аналитика', description: 'Тепловые карты, воронки, UTM-метки — всё без Google Analytics.' },
    ],
    columns: 3,
    background: '#ffffff',
    accentColor: '#5b6af0',
    cardStyle: 'outlined',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Возможности' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    features: { type: 'array', label: 'Функции', default: [] },
    columns: { type: 'number', label: 'Колонок (2-4)', default: 3, min: 2, max: 4 },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
    cardStyle: { type: 'select', label: 'Стиль карточек', default: 'outlined', options: [{ value: 'outlined', label: 'Обводка' }, { value: 'filled', label: 'Заливка' }, { value: 'minimal', label: 'Минимальный' }] },
  },
  tags: ['features', 'icons', 'grid', 'numbered', 'hover', 'функции', 'иконки', 'сетка'],
})

registerBlock({
  type: 'progress-journey',
  label: 'Путь / прогресс-шкала',
  category: 'content',
  icon: '🛤️',
  description: 'Визуализация пути пользователя, прогресса компании или шагов достижения цели',
  defaultData: {
    title: 'Наш путь',
    subtitle: 'Как BST вырос с 0 до 10 000 клиентов за 2 года',
    milestones: [
      { year: '2023 Q1', title: 'Первый код', description: 'Запустили разработку и собрали команду из 5 человек.', icon: '🌱', metric: '5 человек в команде' },
      { year: '2023 Q3', title: 'Первые клиенты', description: 'Запустили закрытую бету и привлекли первые 100 платящих клиентов.', icon: '🎯', metric: '100 клиентов' },
      { year: '2024 Q1', title: 'Публичный запуск', description: 'Открытый запуск с 200+ блоками и мобильным редактором.', icon: '🚀', metric: '2 000 клиентов' },
      { year: '2024 Q3', title: 'Series A', description: 'Привлекли инвестиции на масштабирование и расширение команды.', icon: '💰', metric: '5 000 клиентов' },
      { year: '2025 Q1', title: 'Международный выход', description: 'Запустили платформу на международный рынок с мультиязычностью.', icon: '🌍', metric: '10 000+ клиентов' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
    lineColor: '#e5e7eb',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наш путь' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    milestones: { type: 'array', label: 'Вехи пути', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
    lineColor: { type: 'color', label: 'Цвет линии', default: '#e5e7eb' },
  },
  tags: ['journey', 'progress', 'milestones', 'timeline', 'growth', 'путь', 'прогресс', 'вехи', 'рост'],
})

registerBlock({
  type: 'app-download-hero',
  label: 'Скачать приложение',
  category: 'hero',
  icon: '📱',
  description: 'Секция загрузки мобильного приложения с кнопками App Store и Google Play и скриншотом',
  defaultData: {
    eyebrow: 'Доступно на iOS и Android',
    title: 'BST Editor — создавайте сайты с телефона',
    subtitle: 'Редактируйте, публикуйте и следите за статистикой прямо с мобильного. Бесплатно.',
    appStoreUrl: '#',
    googlePlayUrl: '#',
    appStoreRating: '4.8',
    googlePlayRating: '4.7',
    screenshot: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=400&q=80',
    features: ['Визуальный редактор блоков', 'Push-уведомления о заявках', 'Аналитика в реальном времени'],
    background: '#f0f4ff',
    accentColor: '#5b6af0',
  },
  schema: {
    eyebrow: { type: 'text', label: 'Надбровный текст', default: 'Доступно на iOS и Android' },
    title: { type: 'text', label: 'Заголовок', default: '' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    appStoreUrl: { type: 'text', label: 'Ссылка App Store', default: '#' },
    googlePlayUrl: { type: 'text', label: 'Ссылка Google Play', default: '#' },
    appStoreRating: { type: 'text', label: 'Рейтинг App Store', default: '4.8' },
    googlePlayRating: { type: 'text', label: 'Рейтинг Google Play', default: '4.7' },
    screenshot: { type: 'image', label: 'Скриншот приложения', default: '' },
    features: { type: 'array', label: 'Преимущества', default: [], stringArray: true },
    background: { type: 'color', label: 'Фон', default: '#f0f4ff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['app', 'download', 'mobile', 'ios', 'android', 'store', 'приложение', 'скачать'],
})

registerBlock({
  type: 'media-mentions',
  label: 'Упоминания в СМИ',
  category: 'content',
  icon: '📰',
  description: 'Блок «О нас пишут» с логотипами СМИ и цитатами из публикаций',
  defaultData: {
    eyebrow: 'О нас пишут',
    title: 'Нам доверяют ведущие медиа',
    logos: [
      { name: 'Forbes', url: '#', logo: '' },
      { name: 'RBC', url: '#', logo: '' },
      { name: 'vc.ru', url: '#', logo: '' },
      { name: 'Коммерсантъ', url: '#', logo: '' },
      { name: 'TechCrunch', url: '#', logo: '' },
    ],
    featuredQuote: '«BST — один из самых перспективных конструкторов сайтов на российском рынке»',
    featuredSource: 'Forbes Russia, 2025',
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    eyebrow: { type: 'text', label: 'Надбровный текст', default: 'О нас пишут' },
    title: { type: 'text', label: 'Заголовок', default: '' },
    logos: { type: 'array', label: 'Логотипы СМИ', default: [] },
    featuredQuote: { type: 'text', label: 'Цитата из статьи', default: '' },
    featuredSource: { type: 'text', label: 'Источник цитаты', default: '' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['press', 'media', 'mentions', 'logos', 'publications', 'СМИ', 'пресса', 'упоминания'],
})

registerBlock({
  type: 'integration-logos',
  label: 'Логотипы интеграций',
  category: 'content',
  icon: '🔌',
  description: 'Сетка логотипов инструментов и сервисов, с которыми интегрируется продукт',
  defaultData: {
    title: 'Интегрируется с любимыми инструментами',
    subtitle: 'Подключите CRM, мессенджеры, платёжные системы и аналитику за минуту',
    count: '50+',
    countLabel: 'интеграций',
    integrations: [
      { name: 'amoCRM', icon: '📊', category: 'CRM' },
      { name: 'Bitrix24', icon: '🏢', category: 'CRM' },
      { name: 'Telegram', icon: '✈️', category: 'Мессенджер' },
      { name: 'WhatsApp', icon: '💬', category: 'Мессенджер' },
      { name: 'Stripe', icon: '💳', category: 'Оплата' },
      { name: 'ЮKassa', icon: '💰', category: 'Оплата' },
      { name: 'Яндекс.Метрика', icon: '📈', category: 'Аналитика' },
      { name: 'Google Analytics', icon: '📉', category: 'Аналитика' },
      { name: 'Mailchimp', icon: '📧', category: 'Email' },
      { name: 'Zapier', icon: '⚡', category: 'Автоматизация' },
      { name: 'Notion', icon: '📓', category: 'Контент' },
      { name: 'Tilda', icon: '🔄', category: 'Миграция' },
    ],
    ctaText: 'Смотреть все интеграции',
    ctaUrl: '#',
    background: '#f8fafc',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Интегрируется с любимыми инструментами' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    count: { type: 'text', label: 'Число интеграций', default: '50+' },
    countLabel: { type: 'text', label: 'Подпись числа', default: 'интеграций' },
    integrations: { type: 'array', label: 'Интеграции', default: [] },
    ctaText: { type: 'text', label: 'Текст CTA', default: 'Смотреть все' },
    ctaUrl: { type: 'text', label: 'URL CTA', default: '#' },
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['integrations', 'logos', 'tools', 'connect', 'интеграции', 'инструменты', 'подключение'],
})

registerBlock({
  type: 'video-testimonials',
  label: 'Видео-отзывы',
  category: 'embed',
  icon: '🎥',
  description: 'Карточки видео-отзывов клиентов с превью YouTube и воспроизведением по клику',
  defaultData: {
    title: 'Что говорят наши клиенты',
    subtitle: 'Видео-отзывы от реальных пользователей BST',
    videos: [
      { name: 'Дмитрий Волков', role: 'CEO, StartupX', youtubeId: 'dQw4w9WgXcQ', thumbnail: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=80', quote: 'BST сократил время запуска лендингов с 2 недель до 1 дня!' },
      { name: 'Анна Петрова', role: 'Маркетолог', youtubeId: 'dQw4w9WgXcQ', thumbnail: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&q=80', quote: 'Лучший инструмент для A/B тестирования лендингов' },
      { name: 'Иван Козлов', role: 'Freelancer', youtubeId: 'dQw4w9WgXcQ', thumbnail: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&q=80', quote: 'За год построил 40 сайтов для клиентов. Рекомендую!' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Что говорят клиенты' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    videos: { type: 'array', label: 'Видео-отзывы', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['video', 'testimonials', 'youtube', 'reviews', 'видео', 'отзывы', 'ютуб'],
})

registerBlock({
  type: 'team-grid-v2',
  label: 'Команда v2',
  category: 'content',
  icon: '👥',
  description: 'Расширенный блок команды с фильтром по отделам, социальными ссылками и ролями',
  defaultData: {
    title: 'Наша команда',
    subtitle: 'Профессионалы, которые строят BST каждый день',
    departments: ['Все', 'Продукт', 'Маркетинг', 'Разработка'],
    members: [
      { name: 'Иван Петров', role: 'CEO & Co-founder', department: 'Продукт', photo: 'https://i.pravatar.cc/200?img=11', bio: 'Серийный предприниматель, 10 лет в SaaS', linkedin: '#', twitter: '#' },
      { name: 'Мария Сидорова', role: 'Head of Design', department: 'Продукт', photo: 'https://i.pravatar.cc/200?img=22', bio: 'UX/UI дизайнер с опытом в Яндексе и Авито', linkedin: '#', twitter: '' },
      { name: 'Алексей Козлов', role: 'CTO', department: 'Разработка', photo: 'https://i.pravatar.cc/200?img=33', bio: 'Full-stack разработчик, ex-Сбер', linkedin: '#', twitter: '#' },
      { name: 'Ольга Смирнова', role: 'Head of Marketing', department: 'Маркетинг', photo: 'https://i.pravatar.cc/200?img=44', bio: 'Growth hacker, 7 лет в digital-маркетинге', linkedin: '#', twitter: '' },
      { name: 'Дмитрий Волков', role: 'Senior Engineer', department: 'Разработка', photo: 'https://i.pravatar.cc/200?img=55', bio: 'Специалист по высоконагруженным системам', linkedin: '#', twitter: '' },
      { name: 'Екатерина Новикова', role: 'Marketing Manager', department: 'Маркетинг', photo: 'https://i.pravatar.cc/200?img=47', bio: 'Эксперт по контент-маркетингу и SEO', linkedin: '#', twitter: '#' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наша команда' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    departments: { type: 'array', label: 'Фильтр отделов', default: [], stringArray: true },
    members: { type: 'array', label: 'Члены команды', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['team', 'people', 'staff', 'filter', 'departments', 'команда', 'люди', 'сотрудники'],
})

registerBlock({
  type: 'waitlist',
  label: 'Лист ожидания',
  category: 'form',
  icon: '⏳',
  description: 'Форма для сбора email в лист ожидания с счётчиком участников и прогресс-баром',
  defaultData: {
    title: 'Будьте первыми',
    subtitle: 'Мы запускаемся совсем скоро. Оставьте email и получите ранний доступ + скидку 40%.',
    placeholderEmail: 'your@email.com',
    ctaText: 'Записаться в очередь',
    joinedCount: 1247,
    joinedLabel: 'человек уже в очереди',
    spotsLeft: 153,
    spotsLabel: 'мест осталось',
    totalSpots: 1500,
    benefits: ['Ранний доступ на 2 недели раньше', 'Скидка 40% на первый год', 'Личная онбординг-сессия'],
    successMessage: 'Вы в списке! Ждите письма.',
    background: '#0f172a',
    accentColor: '#5b6af0',
    textColor: '#ffffff',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Будьте первыми' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    placeholderEmail: { type: 'text', label: 'Placeholder email', default: 'your@email.com' },
    ctaText: { type: 'text', label: 'Текст кнопки', default: 'Записаться' },
    joinedCount: { type: 'number', label: 'Количество участников', default: 1000, min: 0 },
    joinedLabel: { type: 'text', label: 'Подпись счётчика', default: 'человек уже записались' },
    spotsLeft: { type: 'number', label: 'Мест осталось', default: 200, min: 0 },
    spotsLabel: { type: 'text', label: 'Подпись оставшихся мест', default: 'мест осталось' },
    totalSpots: { type: 'number', label: 'Всего мест', default: 1500, min: 1 },
    benefits: { type: 'array', label: 'Что получат участники', default: [], stringArray: true },
    successMessage: { type: 'text', label: 'Сообщение после отправки', default: 'Вы в списке!' },
    background: { type: 'color', label: 'Фон', default: '#0f172a' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
  },
  tags: ['waitlist', 'email', 'launch', 'beta', 'coming-soon', 'лист ожидания', 'запуск', 'email'],
})

registerBlock({
  type: 'newsletter-v2',
  label: 'Подписка на рассылку v2',
  category: 'form',
  icon: '📬',
  description: 'Улучшенный блок подписки с перечислением выгод, тематикой писем и счётчиком подписчиков',
  defaultData: {
    title: 'Подпишитесь и растите быстрее',
    subtitle: 'Еженедельные инсайты по маркетингу, дизайну и росту бизнеса',
    frequency: 'Письмо раз в неделю. Без спама.',
    subscriberCount: '12 400+',
    subscriberLabel: 'подписчиков уже читают',
    topics: [
      { icon: '🚀', label: 'Советы по росту' },
      { icon: '🎨', label: 'Тренды дизайна' },
      { icon: '📊', label: 'Кейсы и аналитика' },
      { icon: '🛠️', label: 'Обзоры инструментов' },
    ],
    placeholderEmail: 'email@example.com',
    ctaText: 'Подписаться бесплатно',
    privacyNote: 'Нажимая «Подписаться», вы соглашаетесь с политикой конфиденциальности. Отписка в 1 клик.',
    successMessage: 'Спасибо! Проверьте почту для подтверждения.',
    background: '#f8fafc',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Подпишитесь' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    frequency: { type: 'text', label: 'Частота рассылки', default: '' },
    subscriberCount: { type: 'text', label: 'Число подписчиков', default: '' },
    subscriberLabel: { type: 'text', label: 'Подпись числа', default: 'подписчиков' },
    topics: { type: 'array', label: 'Темы рассылки', default: [] },
    placeholderEmail: { type: 'text', label: 'Placeholder email', default: 'email@example.com' },
    ctaText: { type: 'text', label: 'Текст кнопки', default: 'Подписаться' },
    privacyNote: { type: 'text', label: 'Примечание о конфиденциальности', default: '' },
    successMessage: { type: 'text', label: 'Сообщение после отправки', default: 'Спасибо!' },
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['newsletter', 'email', 'subscribe', 'mailing', 'рассылка', 'подписка', 'email'],
})

registerBlock({
  type: 'process-flow',
  label: 'Процесс/Схема работы',
  category: 'content',
  icon: '🔄',
  description: 'Горизонтальная схема процесса с шагами, иконками и стрелками-соединениями',
  defaultData: {
    title: 'Как это работает',
    subtitle: 'От регистрации до первого опубликованного сайта — 3 простых шага',
    steps: [
      { number: '01', icon: '📝', title: 'Зарегистрируйтесь', description: 'Создайте аккаунт за 30 секунд. Кредитная карта не нужна.' },
      { number: '02', icon: '🎨', title: 'Выберите шаблон', description: 'Из 50+ профессиональных шаблонов или начните с нуля.' },
      { number: '03', icon: '✏️', title: 'Настройте контент', description: 'Добавьте тексты, фото и блоки перетаскиванием. Без кода.' },
      { number: '04', icon: '🚀', title: 'Опубликуйте', description: 'Один клик — и сайт доступен по всему миру на CDN.' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
    arrowColor: '#e5e7eb',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Как это работает' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    steps: { type: 'array', label: 'Шаги', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
    arrowColor: { type: 'color', label: 'Цвет стрелок', default: '#e5e7eb' },
  },
  tags: ['process', 'flow', 'steps', 'how-it-works', 'workflow', 'процесс', 'шаги', 'схема'],
})

registerBlock({
  type: 'portfolio-grid',
  label: 'Портфолио сетка',
  category: 'gallery',
  icon: '🖼️',
  description: 'Сетка работ/проектов с фильтром по категориям и hover-эффектом с деталями',
  defaultData: {
    title: 'Наши работы',
    subtitle: 'Проекты, которыми мы гордимся',
    categories: ['Все', 'Лендинги', 'Интернет-магазины', 'Корпоративные', 'Портфолио'],
    items: [
      { title: 'Лендинг для IT-стартапа', category: 'Лендинги', image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=500&q=80', tags: ['SaaS', 'B2B'], url: '#' },
      { title: 'Магазин модной одежды', category: 'Интернет-магазины', image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=500&q=80', tags: ['Fashion', 'eCommerce'], url: '#' },
      { title: 'Сайт юридической фирмы', category: 'Корпоративные', image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=500&q=80', tags: ['Law', 'Corporate'], url: '#' },
      { title: 'Портфолио дизайнера', category: 'Портфолио', image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=500&q=80', tags: ['Design', 'Creative'], url: '#' },
      { title: 'Лендинг мобильного приложения', category: 'Лендинги', image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=500&q=80', tags: ['App', 'Mobile'], url: '#' },
      { title: 'Строительная компания', category: 'Корпоративные', image: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=500&q=80', tags: ['Construction', 'B2B'], url: '#' },
    ],
    background: '#f8fafc',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наши работы' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    categories: { type: 'array', label: 'Категории фильтра', default: [], stringArray: true },
    items: { type: 'array', label: 'Проекты', default: [] },
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['portfolio', 'gallery', 'work', 'projects', 'filter', 'портфолио', 'работы', 'проекты'],
})

registerBlock({
  type: 'skill-bars',
  label: 'Шкалы навыков',
  category: 'content',
  icon: '📊',
  description: 'Горизонтальные прогресс-бары с анимацией для отображения компетенций и технологий',
  defaultData: {
    title: 'Наши компетенции',
    subtitle: 'Технологии и инструменты, которыми мы владеем',
    skills: [
      { name: 'UI/UX Дизайн', percent: 95, color: '#5b6af0' },
      { name: 'React / Next.js', percent: 90, color: '#0ea5e9' },
      { name: 'Node.js / NestJS', percent: 85, color: '#16a34a' },
      { name: 'PostgreSQL', percent: 80, color: '#d97706' },
      { name: 'DevOps / Kubernetes', percent: 70, color: '#dc2626' },
      { name: 'Mobile (React Native)', percent: 75, color: '#7c3aed' },
    ],
    columns: 2,
    background: '#ffffff',
    barBackground: '#f1f5f9',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наши компетенции' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    skills: { type: 'array', label: 'Навыки', default: [] },
    columns: { type: 'number', label: 'Колонок (1-2)', default: 2, min: 1, max: 2 },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    barBackground: { type: 'color', label: 'Фон прогресс-бара', default: '#f1f5f9' },
  },
  tags: ['skills', 'progress', 'bars', 'competencies', 'tech', 'навыки', 'компетенции', 'технологии'],
})

registerBlock({
  type: 'community-stats',
  label: 'Статистика сообщества',
  category: 'content',
  icon: '🌐',
  description: 'Блок с метриками активности сообщества: Discord, Telegram, GitHub, YouTube и т.д.',
  defaultData: {
    title: 'Присоединяйтесь к сообществу',
    subtitle: 'Тысячи создателей сайтов уже вместе с нами',
    channels: [
      { platform: 'Telegram', icon: '✈️', count: '8 400', label: 'подписчиков', url: '#', color: '#229ED9', bg: '#e8f7fe' },
      { platform: 'Discord', icon: '🎮', count: '3 200', label: 'участников', url: '#', color: '#5865F2', bg: '#eef0fe' },
      { platform: 'YouTube', icon: '▶️', count: '12 000', label: 'подписчиков', url: '#', color: '#FF0000', bg: '#ffe4e4' },
      { platform: 'GitHub', icon: '🐙', count: '2 100', label: 'звёзд', url: '#', color: '#333', bg: '#f3f4f6' },
    ],
    ctaText: 'Вступить в Telegram',
    ctaUrl: '#',
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Присоединяйтесь к сообществу' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    channels: { type: 'array', label: 'Каналы/платформы', default: [] },
    ctaText: { type: 'text', label: 'Текст CTA', default: 'Вступить' },
    ctaUrl: { type: 'text', label: 'URL CTA', default: '#' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['community', 'discord', 'telegram', 'social', 'stats', 'сообщество', 'подписчики', 'социальные'],
})

registerBlock({
  type: 'location-cards',
  label: 'Карточки офисов/локаций',
  category: 'content',
  icon: '📍',
  description: 'Карточки с адресами, телефонами и часами работы офисов, магазинов или филиалов',
  defaultData: {
    title: 'Наши офисы',
    subtitle: 'Мы работаем в Москве, Санкт-Петербурге и Новосибирске',
    locations: [
      { city: 'Москва', address: 'Пресненская набережная, 6, стр. 2, офис 1204', phone: '+7 (495) 123-45-67', hours: 'Пн-Пт: 9:00–18:00', email: 'moscow@bst.ru', mapUrl: '#', image: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=400&q=80', badge: 'Главный офис' },
      { city: 'Санкт-Петербург', address: 'Малая Конюшенная ул., 2, офис 301', phone: '+7 (812) 987-65-43', hours: 'Пн-Пт: 9:00–18:00', email: 'spb@bst.ru', mapUrl: '#', image: 'https://images.unsplash.com/photo-1556905055-8f358a7a47b2?w=400&q=80', badge: '' },
      { city: 'Новосибирск', address: 'ул. Ленина, 50, офис 15', phone: '+7 (383) 456-78-90', hours: 'Пн-Пт: 9:00–17:00', email: 'nsk@bst.ru', mapUrl: '#', image: 'https://images.unsplash.com/photo-1486325212027-8081e485255e?w=400&q=80', badge: '' },
    ],
    background: '#f8fafc',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наши офисы' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    locations: { type: 'array', label: 'Локации', default: [] },
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['locations', 'offices', 'address', 'map', 'branches', 'офис', 'адрес', 'локации', 'филиалы'],
})

registerBlock({
  type: 'pricing-addons',
  label: 'Тарифы с дополнениями',
  category: 'commerce',
  icon: '➕',
  description: 'Базовый тариф с выбором дополнительных опций и автоматическим пересчётом итоговой цены',
  defaultData: {
    title: 'Соберите свой пакет',
    subtitle: 'Начните с базового плана и добавьте нужные функции',
    basePrice: 990,
    currency: '₽',
    basePeriod: 'мес',
    baseName: 'BST Pro',
    baseFeatures: ['10 сайтов', '10 ГБ хранилища', 'Аналитика', 'Поддержка email'],
    addons: [
      { id: 'custom-domain', name: 'Свой домен', description: 'Подключите любой домен', price: 150, icon: '🌐' },
      { id: 'ssl', name: 'Premium SSL', description: 'Wildcard сертификат', price: 200, icon: '🔒' },
      { id: 'analytics-pro', name: 'Аналитика Pro', description: 'Тепловые карты + записи сессий', price: 300, icon: '📊' },
      { id: 'ai-content', name: 'AI-копирайтинг', description: 'Безлимит AI-генерации текстов', price: 490, icon: '🤖' },
      { id: 'priority-support', name: 'Приоритетная поддержка', description: 'Ответ в течение 1 часа', price: 390, icon: '⚡' },
    ],
    ctaText: 'Оформить выбранный план',
    ctaUrl: '#',
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Соберите свой пакет' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    basePrice: { type: 'number', label: 'Базовая цена', default: 990, min: 0 },
    currency: { type: 'text', label: 'Валюта', default: '₽' },
    basePeriod: { type: 'text', label: 'Период', default: 'мес' },
    baseName: { type: 'text', label: 'Название базового плана', default: 'Pro' },
    baseFeatures: { type: 'array', label: 'Базовые функции', default: [], stringArray: true },
    addons: { type: 'array', label: 'Дополнения', default: [] },
    ctaText: { type: 'text', label: 'Текст CTA', default: 'Оформить' },
    ctaUrl: { type: 'text', label: 'URL CTA', default: '#' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['pricing', 'addons', 'options', 'custom', 'builder', 'тариф', 'дополнения', 'конструктор'],
})

registerBlock({
  type: 'mockup-showcase',
  label: 'Демо в рамке устройства',
  category: 'media',
  icon: '💻',
  description: 'Скриншот продукта или сайта в реалистичной рамке ноутбука или смартфона с callout-подписями',
  defaultData: {
    title: 'Мощный редактор в браузере',
    subtitle: 'Всё что нужно — прямо в интерфейсе. Без установки, без обновлений.',
    deviceType: 'laptop',
    screenshot: 'https://images.unsplash.com/photo-1551650975-87deedd944c3?w=900&q=80',
    callouts: [
      { text: 'Drag & Drop', position: 'top-left' },
      { text: 'Предпросмотр', position: 'top-right' },
      { text: '200+ блоков', position: 'bottom-left' },
      { text: 'История версий', position: 'bottom-right' },
    ],
    background: '#f0f4ff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: '' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    deviceType: { type: 'select', label: 'Тип устройства', default: 'laptop', options: [{ value: 'laptop', label: 'Ноутбук' }, { value: 'phone', label: 'Смартфон' }] },
    screenshot: { type: 'image', label: 'Скриншот', default: '' },
    callouts: { type: 'array', label: 'Подписи-выноски', default: [] },
    background: { type: 'color', label: 'Фон', default: '#f0f4ff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['mockup', 'device', 'laptop', 'phone', 'screenshot', 'showcase', 'демо', 'макет', 'скриншот'],
})

registerBlock({
  type: 'stat-counters-v2',
  label: 'Счётчики цифр v2',
  category: 'content',
  icon: '🔢',
  description: 'Блок анимированных счётчиков с иконками, запускается при попадании в viewport',
  defaultData: {
    title: 'BST в цифрах',
    subtitle: 'Результаты, которые говорят сами за себя',
    stats: [
      { icon: '🌍', value: 10000, suffix: '+', label: 'Клиентов по всей России', color: '#5b6af0' },
      { icon: '🌐', value: 50000, suffix: '+', label: 'Опубликованных сайтов', color: '#0ea5e9' },
      { icon: '⭐', value: 4.9, suffix: '/5', label: 'Средний рейтинг платформы', color: '#f59e0b', decimals: 1 },
      { icon: '📦', value: 236, suffix: '+', label: 'Готовых блоков', color: '#16a34a' },
    ],
    background: '#0f172a',
    textColor: '#ffffff',
    cardBg: '#1e293b',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'В цифрах' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    stats: { type: 'array', label: 'Счётчики', default: [] },
    background: { type: 'color', label: 'Фон', default: '#0f172a' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
    cardBg: { type: 'color', label: 'Фон карточки', default: '#1e293b' },
  },
  tags: ['stats', 'numbers', 'counters', 'animated', 'metrics', 'цифры', 'счётчики', 'анимация'],
})

registerBlock({
  type: 'guarantee-block',
  label: 'Гарантия возврата',
  category: 'content',
  icon: '🛡️',
  description: 'Блок гарантии с большим значком, условиями и кнопкой — повышает конверсию перед оплатой',
  defaultData: {
    days: 30,
    title: '30-дневная гарантия возврата денег',
    subtitle: 'Мы уверены в качестве платформы. Если что-то пойдёт не так — вернём 100% оплаты без вопросов.',
    trustPoints: [
      'Возврат в течение 30 дней без объяснения причин',
      'Полный возврат — не частичный',
      'Обработка за 2-3 рабочих дня',
      'Никакого бюрократического ада',
    ],
    ctaText: 'Попробовать с гарантией',
    ctaUrl: '#',
    contactText: 'Есть вопросы? Напишите нам',
    contactUrl: '#',
    background: '#f0fdf4',
    accentColor: '#16a34a',
    iconColor: '#16a34a',
  },
  schema: {
    days: { type: 'number', label: 'Дней гарантии', default: 30, min: 1 },
    title: { type: 'text', label: 'Заголовок гарантии', default: '30-дневная гарантия возврата' },
    subtitle: { type: 'text', label: 'Описание', default: '' },
    trustPoints: { type: 'array', label: 'Пункты гарантии', default: [], stringArray: true },
    ctaText: { type: 'text', label: 'Текст CTA', default: 'Попробовать' },
    ctaUrl: { type: 'text', label: 'URL CTA', default: '#' },
    contactText: { type: 'text', label: 'Текст контактной ссылки', default: '' },
    contactUrl: { type: 'text', label: 'URL контакта', default: '#' },
    background: { type: 'color', label: 'Фон', default: '#f0fdf4' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#16a34a' },
    iconColor: { type: 'color', label: 'Цвет иконки', default: '#16a34a' },
  },
  tags: ['guarantee', 'refund', 'money-back', 'trust', 'гарантия', 'возврат', 'доверие'],
})

registerBlock({
  type: 'accordion-rich',
  label: 'Аккордеон с иконками',
  category: 'content',
  icon: '📂',
  description: 'Раскрывающиеся секции с иконками, цветной полоской и анимацией — для FAQ или инструкций',
  defaultData: {
    title: 'Часто задаваемые вопросы',
    subtitle: 'Ответы на самые популярные вопросы',
    items: [
      { icon: '💳', color: '#5b6af0', question: 'Нужна ли кредитная карта для регистрации?', answer: 'Нет! Бесплатный план доступен без ввода платёжных данных. Кредитная карта потребуется только при переходе на платный тариф.' },
      { icon: '🌐', color: '#0ea5e9', question: 'Могу ли я подключить собственный домен?', answer: 'Да, на планах Pro и выше вы можете подключить любой домен. Мы автоматически выпустим SSL-сертификат. Весь процесс занимает около 10 минут.' },
      { icon: '📦', color: '#16a34a', question: 'Сколько сайтов я могу создать?', answer: 'На бесплатном плане — 1 сайт. На Pro — до 10. На плане Бизнес — без ограничений. Сайты можно передавать клиентам через White-label режим.' },
      { icon: '🤖', color: '#7c3aed', question: 'Как работает AI-ассистент?', answer: 'AI-ассистент на базе GPT-4 генерирует тексты для каждого блока, предлагает структуру страниц, переводит на 15 языков и проводит SEO-аудит. Запросы списываются с AI-кредитов тарифа.' },
      { icon: '🔐', color: '#d97706', question: 'Мои данные в безопасности?', answer: 'Да. Данные хранятся на серверах в России и EU согласно GDPR. Резервные копии создаются ежечасно. SSL-шифрование на всех уровнях.' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'FAQ' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    items: { type: 'array', label: 'Пункты аккордеона', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['accordion', 'faq', 'collapse', 'icons', 'questions', 'аккордеон', 'вопросы', 'ответы'],
})

registerBlock({
  type: 'product-spec-sheet',
  label: 'Технические характеристики',
  category: 'content',
  icon: '📋',
  description: 'Таблица технических характеристик продукта, сгруппированных по категориям',
  defaultData: {
    title: 'Технические характеристики',
    product: 'BST Editor v2.5',
    groups: [
      { name: 'Производительность', specs: [{ label: 'Время загрузки страниц', value: '< 0.8 сек (CDN)' }, { label: 'Uptime', value: '99.9% SLA' }, { label: 'Одновременных посетителей', value: 'Без ограничений' }] },
      { name: 'Возможности', specs: [{ label: 'Количество блоков', value: '236+' }, { label: 'Шаблонов', value: '50+' }, { label: 'Языков AI-перевода', value: '15' }, { label: 'Интеграций', value: '50+' }] },
      { name: 'Безопасность', specs: [{ label: 'SSL сертификат', value: 'Бесплатно (Let\'s Encrypt)' }, { label: 'Резервные копии', value: 'Ежечасно, 30 дней' }, { label: 'GDPR', value: 'Соответствует' }] },
      { name: 'Поддержка', specs: [{ label: 'Email поддержка', value: 'Пн-Пт, ответ 24 ч' }, { label: 'Приоритетная поддержка', value: 'Pro тариф (ответ 1 ч)' }, { label: 'Telegram-сообщество', value: '8 400+ участников' }] },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Характеристики' },
    product: { type: 'text', label: 'Название продукта/модели', default: '' },
    groups: { type: 'array', label: 'Группы характеристик', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['specs', 'specifications', 'technical', 'table', 'product', 'характеристики', 'технические', 'таблица'],
})

registerBlock({
  type: 'comparison-widget',
  label: 'Слайдер сравнения до/после',
  category: 'interactive',
  icon: '↔️',
  description: 'Интерактивный слайдер для сравнения двух изображений (до и после), перетаскиванием',
  defaultData: {
    title: 'До и После',
    subtitle: 'Перетащите ползунок, чтобы сравнить',
    beforeImage: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&q=80',
    afterImage: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=800&q=80',
    beforeLabel: 'До',
    afterLabel: 'После',
    initialPosition: 50,
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'До и После' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    beforeImage: { type: 'image', label: 'Изображение ДО', default: '' },
    afterImage: { type: 'image', label: 'Изображение ПОСЛЕ', default: '' },
    beforeLabel: { type: 'text', label: 'Метка ДО', default: 'До' },
    afterLabel: { type: 'text', label: 'Метка ПОСЛЕ', default: 'После' },
    initialPosition: { type: 'number', label: 'Начальная позиция (0-100)', default: 50, min: 0, max: 100 },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['before-after', 'comparison', 'slider', 'interactive', 'image', 'до-после', 'слайдер', 'сравнение'],
})

registerBlock({
  type: 'feature-comparison-v2',
  label: 'Сравнение с конкурентами',
  category: 'content',
  icon: '⚔️',
  description: 'Таблица сравнения вашего продукта с 2-3 конкурентами по ключевым функциям',
  defaultData: {
    title: 'BST vs конкуренты',
    subtitle: 'Честное сравнение по ключевым параметрам',
    yourProductName: 'BST',
    competitors: ['Tilda', 'Wix'],
    features: [
      { name: 'Бесплатный план', yours: true, values: [true, true] },
      { name: 'Без кода', yours: true, values: [true, true] },
      { name: 'AI-копирайтинг', yours: true, values: [false, false] },
      { name: 'Встроенная аналитика', yours: true, values: [false, true] },
      { name: 'Тепловые карты', yours: true, values: [false, false] },
      { name: 'Свой домен (бесплатный план)', yours: false, values: [false, false] },
      { name: 'Хостинг в России', yours: true, values: [false, false] },
      { name: 'White-label', yours: true, values: [false, false] },
      { name: 'API доступ', yours: true, values: [true, false] },
      { name: 'Поддержка 24/7', yours: true, values: [false, true] },
    ],
    ctaText: 'Перейти на BST',
    ctaUrl: '#',
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Сравнение с конкурентами' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    yourProductName: { type: 'text', label: 'Ваш продукт', default: 'Мы' },
    competitors: { type: 'array', label: 'Конкуренты (названия)', default: [], stringArray: true },
    features: { type: 'array', label: 'Сравниваемые функции', default: [] },
    ctaText: { type: 'text', label: 'Текст CTA', default: 'Попробовать' },
    ctaUrl: { type: 'text', label: 'URL CTA', default: '#' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['comparison', 'competitors', 'features', 'table', 'vs', 'сравнение', 'конкуренты', 'функции'],
})

registerBlock({
  type: 'sticky-announcement',
  label: 'Announcement Bar',
  category: 'navigation',
  icon: '📢',
  description: 'Липкая строка объявлений вверху страницы с кнопкой закрытия',
  defaultData: {
    text: '🎉 Специальное предложение: скидка 20% до конца месяца!',
    ctaText: 'Узнать больше',
    ctaUrl: '#',
    background: '#5b6af0',
    textColor: '#ffffff',
    closable: true,
  },
  schema: {
    text: { type: 'text', label: 'Текст объявления', default: '🎉 Специальное предложение: скидка 20% до конца месяца!' },
    ctaText: { type: 'text', label: 'Текст кнопки', default: 'Узнать больше' },
    ctaUrl: { type: 'text', label: 'URL кнопки', default: '#' },
    background: { type: 'color', label: 'Фон', default: '#5b6af0' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
  },
  tags: ['announcement', 'bar', 'banner', 'sticky', 'top', 'объявление', 'баннер'],
})

registerBlock({
  type: 'split-feature',
  label: 'Split Feature Section',
  category: 'content',
  icon: '🔀',
  description: 'Секция с картинкой слева/справа и списком преимуществ',
  defaultData: {
    title: 'Мощные возможности для вашего бизнеса',
    subtitle: 'Всё что нужно для роста — в одном инструменте',
    image: '',
    imagePosition: 'right',
    points: [
      { icon: '⚡', title: 'Быстрый старт', text: 'Запуск сайта за 10 минут без кода' },
      { icon: '🎨', title: 'Дизайн под вас', text: 'Полная кастомизация каждого блока' },
      { icon: '📈', title: 'Аналитика', text: 'Видите всё: откуда приходят клиенты' },
    ],
    ctaText: 'Попробовать бесплатно',
    ctaUrl: '#',
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Мощные возможности для вашего бизнеса' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    image: { type: 'image', label: 'Изображение', default: '' },
    imagePosition: { type: 'select', label: 'Позиция изображения', default: 'right', options: [{ value: 'left', label: 'Слева' }, { value: 'right', label: 'Справа' }] },
    points: { type: 'array', label: 'Пункты', default: [] },
    ctaText: { type: 'text', label: 'Текст кнопки', default: 'Попробовать бесплатно' },
    ctaUrl: { type: 'text', label: 'URL кнопки', default: '#' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['split', 'feature', 'image', 'benefits', 'points', 'разделённый', 'преимущества'],
})

registerBlock({
  type: 'floating-cta',
  label: 'Floating CTA Button',
  category: 'interactive',
  icon: '💬',
  description: 'Плавающая кнопка призыва к действию в углу экрана',
  defaultData: {
    text: 'Написать нам',
    url: '#',
    icon: '💬',
    position: 'bottom-right',
    background: '#5b6af0',
    textColor: '#ffffff',
    showPulse: true,
  },
  schema: {
    text: { type: 'text', label: 'Текст кнопки', default: 'Написать нам' },
    url: { type: 'text', label: 'URL', default: '#' },
    icon: { type: 'text', label: 'Иконка (эмодзи)', default: '💬' },
    position: { type: 'select', label: 'Позиция', default: 'bottom-right', options: [{ value: 'bottom-right', label: 'Правый низ' }, { value: 'bottom-left', label: 'Левый низ' }] },
    background: { type: 'color', label: 'Цвет фона', default: '#5b6af0' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
  },
  tags: ['floating', 'cta', 'button', 'fixed', 'chat', 'плавающий', 'кнопка'],
})

registerBlock({
  type: 'timeline-v2',
  label: 'Timeline v2',
  category: 'content',
  icon: '⏳',
  description: 'Вертикальная временная шкала с иконками и описаниями',
  defaultData: {
    title: 'История компании',
    subtitle: 'Наш путь с момента основания',
    events: [
      { year: '2020', title: 'Основание', text: 'Стартовали в небольшом офисе с командой из 3 человек', icon: '🚀' },
      { year: '2021', title: 'Первые клиенты', text: 'Подключили 100+ компаний по всей России', icon: '🎯' },
      { year: '2022', title: 'Раунд A', text: 'Привлекли $2M инвестиций от ведущих фондов', icon: '💰' },
      { year: '2023', title: 'Международный рост', text: 'Вышли на рынки СНГ с командой 50+ человек', icon: '🌍' },
    ],
    accentColor: '#5b6af0',
    background: '#ffffff',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'История компании' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    events: { type: 'array', label: 'События', default: [] },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
  },
  tags: ['timeline', 'history', 'events', 'years', 'шкала', 'история', 'события'],
})

registerBlock({
  type: 'mega-hero',
  label: 'Mega Hero',
  category: 'hero',
  icon: '🌟',
  description: 'Большой hero с видеофоном, крупным заголовком и двумя кнопками',
  defaultData: {
    title: 'Создавайте сайты быстрее чем когда-либо',
    subtitle: 'Профессиональный конструктор сайтов с AI, блоками и аналитикой в одном месте',
    videoUrl: '',
    posterImage: '',
    primaryCta: 'Начать бесплатно',
    primaryUrl: '#',
    secondaryCta: 'Смотреть демо',
    secondaryUrl: '#',
    showArrow: true,
    overlay: 0.5,
    background: '#0f172a',
    textColor: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Создавайте сайты быстрее чем когда-либо' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    videoUrl: { type: 'text', label: 'URL видео (MP4)', default: '' },
    posterImage: { type: 'image', label: 'Постер (если нет видео)', default: '' },
    primaryCta: { type: 'text', label: 'Кнопка 1 текст', default: 'Начать бесплатно' },
    primaryUrl: { type: 'text', label: 'Кнопка 1 URL', default: '#' },
    secondaryCta: { type: 'text', label: 'Кнопка 2 текст', default: 'Смотреть демо' },
    secondaryUrl: { type: 'text', label: 'Кнопка 2 URL', default: '#' },
    overlay: { type: 'number', label: 'Затемнение (0-1)', default: 0.5 },
    background: { type: 'color', label: 'Цвет фона', default: '#0f172a' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['hero', 'video', 'mega', 'fullscreen', 'cta', 'видео', 'геро', 'главный'],
})

registerBlock({
  type: 'awards-ribbon',
  label: 'Awards & Badges',
  category: 'content',
  icon: '🏆',
  description: 'Горизонтальная лента наград, сертификатов и значков',
  defaultData: {
    title: 'Наши награды',
    awards: [
      { icon: '🏆', name: 'Лучший стартап 2023', org: 'TechAwards' },
      { icon: '⭐', name: 'Выбор редакции', org: 'ProductHunt' },
      { icon: '🥇', name: '#1 в категории', org: 'AppStore' },
      { icon: '🎖', name: 'TOP-10 SaaS', org: 'G2 Crowd' },
    ],
    background: '#f8fafc',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наши награды' },
    awards: { type: 'array', label: 'Награды', default: [] },
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['awards', 'badges', 'certificates', 'recognition', 'награды', 'значки', 'сертификаты'],
})

registerBlock({
  type: 'pricing-v3',
  label: 'Pricing Cards v3',
  category: 'commerce',
  icon: '💳',
  description: 'Современные карточки тарифов с тегами и градиентами',
  defaultData: {
    title: 'Выберите подходящий тариф',
    subtitle: 'Без скрытых платежей. Отмена в любой момент.',
    plans: [
      { name: 'Старт', price: '0', period: 'мес', badge: '', features: ['1 сайт', '10 блоков', 'SSL'], highlight: false, cta: 'Начать', url: '#' },
      { name: 'Про', price: '790', period: 'мес', badge: 'Популярный', features: ['5 сайтов', 'Все блоки', 'Свой домен', 'Аналитика'], highlight: true, cta: 'Выбрать Про', url: '#' },
      { name: 'Бизнес', price: '2490', period: 'мес', badge: '', features: ['Неограниченно', 'White Label', 'CMS API', 'Приоритет'], highlight: false, cta: 'Выбрать Бизнес', url: '#' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Выберите подходящий тариф' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    plans: { type: 'array', label: 'Тарифы', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['pricing', 'plans', 'cards', 'subscription', 'тарифы', 'цены', 'подписка'],
})

registerBlock({
  type: 'client-logos-v2',
  label: 'Client Logos v2 (Marquee)',
  category: 'content',
  icon: '🏢',
  description: 'Бегущая строка логотипов клиентов с анимацией',
  defaultData: {
    title: 'Нам доверяют лидеры рынка',
    logos: [
      { name: 'Яндекс', logo: '' },
      { name: 'Сбер', logo: '' },
      { name: 'VK', logo: '' },
      { name: 'Ozon', logo: '' },
      { name: 'Wildberries', logo: '' },
      { name: 'МТС', logo: '' },
    ],
    speed: 30,
    background: '#f8fafc',
    grayscale: true,
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Нам доверяют лидеры рынка' },
    logos: { type: 'array', label: 'Логотипы', default: [] },
    speed: { type: 'number', label: 'Скорость (сек)', default: 30 },
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
  },
  tags: ['logos', 'clients', 'marquee', 'scroll', 'brands', 'логотипы', 'клиенты', 'марке'],
})

registerBlock({
  type: 'contact-v2',
  label: 'Contact Section v2',
  category: 'form',
  icon: '📬',
  description: 'Секция контактов с формой, картой и контактными данными',
  defaultData: {
    title: 'Свяжитесь с нами',
    subtitle: 'Ответим в течение 24 часов',
    phone: '+7 (800) 123-45-67',
    email: 'hello@company.ru',
    address: 'Москва, ул. Примерная, 1',
    namePlaceholder: 'Ваше имя',
    emailPlaceholder: 'Email',
    messagePlaceholder: 'Сообщение',
    submitText: 'Отправить',
    successMessage: 'Спасибо! Мы свяжемся с вами.',
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Свяжитесь с нами' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    phone: { type: 'text', label: 'Телефон', default: '' },
    email: { type: 'text', label: 'Email', default: '' },
    address: { type: 'text', label: 'Адрес', default: '' },
    namePlaceholder: { type: 'text', label: 'Плейсхолдер имени', default: 'Ваше имя' },
    emailPlaceholder: { type: 'text', label: 'Плейсхолдер email', default: 'Email' },
    messagePlaceholder: { type: 'text', label: 'Плейсхолдер сообщения', default: 'Сообщение' },
    submitText: { type: 'text', label: 'Текст кнопки', default: 'Отправить' },
    successMessage: { type: 'text', label: 'Сообщение после отправки', default: 'Спасибо!' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['contact', 'form', 'email', 'phone', 'address', 'контакт', 'форма', 'связь'],
})

registerBlock({
  type: 'breadcrumb',
  label: 'Breadcrumb Navigation',
  category: 'navigation',
  icon: '🍞',
  description: 'Хлебные крошки для навигации по разделам сайта',
  defaultData: {
    items: [
      { label: 'Главная', url: '/' },
      { label: 'Блог', url: '/blog' },
      { label: 'Статья', url: '' },
    ],
    separator: '/',
    background: '#f8fafc',
    textColor: '#6b7280',
    accentColor: '#5b6af0',
  },
  schema: {
    items: { type: 'array', label: 'Пункты', default: [] },
    separator: { type: 'text', label: 'Разделитель', default: '/' },
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#6b7280' },
    accentColor: { type: 'color', label: 'Цвет активного', default: '#5b6af0' },
  },
  tags: ['breadcrumb', 'navigation', 'path', 'хлебные крошки', 'навигация', 'путь'],
})

registerBlock({
  type: 'icon-grid',
  label: 'Icon Grid',
  category: 'content',
  icon: '🔲',
  description: 'Сетка иконок с подписями — для категорий, услуг, особенностей',
  defaultData: {
    title: 'Что вы получаете',
    columns: 4,
    items: [
      { icon: '⚡', label: 'Скорость' },
      { icon: '🔒', label: 'Безопасность' },
      { icon: '📱', label: 'Мобильность' },
      { icon: '🎨', label: 'Дизайн' },
      { icon: '🤖', label: 'AI' },
      { icon: '📊', label: 'Аналитика' },
      { icon: '🌐', label: 'Мультиязычность' },
      { icon: '💬', label: 'Поддержка' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Что вы получаете' },
    columns: { type: 'number', label: 'Колонок', default: 4 },
    items: { type: 'array', label: 'Элементы', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['icons', 'grid', 'categories', 'features', 'иконки', 'сетка', 'категории'],
})

registerBlock({
  type: 'product-hero',
  label: 'Product Hero',
  category: 'hero',
  icon: '📦',
  description: 'Hero-секция для лендинга продукта с рейтингом, ценой и отзывами',
  defaultData: {
    badge: 'Новинка сезона',
    title: 'Умные наушники Pro X',
    subtitle: 'Активное шумоподавление нового поколения. 30 часов автономной работы.',
    price: '12 990 ₽',
    oldPrice: '16 990 ₽',
    rating: 4.8,
    reviewCount: 1240,
    image: '',
    ctaText: 'Купить сейчас',
    ctaUrl: '#',
    secondaryCta: 'Подробнее',
    secondaryUrl: '#',
    features: ['Bluetooth 5.3', 'ANC', 'IPX4', '30ч работы'],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    badge: { type: 'text', label: 'Бейдж', default: 'Новинка' },
    title: { type: 'text', label: 'Название продукта', default: 'Умные наушники Pro X' },
    subtitle: { type: 'text', label: 'Описание', default: '' },
    price: { type: 'text', label: 'Цена', default: '12 990 ₽' },
    oldPrice: { type: 'text', label: 'Старая цена', default: '' },
    rating: { type: 'number', label: 'Рейтинг (0-5)', default: 4.8 },
    reviewCount: { type: 'number', label: 'Кол-во отзывов', default: 0 },
    image: { type: 'image', label: 'Изображение продукта', default: '' },
    ctaText: { type: 'text', label: 'Текст кнопки', default: 'Купить сейчас' },
    ctaUrl: { type: 'text', label: 'URL кнопки', default: '#' },
    secondaryCta: { type: 'text', label: 'Текст кнопки 2', default: 'Подробнее' },
    secondaryUrl: { type: 'text', label: 'URL кнопки 2', default: '#' },
    features: { type: 'array', label: 'Ключевые характеристики', default: [], stringArray: true },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['product', 'hero', 'ecommerce', 'price', 'rating', 'продукт', 'товар', 'цена'],
})

registerBlock({
  type: 'pricing-faq',
  label: 'Pricing FAQ',
  category: 'commerce',
  icon: '❓',
  description: 'FAQ специально для страницы цен — аккордеон с вопросами о тарифах',
  defaultData: {
    title: 'Частые вопросы о тарифах',
    faqs: [
      { question: 'Можно ли отменить подписку в любой момент?', answer: 'Да, вы можете отменить подписку в любой момент без штрафов и скрытых платежей.' },
      { question: 'Есть ли пробный период?', answer: 'Бесплатный тариф доступен бессрочно. Pro и Agency — 14 дней бесплатного пробного периода.' },
      { question: 'Что происходит с сайтом при отмене?', answer: 'Сайт переходит на бесплатный тариф с ограничениями. Все данные сохраняются.' },
      { question: 'Принимаете ли вы карты?', answer: 'Да, принимаем Visa, Mastercard, МИР и оплату через СБП.' },
    ],
    background: '#f8fafc',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Частые вопросы о тарифах' },
    faqs: { type: 'array', label: 'Вопросы и ответы', default: [] },
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['faq', 'pricing', 'questions', 'accordion', 'вопросы', 'цены', 'аккордеон'],
})

registerBlock({
  type: 'steps-numbered',
  label: 'Numbered Steps',
  category: 'content',
  icon: '🔢',
  description: 'Пронумерованные шаги с крупными цифрами и описаниями',
  defaultData: {
    title: 'Как это работает',
    subtitle: 'Три простых шага до готового сайта',
    steps: [
      { title: 'Выберите шаблон', text: 'Более 20 готовых шаблонов для любой ниши', icon: '🎨' },
      { title: 'Настройте дизайн', text: 'Измените цвета, шрифты, изображения без кода', icon: '⚙️' },
      { title: 'Опубликуйте', text: 'Один клик — и ваш сайт онлайн с SSL и CDN', icon: '🚀' },
    ],
    layout: 'horizontal',
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Как это работает' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    steps: { type: 'array', label: 'Шаги', default: [] },
    layout: { type: 'select', label: 'Макет', default: 'horizontal', options: [{ value: 'horizontal', label: 'Горизонтальный' }, { value: 'vertical', label: 'Вертикальный' }] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['steps', 'numbered', 'how', 'process', 'шаги', 'как', 'процесс'],
})

registerBlock({
  type: 'cta-banner',
  label: 'CTA Banner',
  category: 'interactive',
  icon: '📣',
  description: 'Широкий баннер с призывом к действию и фоновым изображением',
  defaultData: {
    title: 'Готовы начать?',
    subtitle: 'Запустите свой сайт за 10 минут. Бесплатно.',
    ctaText: 'Начать бесплатно',
    ctaUrl: '#',
    secondaryCta: 'Посмотреть примеры',
    secondaryUrl: '#',
    background: '#5b6af0',
    textColor: '#ffffff',
    backgroundImage: '',
    overlay: 0.6,
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Готовы начать?' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    ctaText: { type: 'text', label: 'Кнопка 1 текст', default: 'Начать бесплатно' },
    ctaUrl: { type: 'text', label: 'Кнопка 1 URL', default: '#' },
    secondaryCta: { type: 'text', label: 'Кнопка 2 текст', default: '' },
    secondaryUrl: { type: 'text', label: 'Кнопка 2 URL', default: '#' },
    backgroundImage: { type: 'image', label: 'Фоновое изображение', default: '' },
    overlay: { type: 'number', label: 'Затемнение (0-1)', default: 0.6 },
    background: { type: 'color', label: 'Цвет фона', default: '#5b6af0' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
  },
  tags: ['cta', 'banner', 'call-to-action', 'conversion', 'призыв', 'баннер', 'конверсия'],
})

registerBlock({
  type: 'blog-list',
  label: 'Blog List',
  category: 'content',
  icon: '📝',
  description: 'Список статей блога с тегами, датами и авторами',
  defaultData: {
    title: 'Блог',
    subtitle: 'Полезные статьи и советы',
    posts: [
      { title: '10 способов увеличить конверсию лендинга', date: '12 июл 2024', author: 'Анна К.', tag: 'Конверсия', image: '', url: '#', excerpt: 'Разбираем основные ошибки на лендинге и способы их исправить.' },
      { title: 'Как написать заголовок, который продаёт', date: '5 июл 2024', author: 'Дмитрий С.', tag: 'Копирайтинг', image: '', url: '#', excerpt: 'Формулы цепляющих заголовков с примерами из практики.' },
      { title: 'SEO для малого бизнеса: с чего начать', date: '28 июн 2024', author: 'Мария В.', tag: 'SEO', image: '', url: '#', excerpt: 'Минимальный набор SEO-действий который реально работает.' },
    ],
    columns: 3,
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Блог' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    posts: { type: 'array', label: 'Статьи', default: [] },
    columns: { type: 'number', label: 'Колонок', default: 3 },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['blog', 'articles', 'posts', 'list', 'блог', 'статьи', 'посты'],
})

registerBlock({
  type: 'pricing-table-v2',
  label: 'Pricing Table v2',
  category: 'commerce',
  icon: '📊',
  description: 'Детальная таблица сравнения тарифов по категориям функций',
  defaultData: {
    title: 'Подробное сравнение тарифов',
    plans: ['Старт', 'Про', 'Бизнес'],
    categories: [
      { name: 'Сайты', rows: [{ feature: 'Количество сайтов', values: ['1', '5', '∞'] }, { feature: 'Собственный домен', values: ['—', '✓', '✓'] }] },
      { name: 'Функции', rows: [{ feature: 'Блоков в наборе', values: ['20', '200+', '250+'] }, { feature: 'AI-генерация', values: ['—', '✓', '✓'] }] },
    ],
    highlight: 1,
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Подробное сравнение тарифов' },
    plans: { type: 'array', label: 'Тарифы (названия)', default: [], stringArray: true },
    categories: { type: 'array', label: 'Категории функций', default: [] },
    highlight: { type: 'number', label: 'Выделенный столбец (0-based)', default: 1 },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['pricing', 'table', 'comparison', 'features', 'тарифы', 'таблица', 'сравнение'],
})

registerBlock({
  type: 'cookie-banner',
  label: 'Cookie Consent Banner',
  category: 'interactive',
  icon: '🍪',
  description: 'Всплывающий баннер согласия на cookies с кнопками принять/отклонить',
  defaultData: {
    title: 'Мы используем cookies',
    text: 'Мы используем файлы cookie для улучшения работы сайта, анализа трафика и персонализации контента.',
    acceptText: 'Принять все',
    declineText: 'Только необходимые',
    policyText: 'Политика конфиденциальности',
    policyUrl: '#',
    position: 'bottom',
    background: '#1e293b',
    textColor: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Мы используем cookies' },
    text: { type: 'text', label: 'Текст', default: 'Мы используем файлы cookie для улучшения работы сайта.' },
    acceptText: { type: 'text', label: 'Кнопка принять', default: 'Принять все' },
    declineText: { type: 'text', label: 'Кнопка отклонить', default: 'Только необходимые' },
    policyText: { type: 'text', label: 'Текст ссылки', default: 'Политика конфиденциальности' },
    policyUrl: { type: 'text', label: 'URL политики', default: '#' },
    position: { type: 'select', label: 'Позиция', default: 'bottom', options: [{ value: 'bottom', label: 'Снизу' }, { value: 'top', label: 'Сверху' }] },
    background: { type: 'color', label: 'Фон', default: '#1e293b' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['cookie', 'consent', 'gdpr', 'banner', 'cookies', 'согласие', 'баннер'],
})

registerBlock({
  type: 'scroll-progress',
  label: 'Scroll Progress Bar',
  category: 'interactive',
  icon: '📏',
  description: 'Прогресс-бар прокрутки страницы в шапке',
  defaultData: {
    height: 4,
    color: '#5b6af0',
    position: 'top',
    zIndex: 9999,
  },
  schema: {
    height: { type: 'number', label: 'Высота (px)', default: 4 },
    color: { type: 'color', label: 'Цвет полосы', default: '#5b6af0' },
    position: { type: 'select', label: 'Позиция', default: 'top', options: [{ value: 'top', label: 'Вверху' }, { value: 'bottom', label: 'Внизу' }] },
  },
  tags: ['scroll', 'progress', 'bar', 'reading', 'прокрутка', 'прогресс', 'чтение'],
})

registerBlock({
  type: 'image-gallery-v2',
  label: 'Image Gallery v2',
  category: 'gallery',
  icon: '🖼️',
  description: 'Галерея с лайтбоксом — клик открывает фото на весь экран',
  defaultData: {
    title: 'Галерея',
    images: [
      { src: '', alt: 'Фото 1', caption: '' },
      { src: '', alt: 'Фото 2', caption: '' },
      { src: '', alt: 'Фото 3', caption: '' },
      { src: '', alt: 'Фото 4', caption: '' },
    ],
    columns: 3,
    gap: 12,
    borderRadius: 12,
    background: '#ffffff',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Галерея' },
    images: { type: 'array', label: 'Изображения', default: [] },
    columns: { type: 'number', label: 'Колонок', default: 3 },
    gap: { type: 'number', label: 'Отступ (px)', default: 12 },
    borderRadius: { type: 'number', label: 'Скругление (px)', default: 12 },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
  },
  tags: ['gallery', 'lightbox', 'images', 'photos', 'галерея', 'лайтбокс', 'фото'],
})

registerBlock({
  type: 'number-features',
  label: 'Number Features',
  category: 'content',
  icon: '🔢',
  description: 'Карточки с большими числами и описаниями достижений/статистики',
  defaultData: {
    title: 'Результаты говорят сами за себя',
    items: [
      { number: '10K+', label: 'Активных пользователей', icon: '👥' },
      { number: '98%', label: 'Довольных клиентов', icon: '⭐' },
      { number: '2M+', label: 'Созданных страниц', icon: '📄' },
      { number: '50+', label: 'Стран присутствия', icon: '🌍' },
    ],
    background: '#0f172a',
    textColor: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Результаты говорят сами за себя' },
    items: { type: 'array', label: 'Элементы', default: [] },
    background: { type: 'color', label: 'Фон', default: '#0f172a' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['numbers', 'stats', 'achievements', 'results', 'числа', 'статистика', 'достижения'],
})

registerBlock({
  type: 'product-reviews',
  label: 'Product Reviews',
  category: 'content',
  icon: '⭐',
  description: 'Отзывы с рейтингом и разбивкой по звёздам',
  defaultData: {
    title: 'Отзывы покупателей',
    averageRating: 4.7,
    totalReviews: 1842,
    breakdown: [
      { stars: 5, count: 1320 },
      { stars: 4, count: 310 },
      { stars: 3, count: 140 },
      { stars: 2, count: 42 },
      { stars: 1, count: 30 },
    ],
    reviews: [
      { name: 'Александр М.', rating: 5, date: '10 июл 2024', text: 'Отличный продукт! Пользуюсь уже 3 месяца, никаких нареканий.', verified: true },
      { name: 'Елена К.', rating: 5, date: '5 июл 2024', text: 'Очень довольна покупкой. Качество на высоком уровне, доставка быстрая.', verified: true },
      { name: 'Дмитрий В.', rating: 4, date: '28 июн 2024', text: 'Хороший товар, но упаковка могла быть лучше. В целом рекомендую.', verified: false },
    ],
    background: '#ffffff',
    accentColor: '#f59e0b',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Отзывы покупателей' },
    averageRating: { type: 'number', label: 'Средний рейтинг', default: 4.7 },
    totalReviews: { type: 'number', label: 'Всего отзывов', default: 0 },
    reviews: { type: 'array', label: 'Отзывы', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Цвет звёзд', default: '#f59e0b' },
  },
  tags: ['reviews', 'rating', 'stars', 'testimonials', 'отзывы', 'рейтинг', 'звёзды'],
})

registerBlock({
  type: 'promo-code',
  label: 'Promo Code Display',
  category: 'interactive',
  icon: '🎟️',
  description: 'Блок промокода с кнопкой копирования и таймером',
  defaultData: {
    title: 'Ваш промокод',
    subtitle: 'Введите при оформлении заказа',
    code: 'SAVE20',
    discount: 'Скидка 20%',
    expiresLabel: 'Предложение действует до',
    expiresDate: '',
    ctaText: 'Использовать',
    ctaUrl: '#',
    background: '#f0f4ff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Ваш промокод' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: 'Введите при оформлении заказа' },
    code: { type: 'text', label: 'Промокод', default: 'SAVE20' },
    discount: { type: 'text', label: 'Описание скидки', default: 'Скидка 20%' },
    expiresDate: { type: 'text', label: 'Дата окончания (ISO)', default: '' },
    ctaText: { type: 'text', label: 'Текст кнопки', default: 'Использовать' },
    ctaUrl: { type: 'text', label: 'URL кнопки', default: '#' },
    background: { type: 'color', label: 'Фон', default: '#f0f4ff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['promo', 'code', 'coupon', 'discount', 'промокод', 'купон', 'скидка'],
})

registerBlock({
  type: 'editorial-columns',
  label: 'Editorial Columns',
  category: 'text',
  icon: '📰',
  description: 'Газетная вёрстка с несколькими текстовыми колонками',
  defaultData: {
    columns: [
      { heading: 'Вступление', text: 'Первый абзац задаёт тон всему материалу. Здесь вы привлекаете читателя и обозначаете тему, которой посвящён текст.' },
      { heading: 'Основная часть', text: 'Раскрывайте детали, приводите примеры и аргументы. Структурированный текст читается легче и воспринимается лучше.' },
      { heading: 'Заключение', text: 'Резюмируйте ключевые мысли и призовите читателя к действию. Хорошее заключение запоминается надолго.' },
    ],
    numColumns: 3,
    showHeadings: true,
    background: '#ffffff',
    textColor: '#1a1a1a',
    accentColor: '#5b6af0',
  },
  schema: {
    columns: { type: 'array', label: 'Колонки', default: [] },
    numColumns: { type: 'number', label: 'Число колонок', default: 3 },
    showHeadings: { type: 'boolean', label: 'Показывать заголовки колонок', default: true },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#1a1a1a' },
    accentColor: { type: 'color', label: 'Цвет заголовков', default: '#5b6af0' },
  },
  tags: ['editorial', 'columns', 'newspaper', 'text', 'колонки', 'редакторская', 'газета'],
})

registerBlock({
  type: 'kpi-dashboard',
  label: 'KPI Dashboard',
  category: 'content',
  icon: '📈',
  description: 'Дашборд KPI с метриками и трендами',
  defaultData: {
    title: 'Ключевые показатели',
    subtitle: 'Результаты за последний квартал',
    metrics: [
      { label: 'Выручка', value: '12.4M ₽', change: '+24%', trend: 'up', icon: '💰' },
      { label: 'Новые клиенты', value: '1 842', change: '+18%', trend: 'up', icon: '👥' },
      { label: 'Конверсия', value: '3.7%', change: '+0.4%', trend: 'up', icon: '🎯' },
      { label: 'Отток', value: '2.1%', change: '-0.3%', trend: 'down', icon: '📉' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Ключевые показатели' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    metrics: { type: 'array', label: 'Метрики', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['kpi', 'dashboard', 'metrics', 'analytics', 'кпи', 'дашборд', 'метрики', 'аналитика'],
})

registerBlock({
  type: 'careers-culture',
  label: 'Careers & Culture',
  category: 'content',
  icon: '🏢',
  description: 'Секция карьеры: ценности компании + вакансии',
  defaultData: {
    title: 'Присоединяйтесь к нашей команде',
    subtitle: 'Мы создаём продукт, который меняет жизни миллионов людей',
    values: [
      { icon: '🚀', title: 'Амбиции', text: 'Мы думаем масштабно и не боимся брать большие задачи' },
      { icon: '🤝', title: 'Доверие', text: 'Открытое общение и взаимное уважение — основа нашей работы' },
      { icon: '🌱', title: 'Рост', text: 'Каждый участник команды развивается вместе с компанией' },
    ],
    perks: ['Удалённая работа', 'ДМС с первого дня', 'Бюджет на обучение', 'Stock options', 'Гибкий график'],
    vacancies: [
      { title: 'Senior Frontend Engineer', department: 'Engineering', location: 'Remote', type: 'Full-time', url: '#' },
      { title: 'Product Designer', department: 'Design', location: 'Москва', type: 'Full-time', url: '#' },
      { title: 'Growth Marketer', department: 'Marketing', location: 'Remote', type: 'Full-time', url: '#' },
    ],
    ctaText: 'Смотреть все вакансии',
    ctaUrl: '#',
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Присоединяйтесь к нашей команде' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    values: { type: 'array', label: 'Ценности', default: [] },
    perks: { type: 'array', label: 'Плюшки (список)', default: [], stringArray: true },
    vacancies: { type: 'array', label: 'Вакансии', default: [] },
    ctaText: { type: 'text', label: 'Текст кнопки', default: 'Смотреть все вакансии' },
    ctaUrl: { type: 'text', label: 'URL кнопки', default: '#' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['careers', 'jobs', 'culture', 'team', 'вакансии', 'карьера', 'команда', 'культура'],
})

registerBlock({
  type: 'resource-library',
  label: 'Resource Library',
  category: 'content',
  icon: '📚',
  description: 'Библиотека ресурсов: гайды, шаблоны, видео с фильтром по категории',
  defaultData: {
    title: 'Библиотека ресурсов',
    subtitle: 'Всё что нужно для работы — в одном месте',
    categories: ['Все', 'Гайды', 'Шаблоны', 'Видео', 'Чеклисты'],
    resources: [
      { title: 'Как увеличить конверсию лендинга', type: 'Гайд', icon: '📖', url: '#', category: 'Гайды', desc: '12 страниц с примерами и разбором' },
      { title: 'Шаблон контент-плана', type: 'Шаблон', icon: '📋', url: '#', category: 'Шаблоны', desc: 'Excel-таблица на 3 месяца' },
      { title: 'SEO для начинающих', type: 'Видео', icon: '▶️', url: '#', category: 'Видео', desc: '45 минут, всё с нуля' },
      { title: 'Чеклист запуска сайта', type: 'Чеклист', icon: '✅', url: '#', category: 'Чеклисты', desc: '47 пунктов для идеального запуска' },
    ],
    background: '#f8fafc',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Библиотека ресурсов' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    categories: { type: 'array', label: 'Категории', default: [], stringArray: true },
    resources: { type: 'array', label: 'Ресурсы', default: [] },
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['resources', 'library', 'guides', 'templates', 'ресурсы', 'библиотека', 'гайды'],
})

registerBlock({
  type: 'split-testimonial',
  label: 'Split Testimonial',
  category: 'content',
  icon: '💬',
  description: 'Крупный одиночный отзыв с фото клиента и результатами',
  defaultData: {
    quote: 'За первые три месяца работы с этим сервисом наша выручка выросла на 47%. Команда поддержки реагирует мгновенно, а продукт делает именно то, что обещает.',
    name: 'Александр Петров',
    role: 'CEO',
    company: 'GrowthLab',
    photo: '',
    stats: [
      { value: '+47%', label: 'рост выручки' },
      { value: '3x', label: 'ROI за квартал' },
      { value: '98%', label: 'удовлетворённость' },
    ],
    background: '#0f172a',
    textColor: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    quote: { type: 'text', label: 'Цитата', default: 'За первые три месяца...' },
    name: { type: 'text', label: 'Имя', default: 'Александр Петров' },
    role: { type: 'text', label: 'Должность', default: 'CEO' },
    company: { type: 'text', label: 'Компания', default: '' },
    photo: { type: 'image', label: 'Фото', default: '' },
    stats: { type: 'array', label: 'Статистика', default: [] },
    background: { type: 'color', label: 'Фон', default: '#0f172a' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['testimonial', 'quote', 'social-proof', 'отзыв', 'цитата', 'клиент'],
})

registerBlock({
  type: 'dark-feature-grid',
  label: 'Dark Feature Grid',
  category: 'content',
  icon: '✨',
  description: 'Тёмная сетка фич с глоу-иконками и описаниями',
  defaultData: {
    title: 'Всё что нужно для победы',
    subtitle: 'Каждая функция создана с одной целью — ваш результат',
    features: [
      { icon: '⚡', title: 'Молниеносная скорость', text: 'Lighthouse 90+ на каждом сайте по умолчанию' },
      { icon: '🤖', title: 'AI-помощник', text: 'Создаёт тексты, изображения и целые сайты' },
      { icon: '🔒', title: 'Безопасность', text: 'SSL, DDOS-защита, двухфакторная аутентификация' },
      { icon: '📊', title: 'Аналитика', text: 'Тепловые карты, воронки, сессионные записи' },
      { icon: '🌐', title: 'Мультиязычность', text: 'Переводы на 15 языков одной кнопкой' },
      { icon: '🔗', title: 'Интеграции', text: 'Более 50 сервисов из коробки' },
    ],
    columns: 3,
    background: '#0a0f1e',
    textColor: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Всё что нужно для победы' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    features: { type: 'array', label: 'Функции', default: [] },
    columns: { type: 'number', label: 'Колонок', default: 3 },
    background: { type: 'color', label: 'Фон', default: '#0a0f1e' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['dark', 'features', 'grid', 'glow', 'тёмный', 'функции', 'сетка'],
})

registerBlock({
  type: 'cta-countdown',
  label: 'CTA with Countdown',
  category: 'interactive',
  icon: '⏱️',
  description: 'CTA-секция с обратным отсчётом для создания срочности',
  defaultData: {
    title: 'Акция заканчивается через',
    subtitle: 'Успейте воспользоваться специальным предложением',
    endDate: '',
    labels: { days: 'дней', hours: 'часов', minutes: 'минут', seconds: 'секунд' },
    ctaText: 'Забрать скидку',
    ctaUrl: '#',
    badge: 'Скидка 30%',
    background: '#0f172a',
    textColor: '#ffffff',
    accentColor: '#ef4444',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Акция заканчивается через' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    endDate: { type: 'text', label: 'Дата окончания (ISO)', default: '' },
    ctaText: { type: 'text', label: 'Текст кнопки', default: 'Забрать скидку' },
    ctaUrl: { type: 'text', label: 'URL кнопки', default: '#' },
    badge: { type: 'text', label: 'Бейдж скидки', default: '' },
    background: { type: 'color', label: 'Фон', default: '#0f172a' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#ef4444' },
  },
  tags: ['cta', 'countdown', 'urgency', 'timer', 'кта', 'таймер', 'срочность', 'отсчёт'],
})

registerBlock({
  type: 'service-packages',
  label: 'Service Packages',
  category: 'commerce',
  icon: '📦',
  description: 'Пакеты услуг в горизонтальных карточках',
  defaultData: {
    title: 'Выберите пакет услуг',
    subtitle: 'Подберём решение под любой бюджет и задачу',
    packages: [
      { name: 'Базовый', icon: '🌱', price: '15 000 ₽', duration: 'разово', description: 'Идеально для старта', features: ['Аудит текущего сайта', '3 правки дизайна', 'Базовая SEO-оптимизация', '1 месяц поддержки'], highlighted: false, cta: 'Выбрать', url: '#' },
      { name: 'Стандарт', icon: '🚀', price: '35 000 ₽', duration: 'разово', description: 'Для растущего бизнеса', features: ['Всё из Базового', 'Разработка с нуля', 'AI-копирайтинг', '3 месяца поддержки', 'Аналитика и воронки'], highlighted: true, cta: 'Выбрать', url: '#' },
      { name: 'Премиум', icon: '💎', price: 'от 80 000 ₽', duration: 'проект', description: 'Комплексное решение', features: ['Всё из Стандарта', 'Кастомные блоки', 'CRM-интеграция', '12 месяцев поддержки', 'Персональный менеджер'], highlighted: false, cta: 'Обсудить', url: '#' },
    ],
    background: '#f8fafc',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Выберите пакет услуг' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    packages: { type: 'array', label: 'Пакеты', default: [] },
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['services', 'packages', 'pricing', 'услуги', 'пакеты', 'тарифы'],
})

registerBlock({
  type: 'benefits-checklist',
  label: 'Benefits Checklist',
  category: 'content',
  icon: '✅',
  description: 'Сравнение с конкурентами: «Мы» vs «Другие» в два столбца',
  defaultData: {
    title: 'Почему выбирают нас',
    ourLabel: 'Мы',
    theirLabel: 'Конкуренты',
    items: [
      { text: 'Бесплатный период 14 дней', ours: true, theirs: false },
      { text: 'Поддержка 24/7', ours: true, theirs: false },
      { text: 'Без скрытых платежей', ours: true, theirs: false },
      { text: 'AI-инструменты в базовом тарифе', ours: true, theirs: false },
      { text: 'Настройка за 10 минут', ours: true, theirs: true },
      { text: 'Интеграция с 50+ сервисами', ours: true, theirs: false },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Почему выбирают нас' },
    ourLabel: { type: 'text', label: 'Наш столбец', default: 'Мы' },
    theirLabel: { type: 'text', label: 'Чужой столбец', default: 'Конкуренты' },
    items: { type: 'array', label: 'Пункты сравнения', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['checklist', 'comparison', 'benefits', 'vs', 'чеклист', 'сравнение', 'преимущества'],
})

registerBlock({
  type: 'api-preview',
  label: 'API Code Preview',
  category: 'content',
  icon: '🔌',
  description: 'Секция с примером API-запроса и ответа для tech-компаний',
  defaultData: {
    title: 'Простая интеграция за 5 минут',
    subtitle: 'REST API с документацией, SDK для популярных языков и Webhook-уведомления',
    language: 'curl',
    requestCode: 'curl -X POST https://api.example.com/v1/analyze \\\n  -H "Authorization: Bearer YOUR_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"url": "https://your-site.com", "depth": 3}\'',
    responseCode: '{\n  "status": "success",\n  "score": 94,\n  "issues": [],\n  "suggestions": 3,\n  "processed_at": "2024-07-10T12:00:00Z"\n}',
    features: ['REST API', 'WebSockets', 'Webhooks', 'SDK: JS, Python, Go'],
    docsText: 'Читать документацию',
    docsUrl: '#',
    background: '#0f172a',
    textColor: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Простая интеграция за 5 минут' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    requestCode: { type: 'text', label: 'Код запроса', default: '' },
    responseCode: { type: 'text', label: 'Код ответа', default: '' },
    features: { type: 'array', label: 'Особенности API', default: [], stringArray: true },
    docsText: { type: 'text', label: 'Текст ссылки на docs', default: 'Документация' },
    docsUrl: { type: 'text', label: 'URL документации', default: '#' },
    background: { type: 'color', label: 'Фон', default: '#0f172a' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['api', 'code', 'developer', 'integration', 'апи', 'код', 'разработчики', 'интеграция'],
})

registerBlock({
  type: 'notification-feed',
  label: 'Activity Feed',
  category: 'content',
  icon: '🔔',
  description: 'Лента активности / уведомлений — для SaaS-дашбордов',
  defaultData: {
    title: 'Последние события',
    events: [
      { icon: '🛒', text: 'Новый заказ #1284 от Александра М. на сумму 4 590 ₽', time: '2 мин назад', type: 'order' },
      { icon: '✉️', text: 'Новая заявка с сайта «О компании»', time: '15 мин назад', type: 'lead' },
      { icon: '⭐', text: 'Новый отзыв: 5 звёзд от Елены К.', time: '1 час назад', type: 'review' },
      { icon: '🚀', text: 'Сайт «Лендинг» опубликован успешно', time: '3 часа назад', type: 'publish' },
      { icon: '👤', text: 'Новая регистрация: mikhail@example.com', time: '5 часов назад', type: 'user' },
    ],
    maxItems: 5,
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Последние события' },
    events: { type: 'array', label: 'События', default: [] },
    maxItems: { type: 'number', label: 'Макс. событий', default: 5 },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['activity', 'feed', 'notifications', 'events', 'активность', 'уведомления', 'события'],
})

registerBlock({
  type: 'photo-masonry',
  label: 'Photo Masonry',
  category: 'gallery',
  icon: '🧱',
  description: 'Масонри-сетка фотографий разной высоты',
  defaultData: {
    title: 'Наши работы',
    images: [
      { src: '', alt: '', caption: '' },
      { src: '', alt: '', caption: '' },
      { src: '', alt: '', caption: '' },
      { src: '', alt: '', caption: '' },
      { src: '', alt: '', caption: '' },
      { src: '', alt: '', caption: '' },
    ],
    columns: 3,
    gap: 12,
    background: '#ffffff',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наши работы' },
    images: { type: 'array', label: 'Изображения', default: [] },
    columns: { type: 'number', label: 'Колонок', default: 3 },
    gap: { type: 'number', label: 'Отступ (px)', default: 12 },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
  },
  tags: ['masonry', 'gallery', 'photos', 'grid', 'масонри', 'галерея', 'фото', 'сетка'],
})

registerBlock({
  type: 'milestone-roadmap',
  label: 'Milestone Roadmap',
  category: 'content',
  icon: '🗺️',
  description: 'Горизонтальный роадмап компании / продукта с чекбоксами',
  defaultData: {
    title: 'Дорожная карта',
    subtitle: 'Что уже сделано и что ждёт впереди',
    milestones: [
      { quarter: 'Q1 2024', title: 'MVP запуск', desc: 'Базовый редактор и первые 100 пользователей', done: true },
      { quarter: 'Q2 2024', title: 'E-commerce', desc: 'Полный интернет-магазин с оплатой', done: true },
      { quarter: 'Q3 2024', title: 'AI-генерация', desc: 'Сайт из промпта за 30 секунд', done: true },
      { quarter: 'Q4 2024', title: 'Мобильное приложение', desc: 'iOS и Android редактор', done: false },
      { quarter: 'Q1 2025', title: 'Marketplace', desc: '500+ блоков от сообщества', done: false },
    ],
    accentColor: '#5b6af0',
    background: '#ffffff',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Дорожная карта' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    milestones: { type: 'array', label: 'Этапы', default: [] },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
  },
  tags: ['roadmap', 'milestones', 'timeline', 'роадмап', 'этапы', 'шкала', 'план'],
})

registerBlock({
  type: 'loyalty-program',
  label: 'Loyalty Program',
  category: 'commerce',
  icon: '🏅',
  description: 'Программа лояльности с уровнями и привилегиями',
  defaultData: {
    title: 'Программа лояльности',
    subtitle: 'Копите баллы и получайте привилегии',
    tiers: [
      { name: 'Серебро', icon: '🥈', points: '0–999 баллов', benefits: ['Скидка 5%', 'Приоритетная поддержка', 'Ранний доступ к акциям'], color: '#9ca3af' },
      { name: 'Золото', icon: '🥇', points: '1000–4999 баллов', benefits: ['Скидка 10%', 'Бесплатная доставка', 'Персональный менеджер', 'Закрытые распродажи'], color: '#f59e0b' },
      { name: 'Платина', icon: '💎', points: 'от 5000 баллов', benefits: ['Скидка 20%', 'VIP-доставка', 'Эксклюзивные товары', 'Подарок в день рождения', 'Доступ к Platinum-клубу'], color: '#818cf8' },
    ],
    ctaText: 'Начать копить баллы',
    ctaUrl: '#',
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Программа лояльности' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    tiers: { type: 'array', label: 'Уровни', default: [] },
    ctaText: { type: 'text', label: 'Текст кнопки', default: 'Начать копить баллы' },
    ctaUrl: { type: 'text', label: 'URL кнопки', default: '#' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['loyalty', 'rewards', 'program', 'points', 'лояльность', 'баллы', 'программа'],
})

registerBlock({
  type: 'embed-iframe',
  label: 'Embed / iFrame',
  category: 'embed',
  icon: '🪟',
  description: 'Встраивание любого внешнего виджета через iframe',
  defaultData: {
    title: '',
    url: 'https://www.openstreetmap.org/export/embed.html?bbox=37.5%2C55.7%2C37.7%2C55.8&layer=mapnik',
    height: 450,
    allowFullscreen: true,
    sandbox: 'allow-scripts allow-same-origin allow-popups allow-forms',
    background: '#ffffff',
  },
  schema: {
    title: { type: 'text', label: 'Подпись (необязательно)', default: '' },
    url: { type: 'text', label: 'URL для встраивания', default: '' },
    height: { type: 'number', label: 'Высота (px)', default: 450 },
    allowFullscreen: { type: 'boolean', label: 'Разрешить полный экран', default: true },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
  },
  tags: ['embed', 'iframe', 'widget', 'external', 'встраивание', 'виджет', 'внешний'],
})

registerBlock({
  type: 'partner-logos',
  label: 'Partner Logos Grid',
  category: 'content',
  icon: '🤝',
  description: 'Статичная сетка логотипов партнёров с ссылками',
  defaultData: {
    title: 'Наши партнёры',
    subtitle: '',
    partners: [
      { name: 'Google', logo: '', url: '#' },
      { name: 'Microsoft', logo: '', url: '#' },
      { name: 'AWS', logo: '', url: '#' },
      { name: 'Stripe', logo: '', url: '#' },
      { name: 'Vercel', logo: '', url: '#' },
      { name: 'Cloudflare', logo: '', url: '#' },
    ],
    columns: 6,
    background: '#ffffff',
    grayscale: true,
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наши партнёры' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    partners: { type: 'array', label: 'Партнёры', default: [] },
    columns: { type: 'number', label: 'Колонок', default: 6 },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
  },
  tags: ['partners', 'logos', 'brands', 'grid', 'партнёры', 'логотипы', 'сетка'],
})

registerBlock({
  type: 'photo-hero-split',
  label: 'Photo Hero Split',
  category: 'hero',
  icon: '🖼️',
  description: 'Половина экрана — фото, половина — текст и кнопки',
  defaultData: {
    title: 'Создайте сайт который работает',
    subtitle: 'Профессиональный дизайн, молниеносная скорость и мощные инструменты — всё в одном месте',
    image: '',
    imagePosition: 'right',
    ctaText: 'Начать бесплатно',
    ctaUrl: '#',
    secondaryCta: 'Посмотреть примеры',
    secondaryUrl: '#',
    features: ['Без кода', 'Запуск за 10 минут', 'Lighthouse 90+'],
    background: '#ffffff',
    textBackground: '#f8fafc',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Создайте сайт который работает' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    image: { type: 'image', label: 'Изображение', default: '' },
    imagePosition: { type: 'select', label: 'Фото', default: 'right', options: [{ value: 'left', label: 'Слева' }, { value: 'right', label: 'Справа' }] },
    ctaText: { type: 'text', label: 'Кнопка 1', default: 'Начать бесплатно' },
    ctaUrl: { type: 'text', label: 'URL кнопки 1', default: '#' },
    secondaryCta: { type: 'text', label: 'Кнопка 2', default: '' },
    secondaryUrl: { type: 'text', label: 'URL кнопки 2', default: '#' },
    features: { type: 'array', label: 'Фишки под кнопками', default: [], stringArray: true },
    background: { type: 'color', label: 'Фон текстовой части', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['hero', 'split', 'photo', 'image', 'геро', 'разделённый', 'фото'],
})

registerBlock({
  type: 'glossary',
  label: 'Glossary / Dictionary',
  category: 'content',
  icon: '📖',
  description: 'Алфавитный глоссарий терминов / словарь',
  defaultData: {
    title: 'Глоссарий',
    subtitle: 'Все ключевые термины в одном месте',
    terms: [
      { term: 'API', definition: 'Application Programming Interface — набор протоколов для взаимодействия между приложениями' },
      { term: 'CMS', definition: 'Content Management System — система управления контентом сайта' },
      { term: 'SEO', definition: 'Search Engine Optimization — оптимизация сайта для поисковых систем' },
      { term: 'SaaS', definition: 'Software as a Service — программное обеспечение как услуга по подписке' },
      { term: 'UI/UX', definition: 'User Interface / User Experience — дизайн интерфейса и пользовательский опыт' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Глоссарий' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    terms: { type: 'array', label: 'Термины', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['glossary', 'dictionary', 'terms', 'definitions', 'глоссарий', 'термины', 'словарь'],
})

registerBlock({
  type: 'faq-accordion-v2',
  label: 'FAQ с поиском',
  category: 'content',
  icon: '🔍',
  description: 'Аккордеон FAQ с поиском по вопросам',
  defaultData: {
    title: 'Часто задаваемые вопросы',
    subtitle: 'Найдите ответ на свой вопрос',
    showSearch: true,
    searchPlaceholder: 'Поиск по вопросам…',
    items: [
      { question: 'Как начать использовать продукт?', answer: 'Просто зарегистрируйтесь на нашем сайте и следуйте инструкциям по началу работы.' },
      { question: 'Есть ли бесплатный тариф?', answer: 'Да, у нас есть бесплатный план с основными функциями без ограничений по времени.' },
      { question: 'Как отменить подписку?', answer: 'Вы можете отменить подписку в любое время в настройках вашего аккаунта.' },
      { question: 'Предоставляете ли вы техподдержку?', answer: 'Да, мы предоставляем поддержку по email и в чате для всех пользователей.' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Часто задаваемые вопросы' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    showSearch: { type: 'boolean', label: 'Показать поиск', default: true },
    searchPlaceholder: { type: 'text', label: 'Плейсхолдер поиска', default: 'Поиск по вопросам…' },
    items: { type: 'array', label: 'Вопросы и ответы', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['faq', 'accordion', 'search', 'questions', 'answers', 'вопросы', 'ответы', 'поиск'],
})

registerBlock({
  type: 'team-grid-v2',
  label: 'Команда v2',
  category: 'content',
  icon: '👥',
  description: 'Сетка команды с соцсетями при наведении',
  defaultData: {
    title: 'Наша команда',
    subtitle: 'Люди, которые делают это возможным',
    columns: 3,
    members: [
      { name: 'Алексей Петров', role: 'CEO & Основатель', bio: 'Более 10 лет опыта в отрасли. Строит команды мечты.', linkedin: '', twitter: '', github: '' },
      { name: 'Мария Иванова', role: 'CTO', bio: 'Эксперт по архитектуре и масштабированию систем.', linkedin: '', twitter: '', github: '' },
      { name: 'Дмитрий Смирнов', role: 'Head of Design', bio: 'Создаёт интерфейсы, которые влюбляют пользователей.', linkedin: '', twitter: '', github: '' },
    ],
    background: '#f9fafb',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наша команда' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    columns: { type: 'select', label: 'Столбцы', default: '3', options: [{ value: '2', label: '2 столбца' }, { value: '3', label: '3 столбца' }, { value: '4', label: '4 столбца' }] },
    members: { type: 'array', label: 'Участники команды', default: [] },
    background: { type: 'color', label: 'Фон', default: '#f9fafb' },
  },
  tags: ['team', 'staff', 'members', 'people', 'social', 'linkedin', 'команда', 'сотрудники'],
})

registerBlock({
  type: 'pricing-comparison',
  label: 'Сравнение тарифов',
  category: 'commerce',
  icon: '💎',
  description: 'Карточки тарифов с выделенным лучшим планом',
  defaultData: {
    title: 'Выберите свой тариф',
    subtitle: 'Начните бесплатно, масштабируйтесь по мере роста',
    plans: [
      { name: 'Starter', price: 'Бесплатно', period: 'мес', desc: 'Для старта.', features: ['1 проект', '5 GB хранилища', 'Email поддержка'], highlighted: false, cta: 'Начать бесплатно', ctaUrl: '#' },
      { name: 'Pro', price: '990 ₽', period: 'мес', desc: 'Для профессионалов.', features: ['10 проектов', '50 GB хранилища', 'Приоритетная поддержка', 'API доступ'], highlighted: true, cta: 'Попробовать Pro', ctaUrl: '#' },
      { name: 'Enterprise', price: '4 990 ₽', period: 'мес', desc: 'Для команд.', features: ['Неограниченные проекты', '500 GB хранилища', 'SLA поддержка', 'SSO и аудит'], highlighted: false, cta: 'Связаться', ctaUrl: '#' },
    ],
    background: '#f9fafb',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Выберите свой тариф' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    plans: { type: 'array', label: 'Тарифные планы', default: [] },
    background: { type: 'color', label: 'Фон', default: '#f9fafb' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['pricing', 'plans', 'comparison', 'cards', 'тарифы', 'сравнение', 'цены'],
})

registerBlock({
  type: 'hero-split-v2',
  label: 'Герой Split v2',
  category: 'hero',
  icon: '⚡',
  description: 'Разделённый герой с анимацией изображения',
  defaultData: {
    badge: 'Новый продукт 2025',
    title: 'Создайте что-то невероятное',
    subtitle: 'Современный инструмент для профессионалов. Запустите свой проект быстрее, чем когда-либо.',
    primaryCta: 'Начать бесплатно',
    primaryCtaUrl: '#',
    secondaryCta: 'Смотреть видео',
    secondaryCtaUrl: '#',
    image: '',
    imagePosition: 'right',
    background: '#0f172a',
    accentColor: '#5b6af0',
  },
  schema: {
    badge: { type: 'text', label: 'Бейдж', default: '' },
    title: { type: 'text', label: 'Заголовок', default: 'Заголовок героя' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    primaryCta: { type: 'text', label: 'Кнопка 1', default: 'Начать' },
    primaryCtaUrl: { type: 'text', label: 'URL кнопки 1', default: '#' },
    secondaryCta: { type: 'text', label: 'Кнопка 2', default: '' },
    secondaryCtaUrl: { type: 'text', label: 'URL кнопки 2', default: '#' },
    image: { type: 'image', label: 'Изображение', default: '' },
    imagePosition: { type: 'select', label: 'Позиция изображения', default: 'right', options: [{ value: 'left', label: 'Слева' }, { value: 'right', label: 'Справа' }] },
    background: { type: 'color', label: 'Фон', default: '#0f172a' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['hero', 'split', 'animated', 'full', 'герой', 'главный экран', 'анимация'],
})

registerBlock({
  type: 'cta-with-form',
  label: 'CTA с формой подписки',
  category: 'form',
  icon: '📧',
  description: 'Блок призыва к действию со встроенной формой email',
  defaultData: {
    title: 'Получайте обновления первыми',
    subtitle: 'Подпишитесь на нашу рассылку и узнавайте о новостях первыми.',
    placeholder: 'Введите ваш email',
    buttonText: 'Подписаться',
    successMessage: 'Спасибо! Вы успешно подписались.',
    background: '#5b6af0',
    textColor: '#ffffff',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Получайте обновления первыми' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    placeholder: { type: 'text', label: 'Плейсхолдер поля', default: 'Введите ваш email' },
    buttonText: { type: 'text', label: 'Текст кнопки', default: 'Подписаться' },
    successMessage: { type: 'text', label: 'Сообщение успеха', default: 'Спасибо! Вы успешно подписались.' },
    background: { type: 'color', label: 'Фон', default: '#5b6af0' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
  },
  tags: ['cta', 'email', 'subscribe', 'form', 'newsletter', 'форма', 'подписка', 'рассылка'],
})

registerBlock({
  type: 'feature-tabs',
  label: 'Функции с вкладками',
  category: 'content',
  icon: '🗂️',
  description: 'Вкладки для демонстрации функций продукта',
  defaultData: {
    title: 'Всё что вам нужно',
    subtitle: 'Изучите ключевые возможности нашего продукта',
    tabs: [
      { label: 'Дизайн', icon: '🎨', title: 'Мощный дизайн-инструмент', desc: 'Создавайте красивые интерфейсы без знания кода. Готовые компоненты, темы и шаблоны.', features: ['Drag & drop редактор', '200+ готовых компонентов', 'Адаптивный дизайн'], image: '' },
      { label: 'Аналитика', icon: '📊', title: 'Глубокая аналитика', desc: 'Понимайте своих пользователей с помощью детальных отчётов и визуализаций.', features: ['Real-time данные', 'Воронки конверсий', 'A/B тестирование'], image: '' },
      { label: 'Интеграции', icon: '🔗', title: 'Сотни интеграций', desc: 'Подключите любой инструмент, которым пользуетесь. API-первый подход.', features: ['REST API', 'Webhooks', 'Zapier/Make'], image: '' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Всё что вам нужно' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    tabs: { type: 'array', label: 'Вкладки', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['tabs', 'features', 'product', 'showcase', 'вкладки', 'функции', 'продукт'],
})

registerBlock({
  type: 'video-hero',
  label: 'Видео-герой',
  category: 'hero',
  icon: '🎬',
  description: 'Герой с видео на фоне и оверлеем',
  defaultData: {
    badge: '',
    title: 'Видео говорит само за себя',
    subtitle: 'Привлеките внимание с первой секунды мощным видеофоном.',
    primaryCta: 'Начать',
    primaryCtaUrl: '#',
    secondaryCta: '',
    secondaryCtaUrl: '#',
    videoUrl: '',
    height: '100vh',
    overlayColor: '#000000',
    overlayOpacity: '0.5',
    scrollIndicator: true,
  },
  schema: {
    badge: { type: 'text', label: 'Бейдж', default: '' },
    title: { type: 'text', label: 'Заголовок', default: 'Заголовок' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    primaryCta: { type: 'text', label: 'Кнопка 1', default: 'Начать' },
    primaryCtaUrl: { type: 'text', label: 'URL кнопки 1', default: '#' },
    secondaryCta: { type: 'text', label: 'Кнопка 2', default: '' },
    secondaryCtaUrl: { type: 'text', label: 'URL кнопки 2', default: '#' },
    videoUrl: { type: 'text', label: 'URL видео (mp4)', default: '' },
    height: { type: 'text', label: 'Высота', default: '100vh' },
    overlayColor: { type: 'color', label: 'Цвет оверлея', default: '#000000' },
    overlayOpacity: { type: 'text', label: 'Прозрачность оверлея (0-1)', default: '0.5' },
    scrollIndicator: { type: 'boolean', label: 'Индикатор прокрутки', default: true },
  },
  tags: ['video', 'hero', 'background', 'fullscreen', 'видео', 'герой', 'фон', 'полный экран'],
})

registerBlock({
  type: 'stats-counter-v2',
  label: 'Счётчики v2',
  category: 'content',
  icon: '🔢',
  description: 'Анимированные счётчики на тёмном фоне',
  defaultData: {
    title: 'Наши результаты',
    subtitle: 'Цифры, которые говорят сами за себя',
    stats: [
      { value: 1200, prefix: '', suffix: '+', label: 'Клиентов', desc: 'По всему миру' },
      { value: 98, prefix: '', suffix: '%', label: 'Удовлетворённость', desc: 'По данным опросов' },
      { value: 5, prefix: '×', suffix: '', label: 'Рост выручки', desc: 'За последний год' },
      { value: 24, prefix: '', suffix: '/7', label: 'Поддержка', desc: 'Всегда на связи' },
    ],
    background: '#0f172a',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наши результаты' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    stats: { type: 'array', label: 'Показатели', default: [] },
    background: { type: 'color', label: 'Фон', default: '#0f172a' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['stats', 'counter', 'numbers', 'animated', 'dark', 'цифры', 'счётчик', 'анимация'],
})

registerBlock({
  type: 'image-text-v2',
  label: 'Изображение + текст v2',
  category: 'content',
  icon: '🖼️',
  description: 'Секция с изображением и текстом с буллетами',
  defaultData: {
    badge: 'О нас',
    title: 'Почему выбирают нас',
    text: 'Мы сфокусированы на результате. Каждое решение, которое мы принимаем, направлено на рост вашего бизнеса.',
    bullets: [
      { icon: '🚀', text: 'Запуск за 48 часов без лишней бюрократии' },
      { icon: '💡', text: 'Инновационные решения для современного бизнеса' },
      { icon: '🛡️', text: 'Надёжность и безопасность на первом месте' },
    ],
    cta: 'Узнать больше',
    ctaUrl: '#',
    image: '',
    imageAlt: '',
    imageRadius: '16px',
    imagePosition: 'left',
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    badge: { type: 'text', label: 'Бейдж', default: '' },
    title: { type: 'text', label: 'Заголовок', default: 'Заголовок' },
    text: { type: 'text', label: 'Текст', default: '' },
    bullets: { type: 'array', label: 'Буллеты (иконка + текст)', default: [] },
    cta: { type: 'text', label: 'Текст кнопки', default: '' },
    ctaUrl: { type: 'text', label: 'URL кнопки', default: '#' },
    image: { type: 'image', label: 'Изображение', default: '' },
    imageAlt: { type: 'text', label: 'Alt изображения', default: '' },
    imageRadius: { type: 'text', label: 'Радиус изображения', default: '16px' },
    imagePosition: { type: 'select', label: 'Позиция изображения', default: 'left', options: [{ value: 'left', label: 'Слева' }, { value: 'right', label: 'Справа' }] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['image', 'text', 'bullets', 'split', 'изображение', 'текст', 'буллеты'],
})

registerBlock({
  type: 'card-grid',
  label: 'Сетка карточек',
  category: 'content',
  icon: '🃏',
  description: 'Универсальная сетка карточек с иконкой/изображением',
  defaultData: {
    title: 'Наши услуги',
    subtitle: 'Всё что нужно для успешного старта',
    columns: 3,
    cards: [
      { icon: '🎯', title: 'Стратегия', text: 'Разрабатываем стратегию роста вашего бизнеса на основе данных.', link: '#', linkText: 'Подробнее' },
      { icon: '💻', title: 'Разработка', text: 'Создаём современные веб-приложения с использованием последних технологий.', link: '#', linkText: 'Подробнее' },
      { icon: '📈', title: 'Маркетинг', text: 'Привлекаем целевую аудиторию и увеличиваем конверсию.', link: '#', linkText: 'Подробнее' },
    ],
    background: '#f9fafb',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наши услуги' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    columns: { type: 'select', label: 'Столбцы', default: '3', options: [{ value: '1', label: '1 столбец' }, { value: '2', label: '2 столбца' }, { value: '3', label: '3 столбца' }, { value: '4', label: '4 столбца' }] },
    cards: { type: 'array', label: 'Карточки', default: [] },
    background: { type: 'color', label: 'Фон', default: '#f9fafb' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['cards', 'grid', 'services', 'features', 'icons', 'карточки', 'сетка', 'услуги'],
})

registerBlock({
  type: 'testimonial-single',
  label: 'Одиночный отзыв',
  category: 'content',
  icon: '💬',
  description: 'Крупная цитата отзыва на тёмном фоне',
  defaultData: {
    quote: 'Это лучший продукт, которым я когда-либо пользовался. Он полностью изменил то, как я работаю каждый день.',
    name: 'Алексей Петров',
    role: 'CEO, TechCompany',
    avatar: '',
    rating: 5,
    background: '#0f172a',
    accentColor: '#5b6af0',
  },
  schema: {
    quote: { type: 'text', label: 'Цитата', default: 'Отзыв клиента' },
    name: { type: 'text', label: 'Имя', default: 'Имя Фамилия' },
    role: { type: 'text', label: 'Должность', default: 'CEO, Компания' },
    avatar: { type: 'image', label: 'Фото', default: '' },
    rating: { type: 'select', label: 'Рейтинг', default: '5', options: [{ value: '1', label: '1 звезда' }, { value: '2', label: '2 звезды' }, { value: '3', label: '3 звезды' }, { value: '4', label: '4 звезды' }, { value: '5', label: '5 звёзд' }] },
    background: { type: 'color', label: 'Фон', default: '#0f172a' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['testimonial', 'quote', 'review', 'single', 'dark', 'отзыв', 'цитата', 'звёзды'],
})

registerBlock({
  type: 'contact-map',
  label: 'Контакты с картой',
  category: 'content',
  icon: '🗺️',
  description: 'Контактная информация с встроенной картой',
  defaultData: {
    title: 'Как нас найти',
    subtitle: 'Мы находимся в центре города и всегда рады гостям',
    address: 'г. Москва, ул. Арбат, д. 1',
    mapEmbedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2245.3517296748!2d37.5895!3d55.7491!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTXCsDQ0JzU3LjgiTiAzN8KwMzUnMjIuMiJF!5e0!3m2!1sru!2sru!4v1',
    contacts: [
      { icon: '📞', label: 'Телефон', value: '+7 (495) 123-45-67', href: 'tel:+74951234567' },
      { icon: '✉️', label: 'Email', value: 'info@company.ru', href: 'mailto:info@company.ru' },
      { icon: '🕐', label: 'Режим работы', value: 'Пн–Пт: 9:00–18:00', href: '' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Как нас найти' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    address: { type: 'text', label: 'Адрес', default: '' },
    mapEmbedUrl: { type: 'text', label: 'URL embed карты', default: '' },
    contacts: { type: 'array', label: 'Контакты', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['map', 'contacts', 'address', 'location', 'google', 'карта', 'контакты', 'адрес'],
})

registerBlock({
  type: 'portfolio-grid-v2',
  label: 'Портфолио v2',
  category: 'gallery',
  icon: '🎨',
  description: 'Портфолио с фильтрами по категориям и оверлеем',
  defaultData: {
    title: 'Наши работы',
    subtitle: 'Лучшие проекты из нашего портфолио',
    columns: 3,
    items: [
      { image: '', title: 'Проект Альфа', category: 'Веб-дизайн', desc: 'Корпоративный сайт для IT-компании', link: '#' },
      { image: '', title: 'Проект Бета', category: 'Мобильное приложение', desc: 'iOS и Android приложение для e-commerce', link: '#' },
      { image: '', title: 'Проект Гамма', category: 'Веб-дизайн', desc: 'Лендинг для стартапа в сфере финтех', link: '#' },
      { image: '', title: 'Проект Дельта', category: 'Брендинг', desc: 'Фирменный стиль для ресторанной сети', link: '#' },
    ],
    background: '#0f172a',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наши работы' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    columns: { type: 'select', label: 'Столбцы', default: '3', options: [{ value: '2', label: '2 столбца' }, { value: '3', label: '3 столбца' }, { value: '4', label: '4 столбца' }] },
    items: { type: 'array', label: 'Работы', default: [] },
    background: { type: 'color', label: 'Фон', default: '#0f172a' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['portfolio', 'gallery', 'filter', 'categories', 'overlay', 'портфолио', 'галерея', 'фильтр'],
})

registerBlock({
  type: 'hero-minimal',
  label: 'Минималистичный герой',
  category: 'hero',
  icon: '✨',
  description: 'Чистый центрированный герой без изображения',
  defaultData: {
    badge: 'Новинка сезона',
    title: 'Простота — это красота',
    subtitle: 'Минималистичный дизайн, максимальный эффект. Сфокусируйтесь на главном.',
    primaryCta: 'Начать',
    primaryCtaUrl: '#',
    secondaryCta: 'Узнать больше',
    secondaryCtaUrl: '#',
    align: 'center',
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    badge: { type: 'text', label: 'Бейдж', default: '' },
    title: { type: 'text', label: 'Заголовок', default: 'Заголовок' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    primaryCta: { type: 'text', label: 'Кнопка 1', default: 'Начать' },
    primaryCtaUrl: { type: 'text', label: 'URL кнопки 1', default: '#' },
    secondaryCta: { type: 'text', label: 'Кнопка 2', default: '' },
    secondaryCtaUrl: { type: 'text', label: 'URL кнопки 2', default: '#' },
    align: { type: 'select', label: 'Выравнивание', default: 'center', options: [{ value: 'left', label: 'Слева' }, { value: 'center', label: 'По центру' }] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['hero', 'minimal', 'clean', 'centered', 'герой', 'минимализм', 'чистый'],
})

registerBlock({
  type: 'brand-story',
  label: 'История бренда',
  category: 'content',
  icon: '📖',
  description: 'Секция об истории компании с таймлайном',
  defaultData: {
    badge: 'Наша история',
    title: 'Как мы стали теми, кто мы есть',
    text: 'Начав с небольшой команды энтузиастов, мы выросли в компанию, которой доверяют тысячи клиентов по всему миру.',
    image: '',
    timelineTitle: 'Ключевые моменты',
    milestones: [
      { year: '2019', title: 'Основание', text: 'Компания основана тремя друзьями в небольшом гараже.' },
      { year: '2020', title: 'Первые клиенты', text: 'Заключили первые 100 договоров и подтвердили ценность продукта.' },
      { year: '2022', title: 'Серия A', text: 'Привлекли $5M инвестиций и расширили команду до 50 человек.' },
      { year: '2024', title: 'Международный рынок', text: 'Вышли на европейский рынок. 10 000+ клиентов в 15 странах.' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    badge: { type: 'text', label: 'Бейдж', default: '' },
    title: { type: 'text', label: 'Заголовок', default: 'История бренда' },
    text: { type: 'text', label: 'Основной текст', default: '' },
    image: { type: 'image', label: 'Изображение', default: '' },
    timelineTitle: { type: 'text', label: 'Заголовок таймлайна', default: 'Ключевые моменты' },
    milestones: { type: 'array', label: 'Этапы (год, заголовок, текст)', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['story', 'brand', 'about', 'history', 'timeline', 'история', 'бренд', 'о компании'],
})

registerBlock({
  type: 'offer-banner',
  label: 'Баннер предложения',
  category: 'content',
  icon: '🎁',
  description: 'Промо-баннер с обратным отсчётом до конца акции',
  defaultData: {
    badge: 'Акция',
    title: 'Скидка 50% до конца недели',
    subtitle: 'Не упустите шанс — предложение ограничено!',
    endDate: '',
    cta: 'Воспользоваться скидкой',
    ctaUrl: '#',
    background: 'linear-gradient(135deg,#f59e0b,#ef4444)',
  },
  schema: {
    badge: { type: 'text', label: 'Бейдж', default: 'Акция' },
    title: { type: 'text', label: 'Заголовок', default: 'Заголовок акции' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    endDate: { type: 'text', label: 'Дата окончания (ISO 8601)', default: '' },
    cta: { type: 'text', label: 'Текст кнопки', default: 'Узнать больше' },
    ctaUrl: { type: 'text', label: 'URL кнопки', default: '#' },
    background: { type: 'text', label: 'Фон (цвет или градиент)', default: 'linear-gradient(135deg,#f59e0b,#ef4444)' },
  },
  tags: ['offer', 'promo', 'banner', 'countdown', 'sale', 'акция', 'скидка', 'таймер', 'баннер'],
})

registerBlock({
  type: 'comparison-table',
  label: 'Таблица сравнения',
  category: 'content',
  icon: '⚖️',
  description: 'Мы vs конкуренты — таблица сравнения функций',
  defaultData: {
    title: 'Почему мы лучше',
    subtitle: 'Честное сравнение с конкурентами',
    usName: 'Мы',
    themName: 'Конкуренты',
    features: [
      { feature: 'Бесплатный тариф', us: true, them: false },
      { feature: 'API доступ', us: true, them: true },
      { feature: 'Приоритетная поддержка', us: true, them: false },
      { feature: 'Без скрытых платежей', us: true, them: false },
      { feature: 'Мобильное приложение', us: true, them: true },
      { feature: 'Интеграции', usText: '100+', themText: '20+' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Почему мы лучше' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    usName: { type: 'text', label: 'Наше название', default: 'Мы' },
    themName: { type: 'text', label: 'Конкурент', default: 'Конкуренты' },
    features: { type: 'array', label: 'Функции (feature, us, them)', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['comparison', 'table', 'vs', 'features', 'сравнение', 'таблица', 'конкуренты'],
})

registerBlock({
  type: 'team-slider',
  label: 'Слайдер команды',
  category: 'content',
  icon: '👤',
  description: 'Горизонтальный слайдер с карточками команды',
  defaultData: {
    title: 'Познакомьтесь с командой',
    subtitle: 'Люди, стоящие за продуктом',
    members: [
      { name: 'Алексей Петров', role: 'CEO', bio: 'Визионер и стратег. Строит компанию с 2019 года.' },
      { name: 'Мария Иванова', role: 'CTO', bio: 'Эксперт в ML и облачных системах.' },
      { name: 'Дмитрий Смирнов', role: 'CPO', bio: 'Превращает идеи в продукты, которые любят.' },
      { name: 'Елена Козлова', role: 'CMO', bio: 'Строит бренды и привлекает аудиторию.' },
      { name: 'Игорь Новиков', role: 'CFO', bio: 'Отвечает за финансы и стратегию роста.' },
    ],
    background: '#f9fafb',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Познакомьтесь с командой' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    members: { type: 'array', label: 'Участники', default: [] },
    background: { type: 'color', label: 'Фон', default: '#f9fafb' },
  },
  tags: ['team', 'slider', 'carousel', 'members', 'команда', 'слайдер', 'карусель'],
})

registerBlock({
  type: 'recent-posts',
  label: 'Последние посты',
  category: 'content',
  icon: '📝',
  description: 'Сетка последних записей блога с датами',
  defaultData: {
    title: 'Последние статьи',
    subtitle: 'Актуальные материалы от нашей команды',
    columns: 3,
    allPostsLink: '/blog',
    posts: [
      { title: 'Как увеличить конверсию лендинга на 40%', excerpt: 'Делимся проверенными методами оптимизации посадочных страниц.', date: '15 июля 2025', author: 'Алексей П.', category: 'Маркетинг', readTime: '5 мин', link: '#' },
      { title: 'Топ-10 инструментов для дизайнеров в 2025', excerpt: 'Обзор лучших инструментов, которые должен знать каждый UI/UX дизайнер.', date: '10 июля 2025', author: 'Мария И.', category: 'Дизайн', readTime: '7 мин', link: '#' },
      { title: 'Почему скорость сайта напрямую влияет на продажи', excerpt: 'Каждая секунда задержки стоит вам 7% конверсии. Вот что делать.', date: '5 июля 2025', author: 'Дмитрий С.', category: 'Технологии', readTime: '4 мин', link: '#' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Последние статьи' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    columns: { type: 'select', label: 'Столбцы', default: '3', options: [{ value: '1', label: '1 столбец' }, { value: '2', label: '2 столбца' }, { value: '3', label: '3 столбца' }, { value: '4', label: '4 столбца' }] },
    allPostsLink: { type: 'text', label: 'Ссылка "Все статьи"', default: '/blog' },
    posts: { type: 'array', label: 'Публикации', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['blog', 'posts', 'articles', 'news', 'cards', 'блог', 'статьи', 'новости'],
})

registerBlock({
  type: 'cta-video',
  label: 'CTA с видеофоном',
  category: 'hero',
  icon: '🎥',
  description: 'Призыв к действию с видео или фото на фоне',
  defaultData: {
    badge: '',
    title: 'Готовы начать?',
    subtitle: 'Присоединитесь к тысячам довольных клиентов уже сегодня.',
    primaryCta: 'Попробовать бесплатно',
    primaryCtaUrl: '#',
    secondaryCta: 'Узнать больше',
    secondaryCtaUrl: '#',
    videoUrl: '',
    image: '',
    overlayOpacity: '0.65',
  },
  schema: {
    badge: { type: 'text', label: 'Бейдж', default: '' },
    title: { type: 'text', label: 'Заголовок', default: 'Готовы начать?' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    primaryCta: { type: 'text', label: 'Кнопка 1', default: 'Попробовать' },
    primaryCtaUrl: { type: 'text', label: 'URL кнопки 1', default: '#' },
    secondaryCta: { type: 'text', label: 'Кнопка 2', default: '' },
    secondaryCtaUrl: { type: 'text', label: 'URL кнопки 2', default: '#' },
    videoUrl: { type: 'text', label: 'URL видео (mp4)', default: '' },
    image: { type: 'image', label: 'Фоновое изображение (если нет видео)', default: '' },
    overlayOpacity: { type: 'text', label: 'Прозрачность оверлея (0-1)', default: '0.65' },
  },
  tags: ['cta', 'video', 'background', 'hero', 'призыв', 'видео', 'фон'],
})

registerBlock({
  type: 'countdown-timer',
  label: 'Таймер обратного отсчёта',
  category: 'interactive',
  icon: '⏱️',
  description: 'Обратный отсчёт до даты события с дизайном на тёмном фоне',
  defaultData: {
    title: 'До старта осталось',
    subtitle: 'Регистрируйтесь сейчас, пока есть места!',
    endDate: '',
    cta: 'Зарегистрироваться',
    ctaUrl: '#',
    expiredMessage: 'Время истекло!',
    background: '#1f2937',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'До старта осталось' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    endDate: { type: 'text', label: 'Дата окончания (ISO 8601)', default: '' },
    cta: { type: 'text', label: 'Текст кнопки', default: 'Зарегистрироваться' },
    ctaUrl: { type: 'url', label: 'URL кнопки', default: '#' },
    expiredMessage: { type: 'text', label: 'Сообщение истечения', default: 'Время истекло!' },
    background: { type: 'color', label: 'Фон', default: '#1f2937' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['countdown', 'timer', 'event', 'launch', 'таймер', 'отсчёт', 'событие'],
})

registerBlock({
  type: 'services-list',
  label: 'Список услуг с ценами',
  category: 'content',
  icon: '📋',
  description: 'Список услуг с ценами в строку, иконкой и CTA',
  defaultData: {
    title: 'Наши услуги',
    subtitle: 'Прозрачные цены без скрытых условий',
    services: [
      { icon: '🎨', name: 'Дизайн лендинга', desc: 'Уникальный дизайн под ваш бренд', price: 'от 15 000 ₽', unit: 'за проект', cta: 'Заказать', ctaUrl: '#', featured: false },
      { icon: '💻', name: 'Разработка сайта', desc: 'Современный сайт на React или Next.js', price: 'от 50 000 ₽', unit: 'за проект', cta: 'Заказать', ctaUrl: '#', featured: true },
      { icon: '📊', name: 'SEO-продвижение', desc: 'Вывод в ТОП-10 по целевым запросам', price: 'от 25 000 ₽', unit: 'в месяц', cta: 'Заказать', ctaUrl: '#', featured: false },
      { icon: '🔧', name: 'Техническая поддержка', desc: 'Поддержка и развитие вашего сайта', price: 'от 10 000 ₽', unit: 'в месяц', cta: 'Заказать', ctaUrl: '#', featured: false },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наши услуги' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    services: { type: 'array', label: 'Услуги', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['services', 'pricing', 'list', 'price-list', 'услуги', 'прайс', 'цены'],
})

registerBlock({
  type: 'faq-two-col',
  label: 'FAQ в два столбца',
  category: 'content',
  icon: '❓',
  description: 'FAQ-аккордеон в двух колонках',
  defaultData: {
    title: 'Часто задаваемые вопросы',
    subtitle: '',
    items: [
      { question: 'Как начать сотрудничество?', answer: 'Заполните форму на сайте или позвоните нам. Мы ответим в течение одного рабочего дня.' },
      { question: 'Какие сроки выполнения работ?', answer: 'Стандартные проекты занимают 2–4 недели. Срочные проекты — по договорённости.' },
      { question: 'Предоставляете ли вы гарантию?', answer: 'Да, мы предоставляем гарантию 12 месяцев на все выполненные работы.' },
      { question: 'Как происходит оплата?', answer: 'Оплата в два этапа: 50% предоплата и 50% по завершении проекта.' },
      { question: 'Работаете ли вы с нерезидентами?', answer: 'Да, мы работаем с клиентами из любых стран и принимаем международные платежи.' },
      { question: 'Есть ли портфолио работ?', answer: 'Да, вы можете ознакомиться с нашим портфолио на сайте или запросить его у менеджера.' },
    ],
    background: '#f9fafb',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Часто задаваемые вопросы' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    items: { type: 'array', label: 'Вопросы и ответы', default: [] },
    background: { type: 'color', label: 'Фон', default: '#f9fafb' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['faq', 'two-column', 'accordion', 'questions', 'вопросы', 'два столбца'],
})

registerBlock({
  type: 'hero-gradient',
  label: 'Герой с градиентом',
  category: 'hero',
  icon: '🌈',
  description: 'Градиентный герой с паттерном и двумя кнопками',
  defaultData: {
    badge: 'Добро пожаловать',
    title: 'Будущее начинается здесь',
    subtitle: 'Присоединяйтесь к тысячам компаний, которые уже растут с нами.',
    primaryCta: 'Начать бесплатно',
    primaryCtaUrl: '#',
    secondaryCta: 'Узнать больше',
    secondaryCtaUrl: '#',
    gradientFrom: '#5b6af0',
    gradientTo: '#8b5cf6',
  },
  schema: {
    badge: { type: 'text', label: 'Бейдж', default: '' },
    title: { type: 'text', label: 'Заголовок', default: 'Заголовок' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    primaryCta: { type: 'text', label: 'Кнопка 1', default: 'Начать' },
    primaryCtaUrl: { type: 'url', label: 'URL кнопки 1', default: '#' },
    secondaryCta: { type: 'text', label: 'Кнопка 2', default: '' },
    secondaryCtaUrl: { type: 'url', label: 'URL кнопки 2', default: '#' },
    gradientFrom: { type: 'color', label: 'Цвет градиента (начало)', default: '#5b6af0' },
    gradientTo: { type: 'color', label: 'Цвет градиента (конец)', default: '#8b5cf6' },
  },
  tags: ['hero', 'gradient', 'colorful', 'pattern', 'герой', 'градиент', 'цветной'],
})

registerBlock({
  type: 'reviews-grid',
  label: 'Сетка отзывов',
  category: 'content',
  icon: '⭐',
  description: 'Сетка отзывов клиентов с рейтингом и фото',
  defaultData: {
    title: 'Отзывы клиентов',
    subtitle: 'Что говорят наши клиенты',
    columns: 3,
    reviews: [
      { name: 'Алексей П.', role: 'CEO', review: 'Отличный продукт! Сэкономили нам много времени и денег.', rating: 5, date: '12 июл 2025', source: 'Google' },
      { name: 'Мария И.', role: 'Маркетолог', review: 'Очень доволен результатом. Команда профессионалов.', rating: 5, date: '10 июл 2025', source: 'Яндекс' },
      { name: 'Дмитрий С.', role: 'Предприниматель', review: 'Рекомендую всем! Быстро, качественно, в срок.', rating: 5, date: '8 июл 2025', source: '2ГИС' },
    ],
    background: '#f9fafb',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Отзывы клиентов' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    columns: { type: 'select', label: 'Столбцы', default: '3', options: [{ value: '1', label: '1 столбец' }, { value: '2', label: '2 столбца' }, { value: '3', label: '3 столбца' }] },
    reviews: { type: 'array', label: 'Отзывы', default: [] },
    background: { type: 'color', label: 'Фон', default: '#f9fafb' },
  },
  tags: ['reviews', 'testimonials', 'stars', 'rating', 'отзывы', 'звёзды', 'рейтинг'],
})

registerBlock({
  type: 'split-cta',
  label: 'Разделённый CTA с формой',
  category: 'form',
  icon: '📨',
  description: 'Тёмная секция: слева преимущества, справа форма заявки',
  defaultData: {
    badge: 'Готовы начать?',
    title: 'Получите бесплатную консультацию',
    subtitle: 'Наши эксперты готовы помочь вам найти лучшее решение.',
    benefits: ['Ответим в течение 1 рабочего дня', 'Без обязательств и скрытых платежей', 'Бесплатная первичная консультация'],
    formTitle: 'Оставить заявку',
    namePlaceholder: 'Ваше имя',
    emailPlaceholder: 'Email',
    phonePlaceholder: 'Телефон',
    messagePlaceholder: 'Описание задачи',
    submitText: 'Отправить заявку',
    successMessage: 'Заявка принята! Мы свяжемся с вами.',
    showPhone: true,
    showMessage: true,
    background: '#0f172a',
    accentColor: '#5b6af0',
  },
  schema: {
    badge: { type: 'text', label: 'Бейдж', default: '' },
    title: { type: 'text', label: 'Заголовок', default: 'Заголовок' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    benefits: { type: 'array', label: 'Преимущества (строки)', default: [] },
    formTitle: { type: 'text', label: 'Заголовок формы', default: 'Оставить заявку' },
    namePlaceholder: { type: 'text', label: 'Плейсхолдер имени', default: 'Ваше имя' },
    emailPlaceholder: { type: 'text', label: 'Плейсхолдер email', default: 'Email' },
    phonePlaceholder: { type: 'text', label: 'Плейсхолдер телефона', default: 'Телефон' },
    messagePlaceholder: { type: 'text', label: 'Плейсхолдер сообщения', default: 'Сообщение' },
    submitText: { type: 'text', label: 'Текст кнопки', default: 'Отправить заявку' },
    successMessage: { type: 'text', label: 'Сообщение успеха', default: 'Заявка принята!' },
    showPhone: { type: 'boolean', label: 'Поле телефона', default: true },
    showMessage: { type: 'boolean', label: 'Поле сообщения', default: true },
    background: { type: 'color', label: 'Фон', default: '#0f172a' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['cta', 'form', 'lead', 'dark', 'split', 'форма', 'заявка', 'тёмный'],
})

registerBlock({
  type: 'image-slider-v2',
  label: 'Слайдер изображений v2',
  category: 'slider',
  icon: '🖼️',
  description: 'Полноширинный слайдер изображений с подписями',
  defaultData: {
    height: '480px',
    slides: [
      { image: '', caption: 'Первый слайд', link: '' },
      { image: '', caption: 'Второй слайд', link: '' },
      { image: '', caption: 'Третий слайд', link: '' },
    ],
  },
  schema: {
    height: { type: 'text', label: 'Высота слайдера', default: '480px' },
    slides: { type: 'array', label: 'Слайды (изображение, подпись, ссылка)', default: [] },
  },
  tags: ['slider', 'carousel', 'images', 'gallery', 'слайдер', 'карусель', 'изображения'],
})

registerBlock({
  type: 'feature-highlights',
  label: 'Выделенные функции',
  category: 'content',
  icon: '✨',
  description: 'Карточки функций с нумерованными иконками',
  defaultData: {
    title: 'Ключевые возможности',
    subtitle: 'Всё необходимое для успешного старта',
    columns: 2,
    items: [
      { icon: '⚡', title: 'Молниеносная скорость', desc: 'Запустите сайт за минуты. Наш редактор оптимизирован для максимальной производительности.' },
      { icon: '🎨', title: 'Гибкий дизайн', desc: 'Настройте каждый элемент под ваш стиль. Без ограничений и без кода.' },
      { icon: '🔒', title: 'Надёжная безопасность', desc: 'SSL, резервное копирование, защита от атак — всё включено в каждый тариф.' },
      { icon: '📊', title: 'Аналитика в реальном времени', desc: 'Отслеживайте трафик, конверсии и поведение пользователей в удобном дашборде.' },
    ],
    background: '#f9fafb',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Ключевые возможности' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    columns: { type: 'select', label: 'Столбцы', default: '2', options: [{ value: '1', label: '1 столбец' }, { value: '2', label: '2 столбца' }, { value: '3', label: '3 столбца' }] },
    items: { type: 'array', label: 'Функции (иконка, заголовок, описание)', default: [] },
    background: { type: 'color', label: 'Фон', default: '#f9fafb' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['features', 'highlights', 'numbered', 'icons', 'cards', 'функции', 'возможности'],
})

registerBlock({
  type: 'quote-banner',
  label: 'Баннер цитаты',
  category: 'content',
  icon: '💬',
  description: 'Большая вдохновляющая цитата на цветном фоне',
  defaultData: {
    quote: 'Лучший способ предсказать будущее — создать его.',
    author: 'Питер Друкер',
    authorRole: 'Отец современного менеджмента',
    background: '#5b6af0',
    textColor: '#ffffff',
  },
  schema: {
    quote: { type: 'text', label: 'Цитата', default: 'Ваша цитата здесь' },
    author: { type: 'text', label: 'Автор', default: '' },
    authorRole: { type: 'text', label: 'Роль/должность автора', default: '' },
    background: { type: 'color', label: 'Фон', default: '#5b6af0' },
    textColor: { type: 'color', label: 'Цвет текста', default: '#ffffff' },
  },
  tags: ['quote', 'banner', 'inspiration', 'цитата', 'баннер', 'вдохновение'],
})

registerBlock({
  type: 'pricing-toggle-v2',
  label: 'Тарифы с переключателем',
  category: 'commerce',
  icon: '🔄',
  description: 'Тарифные планы с переключением месяц/год',
  defaultData: {
    title: 'Простые и прозрачные тарифы',
    subtitle: 'Начните бесплатно, масштабируйтесь по мере роста',
    annualDiscount: '-20%',
    plans: [
      { name: 'Free', priceMonth: '0 ₽', priceYear: '0 ₽', desc: 'Для старта.', features: ['1 сайт', '500 МБ', 'SSL'], highlighted: false, cta: 'Начать бесплатно', ctaUrl: '#' },
      { name: 'Pro', priceMonth: '790 ₽', priceYear: '632 ₽', desc: 'Для профессионалов.', features: ['5 сайтов', '5 ГБ', 'Свой домен', 'Аналитика'], highlighted: true, cta: 'Выбрать Pro', ctaUrl: '#' },
      { name: 'Agency', priceMonth: '3990 ₽', priceYear: '3192 ₽', desc: 'Для агентств.', features: ['∞ сайтов', '50 ГБ', 'White Label', 'API'], highlighted: false, cta: 'Связаться', ctaUrl: '#' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наши тарифы' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    annualDiscount: { type: 'text', label: 'Скидка за год', default: '-20%' },
    plans: { type: 'array', label: 'Тарифные планы', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['pricing', 'toggle', 'annual', 'monthly', 'тарифы', 'переключатель', 'год', 'месяц'],
})

registerBlock({
  type: 'video-grid',
  label: 'Видеогалерея',
  category: 'media',
  icon: '🎬',
  description: 'Сетка YouTube/Vimeo видео с превью и плеером по клику',
  defaultData: {
    title: 'Видеоматериалы',
    subtitle: 'Смотрите наши обучающие видео и вебинары',
    columns: 2,
    videos: [
      { platform: 'youtube', embedId: 'dQw4w9WgXcQ', title: 'Введение в продукт', duration: '5:32' },
      { platform: 'youtube', embedId: 'dQw4w9WgXcQ', title: 'Быстрый старт', duration: '3:15' },
      { platform: 'youtube', embedId: 'dQw4w9WgXcQ', title: 'Расширенные функции', duration: '8:47' },
      { platform: 'youtube', embedId: 'dQw4w9WgXcQ', title: 'Интеграции и API', duration: '6:20' },
    ],
    background: '#0f172a',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Видеоматериалы' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    columns: { type: 'select', label: 'Столбцы', default: '2', options: [{ value: '1', label: '1 столбец' }, { value: '2', label: '2 столбца' }, { value: '3', label: '3 столбца' }] },
    videos: { type: 'array', label: 'Видео (platform, embedId, title, duration)', default: [] },
    background: { type: 'color', label: 'Фон', default: '#0f172a' },
  },
  tags: ['video', 'youtube', 'vimeo', 'grid', 'gallery', 'видео', 'галерея', 'ютуб'],
})

registerBlock({
  type: 'hero-with-stats',
  label: 'Герой со статистикой',
  category: 'hero',
  icon: '📊',
  description: 'Тёмный герой с полосой статистики снизу',
  defaultData: {
    badge: 'Платформа #1',
    title: 'Растите быстрее с нашей платформой',
    subtitle: 'Тысячи компаний уже используют наш инструмент для достижения своих целей.',
    primaryCta: 'Попробовать бесплатно',
    primaryCtaUrl: '#',
    secondaryCta: 'Смотреть демо',
    secondaryCtaUrl: '#',
    stats: [
      { value: '10 000+', label: 'Пользователей' },
      { value: '98%', label: 'Удовлетворённость' },
      { value: '50+', label: 'Стран' },
      { value: '24/7', label: 'Поддержка' },
    ],
    background: '#0f172a',
    accentColor: '#5b6af0',
  },
  schema: {
    badge: { type: 'text', label: 'Бейдж', default: '' },
    title: { type: 'text', label: 'Заголовок', default: 'Заголовок' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    primaryCta: { type: 'text', label: 'Кнопка 1', default: 'Начать' },
    primaryCtaUrl: { type: 'url', label: 'URL кнопки 1', default: '#' },
    secondaryCta: { type: 'text', label: 'Кнопка 2', default: '' },
    secondaryCtaUrl: { type: 'url', label: 'URL кнопки 2', default: '#' },
    stats: { type: 'array', label: 'Статистика (value, label)', default: [] },
    background: { type: 'color', label: 'Фон', default: '#0f172a' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['hero', 'stats', 'dark', 'numbers', 'герой', 'статистика', 'цифры'],
})

registerBlock({
  type: 'feature-list-v2',
  label: 'Функции с чередованием',
  category: 'content',
  icon: '🔁',
  description: 'Чередующийся список функций с изображением и текстом',
  defaultData: {
    title: 'Почему нас выбирают',
    subtitle: '',
    features: [
      { icon: '⚡', title: 'Молниеносный старт', desc: 'Создайте сайт за 30 минут. Выберите шаблон, настройте под себя, опубликуйте — готово!', image: '' },
      { icon: '🎨', title: 'Полная свобода дизайна', desc: 'Drag & drop редактор, сотни блоков, неограниченные шрифты и цвета. Ваш сайт — ваши правила.', image: '' },
      { icon: '🚀', title: 'Максимальная производительность', desc: 'Core Web Vitals 90+, WebP-изображения, CDN, lazy load — всё автоматически.', image: '' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Почему нас выбирают' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    features: { type: 'array', label: 'Функции (icon, title, desc, image)', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['features', 'alternating', 'list', 'image', 'text', 'функции', 'чередование'],
})

registerBlock({
  type: 'team-minimal',
  label: 'Минимальная команда',
  category: 'content',
  icon: '🤝',
  description: 'Компактная сетка команды: фото, имя, роль',
  defaultData: {
    title: 'Наша команда',
    subtitle: '',
    columns: 4,
    members: [
      { name: 'Алексей П.', role: 'CEO', photo: '' },
      { name: 'Мария И.', role: 'CTO', photo: '' },
      { name: 'Дмитрий С.', role: 'Designer', photo: '' },
      { name: 'Елена К.', role: 'Marketing', photo: '' },
      { name: 'Игорь Н.', role: 'Sales', photo: '' },
      { name: 'Ольга В.', role: 'Support', photo: '' },
      { name: 'Павел М.', role: 'DevOps', photo: '' },
      { name: 'Анна Р.', role: 'PM', photo: '' },
    ],
    background: '#ffffff',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Наша команда' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    columns: { type: 'select', label: 'Столбцы', default: '4', options: [{ value: '2', label: '2' }, { value: '3', label: '3' }, { value: '4', label: '4' }, { value: '5', label: '5' }, { value: '6', label: '6' }] },
    members: { type: 'array', label: 'Участники', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
  },
  tags: ['team', 'minimal', 'compact', 'grid', 'команда', 'компактный', 'сетка'],
})

registerBlock({
  type: 'cta-gradient',
  label: 'CTA с градиентом',
  category: 'hero',
  icon: '🌅',
  description: 'Яркий призыв к действию с кнопками на градиентном фоне',
  defaultData: {
    badge: '',
    title: 'Начните прямо сейчас',
    subtitle: 'Тысячи команд уже работают с нами. Присоединяйтесь!',
    primaryCta: 'Попробовать бесплатно',
    primaryCtaUrl: '#',
    secondaryCta: 'Узнать больше',
    secondaryCtaUrl: '#',
    gradientFrom: '#5b6af0',
    gradientTo: '#8b5cf6',
  },
  schema: {
    badge: { type: 'text', label: 'Бейдж', default: '' },
    title: { type: 'text', label: 'Заголовок', default: 'Начните прямо сейчас' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    primaryCta: { type: 'text', label: 'Кнопка 1', default: 'Начать' },
    primaryCtaUrl: { type: 'url', label: 'URL кнопки 1', default: '#' },
    secondaryCta: { type: 'text', label: 'Кнопка 2', default: '' },
    secondaryCtaUrl: { type: 'url', label: 'URL кнопки 2', default: '#' },
    gradientFrom: { type: 'color', label: 'Цвет градиента (начало)', default: '#5b6af0' },
    gradientTo: { type: 'color', label: 'Цвет градиента (конец)', default: '#8b5cf6' },
  },
  tags: ['cta', 'gradient', 'colorful', 'hero', 'призыв', 'градиент', 'яркий'],
})

registerBlock({
  type: 'steps-horizontal',
  label: 'Шаги горизонтально',
  category: 'content',
  icon: '➡️',
  description: 'Горизонтальные шаги с соединительными линиями',
  defaultData: {
    title: 'Как это работает',
    subtitle: 'Три простых шага до результата',
    steps: [
      { icon: '📝', title: 'Регистрация', desc: 'Создайте аккаунт за 30 секунд' },
      { icon: '🎨', title: 'Настройка', desc: 'Выберите шаблон и настройте дизайн' },
      { icon: '🚀', title: 'Запуск', desc: 'Опубликуйте сайт одной кнопкой' },
    ],
    background: '#f9fafb',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Как это работает' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    steps: { type: 'array', label: 'Шаги (icon, title, desc)', default: [] },
    background: { type: 'color', label: 'Фон', default: '#f9fafb' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['steps', 'how-it-works', 'horizontal', 'process', 'шаги', 'горизонталь', 'процесс'],
})

registerBlock({
  type: 'text-columns',
  label: 'Текстовые колонки',
  category: 'text',
  icon: '📄',
  description: 'Многоколоночный текстовый блок с заголовками и ссылками',
  defaultData: {
    title: 'О компании',
    subtitle: '',
    columns: 2,
    titleAlign: 'left',
    columns_content: [
      { title: 'Наша миссия', text: 'Мы помогаем бизнесу расти с помощью современных цифровых решений. Каждый проект — это возможность создать что-то по-настоящему значимое.', cta: '', ctaUrl: '#' },
      { title: 'Наш подход', text: 'Мы работаем в тесном сотрудничестве с клиентами, погружаясь в их бизнес. Каждое решение основано на данных и проверенных практиках.', cta: 'Узнать больше', ctaUrl: '#' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок блока', default: '' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    columns: { type: 'select', label: 'Количество колонок', default: '2', options: [{ value: '1', label: '1' }, { value: '2', label: '2' }, { value: '3', label: '3' }, { value: '4', label: '4' }] },
    titleAlign: { type: 'select', label: 'Выравнивание заголовка', default: 'left', options: [{ value: 'left', label: 'Слева' }, { value: 'center', label: 'По центру' }] },
    columns_content: { type: 'array', label: 'Содержимое колонок', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['text', 'columns', 'multi-column', 'about', 'текст', 'колонки', 'многоколоночный'],
})

registerBlock({
  type: 'social-proof-bar',
  label: 'Бар социального доверия',
  category: 'content',
  icon: '🏅',
  description: 'Полоса с рейтингом и логотипами брендов-клиентов',
  defaultData: {
    reviewScore: '4.9',
    reviewCount: '2 400+',
    logoLabel: 'Нам доверяют',
    logos: [
      { name: 'Компания 1', image: '' },
      { name: 'Компания 2', image: '' },
      { name: 'Компания 3', image: '' },
      { name: 'Компания 4', image: '' },
      { name: 'Компания 5', image: '' },
    ],
    background: '#f9fafb',
  },
  schema: {
    reviewScore: { type: 'text', label: 'Оценка (напр. 4.9)', default: '4.9' },
    reviewCount: { type: 'text', label: 'Количество отзывов', default: '2 400+' },
    logoLabel: { type: 'text', label: 'Подпись к логотипам', default: 'Нам доверяют' },
    logos: { type: 'array', label: 'Логотипы клиентов', default: [] },
    background: { type: 'color', label: 'Фон', default: '#f9fafb' },
  },
  tags: ['social-proof', 'logos', 'trust', 'rating', 'brands', 'доверие', 'логотипы', 'рейтинг'],
})

registerBlock({
  type: 'divider-wave',
  label: 'Волновой разделитель',
  category: 'layout',
  icon: '〰️',
  description: 'SVG волновой разделитель между секциями',
  defaultData: {
    topColor: '#ffffff',
    bottomColor: '#f9fafb',
    height: '80px',
    style: 'gentle',
    flip: false,
  },
  schema: {
    topColor: { type: 'color', label: 'Цвет сверху', default: '#ffffff' },
    bottomColor: { type: 'color', label: 'Цвет снизу', default: '#f9fafb' },
    height: { type: 'text', label: 'Высота', default: '80px' },
    style: { type: 'select', label: 'Стиль волны', default: 'gentle', options: [{ value: 'gentle', label: 'Мягкая' }, { value: 'double', label: 'Двойная' }, { value: 'sharp', label: 'Острая' }] },
    flip: { type: 'boolean', label: 'Зеркально', default: false },
  },
  tags: ['divider', 'wave', 'separator', 'section', 'разделитель', 'волна'],
})

registerBlock({
  type: 'tabs-content',
  label: 'Вкладки с контентом',
  category: 'content',
  icon: '📂',
  description: 'Горизонтальные вкладки с текстом и изображением',
  defaultData: {
    title: '',
    tabs: [
      { label: 'Вкладка 1', content: 'Содержимое первой вкладки. Здесь может быть любой текст.', image: '' },
      { label: 'Вкладка 2', content: 'Содержимое второй вкладки.', image: '' },
      { label: 'Вкладка 3', content: 'Содержимое третьей вкладки.', image: '' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: '' },
    tabs: { type: 'array', label: 'Вкладки (label, content, image)', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['tabs', 'content', 'navigation', 'вкладки', 'контент', 'навигация'],
})

registerBlock({
  type: 'callout-box',
  label: 'Выноска / Callout',
  category: 'content',
  icon: '📌',
  description: 'Информационная выноска: info, warning, success, error, tip',
  defaultData: {
    type: 'info',
    title: 'Важная информация',
    text: 'Обратите внимание на этот важный момент перед продолжением.',
  },
  schema: {
    type: { type: 'select', label: 'Тип', default: 'info', options: [{ value: 'info', label: 'Информация' }, { value: 'warning', label: 'Предупреждение' }, { value: 'success', label: 'Успех' }, { value: 'error', label: 'Ошибка' }, { value: 'tip', label: 'Совет' }] },
    title: { type: 'text', label: 'Заголовок', default: '' },
    text: { type: 'text', label: 'Текст', default: 'Текст выноски' },
  },
  tags: ['callout', 'info', 'warning', 'alert', 'tip', 'note', 'выноска', 'предупреждение', 'совет'],
})

registerBlock({
  type: 'code-snippet',
  label: 'Блок кода',
  category: 'content',
  icon: '💻',
  description: 'Стилизованный блок кода с кнопкой копирования',
  defaultData: {
    filename: 'example.js',
    language: 'javascript',
    showCopy: true,
    code: `const greeting = "Hello, World!"\nconsole.log(greeting)`,
  },
  schema: {
    filename: { type: 'text', label: 'Имя файла', default: '' },
    language: { type: 'select', label: 'Язык', default: 'javascript', options: [{ value: 'javascript', label: 'JavaScript' }, { value: 'typescript', label: 'TypeScript' }, { value: 'python', label: 'Python' }, { value: 'go', label: 'Go' }, { value: 'rust', label: 'Rust' }, { value: 'bash', label: 'Bash' }, { value: 'html', label: 'HTML' }, { value: 'css', label: 'CSS' }, { value: 'sql', label: 'SQL' }, { value: 'json', label: 'JSON' }] },
    showCopy: { type: 'boolean', label: 'Кнопка копирования', default: true },
    code: { type: 'text', label: 'Код', default: '// your code here' },
  },
  tags: ['code', 'snippet', 'developer', 'highlight', 'код', 'разработчик', 'синтаксис'],
})

registerBlock({
  type: 'podcast-embed',
  label: 'Подкаст / Аудио embed',
  category: 'media',
  icon: '🎙️',
  description: 'Встроенный плеер подкаста (Spotify, SoundCloud, RSS)',
  defaultData: {
    title: 'Наш подкаст',
    subtitle: 'Слушайте нас на всех платформах',
    embedUrl: '',
    height: '152',
    background: '#ffffff',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: '' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    embedUrl: { type: 'url', label: 'URL embed (Spotify/SoundCloud)', default: '' },
    height: { type: 'text', label: 'Высота плеера (px)', default: '152' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
  },
  tags: ['podcast', 'audio', 'spotify', 'soundcloud', 'embed', 'подкаст', 'аудио'],
})

registerBlock({
  type: 'document-card',
  label: 'Карточки документов',
  category: 'content',
  icon: '📁',
  description: 'Список документов для скачивания с иконкой и кнопкой',
  defaultData: {
    title: 'Документы',
    subtitle: 'Скачайте необходимые материалы',
    documents: [
      { icon: '📄', title: 'Коммерческое предложение', desc: 'Полный прайс-лист 2025', fileType: 'PDF', fileSize: '2.4 МБ', url: '#' },
      { icon: '📝', title: 'Договор оферты', desc: 'Публичный договор', fileType: 'DOC', fileSize: '145 КБ', url: '#' },
      { icon: '📊', title: 'Презентация компании', desc: 'Полная презентация', fileType: 'PPT', fileSize: '8.1 МБ', url: '#' },
    ],
    background: '#f9fafb',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Документы' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    documents: { type: 'array', label: 'Документы', default: [] },
    background: { type: 'color', label: 'Фон', default: '#f9fafb' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['documents', 'download', 'files', 'pdf', 'документы', 'скачать', 'файлы'],
})

registerBlock({
  type: 'product-bundle',
  label: 'Комплект продуктов',
  category: 'commerce',
  icon: '🎁',
  description: 'Предложение комплекта с экономией',
  defaultData: {
    badge: '🔥 Выгодный комплект',
    title: 'Купите всё вместе и сэкономьте',
    subtitle: 'Лучшие товары по специальной цене',
    products: [
      { name: 'Товар A', price: '2 500 ₽', image: '' },
      { name: 'Товар B', price: '3 200 ₽', image: '' },
      { name: 'Товар C', price: '1 800 ₽', image: '' },
    ],
    originalPrice: '7 500 ₽',
    bundlePrice: '5 990 ₽',
    savings: '1 510 ₽',
    cta: 'Купить комплект',
    ctaUrl: '#',
    background: '#f9fafb',
    accentColor: '#5b6af0',
  },
  schema: {
    badge: { type: 'text', label: 'Бейдж', default: '' },
    title: { type: 'text', label: 'Заголовок', default: 'Купите всё вместе' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    products: { type: 'array', label: 'Товары', default: [] },
    originalPrice: { type: 'text', label: 'Цена без скидки', default: '' },
    bundlePrice: { type: 'text', label: 'Цена комплекта', default: '' },
    savings: { type: 'text', label: 'Экономия', default: '' },
    cta: { type: 'text', label: 'Текст кнопки', default: 'Купить комплект' },
    ctaUrl: { type: 'url', label: 'URL кнопки', default: '#' },
    background: { type: 'color', label: 'Фон', default: '#f9fafb' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['bundle', 'products', 'sale', 'discount', 'комплект', 'набор', 'скидка'],
})

registerBlock({
  type: 'text-sidebar',
  label: 'Текст с боковой панелью',
  category: 'layout',
  icon: '📰',
  description: 'Основной текст + липкая боковая панель с информацией',
  defaultData: {
    badge: '',
    title: 'Заголовок страницы',
    content: 'Основной контент страницы идёт здесь. Это блок для длинных материалов, статей или описаний услуг.',
    cta: 'Связаться',
    ctaUrl: '#',
    sidebarTitle: 'Информация',
    sidebarBackground: '#f9fafb',
    sidebarItems: [
      { icon: '📞', label: 'Телефон', value: '+7 (495) 123-45-67' },
      { icon: '✉️', label: 'Email', value: 'info@company.ru' },
      { icon: '🕐', label: 'Режим работы', value: 'Пн–Пт 9:00–18:00' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    badge: { type: 'text', label: 'Бейдж', default: '' },
    title: { type: 'text', label: 'Заголовок', default: '' },
    content: { type: 'text', label: 'Основной контент', default: '' },
    cta: { type: 'text', label: 'Текст кнопки', default: '' },
    ctaUrl: { type: 'url', label: 'URL кнопки', default: '#' },
    sidebarTitle: { type: 'text', label: 'Заголовок сайдбара', default: '' },
    sidebarBackground: { type: 'color', label: 'Фон сайдбара', default: '#f9fafb' },
    sidebarItems: { type: 'array', label: 'Элементы сайдбара', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['sidebar', 'layout', 'text', 'article', 'сайдбар', 'статья', 'боковая панель'],
})

registerBlock({
  type: 'dual-image',
  label: 'Два изображения',
  category: 'media',
  icon: '🖼️',
  description: 'Два изображения рядом с подписями',
  defaultData: {
    title: '',
    subtitle: '',
    image1: '',
    caption1: 'Подпись к первому изображению',
    image2: '',
    caption2: 'Подпись ко второму изображению',
    gap: '20px',
    background: '#ffffff',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок блока', default: '' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    image1: { type: 'image', label: 'Изображение 1', default: '' },
    caption1: { type: 'text', label: 'Подпись 1', default: '' },
    image2: { type: 'image', label: 'Изображение 2', default: '' },
    caption2: { type: 'text', label: 'Подпись 2', default: '' },
    gap: { type: 'text', label: 'Отступ между изображениями', default: '20px' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
  },
  tags: ['images', 'dual', 'side-by-side', 'gallery', 'изображения', 'два', 'рядом'],
})

registerBlock({
  type: 'social-share',
  label: 'Кнопки шаринга',
  category: 'interactive',
  icon: '📤',
  description: 'Кнопки для шаринга в соцсети',
  defaultData: {
    label: 'Поделиться:',
    url: '',
    shareTitle: '',
    align: 'center',
    networks: ['vk', 'telegram', 'twitter', 'whatsapp', 'ok'],
    background: '#ffffff',
  },
  schema: {
    label: { type: 'text', label: 'Подпись', default: 'Поделиться:' },
    url: { type: 'url', label: 'URL для шаринга (пусто = текущая страница)', default: '' },
    shareTitle: { type: 'text', label: 'Текст для шаринга', default: '' },
    align: { type: 'select', label: 'Выравнивание', default: 'center', options: [{ value: 'left', label: 'Слева' }, { value: 'center', label: 'По центру' }, { value: 'right', label: 'Справа' }] },
    networks: { type: 'array', label: 'Сети (vk, telegram, twitter, whatsapp, facebook, linkedin, ok)', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
  },
  tags: ['share', 'social', 'vk', 'telegram', 'twitter', 'шаринг', 'поделиться', 'соцсети'],
})

registerBlock({
  type: 'anchor-menu',
  label: 'Якорное меню',
  category: 'navigation',
  icon: '📍',
  description: 'Липкая навигация по разделам страницы',
  defaultData: {
    items: [
      { label: 'О нас', anchor: 'about' },
      { label: 'Услуги', anchor: 'services' },
      { label: 'Портфолио', anchor: 'portfolio' },
      { label: 'Отзывы', anchor: 'reviews' },
      { label: 'Контакты', anchor: 'contacts' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    items: { type: 'array', label: 'Пункты меню (label, anchor)', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['anchor', 'navigation', 'sticky', 'menu', 'in-page', 'якорь', 'навигация', 'меню'],
})

registerBlock({
  type: 'featured-post',
  label: 'Избранная публикация',
  category: 'content',
  icon: '🌟',
  description: 'Большая карточка избранной статьи + связанные посты',
  defaultData: {
    sectionTitle: 'Последние публикации',
    allLink: '/blog',
    image: '',
    category: 'Новость',
    title: 'Название главной статьи блога',
    excerpt: 'Краткое описание статьи, которое заинтересует читателя и заставит его кликнуть.',
    date: '15 июля 2025',
    author: 'Алексей Петров',
    readTime: '5 мин',
    link: '#',
    relatedPosts: [
      { title: 'Вторая статья блога', date: '14 июля 2025', link: '#', image: '' },
      { title: 'Третья статья блога', date: '13 июля 2025', link: '#', image: '' },
      { title: 'Четвёртая статья блога', date: '12 июля 2025', link: '#', image: '' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    sectionTitle: { type: 'text', label: 'Заголовок секции', default: 'Последние публикации' },
    allLink: { type: 'url', label: 'Ссылка "Все публикации"', default: '/blog' },
    image: { type: 'image', label: 'Главное изображение', default: '' },
    category: { type: 'text', label: 'Категория', default: '' },
    title: { type: 'text', label: 'Заголовок статьи', default: '' },
    excerpt: { type: 'text', label: 'Анонс', default: '' },
    date: { type: 'text', label: 'Дата', default: '' },
    author: { type: 'text', label: 'Автор', default: '' },
    readTime: { type: 'text', label: 'Время чтения', default: '' },
    link: { type: 'url', label: 'URL статьи', default: '#' },
    relatedPosts: { type: 'array', label: 'Связанные посты', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['featured', 'blog', 'post', 'article', 'news', 'избранное', 'блог', 'статья'],
})

registerBlock({
  type: 'image-hotspot',
  label: 'Интерактивное изображение',
  category: 'interactive',
  icon: '🗺️',
  description: 'Изображение с кликабельными точками-подсказками',
  defaultData: {
    title: 'Наш продукт',
    subtitle: 'Нажмите на точки для подробностей',
    image: '',
    hotspots: [
      { x: 30, y: 40, title: 'Функция A', text: 'Описание этой функции продукта' },
      { x: 60, y: 25, title: 'Функция B', text: 'Ещё одна важная характеристика' },
      { x: 75, y: 65, title: 'Функция C', text: 'Третья особенность продукта' },
    ],
    background: '#f9fafb',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: '' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    image: { type: 'image', label: 'Изображение', default: '' },
    hotspots: { type: 'array', label: 'Точки (x%, y%, title, text)', default: [] },
    background: { type: 'color', label: 'Фон', default: '#f9fafb' },
  },
  tags: ['hotspot', 'interactive', 'image', 'map', 'точки', 'интерактив', 'изображение'],
})

registerBlock({
  type: 'progress-tracker',
  label: 'Трекер прогресса',
  category: 'content',
  icon: '✅',
  description: 'Вертикальный список шагов с иконками статуса',
  defaultData: {
    title: 'Статус заказа',
    subtitle: '',
    steps: [
      { label: 'Заказ принят', desc: 'Мы получили ваш заказ', status: 'done' },
      { label: 'Обработка', desc: 'Подготавливаем к отправке', status: 'active' },
      { label: 'Отправлен', desc: 'В пути', status: 'pending' },
      { label: 'Доставлен', desc: 'Ожидает получения', status: 'pending' },
    ],
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Статус заказа' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    steps: { type: 'array', label: 'Шаги (label, desc, status: done/active/pending)', default: [] },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['progress', 'tracker', 'steps', 'order', 'status', 'прогресс', 'статус', 'шаги'],
})

registerBlock({
  type: 'event-card-v2',
  label: 'Карточки событий v2',
  category: 'content',
  icon: '📅',
  description: 'Сетка карточек событий с датой, временем и локацией',
  defaultData: {
    title: 'Предстоящие события',
    subtitle: 'Присоединяйтесь к нашим мероприятиям',
    columns: 3,
    events: [
      { title: 'Вебинар: Продвижение в 2025', date: '25 Июл', time: '19:00 МСК', location: 'Онлайн', desc: 'Разбираем лучшие стратегии продвижения.', online: true, badge: 'Бесплатно', link: '#' },
      { title: 'Мастер-класс по дизайну', date: '2 Авг', time: '10:00', location: 'Москва, Арбат 1', desc: 'Практический мастер-класс для дизайнеров.', online: false, badge: '', link: '#' },
      { title: 'Конференция Digital Future', date: '15 Авг', time: '09:00', location: 'Санкт-Петербург', desc: 'Главная digital-конференция года.', online: false, badge: 'VIP', link: '#' },
    ],
    background: '#f9fafb',
    accentColor: '#5b6af0',
  },
  schema: {
    title: { type: 'text', label: 'Заголовок', default: 'Предстоящие события' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    columns: { type: 'select', label: 'Столбцы', default: '3', options: [{ value: '1', label: '1' }, { value: '2', label: '2' }, { value: '3', label: '3' }] },
    events: { type: 'array', label: 'События', default: [] },
    background: { type: 'color', label: 'Фон', default: '#f9fafb' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['events', 'calendar', 'cards', 'schedule', 'события', 'календарь', 'расписание'],
})

registerBlock({
  type: 'drag-compare',
  label: 'Сравнение до/после',
  category: 'interactive',
  icon: '↔️',
  description: 'Перетаскиваемый слайдер сравнения двух изображений',
  defaultData: {
    beforeImage: '',
    afterImage: '',
    beforeLabel: 'До',
    afterLabel: 'После',
    height: '480px',
    borderRadius: '16px',
  },
  schema: {
    beforeImage: { type: 'image', label: 'Изображение ДО', default: '' },
    afterImage: { type: 'image', label: 'Изображение ПОСЛЕ', default: '' },
    beforeLabel: { type: 'text', label: 'Метка ДО', default: 'До' },
    afterLabel: { type: 'text', label: 'Метка ПОСЛЕ', default: 'После' },
    height: { type: 'text', label: 'Высота', default: '480px' },
    borderRadius: { type: 'text', label: 'Скругление', default: '16px' },
  },
  tags: ['before', 'after', 'compare', 'slider', 'drag', 'до', 'после', 'сравнение', 'слайдер'],
})

registerBlock({
  type: 'multi-footer',
  label: 'Многоколоночный футер',
  category: 'navigation',
  icon: '🔗',
  description: 'Полноценный сайт-футер с колонками, соцсетями и копирайтом',
  defaultData: {
    brand: 'BST',
    logoIcon: '⬡',
    desc: 'Современная платформа для создания сайтов без кода.',
    socials: [
      { platform: 'vk', url: '#' },
      { platform: 'telegram', url: '#' },
      { platform: 'instagram', url: '#' },
    ],
    columns: [
      { title: 'Продукт', links: [{ label: 'Возможности', url: '#' }, { label: 'Тарифы', url: '#' }, { label: 'Шаблоны', url: '#' }, { label: 'Блоки', url: '#' }] },
      { title: 'Компания', links: [{ label: 'О нас', url: '#' }, { label: 'Блог', url: '#' }, { label: 'Карьера', url: '#' }, { label: 'Контакты', url: '#' }] },
      { title: 'Поддержка', links: [{ label: 'Документация', url: '#' }, { label: 'FAQ', url: '#' }, { label: 'Сообщество', url: '#' }] },
    ],
    bottomLinks: [{ label: 'Политика конфиденциальности', url: '#' }, { label: 'Условия использования', url: '#' }],
    rights: 'Все права защищены.',
    background: '#1f2937',
    accentColor: '#5b6af0',
  },
  schema: {
    brand: { type: 'text', label: 'Название бренда', default: 'BST' },
    logo: { type: 'image', label: 'Логотип (или пусто для текста)', default: '' },
    logoIcon: { type: 'text', label: 'Иконка логотипа (эмодзи)', default: '' },
    desc: { type: 'text', label: 'Описание компании', default: '' },
    socials: { type: 'array', label: 'Соцсети (platform, url)', default: [] },
    columns: { type: 'array', label: 'Колонки (title, links[])', default: [] },
    bottomLinks: { type: 'array', label: 'Нижние ссылки', default: [] },
    rights: { type: 'text', label: 'Копирайт текст', default: 'Все права защищены.' },
    background: { type: 'color', label: 'Фон', default: '#1f2937' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['footer', 'navigation', 'links', 'social', 'copyright', 'футер', 'подвал', 'ссылки'],
})

registerBlock({
  type: 'simple-footer',
  label: 'Простой футер',
  category: 'navigation',
  icon: '➖',
  description: 'Минималистичный однострочный футер',
  defaultData: {
    brand: 'BST',
    links: [
      { label: 'Главная', url: '/' },
      { label: 'О нас', url: '#' },
      { label: 'Контакты', url: '#' },
      { label: 'Политика', url: '#' },
    ],
    background: '#1f2937',
  },
  schema: {
    brand: { type: 'text', label: 'Название', default: 'BST' },
    logo: { type: 'image', label: 'Логотип', default: '' },
    links: { type: 'array', label: 'Ссылки', default: [] },
    background: { type: 'color', label: 'Фон', default: '#1f2937' },
  },
  tags: ['footer', 'simple', 'minimal', 'links', 'футер', 'простой', 'минималистичный'],
})

registerBlock({
  type: 'hero-with-form',
  label: 'Герой с формой',
  category: 'form',
  icon: '📝',
  description: 'Тёмный герой с преимуществами слева и формой заявки справа',
  defaultData: {
    badge: 'Бесплатная консультация',
    title: 'Запустите проект уже сегодня',
    subtitle: 'Заполните форму и мы свяжемся с вами в течение часа.',
    bullets: ['Без скрытых платежей', 'Бесплатный стартовый анализ', 'Гарантия результата'],
    formTitle: 'Оставить заявку',
    namePlaceholder: 'Ваше имя',
    emailPlaceholder: 'Email',
    phonePlaceholder: 'Телефон',
    showName: true,
    showPhone: true,
    submitText: 'Получить консультацию',
    successMessage: 'Спасибо! Мы свяжемся с вами.',
    disclaimer: 'Нажимая кнопку, вы соглашаетесь с политикой конфиденциальности',
    background: '#0f172a',
    accentColor: '#5b6af0',
  },
  schema: {
    badge: { type: 'text', label: 'Бейдж', default: '' },
    title: { type: 'text', label: 'Заголовок', default: '' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    bullets: { type: 'array', label: 'Буллеты преимуществ', default: [] },
    formTitle: { type: 'text', label: 'Заголовок формы', default: 'Оставить заявку' },
    namePlaceholder: { type: 'text', label: 'Плейсхолдер имени', default: 'Ваше имя' },
    emailPlaceholder: { type: 'text', label: 'Плейсхолдер email', default: 'Email' },
    phonePlaceholder: { type: 'text', label: 'Плейсхолдер телефона', default: 'Телефон' },
    showName: { type: 'boolean', label: 'Поле имени', default: true },
    showPhone: { type: 'boolean', label: 'Поле телефона', default: true },
    submitText: { type: 'text', label: 'Текст кнопки', default: 'Отправить' },
    successMessage: { type: 'text', label: 'Сообщение успеха', default: 'Спасибо!' },
    disclaimer: { type: 'text', label: 'Дисклеймер под кнопкой', default: '' },
    background: { type: 'color', label: 'Фон', default: '#0f172a' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['hero', 'form', 'lead', 'dark', 'герой', 'форма', 'лид', 'заявка'],
})

registerBlock({
  type: 'product-gallery',
  label: 'Галерея продукта',
  category: 'commerce',
  icon: '🛍️',
  description: 'Галерея изображений продукта с описанием и кнопкой "В корзину"',
  defaultData: {
    productName: 'Название продукта',
    price: '2 990 ₽',
    salePrice: '',
    discount: '',
    rating: 5,
    reviewCount: '128',
    desc: 'Подробное описание продукта. Расскажите о его особенностях, преимуществах и применении.',
    features: ['Высокое качество материалов', 'Гарантия 12 месяцев', 'Бесплатная доставка от 3 000 ₽'],
    images: [{ url: '', alt: 'Вид 1' }, { url: '', alt: 'Вид 2' }, { url: '', alt: 'Вид 3' }],
    addToCartText: 'В корзину',
    buyNowText: 'Купить сейчас',
    background: '#ffffff',
    accentColor: '#5b6af0',
  },
  schema: {
    productName: { type: 'text', label: 'Название продукта', default: 'Продукт' },
    price: { type: 'text', label: 'Цена', default: '' },
    salePrice: { type: 'text', label: 'Цена со скидкой', default: '' },
    discount: { type: 'text', label: 'Метка скидки', default: '' },
    rating: { type: 'number', label: 'Рейтинг (1-5)', default: 5, min: 1, max: 5 },
    reviewCount: { type: 'text', label: 'Кол-во отзывов', default: '' },
    desc: { type: 'text', label: 'Описание', default: '' },
    features: { type: 'array', label: 'Особенности', default: [] },
    images: { type: 'array', label: 'Изображения (url, alt)', default: [] },
    addToCartText: { type: 'text', label: 'Текст "В корзину"', default: 'В корзину' },
    buyNowText: { type: 'text', label: 'Текст "Купить сейчас"', default: '' },
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#5b6af0' },
  },
  tags: ['product', 'gallery', 'ecommerce', 'shop', 'галерея', 'продукт', 'магазин', 'корзина'],
})

// ── Batch 12 ──────────────────────────────────────────────────────────────────

registerBlock({
  type: 'hero-video-bg',
  label: 'Герой: видео-фон',
  category: 'hero',
  icon: '🎬',
  description: 'Hero-секция с полноэкранным видео на фоне и кнопками CTA',
  defaultData: {
    videoUrl: '',
    overlayColor: 'rgba(0,0,0,0.55)',
    badge: 'Специальное предложение',
    title: 'Создайте сайт, который работает',
    subtitle: 'Мощный конструктор для профессионалов. Без кода, без компромиссов.',
    ctaText: 'Начать бесплатно',
    ctaUrl: '#',
    secondaryText: 'Смотреть видео',
    secondaryUrl: '#',
    scrollText: 'Прокрутите вниз',
    accentColor: '#f59e0b',
  },
  schema: {
    videoUrl: { type: 'video', label: 'URL видео', default: '' },
    overlayColor: { type: 'color', label: 'Цвет оверлея', default: 'rgba(0,0,0,0.55)' },
    badge: { type: 'text', label: 'Бейдж', default: '' },
    title: { type: 'text', label: 'Заголовок', default: 'Заголовок' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    ctaText: { type: 'text', label: 'Текст CTA', default: '' },
    ctaUrl: { type: 'url', label: 'URL CTA', default: '#' },
    secondaryText: { type: 'text', label: 'Вторичная кнопка', default: '' },
    secondaryUrl: { type: 'url', label: 'URL вторичной кнопки', default: '#' },
    scrollText: { type: 'text', label: 'Текст прокрутки', default: '' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#f59e0b' },
  },
  tags: ['hero', 'video', 'видео', 'герой', 'фон', 'fullscreen'],
})

registerBlock({
  type: 'newsletter-section',
  label: 'Секция подписки',
  category: 'form',
  icon: '📧',
  description: 'Форма подписки на email-рассылку с тёмным фоном',
  defaultData: {
    background: '#0f172a',
    accentColor: '#6366f1',
    label: 'Новости',
    title: 'Будьте в курсе',
    subtitle: 'Подпишитесь на нашу рассылку. Только важное, без спама.',
    placeholder: 'Введите ваш email',
    buttonText: 'Подписаться',
    successText: 'Вы подписаны!',
    privacy: 'Нажимая кнопку, вы соглашаетесь с политикой конфиденциальности',
    layout: 'centered',
  },
  schema: {
    background: { type: 'color', label: 'Фон', default: '#0f172a' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#6366f1' },
    label: { type: 'text', label: 'Лейбл', default: '' },
    title: { type: 'text', label: 'Заголовок', default: 'Подпишитесь' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    placeholder: { type: 'text', label: 'Плейсхолдер email', default: 'Ваш email' },
    buttonText: { type: 'text', label: 'Текст кнопки', default: 'Подписаться' },
    successText: { type: 'text', label: 'Текст успеха', default: 'Вы подписаны!' },
    privacy: { type: 'text', label: 'Текст политики', default: '' },
    layout: { type: 'select', label: 'Макет', default: 'centered', options: [{ value: 'centered', label: 'По центру' }, { value: 'left', label: 'По левому краю' }] },
  },
  tags: ['newsletter', 'email', 'subscribe', 'подписка', 'рассылка', 'форма'],
})

registerBlock({
  type: 'stats-cards',
  label: 'Статистика в карточках',
  category: 'content',
  icon: '📊',
  description: 'Статистика в отдельных карточках с иконками',
  defaultData: {
    background: '#f8fafc',
    accentColor: '#6366f1',
    title: 'Наши достижения',
    subtitle: 'Результаты, которые говорят сами за себя',
    stats: [
      { icon: '🚀', value: '10K+', label: 'Клиентов', color: '#6366f1' },
      { icon: '⭐', value: '4.9', label: 'Рейтинг', color: '#f59e0b' },
      { icon: '🌍', value: '50+', label: 'Стран', color: '#10b981' },
      { icon: '💬', value: '99%', label: 'Довольны', color: '#ef4444' },
    ],
  },
  schema: {
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#6366f1' },
    title: { type: 'text', label: 'Заголовок', default: '' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    stats: { type: 'array', label: 'Статистика (icon, value, label, color)', default: [] },
  },
  tags: ['stats', 'numbers', 'cards', 'statistics', 'статистика', 'карточки', 'цифры'],
})

registerBlock({
  type: 'testimonials-carousel',
  label: 'Карусель отзывов',
  category: 'content',
  icon: '🎠',
  description: 'Слайдер с отзывами клиентов, стрелками и точками',
  defaultData: {
    background: '#ffffff',
    accentColor: '#6366f1',
    title: 'Что говорят клиенты',
    items: [
      { quote: 'Лучший продукт, которым я пользовался. Экономит часы работы каждый день.', name: 'Анна К.', role: 'Дизайнер', rating: 5 },
      { quote: 'Команда поддержки всегда помогает. Рекомендую всем коллегам!', name: 'Максим С.', role: 'Разработчик', rating: 5 },
      { quote: 'Отличное решение для нашего бизнеса. Окупилось за первый месяц.', name: 'Ольга В.', role: 'CEO', rating: 5 },
    ],
  },
  schema: {
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#6366f1' },
    title: { type: 'text', label: 'Заголовок', default: '' },
    items: { type: 'array', label: 'Отзывы (quote, name, role, avatar, rating)', default: [] },
  },
  tags: ['testimonials', 'carousel', 'slider', 'reviews', 'отзывы', 'карусель', 'слайдер'],
})

registerBlock({
  type: 'features-grid-v2',
  label: 'Сетка функций v2',
  category: 'content',
  icon: '🧩',
  description: 'Функции в сетке с иконками, цветными значками и описаниями',
  defaultData: {
    background: '#ffffff',
    accentColor: '#6366f1',
    label: 'Возможности',
    title: 'Всё необходимое для успеха',
    subtitle: 'Мощные инструменты, простой интерфейс',
    columns: 3,
    items: [
      { icon: '⚡', title: 'Молниеносная скорость', desc: 'Сайты загружаются мгновенно благодаря умной оптимизации.', color: '#f59e0b' },
      { icon: '🔒', title: 'Безопасность', desc: 'SSL, защита от DDoS и автоматические бэкапы включены.', color: '#10b981' },
      { icon: '📱', title: 'Адаптивность', desc: 'Идеально выглядит на всех устройствах и экранах.', color: '#6366f1' },
      { icon: '🤖', title: 'AI-помощник', desc: 'Создавайте контент с помощью искусственного интеллекта.', color: '#8b5cf6' },
      { icon: '📊', title: 'Аналитика', desc: 'Подробная статистика и отчёты в реальном времени.', color: '#ef4444' },
      { icon: '🌍', title: 'CDN по всему миру', desc: 'Серверы в 30+ странах для быстрой доставки.', color: '#06b6d4' },
    ],
  },
  schema: {
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#6366f1' },
    cardBg: { type: 'color', label: 'Фон карточки', default: '#f8fafc' },
    label: { type: 'text', label: 'Лейбл', default: '' },
    title: { type: 'text', label: 'Заголовок', default: '' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    columns: { type: 'number', label: 'Колонок', default: 3, min: 2, max: 4 },
    items: { type: 'array', label: 'Элементы (icon, title, desc, color)', default: [] },
  },
  tags: ['features', 'grid', 'cards', 'функции', 'сетка', 'возможности', 'карточки'],
})

registerBlock({
  type: 'contact-form-v2',
  label: 'Форма контакта v2',
  category: 'form',
  icon: '📬',
  description: 'Контактная форма с адресом, телефоном, email и картой',
  defaultData: {
    background: '#f8fafc',
    accentColor: '#6366f1',
    title: 'Свяжитесь с нами',
    subtitle: 'Ответим в течение рабочего дня',
    namePlaceholder: 'Ваше имя',
    emailPlaceholder: 'Email',
    phonePlaceholder: 'Телефон',
    subjectPlaceholder: 'Тема сообщения',
    messagePlaceholder: 'Ваше сообщение',
    submitText: 'Отправить сообщение',
    successText: 'Сообщение отправлено!',
    showPhone: true,
    showSubject: true,
    address: 'Москва, ул. Примерная, 1',
    phone: '+7 (999) 123-45-67',
    email: 'info@example.com',
    mapEmbed: '',
  },
  schema: {
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#6366f1' },
    title: { type: 'text', label: 'Заголовок', default: '' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    submitText: { type: 'text', label: 'Текст кнопки', default: 'Отправить' },
    successText: { type: 'text', label: 'Текст успеха', default: 'Отправлено!' },
    showPhone: { type: 'boolean', label: 'Поле телефона', default: true },
    showSubject: { type: 'boolean', label: 'Поле темы', default: false },
    address: { type: 'text', label: 'Адрес', default: '' },
    phone: { type: 'text', label: 'Телефон', default: '' },
    email: { type: 'text', label: 'Email', default: '' },
    mapEmbed: { type: 'url', label: 'URL Google Maps embed', default: '' },
    namePlaceholder: { type: 'text', label: 'Плейсхолдер имени', default: 'Имя' },
    emailPlaceholder: { type: 'text', label: 'Плейсхолдер email', default: 'Email' },
    phonePlaceholder: { type: 'text', label: 'Плейсхолдер телефона', default: 'Телефон' },
    messagePlaceholder: { type: 'text', label: 'Плейсхолдер сообщения', default: 'Сообщение' },
    subjectPlaceholder: { type: 'text', label: 'Плейсхолдер темы', default: 'Тема' },
  },
  tags: ['contact', 'form', 'map', 'контакты', 'форма', 'карта', 'адрес', 'телефон'],
})

registerBlock({
  type: 'blog-grid-v2',
  label: 'Блог-сетка v2',
  category: 'content',
  icon: '📰',
  description: 'Сетка статей блога с изображением, категорией и мета-данными',
  defaultData: {
    background: '#ffffff',
    accentColor: '#6366f1',
    label: 'Блог',
    title: 'Последние статьи',
    subtitle: 'Полезные материалы и советы',
    allPostsText: 'Все статьи',
    allPostsUrl: '/blog',
    posts: [
      { image: '', category: 'Маркетинг', title: '10 способов увеличить конверсию', excerpt: 'Разбираем проверенные техники, которые помогают превращать посетителей в клиентов.', date: '15 января', readTime: '5 мин', author: 'Анна К.', link: '#' },
      { image: '', category: 'Дизайн', title: 'Тренды веб-дизайна 2025', excerpt: 'Что будет популярно в дизайне сайтов в следующем году и как подготовиться.', date: '12 января', readTime: '7 мин', author: 'Иван С.', link: '#' },
      { image: '', category: 'SEO', title: 'Как попасть в топ Google', excerpt: 'Пошаговое руководство по поисковой оптимизации для небольших сайтов.', date: '8 января', readTime: '9 мин', author: 'Мария В.', link: '#' },
    ],
  },
  schema: {
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#6366f1' },
    label: { type: 'text', label: 'Лейбл', default: '' },
    title: { type: 'text', label: 'Заголовок', default: '' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    allPostsText: { type: 'text', label: 'Текст ссылки "Все статьи"', default: '' },
    allPostsUrl: { type: 'url', label: 'URL всех статей', default: '/blog' },
    posts: { type: 'array', label: 'Статьи (image, category, title, excerpt, date, readTime, author, link)', default: [] },
  },
  tags: ['blog', 'posts', 'grid', 'news', 'блог', 'статьи', 'новости', 'сетка'],
})

registerBlock({
  type: 'partners-logos-v2',
  label: 'Логотипы партнёров v2',
  category: 'content',
  icon: '🤝',
  description: 'Секция с логотипами партнёров или клиентов с эффектом серого',
  defaultData: {
    background: '#ffffff',
    title: 'Нам доверяют',
    logos: [
      { name: 'Company A', url: '', link: '' },
      { name: 'Company B', url: '', link: '' },
      { name: 'Company C', url: '', link: '' },
      { name: 'Company D', url: '', link: '' },
      { name: 'Company E', url: '', link: '' },
    ],
  },
  schema: {
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    title: { type: 'text', label: 'Заголовок', default: '' },
    logos: { type: 'array', label: 'Логотипы (url, name, link)', default: [] },
  },
  tags: ['partners', 'logos', 'clients', 'партнёры', 'логотипы', 'клиенты', 'доверие'],
})

registerBlock({
  type: 'service-cards',
  label: 'Карточки услуг',
  category: 'content',
  icon: '🛠️',
  description: 'Услуги в красивых карточках с иконкой, списком функций и ценой',
  defaultData: {
    background: '#f8fafc',
    accentColor: '#6366f1',
    label: 'Услуги',
    title: 'Что мы предлагаем',
    subtitle: 'Полный спектр решений для вашего бизнеса',
    services: [
      { icon: '🎨', title: 'Дизайн', desc: 'Создаём красивые и функциональные интерфейсы для любых задач.', features: ['UI/UX дизайн', 'Прототипирование', 'Брендинг'], price: 'от 30 000 ₽', ctaText: 'Узнать подробнее', ctaUrl: '#', color: '#6366f1' },
      { icon: '⚙️', title: 'Разработка', desc: 'Разрабатываем надёжные технические решения любой сложности.', features: ['Frontend', 'Backend', 'API интеграции'], price: 'от 60 000 ₽', ctaText: 'Узнать подробнее', ctaUrl: '#', color: '#10b981' },
      { icon: '📈', title: 'Маркетинг', desc: 'Продвигаем сайты и увеличиваем продажи проверенными методами.', features: ['SEO оптимизация', 'Контекстная реклама', 'SMM'], price: 'от 20 000 ₽', ctaText: 'Узнать подробнее', ctaUrl: '#', color: '#f59e0b' },
    ],
  },
  schema: {
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#6366f1' },
    label: { type: 'text', label: 'Лейбл', default: '' },
    title: { type: 'text', label: 'Заголовок', default: '' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    services: { type: 'array', label: 'Услуги (icon, title, desc, features, price, ctaText, ctaUrl, color)', default: [] },
  },
  tags: ['services', 'cards', 'pricing', 'услуги', 'карточки', 'предложения'],
})

registerBlock({
  type: 'pricing-cards-v3',
  label: 'Тарифы v3',
  category: 'content',
  icon: '💳',
  description: 'Тарифные планы в карточках с выделением популярного тарифа',
  defaultData: {
    background: '#f8fafc',
    accentColor: '#6366f1',
    popularLabel: '⭐ Популярный',
    title: 'Выберите тариф',
    subtitle: 'Начните бесплатно, масштабируйтесь по мере роста',
    plans: [
      { name: 'Старт', price: '0 ₽', period: 'мес', desc: 'Для личных проектов', features: ['1 сайт', 'Поддомен', '500 МБ'], cta: 'Начать бесплатно', ctaUrl: '#', popular: false, color: '#64748b' },
      { name: 'Про', price: '790 ₽', period: 'мес', desc: 'Для профессионалов', features: ['5 сайтов', 'Свой домен', '5 ГБ', 'Все блоки', 'Поддержка'], cta: 'Выбрать Про', ctaUrl: '#', popular: true, color: '#6366f1' },
      { name: 'Агентство', price: '3 990 ₽', period: 'мес', desc: 'Для команд', features: ['∞ сайтов', 'White Label', 'API', 'E-commerce', 'Аналитика'], cta: 'Связаться', ctaUrl: '#', popular: false, color: '#0f172a' },
    ],
  },
  schema: {
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#6366f1' },
    popularLabel: { type: 'text', label: 'Метка популярного', default: '⭐ Популярный' },
    title: { type: 'text', label: 'Заголовок', default: '' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    plans: { type: 'array', label: 'Тарифы (name, price, period, desc, features, cta, ctaUrl, popular, color)', default: [] },
  },
  tags: ['pricing', 'plans', 'cards', 'тарифы', 'цены', 'планы'],
})

registerBlock({
  type: 'gallery-lightbox',
  label: 'Галерея с лайтбоксом',
  category: 'gallery',
  icon: '🖼️',
  description: 'Сетка изображений с всплывающим просмотром и навигацией',
  defaultData: {
    background: '#ffffff',
    accentColor: '#6366f1',
    title: 'Наши работы',
    images: [
      { url: '', alt: 'Работа 1', caption: '' },
      { url: '', alt: 'Работа 2', caption: '' },
      { url: '', alt: 'Работа 3', caption: '' },
      { url: '', alt: 'Работа 4', caption: '' },
      { url: '', alt: 'Работа 5', caption: '' },
      { url: '', alt: 'Работа 6', caption: '' },
    ],
  },
  schema: {
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#6366f1' },
    title: { type: 'text', label: 'Заголовок', default: '' },
    images: { type: 'array', label: 'Изображения (url, alt, caption)', default: [] },
  },
  tags: ['gallery', 'lightbox', 'images', 'галерея', 'фото', 'лайтбокс', 'просмотр'],
})

registerBlock({
  type: 'image-banner',
  label: 'Баннер с изображением',
  category: 'hero',
  icon: '🖼️',
  description: 'Баннер с текстом и изображением, тёмным фоном',
  defaultData: {
    background: '#1e293b',
    accentColor: '#f59e0b',
    textSide: 'left',
    badge: 'Новинка',
    title: 'Создайте сайт вашей мечты',
    subtitle: 'Красивый дизайн, быстрая загрузка, простое управление.',
    ctaText: 'Попробовать',
    ctaUrl: '#',
    image: '',
  },
  schema: {
    background: { type: 'color', label: 'Фон', default: '#1e293b' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#f59e0b' },
    textSide: { type: 'select', label: 'Сторона текста', default: 'left', options: [{ value: 'left', label: 'Слева' }, { value: 'right', label: 'Справа' }] },
    badge: { type: 'text', label: 'Бейдж', default: '' },
    title: { type: 'text', label: 'Заголовок', default: '' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    ctaText: { type: 'text', label: 'Текст CTA', default: '' },
    ctaUrl: { type: 'url', label: 'URL CTA', default: '#' },
    image: { type: 'image', label: 'Изображение', default: '' },
  },
  tags: ['banner', 'image', 'hero', 'баннер', 'изображение', 'герой'],
})

registerBlock({
  type: 'faq-minimal',
  label: 'FAQ минимальный',
  category: 'content',
  icon: '❓',
  description: 'Часто задаваемые вопросы в простом аккордеоне',
  defaultData: {
    background: '#ffffff',
    accentColor: '#6366f1',
    title: 'Часто задаваемые вопросы',
    subtitle: 'Ответы на самые популярные вопросы',
    ctaText: 'Задать вопрос',
    ctaUrl: '#',
    items: [
      { q: 'Сколько стоит создание сайта?', a: 'Стоимость зависит от сложности проекта. Начальная цена — от 0 ₽ для простых сайтов на нашем конструкторе.' },
      { q: 'Как долго длится разработка?', a: 'Простой лендинг можно создать за несколько часов. Сложные проекты занимают от нескольких дней до нескольких недель.' },
      { q: 'Нужны ли технические знания?', a: 'Нет! Наш конструктор создан так, чтобы любой мог создать профессиональный сайт без единой строки кода.' },
      { q: 'Можно ли подключить свой домен?', a: 'Да, на тарифах Про и выше вы можете подключить любой домен, который уже есть у вас или купить новый.' },
    ],
  },
  schema: {
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#6366f1' },
    title: { type: 'text', label: 'Заголовок', default: '' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    ctaText: { type: 'text', label: 'Текст CTA', default: '' },
    ctaUrl: { type: 'url', label: 'URL CTA', default: '#' },
    items: { type: 'array', label: 'Вопросы (q, a)', default: [] },
  },
  tags: ['faq', 'questions', 'accordion', 'вопросы', 'ответы', 'аккордеон'],
})

registerBlock({
  type: 'hero-dark',
  label: 'Тёмный герой',
  category: 'hero',
  icon: '🌑',
  description: 'Минималистичный тёмный Hero с градиентом и метриками',
  defaultData: {
    background: '#0a0a0a',
    accentColor: '#a78bfa',
    badge: 'Новый уровень',
    title: 'Технологии будущего уже здесь',
    subtitle: 'Создавайте мощные продукты с помощью нашей платформы нового поколения.',
    ctaText: 'Начать',
    ctaUrl: '#',
    secondaryText: 'Узнать больше',
    secondaryUrl: '#',
    showMetrics: true,
    metrics: [
      { value: '99.9%', label: 'Uptime' },
      { value: '<50ms', label: 'Latency' },
      { value: '10M+', label: 'Requests/day' },
      { value: 'SOC2', label: 'Certified' },
    ],
  },
  schema: {
    background: { type: 'color', label: 'Фон', default: '#0a0a0a' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#a78bfa' },
    badge: { type: 'text', label: 'Бейдж', default: '' },
    title: { type: 'text', label: 'Заголовок', default: '' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    ctaText: { type: 'text', label: 'Текст CTA', default: '' },
    ctaUrl: { type: 'url', label: 'URL CTA', default: '#' },
    secondaryText: { type: 'text', label: 'Вторичная кнопка', default: '' },
    secondaryUrl: { type: 'url', label: 'URL вторичной кнопки', default: '#' },
    image: { type: 'image', label: 'Фоновое изображение', default: '' },
    imageOpacity: { type: 'number', label: 'Прозрачность фото (0-1)', default: 0.2, min: 0, max: 1 },
    showMetrics: { type: 'boolean', label: 'Показать метрики', default: false },
    metrics: { type: 'array', label: 'Метрики (value, label)', default: [] },
  },
  tags: ['hero', 'dark', 'gradient', 'герой', 'тёмный', 'tech', 'технологии'],
})

registerBlock({
  type: 'cta-colored',
  label: 'CTA цветной',
  category: 'content',
  icon: '🎨',
  description: 'Призыв к действию с ярким цветным фоном',
  defaultData: {
    background: '#6366f1',
    emoji: '🚀',
    label: 'Ограниченное предложение',
    title: 'Готовы начать?',
    subtitle: 'Присоединяйтесь к тысячам компаний, которые уже используют нашу платформу.',
    ctaText: 'Начать бесплатно',
    ctaUrl: '#',
    secondaryText: 'Смотреть демо',
    secondaryUrl: '#',
    note: 'Кредитная карта не нужна. Отмена в любой момент.',
  },
  schema: {
    background: { type: 'color', label: 'Фон', default: '#6366f1' },
    emoji: { type: 'text', label: 'Эмодзи', default: '' },
    label: { type: 'text', label: 'Лейбл', default: '' },
    title: { type: 'text', label: 'Заголовок', default: '' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    ctaText: { type: 'text', label: 'Текст CTA', default: '' },
    ctaUrl: { type: 'url', label: 'URL CTA', default: '#' },
    secondaryText: { type: 'text', label: 'Вторичная кнопка', default: '' },
    secondaryUrl: { type: 'url', label: 'URL вторичной кнопки', default: '#' },
    note: { type: 'text', label: 'Примечание', default: '' },
  },
  tags: ['cta', 'colored', 'action', 'призыв', 'цветной', 'кнопка'],
})

registerBlock({
  type: 'video-banner',
  label: 'Видео-баннер',
  category: 'media',
  icon: '▶️',
  description: 'Раздел с превью видео, большой кнопкой воспроизведения и CTA',
  defaultData: {
    background: '#0f172a',
    accentColor: '#6366f1',
    title: 'Посмотрите, как это работает',
    subtitle: 'Узнайте за 2 минуты, как наша платформа изменит ваш бизнес.',
    thumbnail: '',
    videoUrl: '',
    ctaText: 'Попробовать бесплатно',
    ctaUrl: '#',
  },
  schema: {
    background: { type: 'color', label: 'Фон', default: '#0f172a' },
    accentColor: { type: 'color', label: 'Цвет кнопки', default: '#6366f1' },
    title: { type: 'text', label: 'Заголовок', default: '' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    thumbnail: { type: 'image', label: 'Превью-изображение', default: '' },
    videoUrl: { type: 'video', label: 'URL видео', default: '' },
    ctaText: { type: 'text', label: 'Текст CTA', default: '' },
    ctaUrl: { type: 'url', label: 'URL CTA', default: '#' },
  },
  tags: ['video', 'banner', 'play', 'видео', 'баннер', 'воспроизведение', 'превью'],
})

registerBlock({
  type: 'team-timeline',
  label: 'Таймлайн команды',
  category: 'content',
  icon: '📅',
  description: 'История компании или вехи в вертикальном таймлайне',
  defaultData: {
    background: '#ffffff',
    accentColor: '#6366f1',
    title: 'Наш путь',
    subtitle: 'Как мы росли год за годом',
    events: [
      { year: '2020', icon: '🚀', title: 'Основание компании', desc: 'Начали с небольшой команды из 3 человек и большой идеей изменить рынок.' },
      { year: '2021', icon: '📈', title: 'Первые 1000 клиентов', desc: 'Достигли отметки в 1000 клиентов всего за 8 месяцев после запуска.' },
      { year: '2022', icon: '🌍', title: 'Международная экспансия', desc: 'Вышли на рынки Европы и США, открыли офисы в 3 странах.' },
      { year: '2023', icon: '🏆', title: 'Лучший стартап года', desc: 'Получили награду как лучший технологический стартап по версии TechAwards.' },
    ],
  },
  schema: {
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#6366f1' },
    title: { type: 'text', label: 'Заголовок', default: '' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    events: { type: 'array', label: 'События (year, icon, title, desc)', default: [] },
  },
  tags: ['timeline', 'history', 'story', 'таймлайн', 'история', 'события', 'вехи'],
})

registerBlock({
  type: 'two-column-text',
  label: 'Двухколоночный текст',
  category: 'text',
  icon: '📄',
  description: 'Текст в двух колонках с заголовками и ссылками',
  defaultData: {
    background: '#ffffff',
    accentColor: '#6366f1',
    label: 'О нас',
    title: 'Мы создаём продукты, которые меняют мир',
    col1Title: 'Наша миссия',
    col1Text: 'Мы верим, что каждый человек заслуживает доступа к качественным инструментам. Наша платформа делает профессиональные технологии доступными для всех.',
    col1Link: 'Подробнее о нас',
    col1Url: '#',
    col2Title: 'Наши ценности',
    col2Text: 'Прозрачность, инновации и забота о клиенте — основа всего, что мы делаем. Мы строим долгосрочные отношения, основанные на доверии.',
    col2Link: 'Наша команда',
    col2Url: '#',
  },
  schema: {
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#6366f1' },
    label: { type: 'text', label: 'Лейбл', default: '' },
    title: { type: 'text', label: 'Главный заголовок', default: '' },
    col1Title: { type: 'text', label: 'Заголовок колонки 1', default: '' },
    col1Text: { type: 'richtext', label: 'Текст колонки 1', default: '' },
    col1Link: { type: 'text', label: 'Текст ссылки 1', default: '' },
    col1Url: { type: 'url', label: 'URL ссылки 1', default: '#' },
    col2Title: { type: 'text', label: 'Заголовок колонки 2', default: '' },
    col2Text: { type: 'richtext', label: 'Текст колонки 2', default: '' },
    col2Link: { type: 'text', label: 'Текст ссылки 2', default: '' },
    col2Url: { type: 'url', label: 'URL ссылки 2', default: '#' },
  },
  tags: ['text', 'columns', 'about', 'текст', 'колонки', 'о нас'],
})

registerBlock({
  type: 'icon-grid',
  label: 'Сетка иконок',
  category: 'content',
  icon: '🔷',
  description: 'Иконки с заголовками и описаниями в сетке',
  defaultData: {
    background: '#f8fafc',
    accentColor: '#6366f1',
    title: 'Почему нас выбирают',
    subtitle: 'Ключевые преимущества нашего продукта',
    columns: 4,
    showLinks: false,
    items: [
      { icon: '⚡', title: 'Быстро', desc: 'Мгновенная загрузка на любом устройстве' },
      { icon: '🔒', title: 'Безопасно', desc: 'SSL и защита данных включены' },
      { icon: '📱', title: 'Адаптивно', desc: 'Выглядит отлично на всех экранах' },
      { icon: '🤖', title: 'AI', desc: 'Умные подсказки и автоматизация' },
      { icon: '🌍', title: 'Глобально', desc: 'CDN серверы по всему миру' },
      { icon: '📊', title: 'Аналитика', desc: 'Детальная статистика и отчёты' },
      { icon: '💬', title: 'Поддержка', desc: 'Помощь специалистов 24/7' },
      { icon: '🔧', title: 'Интеграции', desc: 'Подключение любых сервисов' },
    ],
  },
  schema: {
    background: { type: 'color', label: 'Фон', default: '#f8fafc' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#6366f1' },
    title: { type: 'text', label: 'Заголовок', default: '' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    columns: { type: 'number', label: 'Колонок', default: 4, min: 2, max: 6 },
    showLinks: { type: 'boolean', label: 'Показывать ссылки', default: false },
    items: { type: 'array', label: 'Иконки (icon, title, desc, link, linkText, color)', default: [] },
  },
  tags: ['icons', 'grid', 'features', 'иконки', 'сетка', 'преимущества'],
})

registerBlock({
  type: 'page-footer-v2',
  label: 'Футер v2',
  category: 'navigation',
  icon: '📋',
  description: 'Тёмный футер с колонками ссылок, соцсетями и копирайтом',
  defaultData: {
    background: '#0f172a',
    accentColor: '#6366f1',
    siteName: 'BST',
    tagline: 'Создавайте сайты, которые работают на вас.',
    copyright: '© 2025 BST. Все права защищены.',
    socials: [
      { platform: 'telegram', url: '#' },
      { platform: 'instagram', url: '#' },
      { platform: 'vk', url: '#' },
    ],
    columns: [
      { title: 'Продукт', links: [{ text: 'Возможности', url: '#' }, { text: 'Тарифы', url: '#' }, { text: 'Шаблоны', url: '#' }] },
      { title: 'Компания', links: [{ text: 'О нас', url: '#' }, { text: 'Блог', url: '#' }, { text: 'Карьера', url: '#' }] },
      { title: 'Поддержка', links: [{ text: 'Документация', url: '#' }, { text: 'Помощь', url: '#' }, { text: 'Контакты', url: '#' }] },
    ],
    bottomLinks: [
      { text: 'Политика конфиденциальности', url: '#' },
      { text: 'Условия использования', url: '#' },
    ],
  },
  schema: {
    background: { type: 'color', label: 'Фон', default: '#0f172a' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#6366f1' },
    logo: { type: 'image', label: 'Логотип', default: '' },
    siteName: { type: 'text', label: 'Название сайта', default: '' },
    tagline: { type: 'text', label: 'Слоган', default: '' },
    copyright: { type: 'text', label: 'Копирайт', default: '' },
    socials: { type: 'array', label: 'Соцсети (platform, url)', default: [] },
    columns: { type: 'array', label: 'Колонки (title, links[])', default: [] },
    bottomLinks: { type: 'array', label: 'Нижние ссылки (text, url)', default: [] },
  },
  tags: ['footer', 'navigation', 'links', 'футер', 'навигация', 'ссылки', 'соцсети'],
})

registerBlock({
  type: 'map-section',
  label: 'Секция с картой',
  category: 'embed',
  icon: '🗺️',
  description: 'Google Maps или Яндекс.Карты с информационной строкой',
  defaultData: {
    background: '#ffffff',
    title: 'Как нас найти',
    subtitle: 'Мы находимся в центре города',
    embedUrl: '',
    mapHeight: '400px',
    fullWidth: false,
    infoBar: true,
    infoItems: [
      { icon: '📍', label: 'Адрес', value: 'ул. Примерная, 1' },
      { icon: '🕐', label: 'Часы работы', value: 'Пн–Пт 9:00–18:00' },
      { icon: '📞', label: 'Телефон', value: '+7 (999) 123-45-67' },
    ],
  },
  schema: {
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    title: { type: 'text', label: 'Заголовок', default: '' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    embedUrl: { type: 'url', label: 'URL embed карты', default: '' },
    mapHeight: { type: 'text', label: 'Высота карты', default: '400px' },
    fullWidth: { type: 'boolean', label: 'На всю ширину', default: false },
    infoBar: { type: 'boolean', label: 'Информационная полоса', default: true },
    infoItems: { type: 'array', label: 'Элементы инфо (icon, label, value)', default: [] },
  },
  tags: ['map', 'embed', 'location', 'карта', 'адрес', 'местоположение'],
})

registerBlock({
  type: 'cta-countdown',
  label: 'CTA с таймером',
  category: 'interactive',
  icon: '⏰',
  description: 'Призыв к действию с обратным отсчётом до дедлайна',
  defaultData: {
    background: '#0f172a',
    accentColor: '#f59e0b',
    badge: '⚡ Акция заканчивается',
    title: 'Успейте до конца предложения',
    subtitle: 'Скидка 40% только до истечения таймера. Не упустите возможность!',
    deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    ctaText: 'Получить скидку',
    ctaUrl: '#',
    note: 'Предложение действует только для новых клиентов',
  },
  schema: {
    background: { type: 'color', label: 'Фон', default: '#0f172a' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#f59e0b' },
    badge: { type: 'text', label: 'Бейдж', default: '' },
    title: { type: 'text', label: 'Заголовок', default: '' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    deadline: { type: 'text', label: 'Дата дедлайна (ISO)', default: '' },
    ctaText: { type: 'text', label: 'Текст CTA', default: '' },
    ctaUrl: { type: 'url', label: 'URL CTA', default: '#' },
    note: { type: 'text', label: 'Примечание', default: '' },
  },
  tags: ['cta', 'countdown', 'timer', 'deadline', 'таймер', 'обратный отсчёт', 'акция'],
})

registerBlock({
  type: 'accordion-tabs',
  label: 'Вкладки-аккордеон',
  category: 'interactive',
  icon: '📂',
  description: 'Переключаемые вкладки с контентом в виде боковой панели',
  defaultData: {
    background: '#ffffff',
    accentColor: '#6366f1',
    title: 'Как это работает',
    subtitle: 'Подробно о каждом шаге',
    tabs: [
      { label: 'Шаг 1: Регистрация', icon: '📝', content: 'Создайте бесплатный аккаунт за 30 секунд. Никакой кредитной карты не нужно — просто введите email и пароль.' },
      { label: 'Шаг 2: Выбор шаблона', icon: '🎨', content: 'Выберите один из 30+ готовых шаблонов или начните с чистого листа. Все шаблоны адаптивны и оптимизированы для SEO.' },
      { label: 'Шаг 3: Настройка', icon: '⚙️', content: 'Добавляйте и перетаскивайте блоки, меняйте цвета и шрифты, загружайте изображения. Никакого кода не нужно.' },
      { label: 'Шаг 4: Публикация', icon: '🚀', content: 'Нажмите кнопку "Опубликовать" и ваш сайт сразу доступен в интернете. Можно подключить свой домен.' },
    ],
  },
  schema: {
    background: { type: 'color', label: 'Фон', default: '#ffffff' },
    accentColor: { type: 'color', label: 'Акцентный цвет', default: '#6366f1' },
    title: { type: 'text', label: 'Заголовок', default: '' },
    subtitle: { type: 'text', label: 'Подзаголовок', default: '' },
    tabs: { type: 'array', label: 'Вкладки (label, icon, content, image)', default: [] },
  },
  tags: ['tabs', 'accordion', 'steps', 'вкладки', 'шаги', 'аккордеон', 'интерактив'],
})

registerBlock({
  type: 'auth-form',
  label: 'Вход / Регистрация + Личный кабинет',
  category: 'form',
  icon: '🔐',
  description: 'Форма входа и регистрации + настраиваемый личный кабинет для посетителей. Пользователи видны в разделе «Пользователи сайта».',
  defaultData: {
    // ── Форма входа ──────────────────────────────────
    background: '#ffffff',
    accentColor: '#5b6af0',
    title: 'Добро пожаловать',
    subtitle: 'Войдите или создайте аккаунт',
    mode: 'both',
    showName: true,
    successMessage: 'Вы успешно вошли!',
    // ── Личный кабинет ───────────────────────────────
    cabinetTitle: 'Личный кабинет',
    cabinetWelcome: 'Добро пожаловать, {name}!',
    cabinetBg: '#f5f6fa',
    cabinetAccent: '#5b6af0',
    cabinetLayout: 'sidebar',
    iconStyle: 'soft',
    avatarShape: 'circle',
    // ── Разделы кабинета ─────────────────────────────
    showSectionProfile: true,
    showSectionOrders: false,
    showSectionBookings: false,
    showSectionDownloads: false,
    showSectionFavorites: false,
    showSectionSettings: true,
    customSections: [],
  },
  schema: {
    // ── Форма ─────────────────────────────────────────
    background:     { type: 'color',   label: 'Фон формы',              default: '#ffffff' },
    accentColor:    { type: 'color',   label: 'Цвет кнопки',            default: '#5b6af0' },
    title:          { type: 'text',    label: 'Заголовок формы',         default: 'Добро пожаловать' },
    subtitle:       { type: 'text',    label: 'Подзаголовок формы',      default: '' },
    mode: { type: 'select', label: 'Режим формы', options: [
      { label: 'Вход и регистрация', value: 'both' },
      { label: 'Только вход',        value: 'login' },
      { label: 'Только регистрация', value: 'register' },
    ], default: 'both' },
    showName:       { type: 'boolean', label: 'Поле «Имя» при регистрации', default: true },
    successMessage: { type: 'text',    label: 'Сообщение после входа',   default: 'Вы успешно вошли!' },
    // ── Кабинет ───────────────────────────────────────
    cabinetTitle:   { type: 'text',    label: 'Название кабинета',       default: 'Личный кабинет' },
    cabinetWelcome: { type: 'text',    label: 'Приветствие (можно {name})', default: 'Добро пожаловать, {name}!' },
    cabinetBg:      { type: 'color',   label: 'Фон кабинета',            default: '#f5f6fa' },
    cabinetAccent:  { type: 'color',   label: 'Акцент кабинета',         default: '#5b6af0' },
    cabinetLayout: { type: 'select', label: 'Макет кабинета', options: [
      { label: 'Боковое меню',     value: 'sidebar' },
      { label: 'Карточки сеткой',  value: 'grid' },
    ], default: 'sidebar' },
    // ── Стиль ─────────────────────────────────────────
    iconStyle: { type: 'select', label: 'Стиль иконок', options: [
      { label: '✦ Мягкие (светлый фон)',  value: 'soft' },
      { label: '● Круглые (заливка)',      value: 'round' },
      { label: '■ Квадратные (заливка)',   value: 'square' },
      { label: '○ Контурные (без заливки)', value: 'outline' },
    ], default: 'soft' },
    avatarShape: { type: 'select', label: 'Форма аватара', options: [
      { label: 'Круглый',     value: 'circle' },
      { label: 'Квадратный',  value: 'square' },
      { label: 'Без аватара', value: 'none' },
    ], default: 'circle' },
    // ── Разделы ───────────────────────────────────────
    showSectionProfile:   { type: 'boolean', label: '👤 Раздел: Профиль',       default: true },
    showSectionOrders:    { type: 'boolean', label: '📦 Раздел: Мои заказы',    default: false },
    showSectionBookings:  { type: 'boolean', label: '📅 Раздел: Бронирования',  default: false },
    showSectionDownloads: { type: 'boolean', label: '📥 Раздел: Мои файлы',     default: false },
    showSectionFavorites: { type: 'boolean', label: '❤️ Раздел: Избранное',     default: false },
    showSectionSettings:  { type: 'boolean', label: '⚙️ Раздел: Настройки',    default: true },
    customSections: { type: 'array', label: 'Кастомные разделы (label, icon, content)', default: [] },
  },
  tags: ['login', 'register', 'auth', 'form', 'cabinet', 'вход', 'регистрация', 'кабинет', 'аккаунт', 'профиль'],
})

registerBlock({
  type: 'free-layout',
  label: 'Свободная компоновка',
  category: 'layout',
  icon: '⊞',
  description: 'Контейнер для произвольных элементов. Выберите этот блок, затем добавляйте заголовки, текст, изображения и кнопки из библиотеки прямо внутрь него',
  tags: ['free', 'layout', 'container', 'grid', 'columns', 'custom', 'свободный', 'контейнер', 'произвольный'],
  defaultData: {
    elements: [],
    columns: 1,
    gap: 16,
  },
  schema: {
    columns: { type: 'number', label: 'Колонки', default: 1, min: 1, max: 4 },
    gap: { type: 'number', label: 'Расстояние (px)', default: 16, min: 0, max: 64 },
  },
})

// ── Swiss Style Blocks (10 блоков = 1 полная страница) ───────────────────────

const SWISS_VARIANT_FIELD = {
  variant: {
    type: 'select' as const,
    label: 'Цветовой вариант',
    default: 'bst',
    options: [
      { value: 'bst',    label: 'Classic — BST (красный)' },
      { value: 'nord',   label: 'Warm — NORD (охристый)' },
      { value: 'klima',  label: 'Earth — KLIMA (оранжевый)' },
      { value: 'orbita', label: 'Dark — ORBITA (синий)' },
    ],
  },
}

registerBlock({
  type: 'swiss-page',
  label: 'Swiss · Полная страница',
  category: 'hero',
  icon: '◼',
  description: 'Монолитная Swiss-страница — полный дизайн-система BST с редактируемым контентом',
  defaultData: { variant: 'bst' },
  schema: {},
  tags: ['swiss', 'page', 'full', 'landing', 'complete'],
})

registerBlock({
  type: 'glassmorphism-page',
  label: 'Modern SaaS · Полная страница',
  category: 'hero',
  icon: '◈',
  description: 'Modern SaaS glassmorphism лендинг — тёмный фон, размытое стекло, дашборд-мокап, 4 цветовых бренда',
  defaultData: { brand: 'pulse' },
  schema: {
    brand: { type: 'select', label: 'Бренд', default: 'pulse', options: [
      { value: 'pulse',  label: 'Pulse — фиолетовый неон' },
      { value: 'aurora', label: 'Aurora — полярная бирюза' },
      { value: 'ember',  label: 'Ember — тёплый закат' },
      { value: 'nova',   label: 'Nova — роза и лайм' },
    ]},
  },
  tags: ['glassmorphism', 'saas', 'page', 'full', 'landing', 'dark', 'modern'],
})

registerBlock({
  type: 'swiss-landing-page',
  label: 'Swiss Landing · Полная страница',
  category: 'hero',
  icon: '◻',
  description: 'Швейцарский типографический стиль — полная лендинг-страница, 4 цветовых бренда',
  defaultData: { brand: 'bst' },
  schema: {
    brand: { type: 'select', label: 'Бренд', default: 'bst', options: [
      { value: 'bst', label: 'BST Classic' },
      { value: 'nord', label: 'Nord' },
      { value: 'klima', label: 'Klima' },
      { value: 'orbita', label: 'Orbita' },
    ]},
  },
  tags: ['swiss', 'landing', 'page', 'full', 'typography', 'minimal'],
})

registerBlock({
  type: 'brutal-landing-page',
  label: 'Brutal Landing · Полная страница',
  category: 'hero',
  icon: '✚',
  description: 'Нео-брутализм — полная лендинг-страница, 4 цветовых бренда',
  defaultData: { brand: 'acid' },
  schema: {
    brand: { type: 'select', label: 'Бренд', default: 'acid', options: [
      { value: 'acid', label: 'Acid' },
      { value: 'volt', label: 'Volt' },
      { value: 'pop', label: 'Pop' },
      { value: 'noir', label: 'Noir' },
    ]},
  },
  tags: ['brutal', 'brutalism', 'landing', 'page', 'full', 'bold'],
})

registerBlock({
  type: 'serif-landing-page',
  label: 'Serif Landing · Полная страница',
  category: 'hero',
  icon: '❝',
  description: 'Сериф-типографика — полная лендинг-страница, 4 цветовых бренда',
  defaultData: { brand: 'noir' },
  schema: {
    brand: { type: 'select', label: 'Бренд', default: 'noir', options: [
      { value: 'noir', label: 'Noir' },
      { value: 'archi', label: 'Archi' },
      { value: 'tech', label: 'Tech' },
      { value: 'organic', label: 'Organic' },
    ]},
  },
  tags: ['serif', 'landing', 'page', 'full', 'editorial'],
})

registerBlock({
  type: 'cinema-landing-page',
  label: 'Cinema Landing · Полная страница',
  category: 'hero',
  icon: '▶',
  description: 'Кинематографический стиль — полная лендинг-страница, 4 цветовых бренда',
  defaultData: { brand: 'noir' },
  schema: {
    brand: { type: 'select', label: 'Бренд', default: 'noir', options: [
      { value: 'noir', label: 'Noir' },
      { value: 'archi', label: 'Archi' },
      { value: 'tech', label: 'Tech' },
      { value: 'organic', label: 'Organic' },
    ]},
  },
  tags: ['cinema', 'film', 'landing', 'page', 'full', 'dark'],
})

registerBlock({
  type: 'maximal-landing-page',
  label: 'Maximal Landing · Полная страница',
  category: 'hero',
  icon: '✦',
  description: 'Максимализм — полная лендинг-страница, 4 цветовых бренда',
  defaultData: { brand: 'brutal' },
  schema: {
    brand: { type: 'select', label: 'Бренд', default: 'brutal', options: [
      { value: 'brutal', label: 'Brutal' },
      { value: 'luxe', label: 'Luxe' },
      { value: 'acid', label: 'Acid' },
      { value: 'candy', label: 'Candy' },
    ]},
  },
  tags: ['maximal', 'bold', 'landing', 'page', 'full'],
})

registerBlock({
  type: 'y2k-landing-page',
  label: 'Y2K Landing · Полная страница',
  category: 'hero',
  icon: '✿',
  description: 'Y2K / Кибер-ретро — полная лендинг-страница, 4 цветовых бренда',
  defaultData: { brand: 'core' },
  schema: {
    brand: { type: 'select', label: 'Бренд', default: 'core', options: [
      { value: 'core', label: 'Core' },
      { value: 'heat', label: 'Heat' },
      { value: 'vapor', label: 'Vapor' },
      { value: 'void', label: 'Void' },
    ]},
  },
  tags: ['y2k', 'cyber', 'retro', 'landing', 'page', 'full'],
})

registerBlock({
  type: 'organic-landing-page',
  label: 'Organic Landing · Полная страница',
  category: 'hero',
  icon: '❁',
  description: 'Органическая экоэстетика — полная лендинг-страница, 4 цветовых бренда',
  defaultData: { brand: 'botanica' },
  schema: {
    brand: { type: 'select', label: 'Бренд', default: 'botanica', options: [
      { value: 'botanica', label: 'Botanica' },
      { value: 'ocea', label: 'Ocea' },
      { value: 'noir', label: 'Noir' },
      { value: 'pulse', label: 'Pulse' },
    ]},
  },
  tags: ['organic', 'eco', 'nature', 'landing', 'page', 'full'],
})

registerBlock({
  type: 'film-landing-page',
  label: 'Film Landing · Полная страница',
  category: 'hero',
  icon: '◈',
  description: 'Кино/арт-хаус — полная лендинг-страница, 4 цветовых бренда',
  defaultData: { brand: 'noir' },
  schema: {
    brand: { type: 'select', label: 'Бренд', default: 'noir', options: [
      { value: 'noir', label: 'Noir' },
      { value: 'obsidian', label: 'Obsidian' },
      { value: 'crimson', label: 'Crimson' },
      { value: 'volt', label: 'Volt' },
    ]},
  },
  tags: ['film', 'arthouse', 'landing', 'page', 'full', 'dark'],
})

registerBlock({
  type: 'bento-landing-page',
  label: 'Bento Landing · Полная страница',
  category: 'hero',
  icon: '▦',
  description: 'Bento-сетка — полная лендинг-страница, 4 цветовых бренда',
  defaultData: { brand: 'noir' },
  schema: {
    brand: { type: 'select', label: 'Бренд', default: 'noir', options: [
      { value: 'noir', label: 'Noir' },
      { value: 'atelier', label: 'Atelier' },
      { value: 'roast', label: 'Roast' },
      { value: 'volt', label: 'Volt' },
    ]},
  },
  tags: ['bento', 'grid', 'landing', 'page', 'full'],
})

registerBlock({
  type: 'bento-portfolio-page',
  label: 'Bento Portfolio · Полная страница',
  category: 'hero',
  icon: '▦',
  description: 'Bento-сетка портфолио — полная страница студии дизайна',
  defaultData: {},
  schema: {},
  tags: ['bento', 'portfolio', 'page', 'full', 'studio'],
})

registerBlock({
  type: 'cinema-portfolio-page',
  label: 'Cinema Portfolio · Полная страница',
  category: 'hero',
  icon: '▶',
  description: 'Кино-стиль портфолио — полная страница дизайн-студии',
  defaultData: { brand: 'capital' },
  schema: {
    brand: { type: 'select', label: 'Тема', default: 'capital', options: [
      { value: 'capital', label: 'Capital' },
      { value: 'atelier', label: 'Atelier' },
      { value: 'digital', label: 'Digital' },
      { value: 'form', label: 'Form' },
    ]},
  },
  tags: ['cinema', 'portfolio', 'page', 'full', 'studio'],
})

registerBlock({
  type: 'film-portfolio-page',
  label: 'Film Portfolio · Полная страница',
  category: 'hero',
  icon: '◉',
  description: 'Арт-хаус портфолио — полная страница дизайн-студии',
  defaultData: {},
  schema: {},
  tags: ['film', 'arthouse', 'portfolio', 'page', 'full'],
})

registerBlock({
  type: 'minimal-portfolio-page',
  label: 'Minimal Portfolio · Полная страница',
  category: 'hero',
  icon: '○',
  description: 'Минималистичное портфолио — полная страница студии с громким дизайном',
  defaultData: { brand: 'acid' },
  schema: {
    brand: { type: 'select', label: 'Тема', default: 'acid', options: [
      { value: 'acid', label: 'Acid' },
      { value: 'beton', label: 'Beton' },
      { value: 'candy', label: 'Candy' },
      { value: 'archive', label: 'Archive' },
    ]},
  },
  tags: ['minimal', 'loud', 'portfolio', 'page', 'full', 'studio'],
})

registerBlock({
  type: 'organic-portfolio-page',
  label: 'Organic Portfolio · Полная страница',
  category: 'hero',
  icon: '❁',
  description: 'Органическое портфолио — полная страница студии',
  defaultData: {},
  schema: {},
  tags: ['organic', 'eco', 'portfolio', 'page', 'full'],
})

registerBlock({
  type: 'swiss-portfolio-page',
  label: 'Swiss Portfolio · Полная страница',
  category: 'hero',
  icon: '◻',
  description: 'Швейцарский стиль портфолио — студия айдентики и типографики',
  defaultData: { brand: 't1' },
  schema: {
    brand: { type: 'select', label: 'Тема', default: 't1', options: [
      { value: 't1', label: 'Classic' },
      { value: 't2', label: 'Dark' },
      { value: 't3', label: 'Blue' },
      { value: 't4', label: 'Warm' },
    ]},
  },
  tags: ['swiss', 'portfolio', 'page', 'full', 'identity', 'typography'],
})

registerBlock({
  type: 'brutal-portfolio-page',
  label: 'Brutal Portfolio · Полная страница',
  category: 'hero',
  icon: '✚',
  description: 'Нео-брутализм портфолио — бюро громкого дизайна',
  defaultData: { brand: 'acid' },
  schema: {
    brand: { type: 'select', label: 'Тема', default: 'acid', options: [
      { value: 'acid', label: 'Acid' },
      { value: 'pop', label: 'Pop' },
      { value: 'noir', label: 'Noir' },
      { value: 'candy', label: 'Candy' },
    ]},
  },
  tags: ['brutal', 'portfolio', 'page', 'full', 'loud', 'studio'],
})

registerBlock({
  type: 'immersive-portfolio-page',
  label: 'Immersive Portfolio · Полная страница',
  category: 'hero',
  icon: '◈',
  description: 'Иммерсивное портфолио — noir/gold тема с Cormorant Garamond',
  defaultData: { brand: 'noir' },
  schema: {
    brand: { type: 'select', label: 'Тема', default: 'noir', options: [
      { value: 'noir', label: 'Noir' },
      { value: 'pulse', label: 'Pulse' },
      { value: 'crimson', label: 'Crimson' },
      { value: 'mono', label: 'Mono' },
    ]},
  },
  tags: ['immersive', 'noir', 'portfolio', 'page', 'full', 'luxury'],
})

registerBlock({
  type: 'neo-portfolio-page',
  label: 'Neo Portfolio · Полная страница',
  category: 'hero',
  icon: '⬡',
  description: 'Нео-бенто портфолио — 4 темы: Noir, Atelier, Pulse, Garden',
  defaultData: { brand: 'noir' },
  schema: {
    brand: { type: 'select', label: 'Тема', default: 'noir', options: [
      { value: 'noir', label: 'Noir' },
      { value: 'atelier', label: 'Atelier' },
      { value: 'pulse', label: 'Pulse' },
      { value: 'garden', label: 'Garden' },
    ]},
  },
  tags: ['neo', 'bento', 'portfolio', 'page', 'full', 'multi-brand'],
})

registerBlock({
  type: 'swiss-navbar',
  label: 'Swiss · Навигация',
  category: 'hero',
  icon: '═',
  description: 'Швейцарский стиль — верхний хедер с навигацией, часами и переключателем бренда',
  defaultData: { variant: 'bst' },
  schema: SWISS_VARIANT_FIELD,
  tags: ['swiss', 'navbar', 'header', 'navigation'],
})

registerBlock({
  type: 'swiss-hero',
  label: 'Swiss · Hero',
  category: 'hero',
  icon: '▣',
  description: 'Швейцарский стиль — главный экран с типографическим заголовком',
  defaultData: { variant: 'bst' },
  schema: SWISS_VARIANT_FIELD,
  tags: ['swiss', 'hero'],
})

registerBlock({
  type: 'swiss-marquee',
  label: 'Swiss · Бегущая строка',
  category: 'content',
  icon: '⟹',
  description: 'Швейцарский стиль — акцентная бегущая строка с слоганом',
  defaultData: { variant: 'bst' },
  schema: SWISS_VARIANT_FIELD,
  tags: ['swiss', 'marquee', 'ticker'],
})

registerBlock({
  type: 'swiss-manifest',
  label: 'Swiss · Манифест',
  category: 'content',
  icon: '✦',
  description: 'Швейцарский стиль — секция манифеста с большой цитатой',
  defaultData: { variant: 'bst' },
  schema: SWISS_VARIANT_FIELD,
  tags: ['swiss', 'about', 'manifest'],
})

registerBlock({
  type: 'swiss-stats',
  label: 'Swiss · Показатели',
  category: 'content',
  icon: '▦',
  description: 'Швейцарский стиль — сетка статистики с анимированными числами',
  defaultData: { variant: 'bst' },
  schema: SWISS_VARIANT_FIELD,
  tags: ['swiss', 'stats', 'numbers'],
})

registerBlock({
  type: 'swiss-services',
  label: 'Swiss · Услуги',
  category: 'content',
  icon: '≡',
  description: 'Швейцарский стиль — список услуг с hover-эффектом',
  defaultData: { variant: 'bst' },
  schema: SWISS_VARIANT_FIELD,
  tags: ['swiss', 'services', 'list'],
})

registerBlock({
  type: 'swiss-works',
  label: 'Swiss · Работы',
  category: 'content',
  icon: '⊞',
  description: 'Швейцарский стиль — асимметричная сетка проектов',
  defaultData: { variant: 'bst' },
  schema: SWISS_VARIANT_FIELD,
  tags: ['swiss', 'portfolio', 'works', 'grid'],
})

registerBlock({
  type: 'swiss-quote',
  label: 'Swiss · Цитата',
  category: 'content',
  icon: '❝',
  description: 'Швейцарский стиль — крупная цитата с вертикальным акцентом',
  defaultData: { variant: 'bst' },
  schema: SWISS_VARIANT_FIELD,
  tags: ['swiss', 'quote', 'testimonial'],
})

registerBlock({
  type: 'swiss-contact',
  label: 'Swiss · Контакт',
  category: 'content',
  icon: '→',
  description: 'Швейцарский стиль — CTA-секция с email и призывом',
  defaultData: { variant: 'bst' },
  schema: SWISS_VARIANT_FIELD,
  tags: ['swiss', 'contact', 'cta'],
})

registerBlock({
  type: 'swiss-footer',
  label: 'Swiss · Футер',
  category: 'navigation',
  icon: '▬',
  description: 'Швейцарский стиль — футер с вордмарком и JS-движком',
  defaultData: { variant: 'bst' },
  schema: SWISS_VARIANT_FIELD,
  tags: ['swiss', 'footer'],
})

// Quiz runtime — pure logic for server-side normalization and testing
export * from './quiz-runtime'

// Runaway Button — defined here but NOT registered until FC activates it.
// Exported so FB/FC can reference the canonical schema without re-declaring it.
export const RUNAWAY_BUTTON_BLOCK_DEF: BlockDefinition = {
  type: 'runaway-button',
  label: 'Убегающая кнопка',
  category: 'interactive',
  icon: '🏃',
  description: 'Кнопка, которая убегает от курсора — вирусный интерактивный элемент',
  tags: ['runaway', 'fun', 'interactive', 'viral', 'button', 'ubegayushchaya'],
  defaultData: {
    text: 'Нажми меня!',
    surrenderText: 'Ладно, ты победил!',
    surrenderUrl: '#contact',
    evasions: 5,
    background: '#5b6af0',
    color: '#fff',
    borderRadius: 8,
  },
  schema: {
    text: { type: 'text', label: 'Текст кнопки', default: 'Нажми меня!' },
    surrenderText: { type: 'text', label: 'Текст после капитуляции', default: 'Ладно, ты победил!' },
    surrenderUrl: { type: 'url', label: 'URL после капитуляции', default: '#contact' },
    evasions: { type: 'number', label: 'Число уклонений до капитуляции', default: 5, min: 1, max: 20 },
    background: { type: 'color', label: 'Цвет кнопки', default: '#5b6af0' },
    color: { type: 'color', label: 'Цвет текста', default: '#fff' },
    borderRadius: { type: 'number', label: 'Скругление (px)', default: 8, min: 0, max: 50 },
  },
}

// Re-export types needed by consumers
export type { Block, BlockCategory } from '@bst/types'

// ── Shared block renderer (single source of truth for editor canvas + published site) ──
export {
  blockHtml, blockWithAbWrapper, sectionHtml, postprocessHtml, injectTextOverrides, applyCmsVars,
  configureHtmlSanitizer, CART_HTML, INTERACTIVE_JS, ANIMATION_CSS, SECTION_STYLE_VARS,
} from './render/blockHtml'
export type { RenderOptions } from './render/blockHtml'
export { coreBlockHtml, CORE_BLOCKS_CSS, CORE_BLOCK_TYPES } from './render/coreBlocks'
export { studioPageHtml, applyStudioOverrides } from './render/studioPage'
export type {
  BlockData as RenderBlockData, SectionData as RenderSectionData, SectionLayout as RenderSectionLayout,
} from './render/blockHtml'
export {
  buildSiteThemeCss, buildDesignTokensCss, parseThemeConfig, googleFontFamilies, googleFontsHref, hexLuminance, isDesignSystemV2,
} from './render/theme'
export type { SiteThemeConfig } from './render/theme'

// ── AI Studio page: whole page designed by the AI as one HTML document ─────────
registerBlock({
  type: 'ai-studio-page',
  label: 'AI-дизайн · Цельная страница',
  category: 'hero',
  icon: '✦',
  description: 'Страница, которую ИИ спроектировал и сверстал целиком (режим студии). Тексты и фото меняются кликом прямо на холсте.',
  defaultData: { html: '', textOverrides: {}, accent: '', bg: '', text: '' },
  schema: {
    accent: { type: 'color', label: 'Акцентный цвет', default: '' },
    bg: { type: 'color', label: 'Цвет фона', default: '' },
    text: { type: 'color', label: 'Цвет текста', default: '' },
  },
  tags: ['ai', 'studio', 'page', 'full', 'design'],
  hidden: true,
})
